
import React, { useMemo, useState, useEffect, useRef, useCallback, createContext, useContext, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Mismo orden y opciones que el desplegable PLATFORM del Google Sheet
// (+ OTROS para pedidos viejos que ya lo tenían guardado).
const PLATAFORMAS = ["AMAZON","DOORDASH","ESTIPENDIO","GRUBHUB","INSTACART","LYFT","SEGURO","UBER","UBER PERSONAS","VARIAS APP","OTROS"];
const PC = {
  "AMAZON":       { color:"#FF9900", bg:"bg-orange-400", light:"bg-orange-50",  border:"border-orange-300", text:"text-orange-700",  emoji:"📦" },
  "DOORDASH":     { color:"#FF3008", bg:"bg-red-500",    light:"bg-red-50",     border:"border-red-300",    text:"text-red-700",     emoji:"🍔" },
  "ESTIPENDIO":   { color:"#10B981", bg:"bg-emerald-500", light:"bg-emerald-50", border:"border-emerald-300", text:"text-emerald-700", emoji:"💵" },
  "GRUBHUB":      { color:"#F63440", bg:"bg-rose-500",   light:"bg-rose-50",    border:"border-rose-300",   text:"text-rose-700",    emoji:"🥡" },
  "INSTACART":    { color:"#43B02A", bg:"bg-lime-500",   light:"bg-lime-50",    border:"border-lime-300",   text:"text-lime-700",    emoji:"🥕" },
  "LYFT":         { color:"#FF00BF", bg:"bg-pink-500",   light:"bg-pink-50",    border:"border-pink-300",   text:"text-pink-700",    emoji:"🩷" },
  "SEGURO":       { color:"#64748B", bg:"bg-slate-400",  light:"bg-slate-50",   border:"border-slate-300",  text:"text-slate-700",   emoji:"🛡️" },
  "UBER":         { color:"#1d1d1d", bg:"bg-slate-800",  light:"bg-slate-100",  border:"border-slate-400",  text:"text-slate-800",   emoji:"🚗" },
  "UBER PERSONAS":{ color:"#1E3A5F", bg:"bg-sky-800",    light:"bg-sky-50",     border:"border-sky-300",    text:"text-sky-800",     emoji:"🧑‍✈️" },
  "VARIAS APP":   { color:"#7C3AED", bg:"bg-violet-500", light:"bg-violet-50",  border:"border-violet-300", text:"text-violet-700",  emoji:"📱" },
  "OTROS":        { color:"#0EA5E9", bg:"bg-sky-500",    light:"bg-sky-50",     border:"border-sky-300",    text:"text-sky-700",     emoji:"📋" },
};
// Desplegables del Excel — copiados 1:1 de la validación de datos del Sheet
const EXCEL_TYPE = ["ESPERAR","GARANTIA","PUNTO","RENTA","VENTA","CUPO"];              // columna TYPE
const EXCEL_CREDITO_DEBITO = ["CREDITO","DEBITO","NO PAGO"];                            // columna CREDITO DEBITO
// ── FLUJO DEL PEDIDO (en orden real de trabajo) ──────────────────
// ... → Terminado → VERI FACIAL (última verificación) → Entregado.
// Si VERI FACIAL falla: el pedido se manda a Rehacer, se Cancela o se marca
// Intento Fallido (los tres archivan/cierran). Si pasa: Entregado.
const ESTADOS_FLUJO = [
  "Pendiente Revisión",
  "Aceptado",
  "En Producción",
  "Rehacer",
  "Pendiente Aprobación",
  "Esperando Cliente",
  "Background Check",
  "Terminado",
  "VERI FACIAL",
  "Entregado",
];
// Cierres cuando algo falla (todos archivan el pedido)
const ESTADOS_FALLO = [
  "Intento Fallido",
  "Cancelado",
  "NOT APPROVED",
  "DIRTY BACKGROUND",
  "DESACTIVADA",
];
const ESTADOS = [...ESTADOS_FLUJO, ...ESTADOS_FALLO];
// Para pintar los menús de estado con secciones y orden
const ESTADOS_GRUPOS = [
  { label: "Flujo del pedido (en orden)", items: ESTADOS_FLUJO, flujo: true },
  { label: "Si falla / cierre",           items: ESTADOS_FALLO, flujo: false },
];
const PRIORIDADES = ["Alta","Media","Baja"];
// ── Dimensiones para AGRUPAR la cola de trabajo (igual que el inventario) ──
const GRUPOS_TRABAJO = [
  { k:"",               l:"Sin agrupar" },
  { k:"plataforma",     l:"App" },
  { k:"estado",         l:"Fase" },
  { k:"tipoCuenta",     l:"Tipo" },
  { k:"prioridad",      l:"Prioridad" },
  { k:"asignadoNombre", l:"Asignado" },
];
const SC = {
  "Pendiente Revisión":    { bg:"bg-amber-400",   text:"text-amber-900",   emoji:"🔍" },
  "Aceptado":              { bg:"bg-sky-400",      text:"text-sky-900",     emoji:"✅" },
  "En Producción":         { bg:"bg-blue-500",     text:"text-white",       emoji:"⚙️" },
  "Pendiente Aprobación":  { bg:"bg-violet-500",   text:"text-white",       emoji:"⏳" },
  "Background Check":      { bg:"bg-indigo-500",   text:"text-white",       emoji:"🛂" },
  "Terminado":             { bg:"bg-emerald-400",  text:"text-emerald-900", emoji:"🏁" },
  "VERI FACIAL":           { bg:"bg-cyan-500",     text:"text-white",       emoji:"🤳" },
  "Entregado":             { bg:"bg-zinc-200",     text:"text-zinc-500",    emoji:"📦" },
  "Rehacer":               { bg:"bg-rose-500",     text:"text-white",       emoji:"🔄" },
  "Intento Fallido":       { bg:"bg-red-900",      text:"text-white",       emoji:"🚫" },
  "NOT APPROVED":          { bg:"bg-orange-600",   text:"text-white",       emoji:"⛔" },
  "DIRTY BACKGROUND":      { bg:"bg-red-800",      text:"text-white",       emoji:"⚠️" },
  "DESACTIVADA":           { bg:"bg-zinc-700",     text:"text-white",       emoji:"🔌" },
  "Esperando Cliente":     { bg:"bg-amber-300",    text:"text-amber-900",   emoji:"🕐" },
  "Cancelado":             { bg:"bg-slate-400",    text:"text-white",       emoji:"🚫" },
};

function getToken() { return localStorage.getItem("royal_token"); }
function setToken(t) { localStorage.setItem("royal_token", t); }
function clearToken() { localStorage.removeItem("royal_token"); }

async function apiFetch(path, opts={}) {
  const token = getToken();
  const method = (opts.method || "GET").toUpperCase();
  // Las fotos casi nunca cambian: se dejan cachear (con revalidación ETag) para
  // que reabrir un pedido sea instantáneo. El resto de GET van sin caché para
  // que datos como la bandeja siempre se vean frescos.
  const esFotos = method === "GET" && /\/fotos(\?|$)/.test(path);
  let url = path;
  if (method === "GET" && !esFotos) {
    url = path + (path.includes("?") ? "&" : "?") + "_=" + Date.now();
  }
  const res = await fetch(url, {
    ...opts,
    method,
    cache: esFotos ? "default" : "no-store",
    headers: { "Content-Type":"application/json", ...(token ? { Authorization:"Bearer "+token } : {}), ...opts.headers }
  });
  if (res.status === 401) { clearToken(); window.location.reload(); return; }
  return res.json();
}

function days(d) {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / 86400000);
}

// ── COMPRESIÓN DE IMÁGENES ────────────────────────────────────────
// Comprime a buena calidad (nítida para descargar) pero con un tamaño
// manejable para que suba y cargue rápido y no reviente el límite del
// servidor. Solo baja calidad/tamaño de forma adaptativa si hace falta.
function _fileToImg(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read'));
    reader.onload = e => {
      const img = new Image();
      img.onerror = () => reject(new Error('img'));
      img.onload = () => resolve(img);
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
async function compressImageHQ(file, maxW = 2560, quality = 0.9, maxBytes = 1600000) {
  let img;
  try { img = await _fileToImg(file); }
  catch {
    // Si el navegador no pudo procesarla, guarda el archivo tal cual
    return await new Promise(res => { const r = new FileReader(); r.onload = e => res(e.target.result); r.onerror = () => res(null); r.readAsDataURL(file); });
  }
  const render = (w, q) => {
    const scale = Math.min(1, w / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    if (ctx.imageSmoothingQuality !== undefined) ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', q);
  };
  const bytesDe = s => Math.ceil((s.length - (s.indexOf(',') + 1)) * 0.75);
  let w = maxW, q = quality, out = render(w, q);
  // 1º baja la calidad, 2º reduce el tamaño, hasta caber sin perder demasiado
  while (bytesDe(out) > maxBytes && q > 0.55) { q = Math.round((q - 0.08) * 100) / 100; out = render(w, q); }
  while (bytesDe(out) > maxBytes && w > 1400) { w = Math.round(w * 0.85); out = render(w, q); }
  return out;
}
// Peso aproximado (bytes) de una imagen base64
function bytesDeDataUrl(s) {
  if (typeof s !== 'string' || s.indexOf(',') < 0) return 0;
  return Math.ceil((s.length - (s.indexOf(',') + 1)) * 0.75);
}
// Re-optimiza una foto YA guardada (base64). Si ya es liviana, la deja igual.
async function recompressDataUrl(dataUrl, maxW = 2560, quality = 0.9, maxBytes = 1600000) {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image')) return dataUrl;
  if (bytesDeDataUrl(dataUrl) <= maxBytes) return dataUrl; // ya es liviana
  const img = await new Promise((resolve) => { const im = new Image(); im.onerror = () => resolve(null); im.onload = () => resolve(im); im.src = dataUrl; });
  if (!img) return dataUrl;
  const render = (w, q) => {
    const scale = Math.min(1, w / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.width * scale));
    c.height = Math.max(1, Math.round(img.height * scale));
    const ctx = c.getContext('2d');
    if (ctx.imageSmoothingQuality !== undefined) ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', q);
  };
  let w = maxW, q = quality, out = render(w, q);
  while (bytesDeDataUrl(out) > maxBytes && q > 0.55) { q = Math.round((q - 0.08) * 100) / 100; out = render(w, q); }
  while (bytesDeDataUrl(out) > maxBytes && w > 1400) { w = Math.round(w * 0.85); out = render(w, q); }
  return out;
}

// ── PUNTAJE DE URGENCIA ───────────────────────────────────────────
// Menor = más urgente. Combina los días que faltan para la entrega con
// la prioridad, de forma CONTINUA: si cambias la fecha de entrega, el
// puntaje cambia y el pedido se reordena en la lista de prioridad.
//   - Vencido / hoy → muy negativo (sube hasta arriba)
//   - Sin fecha de entrega → va al fondo, ordenado solo por prioridad
//   - Prioridad Alta resta 2 "días", Baja suma 2 (un empujón, no manda sobre una fecha cercana)
function urgencyScore(o) {
  const prioAjuste = ({ Alta: -2, Media: 0, Baja: 2 })[o.prioridad] ?? 0;
  const d = days(o.entrega);
  if (d === null) return 100000 + prioAjuste;   // sin fecha: al fondo
  return d + prioAjuste;
}

// Pedido entregado hace >6 horas → se trata como "archivado" y sale de la vista activa
// Intento Fallido → archivado de inmediato (sale de activos)
// Cancelado → archivado de inmediato (sale de activos, reactivable)
// NOT APPROVED / DESACTIVADA → archivados de inmediato (terminales negativos, reactivables)
function isArchived(order) {
  if (order.estado === "Intento Fallido") return true;
  if (order.estado === "Cancelado") return true;
  if (order.estado === "NOT APPROVED") return true;
  if (order.estado === "DIRTY BACKGROUND") return true;
  if (order.estado === "DESACTIVADA") return true;
  if (order.estado !== "Entregado") return false;
  if (!order.completedAt) return false;
  const hoursElapsed = (Date.now() - new Date(order.completedAt).getTime()) / 3_600_000;
  return hoursElapsed >= 6;
}




// ── RENTAS MODAL ──────────────────────────────────────────────────
function RentasModal({ order, onClose, onUpdate }) {
  const [rentas, setRentas] = useState([]);
  const [config, setConfig] = useState({ tipoCobro: order.tipoCobro||'simple', montoSemanal: order.montoSemanal||'', totalCredito: order.totalCredito||'', fechas: order.fechasCredito ? JSON.parse(order.fechasCredito||'[]') : ['','','',''] });
  const [form, setForm] = useState({ monto:'', metodo:'Zelle', fechaPago:new Date().toISOString().split('T')[0], notas:'' });
  const [saving, setSaving] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [tab, setTab] = useState('config');
  const loadRentas = () => apiFetch('/api/pedidos/'+order.id+'/rentas').then(d=>setRentas(d||[]));
  useEffect(()=>{ loadRentas(); },[]);

  const saveConfig = async () => {
    setSavingCfg(true);
    const r = await apiFetch('/api/pedidos/'+order.id+'/renta-config',{method:'PUT',body:JSON.stringify({
      tipoCobro: config.tipoCobro,
      montoSemanal: config.montoSemanal,
      totalCredito: config.totalCredito,
      fechasCredito: JSON.stringify(config.fechas)
    })});
    // El servidor mantiene el PRECIO/ABONADO/SALDO del Excel en sincronía con
    // este modal (p. ej. un crédito de $200 pone Precio=$200 en el Sheet).
    onUpdate({
      tipoCobro: config.tipoCobro,
      ...(r && r.precio  != null ? { precio:  String(r.precio)  } : {}),
      ...(r && r.abonado != null ? { abonado: String(r.abonado) } : {}),
      ...(r && r.saldo   != null ? { saldo:   String(r.saldo)   } : {}),
    });
    setSavingCfg(false);
    setTab('registrar');
  };

  const submitPago = async () => {
    if (!form.monto) return;
    setSaving(true);
    const r = await apiFetch('/api/pedidos/'+order.id+'/rentas',{method:'POST',body:JSON.stringify({...form,proximoPago:''})});
    setForm({monto:'',metodo:'Zelle',fechaPago:new Date().toISOString().split('T')[0],notas:''});
    await loadRentas();
    // ARREGLO: antes esto ponía siempre 'pagado', así que un abono parcial
    // dejaba el pedido marcado como saldado. Ahora usa el estado real que
    // calcula el servidor comparando lo pagado contra el total del pedido.
    // Además refleja el ABONADO/SALDO recalculados (mismos números que el Excel).
    onUpdate({
      estadoPago: (r && r.estadoPago) ? r.estadoPago : 'pagado',
      ...(r && r.abonado != null ? { abonado: String(r.abonado) } : {}),
      ...(r && r.saldo   != null ? { saldo:   String(r.saldo)   } : {}),
    });
    setSaving(false); setTab('historial');
  };
  const deletePago = async (id) => { await apiFetch('/api/rentas/'+id,{method:'DELETE'}); loadRentas(); };
  const totalPagado = rentas.reduce((s,r)=>s+r.monto,0);
  const totalCredito = parseFloat(config.totalCredito)||0;
  const faltaCredito = totalCredito ? Math.max(0, totalCredito - totalPagado) : null;

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{background:'rgba(0,0,0,0.75)'}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <motion.div className="w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] shadow-2xl flex flex-col" style={{maxHeight:'88vh'}} initial={{y:80,opacity:0}} animate={{y:0,opacity:1}} exit={{y:80,opacity:0}} transition={{type:'spring',damping:22}}>
        <div className="bg-slate-900 px-5 py-4 sm:rounded-t-[28px] rounded-t-[28px] shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-white font-black text-base">💰 {order.cliente}</div>
              <div className="text-slate-400 text-xs">{order.plataforma} · {rentas.length} pagos</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white text-xl w-8 h-8 flex items-center justify-center">✕</button>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div className="rounded-xl bg-slate-800 px-2 py-2 text-center"><div className="text-emerald-400 font-black">${totalPagado.toFixed(0)}</div><div className="text-slate-500 text-xs">Pagado</div></div>
            {config.tipoCobro==='credito' && totalCredito>0 ? (<>
              <div className="rounded-xl bg-slate-800 px-2 py-2 text-center"><div className="text-blue-400 font-black">${totalCredito.toFixed(0)}</div><div className="text-slate-500 text-xs">Total</div></div>
              <div className={"rounded-xl px-2 py-2 text-center "+(faltaCredito===0?'bg-emerald-800':'bg-red-900')}><div className={(faltaCredito===0?'text-emerald-300':'text-red-300')+' font-black'}>{faltaCredito===0?'✅ Listo':'$'+faltaCredito.toFixed(0)}</div><div className="text-slate-500 text-xs">Falta</div></div>
            </>) : config.tipoCobro==='semanal' && config.montoSemanal ? (<>
              <div className="rounded-xl bg-slate-800 px-2 py-2 text-center"><div className="text-amber-400 font-black">${config.montoSemanal}</div><div className="text-slate-500 text-xs">Semanal</div></div>
              <div className="rounded-xl bg-slate-800 px-2 py-2 text-center"><div className="text-white font-black">{rentas.length}</div><div className="text-slate-500 text-xs">Semanas</div></div>
            </>) : (
              <div className="col-span-2 rounded-xl bg-slate-800 px-2 py-2 text-center"><div className="text-slate-400 text-xs">Configura el tipo de cobro →</div></div>
            )}
          </div>
        </div>

        <div className="flex border-b border-slate-100 shrink-0 overflow-x-auto">
          {[['config','⚙️ Config'],['registrar','+ Pago'],['historial','📋 ('+rentas.length+')']].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} className={"flex-1 py-2.5 text-sm font-bold transition-all whitespace-nowrap px-3 "+(tab===t?'text-slate-900 border-b-2 border-slate-900':'text-slate-400')}>{l}</button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4">
          {tab==='config' && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 block">Tipo de cobro</label>
                <div className="grid grid-cols-3 gap-2">
                  {[['simple','💵 Simple'],['semanal','📅 Semanal'],['credito','🤝 Crédito']].map(([v,l])=>(
                    <button key={v} onClick={()=>setConfig({...config,tipoCobro:v})} className={"py-2.5 rounded-xl text-xs font-bold transition-all "+(config.tipoCobro===v?'bg-slate-900 text-white':'bg-slate-100 text-slate-600 hover:bg-slate-200')}>{l}</button>
                  ))}
                </div>
              </div>

              {config.tipoCobro==='semanal' && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Monto semanal $</label>
                  <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                    <input type="number" className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-3 text-sm outline-none focus:border-slate-400" placeholder="Ej: 25.00" value={config.montoSemanal} onChange={e=>setConfig({...config,montoSemanal:e.target.value})}/></div>
                </div>
              )}

              {config.tipoCobro==='credito' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Total a crédito $</label>
                    <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input type="number" className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-3 text-sm outline-none focus:border-slate-400" placeholder="Ej: 200.00" value={config.totalCredito} onChange={e=>setConfig({...config,totalCredito:e.target.value})}/></div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Fechas de pago programadas</label>
                    <div className="space-y-2">
                      {config.fechas.map((f,i)=>(
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 w-16 shrink-0 font-bold">Pago {i+1}</span>
                          <input type="date" className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400" value={f} onChange={e=>{ const a=[...config.fechas]; a[i]=e.target.value; setConfig({...config,fechas:a}); }}/>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <button onClick={saveConfig} disabled={savingCfg} className="w-full py-3.5 rounded-xl bg-slate-900 text-white font-black text-sm disabled:opacity-40 active:scale-95 transition-all">{savingCfg?'Guardando...':'💾 Guardar configuración'}</button>
            </div>
          )}

          {tab==='registrar' && (
            <div className="space-y-3">
              {config.tipoCobro==='semanal' && config.montoSemanal && (
                <button onClick={()=>setForm({...form,monto:config.montoSemanal,notas:'Pago semanal'})} className="w-full py-2.5 rounded-xl bg-amber-50 border-2 border-amber-200 text-amber-800 text-sm font-bold">⚡ Pago semanal ${config.montoSemanal}</button>
              )}
              {config.tipoCobro==='credito' && faltaCredito!==null && faltaCredito>0 && (
                <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
                  <div className="font-black">Crédito: ${totalPagado.toFixed(0)} / ${totalCredito.toFixed(0)} pagado</div>
                  <div className="text-xs mt-0.5">Faltan <strong>${faltaCredito.toFixed(0)}</strong> por cobrar</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Monto *</label>
                  <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                    <input type="number" className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-3 text-sm outline-none focus:border-slate-400" placeholder="0.00" value={form.monto} onChange={e=>setForm({...form,monto:e.target.value})}/></div></div>
                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Método</label>
                  <select className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" value={form.metodo} onChange={e=>setForm({...form,metodo:e.target.value})}>
                    {['Zelle','Cash','CashApp','PayPal','Venmo','Otro'].map(m=><option key={m}>{m}</option>)}</select></div>
              </div>
              <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Fecha</label>
                <input type="date" className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" value={form.fechaPago} onChange={e=>setForm({...form,fechaPago:e.target.value})}/></div>
              <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Nota (opcional)</label>
                <input className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" placeholder="Ej: semana 3, primer abono..." value={form.notas} onChange={e=>setForm({...form,notas:e.target.value})}/></div>
              <button onClick={submitPago} disabled={saving||!form.monto} className="w-full py-3.5 rounded-xl bg-slate-900 text-white font-black text-sm disabled:opacity-40 active:scale-95 transition-all">{saving?'Guardando...':'💾 Registrar pago'}</button>
            </div>
          )}

          {tab==='historial' && (
            <div className="space-y-2">
              {rentas.length===0 && <div className="text-center text-slate-400 text-sm py-8">Sin pagos aún</div>}
              {rentas.map(r=>(
                <div key={r.id} className="rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="text-emerald-600 font-black text-base">${r.monto}</span><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">{r.metodo}</span></div>
                    <div className="text-xs text-slate-400 mt-0.5">{new Date(r.fechaPago+'T12:00').toLocaleDateString('es-MX',{day:'numeric',month:'short',year:'numeric'})}</div>
                    {r.notas && <div className="text-xs text-slate-500 italic">{r.notas}</div>}
                  </div>
                  <button onClick={()=>deletePago(r.id)} className="text-red-300 hover:text-red-500 text-lg shrink-0">🗑</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── FINANCE PANEL ─────────────────────────────────────────────────
// NOTA: onOpenRentas se usa abajo pero no estaba declarado como prop. Eso es un
// `no-undef` de ESLint, y en Docker (CI=true) un no-undef TUMBA EL BUILD.
// Declararlo aquí es el arreglo mínimo: si nadie lo pasa, llega undefined y el
// botón simplemente no se dibuja, que es justo lo que el código ya esperaba.
function FinancePanel({ onClose, onOpenRentas }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('resumen');
  const [extraForm, setExtraForm] = useState({ monto:'', metodo:'Zelle', descripcion:'', fechaPago:new Date().toISOString().split('T')[0] });
  const [extraSaving, setExtraSaving] = useState(false);
  const [extras, setExtras] = useState([]);
  const loadExtras = () => apiFetch('/api/pagos-extras').then(d=>setExtras(d||[]));
  const submitExtra = async () => {
    if (!extraForm.monto || !extraForm.descripcion.trim()) return;
    setExtraSaving(true);
    await apiFetch('/api/pagos-extras',{method:'POST',body:JSON.stringify(extraForm)});
    setExtraForm({monto:'',metodo:'Zelle',descripcion:'',fechaPago:new Date().toISOString().split('T')[0]});
    loadExtras();
    setExtraSaving(false);
  };
  const deleteExtra = async (id) => { await apiFetch('/api/pagos-extras/'+id,{method:'DELETE'}); loadExtras(); };
  const [cartera, setCartera] = useState([]);
  const [abonoForm, setAbonoForm] = useState({});
  const [abonoSaving, setAbonoSaving] = useState(null);
  const loadCartera = () => apiFetch('/api/cartera').then(d=>setCartera(d||[]));
  const handleAbonoCartera = async (entrada) => {
    const abono = parseFloat(abonoForm[entrada.id]||0);
    if (!abono) return;
    setAbonoSaving(entrada.id);
    const r = await apiFetch('/api/cartera/'+entrada.id+'/abonar', {method:'PUT', body:JSON.stringify({abono, metodo:'Zelle'})});
    // Registrar también como pago en rentas del pedido
    await apiFetch('/api/pedidos/'+entrada.pedidoId+'/rentas', {method:'POST', body:JSON.stringify({
      monto: abono, metodo:'Zelle', fechaPago: new Date().toISOString().split('T')[0], notas:'Abono cartera', proximoPago:''
    })});
    setAbonoSaving(null);
    setAbonoForm(prev=>({...prev,[entrada.id]:''}));
    loadCartera();
  };
  const deleteCartera = async (id) => {
    if (!window.confirm('¿Eliminar esta deuda de la cartera?')) return;
    await apiFetch('/api/cartera/'+id, {method:'DELETE'});
    loadCartera();
  };
  const [semanales, setSemanales] = useState([]);
  const [creditos, setCreditos] = useState([]);
  const [rentasDirectas, setRentasDirectas] = useState([]);
  const [showNuevaRenta, setShowNuevaRenta] = useState(false);
  const [editandoRenta, setEditandoRenta] = useState(null);
  const [rentaForm, setRentaForm] = useState({ cliente:'', plataforma:'OTROS', montoSemanal:'', notas:'' });
  const [savingRenta, setSavingRenta] = useState(false);
  const loadRentasDirectas = () => apiFetch('/api/rentas-directas').then(d=>setRentasDirectas(d||[]));
  const saveRenta = async () => {
    if (!rentaForm.cliente || !rentaForm.montoSemanal) return;
    setSavingRenta(true);
    if (editandoRenta) {
      await apiFetch('/api/rentas-directas/'+editandoRenta, {method:'PUT', body:JSON.stringify(rentaForm)});
    } else {
      await apiFetch('/api/rentas-directas', {method:'POST', body:JSON.stringify(rentaForm)});
    }
    setRentaForm({cliente:'',plataforma:'OTROS',montoSemanal:'',notas:''});
    setShowNuevaRenta(false); setEditandoRenta(null);
    loadRentasDirectas();
    setSavingRenta(false);
  };
  const deleteRentaDirecta = async (id) => {
    if (!window.confirm('¿Eliminar esta renta y todo su historial de pagos?')) return;
    await apiFetch('/api/rentas-directas/'+id, {method:'DELETE'});
    loadRentasDirectas();
  };
  useEffect(()=>{
    apiFetch('/api/finanzas').then(d=>setData(d));
    loadExtras();
    apiFetch('/api/pedidos-semanales').then(d=>setSemanales(d||[]));
    apiFetch('/api/pedidos-credito').then(d=>setCreditos(d||[]));
    loadRentasDirectas();
    loadCartera();
  },[]);
  const fmtDate = (d) => d ? new Date(d+'T12:00').toLocaleDateString('es-MX',{day:'numeric',month:'short',year:'numeric'}) : '—';
  if (!data) return (
    <motion.div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
      <div className="text-white font-black text-xl animate-pulse">Cargando finanzas...</div>
    </motion.div>
  );
  const ECFG = {'al-corriente':{bg:'bg-emerald-100',text:'text-emerald-700',label:'✅ Al corriente'},'debe':{bg:'bg-red-100',text:'text-red-700',label:'🚨 Debe'},'pendiente':{bg:'bg-slate-100',text:'text-slate-600',label:'⏳ Pendiente'}};
  return (
    <motion.div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
      <motion.div className="w-full max-w-lg bg-white rounded-[28px] overflow-hidden shadow-2xl flex flex-col" style={{maxHeight:'90vh'}} initial={{scale:0.9,y:20}} animate={{scale:1,y:0}}>
        <div className="bg-slate-900 px-6 py-5 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="text-white font-black text-xl">💰 Finanzas</div>
            <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[{label:'Total',val:'$'+data.totalRecaudado.toFixed(0),color:'text-emerald-400'},{label:'Este mes',val:'$'+data.pagosMes.toFixed(0),color:'text-blue-400'},{label:'Hoy',val:'$'+data.pagosHoy.toFixed(0),color:'text-amber-400'}].map(s=>(
              <div key={s.label} className="rounded-xl bg-slate-800 px-3 py-3 text-center"><div className={s.color+" font-black text-xl"}>{s.val}</div><div className="text-slate-500 text-xs mt-0.5">{s.label}</div></div>
            ))}
          </div>
        </div>
        <div className="flex border-b border-slate-100 overflow-x-auto shrink-0">
          {[['resumen','📊 Resumen'],['cartera','💳 Cartera'],['rentas','🏠 Rentas'],['semanales','📅 Semanales'],['creditos','🤝 Créditos'],['historial','📋 Historial'],['extras','➕ Extra']].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} className={"px-4 py-2.5 text-xs font-bold whitespace-nowrap transition-all "+(tab===t?'text-slate-900 border-b-2 border-slate-900':'text-slate-400')}>{l}</button>
          ))}
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {tab==='resumen' && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                <div className="font-black text-amber-800 mb-2">⏰ Vencen pronto ({data.vencenProximo.length || semanales.filter(s=>{
                  if(!s.ultimoPago) return true;
                  const dias = Math.ceil((new Date()-new Date(s.ultimoPago+'T12:00'))/86400000);
                  return dias>=6;
                }).length})</div>
                {data.vencenProximo.length===0 && semanales.filter(s=>{
                  if(!s.ultimoPago) return true;
                  const dias = Math.ceil((new Date()-new Date(s.ultimoPago+'T12:00'))/86400000);
                  return dias>=6;
                }).length===0 && <div className="text-amber-600 text-sm">Nadie vence esta semana 🎉</div>}
                {data.vencenProximo.map(v=>(
                  <div key={v.id} className="flex items-center justify-between py-1.5 border-b border-amber-200 last:border-0">
                    <div><div className="font-bold text-slate-800 text-sm">{v.cliente}</div><div className="text-xs text-slate-500">{v.plataforma}</div></div>
                    <div className="text-right"><div className="text-amber-700 font-black text-sm">${v.monto}</div><div className="text-xs text-slate-500">{fmtDate(v.proximoPago)}</div></div>
                  </div>
                ))}
                {data.vencenProximo.length===0 && semanales.filter(s=>{
                  if(!s.ultimoPago) return true;
                  const dias = Math.ceil((new Date()-new Date(s.ultimoPago+'T12:00'))/86400000);
                  return dias>=6;
                }).map(s=>{
                  const dias = s.ultimoPago ? Math.ceil((new Date()-new Date(s.ultimoPago+'T12:00'))/86400000) : null;
                  return (
                    <div key={s.id} className="flex items-center justify-between py-1.5 border-b border-amber-200 last:border-0">
                      <div><div className="font-bold text-slate-800 text-sm">{s.cliente}</div><div className="text-xs text-slate-500">{s.plataforma} · semanal</div></div>
                      <div className="text-right"><div className="text-amber-700 font-black text-sm">${s.montoSemanal}/sem</div><div className="text-xs text-slate-500">{dias?`${dias}d sin pago`:'Sin pagos'}</div></div>
                    </div>
                  );
                })}
              </div>
              <div className="rounded-2xl bg-red-50 border border-red-200 p-4">
                <div className="font-black text-red-800 mb-2">🚨 Deben ({data.deben.filter(d=>d.estadoPago==='debe').length})</div>
                {data.deben.filter(d=>d.estadoPago==='debe').length===0 && <div className="text-red-500 text-sm">Todos al corriente 🎉</div>}
                {data.deben.filter(d=>d.estadoPago==='debe').map(c=>(
                  <div key={c.id} className="flex items-center justify-between py-1.5 border-b border-red-200 last:border-0">
                    <div><div className="font-bold text-slate-800 text-sm">{c.cliente}</div><div className="text-xs text-slate-500">{c.plataforma}</div></div>
                    <div className="text-right"><div className="text-red-600 font-black text-sm">${c.totalPagado} total</div><div className="text-xs text-slate-500">Último: {fmtDate(c.ultimoPago)}</div></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab==='rentas' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-black text-slate-500 uppercase tracking-wide">Rentas activas ({rentasDirectas.length})</div>
                <button onClick={()=>{ setRentaForm({cliente:'',plataforma:'OTROS',montoSemanal:'',notas:''}); setEditandoRenta(null); setShowNuevaRenta(v=>!v); }}
                  className="px-3 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-black active:scale-95 transition-all">
                  {showNuevaRenta?'✕ Cancelar':'+ Nueva renta'}
                </button>
              </div>
              {showNuevaRenta && (
                <div className="rounded-2xl border-2 border-slate-200 p-4 space-y-3">
                  <div className="text-sm font-black text-slate-800">{editandoRenta?'✏️ Editar renta':'➕ Nueva renta'}</div>
                  <input className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="Nombre del cliente *" value={rentaForm.cliente} onChange={e=>setRentaForm({...rentaForm,cliente:e.target.value})}/>
                  <div className="grid grid-cols-2 gap-3">
                    <select className="rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none" value={rentaForm.plataforma} onChange={e=>setRentaForm({...rentaForm,plataforma:e.target.value})}>
                      {['AMAZON','DOORDASH','UBER','LYFT','VARIAS APP','OTROS'].map(p=><option key={p}>{p}</option>)}
                    </select>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">$</span>
                      <input type="number" className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="Monto/sem *" value={rentaForm.montoSemanal} onChange={e=>setRentaForm({...rentaForm,montoSemanal:e.target.value})}/>
                    </div>
                  </div>
                  <input className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="Notas (opcional)" value={rentaForm.notas} onChange={e=>setRentaForm({...rentaForm,notas:e.target.value})}/>
                  <button onClick={saveRenta} disabled={savingRenta||!rentaForm.cliente||!rentaForm.montoSemanal}
                    className="w-full py-3 rounded-xl bg-slate-900 text-white font-black text-sm disabled:opacity-40 active:scale-95 transition-all">
                    {savingRenta?'Guardando...':editandoRenta?'💾 Guardar cambios':'💾 Crear renta'}
                  </button>
                </div>
              )}
              {rentasDirectas.length===0 && !showNuevaRenta && <div className="text-center text-slate-400 text-sm py-8">Sin rentas aún — crea una arriba</div>}
              {rentasDirectas.map(c=>{
                const dias = c.ultimoPago ? Math.floor((Date.now()-new Date(c.ultimoPago+'T12:00').getTime())/86_400_000) : null;
                const overdue = dias===null||dias>=7;
                const pronto = !overdue && dias>=5;
                return (
                  <div key={c.id} className={"rounded-xl border-2 px-4 py-3 space-y-2 "+(overdue?"border-red-300 bg-red-50":pronto?"border-amber-300 bg-amber-50":"border-slate-200 bg-white")}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-black text-slate-900 text-sm">{c.cliente}</div>
                        <div className="text-xs text-slate-500">{c.plataforma} · ${c.montoSemanal}/sem · cobrado: <strong>${c.totalPagado}</strong></div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {onOpenRentas && <button onClick={()=>{onClose();setTimeout(()=>onOpenRentas(c.id),200);}} className="px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-black active:scale-95 transition-all">💰 Cobrar</button>}
                        <button onClick={()=>{ setRentaForm({cliente:c.cliente,plataforma:c.plataforma,montoSemanal:c.montoSemanal,notas:c.notas||''}); setEditandoRenta(c.id); setShowNuevaRenta(true); }} className="px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-black active:scale-95 transition-all">✏️</button>
                        <button onClick={()=>deleteRentaDirecta(c.id)} className="px-2.5 py-1.5 rounded-lg bg-red-50 text-red-400 text-xs font-black active:scale-95 transition-all">🗑</button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={"text-xs font-black px-2 py-0.5 rounded-full "+(overdue?"bg-red-200 text-red-800":pronto?"bg-amber-200 text-amber-800":"bg-slate-100 text-slate-500")}>
                        {dias===null?"⚠️ Sin pagos":overdue?`🚨 ${dias}d sin pagar`:pronto?`⏰ ${dias}d sin pagar`:`✅ ${dias}d sin pagar`}
                      </span>
                      {c.ultimoPago && <div className="text-xs text-slate-400">Último: {fmtDate(c.ultimoPago)}</div>}
                    </div>
                    {c.notas && <div className="text-xs text-slate-500 italic">{c.notas}</div>}
                  </div>
                );
              })}
            </div>
          )}
          {tab==='semanales' && (
            <div className="space-y-2">
              {semanales.length===0 && <div className="text-center text-slate-400 text-sm py-8">Sin clientes semanales aún</div>}
              {[...semanales].sort((a,b)=>{
                const da = a.ultimoPago ? Math.floor((Date.now()-new Date(a.ultimoPago+'T12:00').getTime())/86_400_000) : 999;
                const db = b.ultimoPago ? Math.floor((Date.now()-new Date(b.ultimoPago+'T12:00').getTime())/86_400_000) : 999;
                return db-da;
              }).map(c=>{
                const dias = c.ultimoPago ? Math.floor((Date.now()-new Date(c.ultimoPago+'T12:00').getTime())/86_400_000) : null;
                const overdue = dias===null||dias>=7;
                const pronto  = !overdue && dias>=5;
                return (
                <div key={c.id} className={"rounded-xl border-2 px-4 py-3 "+(overdue?"border-red-300 bg-red-50":pronto?"border-amber-300 bg-amber-50":"border-slate-200 bg-white")}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-black text-slate-900 text-sm">{c.cliente}</div>
                    <span className="text-amber-700 font-black">${c.montoSemanal}/sem</span>
                  </div>
                  <div className="text-xs text-slate-500 mb-1.5">{c.plataforma} · {c.totalPagos} pagos · cobrado: <strong>${c.totalPagado}</strong></div>
                  <div className="flex items-center justify-between">
                    <span className={"text-xs font-black px-2 py-0.5 rounded-full "+(overdue?"bg-red-200 text-red-800":pronto?"bg-amber-200 text-amber-800":"bg-slate-100 text-slate-500")}>
                      {dias===null ? "⚠️ Sin pagos" : overdue ? `🚨 ${dias}d sin pagar` : pronto ? `⏰ ${dias}d sin pagar` : `✅ ${dias}d sin pagar`}
                    </span>
                    {c.ultimoPago && <div className="text-xs text-slate-400">Último: {fmtDate(c.ultimoPago)}</div>}
                  </div>
                </div>
                );
              })}
            </div>
          )}
          {tab==='creditos' && (
            <div className="space-y-2">
              {creditos.length===0 && <div className="text-center text-slate-400 text-sm py-8">Sin ventas a crédito aún</div>}
              {creditos.map(c=>{
                const pagado = parseFloat(c.totalPagado)||0;
                const total = parseFloat(c.totalCredito)||0;
                const falta = Math.max(0, total - pagado);
                const pct = total>0 ? Math.min(100, Math.round(pagado/total*100)) : 0;
                const fechas = JSON.parse(c.fechasCredito||'[]').filter(Boolean);
                return (
                  <div key={c.id} className="rounded-xl border-2 border-blue-200 bg-blue-50 px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="font-black text-slate-900 text-sm">{c.cliente}</div>
                      <span className={falta===0?"text-emerald-600 font-black text-sm":"text-red-600 font-black text-sm"}>{falta===0?'✅ Saldado':'$'+falta.toFixed(0)+' falta'}</span>
                    </div>
                    <div className="text-xs text-slate-500">{c.plataforma} · ${pagado.toFixed(0)} / ${total.toFixed(0)} pagado</div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all" style={{width:pct+'%'}}/>
                    </div>
                    {fechas.length>0 && (
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {fechas.map((f,i)=>(
                          <div key={i} className="text-xs bg-white rounded-lg px-2 py-1 text-slate-600 border border-blue-200">
                            <span className="font-bold text-blue-700">Pago {i+1}:</span> {fmtDate(f)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {tab==='clientes' && (
            <div className="space-y-2">
              {data.deben.length===0 && <div className="text-center text-slate-400 text-sm py-8">Sin clientes recurrentes aún</div>}
              {data.deben.map(c=>{ const ec=ECFG[c.estadoPago]||ECFG['pendiente']; return (
                <div key={c.id} className="rounded-xl border border-slate-200 px-4 py-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold text-slate-900 text-sm">{c.cliente}</div>
                    <span className={"px-2.5 py-0.5 rounded-full text-xs font-bold "+ec.bg+" "+ec.text}>{ec.label}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{c.plataforma}</span><span>Total: <strong className="text-slate-700">${c.totalPagado}</strong></span>
                  </div>
                  {c.proximoPago && <div className="text-xs text-amber-600 font-bold mt-1">Próximo: {fmtDate(c.proximoPago)}</div>}
                </div>
              );})}
            </div>
          )}
          {tab==='historial' && (
            <div className="space-y-2">
              {data.historial.length===0 && <div className="text-center text-slate-400 text-sm py-8">Sin pagos aún</div>}
              {data.historial.map(r=>(
                <div key={r.id} className="rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between">
                  <div><div className="font-bold text-slate-900 text-sm">{r.cliente}</div><div className="text-xs text-slate-400">{r.plataforma} · {r.metodo} · {fmtDate(r.fechaPago)}</div>{r.notas&&<div className="text-xs text-slate-400 italic">{r.notas}</div>}</div>
                  <div className="text-emerald-600 font-black text-lg">${r.monto}</div>
                </div>
              ))}
            </div>
          )}
          {tab==='cartera' && (
            <div className="space-y-3">
              {/* Resumen de cartera */}
              {cartera.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">💳</div>
                  <div className="font-black text-slate-400 text-base">Sin deudas en cartera</div>
                  <div className="text-slate-400 text-sm mt-1">Las deudas aparecen aquí cuando entregas sin cobrar completo</div>
                </div>
              ) : (
                <>
                  {/* Totales */}
                  {(()=>{
                    const totalDeuda = cartera.reduce((s,c)=>s+parseFloat(c.montoTotal||0),0);
                    const totalPagado = cartera.reduce((s,c)=>s+parseFloat(c.montoPagado||0),0);
                    const totalFalta = Math.max(0, totalDeuda - totalPagado);
                    const pendientes = cartera.filter(c=>c.estado!=='saldado').length;
                    return (
                      <div className="grid grid-cols-3 gap-2">
                        <div className="rounded-xl bg-slate-800 p-3 text-center">
                          <div className="text-emerald-400 font-black text-lg">${totalPagado.toFixed(0)}</div>
                          <div className="text-slate-500 text-xs">Cobrado</div>
                        </div>
                        <div className="rounded-xl bg-red-900 p-3 text-center">
                          <div className="text-red-300 font-black text-lg">${totalFalta.toFixed(0)}</div>
                          <div className="text-slate-500 text-xs">Pendiente</div>
                        </div>
                        <div className="rounded-xl bg-slate-800 p-3 text-center">
                          <div className="text-amber-400 font-black text-lg">{pendientes}</div>
                          <div className="text-slate-500 text-xs">Deudores</div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Lista de deudas */}
                  {[...cartera].sort((a,b)=>{
                    if(a.estado==='saldado' && b.estado!=='saldado') return 1;
                    if(a.estado!=='saldado' && b.estado==='saldado') return -1;
                    return new Date(b.createdAt)-new Date(a.createdAt);
                  }).map(c=>{
                    const falta = Math.max(0, parseFloat(c.montoTotal)-parseFloat(c.montoPagado));
                    const pct = parseFloat(c.montoTotal)>0 ? Math.min(100, Math.round(parseFloat(c.montoPagado)/parseFloat(c.montoTotal)*100)) : 0;
                    const saldado = c.estado === 'saldado';
                    return (
                      <div key={c.id} className={"rounded-2xl border-2 p-4 space-y-3 "+(saldado?"border-emerald-200 bg-emerald-50":"border-red-200 bg-white")}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-black text-slate-900 text-base truncate">{c.cliente}</div>
                            <div className="text-xs text-slate-500">{(PC[c.plataforma]||PC['OTROS']).emoji} {c.plataforma}</div>
                            {c.notas && <div className="text-xs text-slate-400 italic mt-0.5">{c.notas}</div>}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <div className={"text-right"}>
                              {saldado ? (
                                <div className="px-2.5 py-1 rounded-full bg-emerald-200 text-emerald-800 text-xs font-black">✅ Saldado</div>
                              ) : (
                                <div className="text-red-600 font-black text-xl">${falta.toFixed(0)}</div>
                              )}
                            </div>
                            <button onClick={()=>deleteCartera(c.id)} className="w-7 h-7 rounded-lg bg-red-50 text-red-300 hover:text-red-500 text-sm flex items-center justify-center shrink-0">🗑</button>
                          </div>
                        </div>
                        {/* Barra de progreso */}
                        <div>
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Pagado: <strong>${parseFloat(c.montoPagado).toFixed(0)}</strong></span>
                            <span>Total: <strong>${parseFloat(c.montoTotal).toFixed(0)}</strong></span>
                          </div>
                          <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={"h-full rounded-full transition-all "+(saldado?"bg-emerald-500":"bg-red-500")} style={{width:pct+'%'}}/>
                          </div>
                        </div>
                        {/* Abono */}
                        {!saldado && (
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                              <input type="number" className="w-full rounded-xl border-2 border-red-200 pl-6 pr-3 py-2 text-sm outline-none focus:border-red-400 font-bold" placeholder="Abono..." value={abonoForm[c.id]||''} onChange={e=>setAbonoForm(prev=>({...prev,[c.id]:e.target.value}))}/>
                            </div>
                            <button onClick={()=>handleAbonoCartera(c)} disabled={abonoSaving===c.id||!abonoForm[c.id]} className="px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-black disabled:opacity-40 active:scale-95 transition-all whitespace-nowrap">
                              {abonoSaving===c.id?'...':'💰 Abonar'}
                            </button>
                          </div>
                        )}
                        <div className="text-xs text-slate-400">Registrado: {fmtDate(c.createdAt.split('T')[0]||c.createdAt)}</div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
          {tab==='extras' && (
            <div className="space-y-4">
              <div className="rounded-2xl border-2 border-slate-200 p-4 space-y-3">
                <div className="font-black text-slate-900 text-sm">Registrar pago suelto</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Monto *</label>
                    <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input type="number" className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="0.00" value={extraForm.monto} onChange={e=>setExtraForm({...extraForm,monto:e.target.value})}/></div></div>
                  <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Método</label>
                    <select className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none" value={extraForm.metodo} onChange={e=>setExtraForm({...extraForm,metodo:e.target.value})}>
                      {['Zelle','Cash','CashApp','PayPal','Venmo','Otro'].map(m=><option key={m}>{m}</option>)}</select></div>
                </div>
                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Descripción *</label>
                  <input className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="Ej: servicio extra, comisión, otro..." value={extraForm.descripcion} onChange={e=>setExtraForm({...extraForm,descripcion:e.target.value})}/></div>
                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Fecha</label>
                  <input type="date" className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none" value={extraForm.fechaPago} onChange={e=>setExtraForm({...extraForm,fechaPago:e.target.value})}/></div>
                <button onClick={submitExtra} disabled={extraSaving||!extraForm.monto||!extraForm.descripcion.trim()} className="w-full py-3 rounded-xl bg-slate-900 text-white font-black text-sm disabled:opacity-40 active:scale-95 transition-all">{extraSaving?'Guardando...':'💾 Registrar'}</button>
              </div>
              <div className="space-y-2">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wide">Pagos registrados ({extras.length})</div>
                {extras.length===0 && <div className="text-center text-slate-400 text-sm py-4">Sin pagos extras aún</div>}
                {extras.map(e=>(
                  <div key={e.id} className="rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2"><span className="text-emerald-600 font-black">${e.monto}</span><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">{e.metodo}</span></div>
                      <div className="text-sm text-slate-700 font-medium">{e.descripcion}</div>
                      <div className="text-xs text-slate-400">{fmtDate(e.fechaPago)}</div>
                    </div>
                    <button onClick={()=>deleteExtra(e.id)} className="text-red-300 hover:text-red-500 text-lg shrink-0">🗑</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── GARANTIA COUNTDOWN ────────────────────────────────────────────
function GarantiaCountdown({ fechaActivacion }) {
  const [tiempo, setTiempo] = useState(null);

  useEffect(() => {
    const calc = () => {
      const inicio = new Date(fechaActivacion);
      const fin = new Date(inicio.getTime() + 20 * 24 * 3600 * 1000);
      const diff = fin - new Date();
      if (diff <= 0) { setTiempo(null); return; }
      const dias  = Math.floor(diff / (1000*60*60*24));
      const horas = Math.floor((diff % (1000*60*60*24)) / (1000*60*60));
      const mins  = Math.floor((diff % (1000*60*60)) / (1000*60));
      const segs  = Math.floor((diff % (1000*60)) / 1000);
      setTiempo({ dias, horas, mins, segs });
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [fechaActivacion]);

  if (!tiempo) return (
    <div className="rounded-2xl bg-red-500/20 border border-red-400/30 px-5 py-4 text-center">
      <div className="text-red-400 font-black text-lg">⛔ Garantía vencida</div>
      <div className="text-red-300 text-sm mt-1">El período de garantía de 20 días ha terminado.</div>
    </div>
  );

  const pct = Math.max(0, (tiempo.dias / 20) * 100);
  return (
    <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-emerald-400 font-black text-sm">🛡️ Garantía activa</div>
        <div className="text-emerald-300 text-xs">{tiempo.dias} días restantes</div>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{width:pct+"%", background: pct>50?"#10b981":pct>20?"#f59e0b":"#ef4444"}}/>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[["días",tiempo.dias],["horas",tiempo.horas],["mins",tiempo.mins],["segs",tiempo.segs]].map(([lbl,val])=>(
          <div key={lbl} className="rounded-xl bg-slate-800 py-2">
            <div className="text-white font-black text-xl">{String(val).padStart(2,"0")}</div>
            <div className="text-slate-500 text-xs">{lbl}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── TRACKING PAGE ─────────────────────────────────────────────────
// Estados en los que el proceso SE TERMINA (mal). No son una pausa: el pedido
// no va a avanzar más. Antes se quedaban clavados a media pista (NOT APPROVED
// en 60%, DESACTIVADA en 50%) como si todavía faltara algo, y el cliente veía
// un carrito parado en la mitad para siempre. Ahora la carrera se cierra.
const ESTADOS_TERMINALES_NEG = ["NOT APPROVED","DIRTY BACKGROUND","DESACTIVADA","Intento Fallido","Cancelado"];

function F1TrackBar({ estado, createdAt, stateChangedAt }) {
  const esFinNegativo = ESTADOS_TERMINALES_NEG.includes(estado);

  // 7 estados distribuidos en la pista 0-100%
  const LANES = {
    "Pendiente Revisión":   [2,  14],
    "Aceptado":             [15, 29],
    "En Producción":        [30, 59],
    "Rehacer":              [20, 39],   // retrocede un poco visualmente
    "Pendiente Aprobación": [60, 79],
    "Esperando Cliente":    [90, 90],   // en manos del cliente: casi listo, fijo en 90%
    "Background Check":      [85, 85],   // verificación de antecedentes (en manos de la plataforma)
    "Terminado":            [80, 92],
    "VERI FACIAL":          [95, 95],   // última verificación antes de entregar: punto fijo casi al final
    "Entregado":            [100, 100],
  };

  // El carrito arranca en el mínimo del carril actual y avanza con el tiempo
  // usando stateChangedAt (cuándo cambió a este estado) o createdAt como fallback
  const calcProgress = () => {
    if (estado === "Entregado") return 100;
    // Proceso terminado (aunque sea con mal resultado): la barra se completa.
    // El color y el ícono son los que dicen que NO terminó bien.
    if (esFinNegativo) return 100;
    const [min, max] = LANES[estado] || [2, 14];
    // Estados de punto fijo (Esperando Cliente, Background Check):
    // marcan exactamente ese porcentaje, sin avanzar con el tiempo.
    if (max <= min) return min;
    // Timestamp desde el que medir avance dentro del carril actual
    const since = new Date(stateChangedAt || createdAt || Date.now()).getTime();
    const hoursElapsed = Math.max(0, (Date.now() - since) / (1000 * 60 * 60));
    const range = max - min - 2;
    // Logarítmica: avanza rápido al inicio, se frena. Referencia: 24h = ~70% del carril
    const gain = Math.min(Math.log1p(hoursElapsed) * (range / Math.log1p(24)), range);
    return Math.floor(min + gain);
  };

  const [progress, setProgress] = useState(calcProgress);

  useEffect(() => {
    if (estado === "Entregado") { setProgress(100); return; }
    setProgress(calcProgress());
    const t = setInterval(() => setProgress(calcProgress()), 3 * 60 * 1000);
    return () => clearInterval(t);
  }, [estado, createdAt]);

  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setDisplayed(progress), 400);
    return () => clearTimeout(t);
  }, [progress]);

  // Lo que el CLIENTE ve — nombres amigables
  const SC2 = {
    "Pendiente Revisión":   { label:"Recibido",         color:"#f59e0b", emoji:"🔍" },
    "Aceptado":             { label:"Aceptado",          color:"#0ea5e9", emoji:"✅" },
    "En Producción":        { label:"En proceso",        color:"#3b82f6", emoji:"⚙️" },
    "Rehacer":              { label:"Revisando",         color:"#f43f5e", emoji:"🔄" },
    "Pendiente Aprobación": { label:"Casi listo",        color:"#7c3aed", emoji:"⏳" },
    "Esperando Cliente":    { label:"Esperando tu acción", color:"#0ea5e9", emoji:"🕐" },
    "Background Check":      { label:"Verificando antecedentes", color:"#6366f1", emoji:"🛂" },
    "DIRTY BACKGROUND":      { label:"Antecedentes con problemas", color:"#b91c1c", emoji:"⚠️" },
    "NOT APPROVED":         { label:"No aprobado",        color:"#ea580c", emoji:"⛔" },
    "DESACTIVADA":          { label:"Desactivada",        color:"#52525b", emoji:"🔌" },
    // Faltaban: sin esto, un pedido cancelado le mostraba "Recibido 🔍" al
    // cliente, como si apenas estuviera empezando.
    "Cancelado":            { label:"Cancelado",          color:"#64748b", emoji:"🚫" },
    "Intento Fallido":      { label:"No se pudo completar",color:"#7f1d1d", emoji:"🚫" },
    "Terminado":            { label:"¡Listo!",           color:"#10b981", emoji:"🏁" },
    "VERI FACIAL":          { label:"Verificación facial", color:"#06b6d4", emoji:"🤳" },
    "Entregado":            { label:"Entregado",         color:"#10b981", emoji:"🏆" },
  };
  const s = SC2[estado] || SC2["Pendiente Revisión"];
  const isDone = estado === "Entregado";

  // Pasos visibles para el cliente (5 hitos lineales, Rehacer no aparece)
  const STEPS = [
    { em:"🔍", lb:"Recibido",  match:["Pendiente Revisión"] },
    { em:"✅", lb:"Aceptado",  match:["Aceptado"] },
    { em:"⚙️", lb:"En proceso",match:["En Producción","Rehacer"] },
    { em:"⏳", lb:"Revisión",  match:["Pendiente Aprobación","Esperando Cliente","Background Check"] },
    { em:"🏁", lb:"Entregado", match:["Terminado","VERI FACIAL","Entregado"] },
  ];

  // Índice del paso actual
  const STEP_ORDER = ["Pendiente Revisión","Aceptado","En Producción","Rehacer","Pendiente Aprobación","Esperando Cliente","Background Check","Terminado","VERI FACIAL","Entregado"];
  const curIdx = STEP_ORDER.indexOf(estado);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{s.emoji}</span>
          <span className="text-white font-black text-sm">{s.label}</span>
        </div>
        {/* Si el proceso terminó mal, el número no dice nada útil: lo que
            importa es que ya no avanza más. */}
        <span className="text-slate-400 text-xs font-bold">
          {esFinNegativo ? "Proceso terminado" : displayed + "%"}
        </span>
      </div>

      {/* Pista F1 */}
      <div className="relative h-10 rounded-full overflow-hidden" style={{background:"#1e293b", border:"2px solid #334155"}}>
        {[20,40,60,80].map(p => (
          <div key={p} className="absolute top-0 bottom-0 w-px opacity-20" style={{left:p+"%", background:"#94a3b8"}}/>
        ))}
        <div className="absolute top-0 left-0 h-full rounded-full transition-all duration-[2000ms] ease-out"
          style={{
            width: displayed + "%",
            background: isDone
              ? "linear-gradient(90deg, #059669, #10b981)"
              : `linear-gradient(90deg, ${s.color}99, ${s.color})`,
            boxShadow: `0 0 12px ${s.color}88`
          }}
        />
        <div className="absolute top-1/2 -translate-y-1/2 transition-all duration-[2000ms] ease-out"
          style={{left: `calc(${Math.min(displayed, 97)}% - 18px)`}}>
          <div style={{fontSize:"22px", lineHeight:1, filter:"drop-shadow(0 0 6px rgba(255,255,255,0.5))"}}>
            {isDone ? "🏆" : esFinNegativo ? "🛑" : "🏎️"}
          </div>
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-base opacity-60">🏁</div>
      </div>

      {/* Cierre explícito cuando el pedido no va a avanzar más */}
      {esFinNegativo && (
        <div className="rounded-xl px-3 py-2 text-center" style={{background:`${s.color}1f`, border:`1px solid ${s.color}55`}}>
          <div className="text-xs font-black" style={{color:s.color}}>{s.emoji} Proceso cerrado — {s.label}</div>
          <div className="text-slate-400" style={{fontSize:"10px"}}>Este pedido ya no continúa. No hay más pasos.</div>
        </div>
      )}

      {/* 5 pasos lineales */}
      {!esFinNegativo && (
      <div className="grid grid-cols-5 gap-1 text-center">
        {STEPS.map(({em,lb,match},si)=>{
          const isActive = match.includes(estado);
          // "completado" si el estado actual está más adelante en el orden
          const isDoneStep = match.every(m => STEP_ORDER.indexOf(m) < curIdx) && !isActive;
          return (
            <div key={lb} className="space-y-1">
              <div className={"text-xs font-bold transition-all " + (isActive ? "text-white scale-110" : isDoneStep ? "text-emerald-400" : "text-slate-600")}>
                {isDoneStep ? "✓" : em}
              </div>
              <div className={"text-slate-500 leading-tight " + (isActive?"text-slate-300":"")} style={{fontSize:"9px"}}>{lb}</div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function TrackingPage({ trackId }) {
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState("loading");
  const [msgText, setMsgText] = useState("");
  const [msgSent, setMsgSent] = useState(false);
  const [msgSending, setMsgSending] = useState(false);

  const loadOrder = () => fetch("/api/track/" + trackId)
    .then(r => { if (r.status === 410) { setStep("expired"); return null; } return r.json(); })
    .then(d => { if (!d) return; if (d.error) setStep("notfound"); else { setOrder(d); setStep("ok"); } })
    .catch(() => setStep("notfound"));

  useEffect(() => { loadOrder(); }, [trackId]);
  // Refresca cada 30 segundos para mostrar actualizaciones en vivo
  useEffect(() => {
    const t = setInterval(() => { if (step === "ok") loadOrder(); }, 30000);
    return () => clearInterval(t);
  }, [step, trackId]);

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    setMsgSending(true);
    try {
      await fetch("/api/track/" + trackId + "/mensaje", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mensaje: msgText.trim() })
      });
      setMsgSent(true);
      setMsgText("");
      loadOrder();
    } catch(e) {}
    setMsgSending(false);
  };

  if (step === "loading") return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center space-y-3">
        <div className="text-4xl animate-bounce">🏎️</div>
        <div className="text-white text-lg font-black animate-pulse">Buscando tu orden...</div>
      </div>
    </div>
  );
  if (step === "expired") return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="text-center space-y-4 max-w-sm">
        <div className="text-6xl">🔒</div>
        <div className="text-2xl font-black text-white">Seguimiento cerrado</div>
        <div className="text-slate-400 text-sm leading-relaxed">El período de seguimiento de esta orden ha finalizado. Si necesitas ayuda, contáctanos directamente por WhatsApp.</div>
        <div className="rounded-2xl bg-amber-500/10 border border-amber-400/20 px-5 py-4 text-amber-300 text-xs">Royal Services · Gracias por confiar en nosotros.</div>
      </div>
    </div>
  );
  if (step === "notfound") return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="text-center space-y-4"><div className="text-6xl">❓</div><div className="text-2xl font-black text-white">Orden no encontrada</div><div className="text-slate-400 text-sm">Verifica el link e intenta de nuevo.</div></div>
    </div>
  );

  const SC2 = {
    "Pendiente Revisión":   { bg:"bg-amber-400",   text:"text-amber-900"   },
    "Aceptado":             { bg:"bg-sky-400",      text:"text-sky-900"     },
    "En Producción":        { bg:"bg-blue-500",     text:"text-white"       },
    "Rehacer":              { bg:"bg-rose-500",     text:"text-white"       },
    "Pendiente Aprobación": { bg:"bg-violet-500",   text:"text-white"       },
    "Esperando Cliente":    { bg:"bg-sky-500",      text:"text-white"       },
    "Background Check":     { bg:"bg-indigo-500",   text:"text-white"       },
    "DIRTY BACKGROUND":     { bg:"bg-red-800",      text:"text-white"       },
    "NOT APPROVED":         { bg:"bg-orange-600",   text:"text-white"       },
    "DESACTIVADA":          { bg:"bg-zinc-700",     text:"text-white"       },
    "Cancelado":            { bg:"bg-slate-400",    text:"text-white"       },
    "Intento Fallido":      { bg:"bg-red-900",      text:"text-white"       },
    "Terminado":            { bg:"bg-emerald-400",  text:"text-emerald-900" },
    "VERI FACIAL":          { bg:"bg-cyan-500",     text:"text-white"       },
    "Entregado":            { bg:"bg-emerald-500",  text:"text-white"       },
  };
  const s = SC2[order.estado] || SC2["Pendiente Revisión"];

  return (
    <div className="min-h-screen py-8 px-4" style={{background:"linear-gradient(180deg,#0f172a 0%,#1e293b 100%)",fontFamily:"'Inter',system-ui,sans-serif"}}>
      <div className="max-w-md mx-auto space-y-5">

        {/* Header */}
        <div className="text-center space-y-1">
          <div className="text-4xl mb-2">🏎️</div>
          <div className="text-2xl font-black text-white">Tu orden en camino</div>
          <div className="text-slate-400 text-sm">Seguimiento en tiempo real</div>
        </div>

        {/* Card principal */}
        <div className="rounded-[24px] p-6 space-y-5" style={{background:"rgba(30,41,59,0.9)", border:"1px solid rgba(148,163,184,0.15)"}}>
          <div className="flex items-center justify-between">
            <div className="text-white font-black text-xl">{order.cliente}</div>
            <span className={"px-3 py-1.5 rounded-full text-xs font-black "+s.bg+" "+s.text}>{order.plataforma}</span>
          </div>

          {order.entrega && (
            <div className="rounded-xl px-4 py-2.5 flex items-center gap-2" style={{background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.2)"}}>
              <span className="text-amber-400">📅</span>
              <span className="text-slate-400 text-sm">Fecha estimada: </span>
              <span className="text-amber-300 font-bold text-sm">{new Date(order.entrega+"T12:00:00").toLocaleDateString("es-MX",{day:"numeric",month:"long",year:"numeric"})}</span>
            </div>
          )}

          {/* F1 TRACK */}
          <F1TrackBar estado={order.estado} createdAt={order.createdAt} stateChangedAt={order.stateChangedAt}/>

          {order.mensajeAdmin && order.mensajeAdmin.trim() && (
            <div className="rounded-2xl p-4 space-y-1" style={{background:"rgba(15,23,42,0.7)", border:"1px solid rgba(148,163,184,0.15)"}}>
              <div className="text-xs font-bold text-slate-400">💬 Mensaje de Royal</div>
              <div className="text-white text-sm leading-relaxed">{order.mensajeAdmin}</div>
            </div>
          )}
        </div>

        {/* Garantía */}
        {order.garantiaActiva ? (
          <div className="space-y-3">
            <GarantiaCountdown fechaActivacion={order.garantiaFecha}/>
            {new Date(order.garantiaFecha).getTime() + 20*24*3600*1000 > new Date().getTime() && (
              <div className="rounded-2xl p-5 space-y-3" style={{background:"rgba(30,41,59,0.9)", border:"1px solid rgba(148,163,184,0.15)"}}>
                <div className="text-white font-black text-sm">🔐 Tus credenciales de acceso</div>
                <div className="space-y-2">
                  <div className="rounded-xl px-4 py-3 flex items-center justify-between" style={{background:"rgba(15,23,42,0.7)"}}>
                    <div><div className="text-xs text-slate-400 mb-0.5">Usuario</div><div className="text-white font-bold text-sm">{order.garantiaUsuario}</div></div>
                    <button onClick={()=>navigator.clipboard.writeText(order.garantiaUsuario)} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg" style={{background:"rgba(148,163,184,0.1)"}}>Copiar</button>
                  </div>
                  <div className="rounded-xl px-4 py-3 flex items-center justify-between" style={{background:"rgba(15,23,42,0.7)"}}>
                    <div><div className="text-xs text-slate-400 mb-0.5">Contraseña</div><div className="text-white font-bold text-sm">{order.garantiaPassword}</div></div>
                    <button onClick={()=>navigator.clipboard.writeText(order.garantiaPassword)} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg" style={{background:"rgba(148,163,184,0.1)"}}>Copiar</button>
                  </div>
                </div>
                <div className="text-xs text-slate-500">Activado el {new Date(order.garantiaFecha).toLocaleDateString("es-MX",{day:"numeric",month:"long",year:"numeric"})}</div>
              </div>
            )}
          </div>
        ) : null}

        <div className="rounded-2xl px-5 py-4 text-sm leading-relaxed" style={{background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.2)"}}>
          <span className="text-amber-300">⏳ <strong>Aviso:</strong> Los servicios a veces toman un par de días en prepararse por la alta demanda. ¡Gracias por tu paciencia!</span>
        </div>

        {/* Sección de mensaje al admin */}
        <div className="rounded-[24px] p-5 space-y-3" style={{background:"rgba(30,41,59,0.9)", border:"1px solid rgba(148,163,184,0.15)"}}>
          <div className="text-white font-black text-sm">💬 ¿Tienes alguna pregunta?</div>
          {order.mensajeCliente && (
            <div className="rounded-xl px-4 py-3 text-xs" style={{background:"rgba(15,23,42,0.7)"}}>
              <div className="text-slate-400 mb-1">Tu último mensaje:</div>
              <div className="text-slate-300">{order.mensajeCliente}</div>
            </div>
          )}
          {msgSent ? (
            <div className="rounded-xl bg-emerald-500/20 border border-emerald-400/30 px-4 py-3 text-emerald-300 text-sm font-bold text-center">✅ Mensaje enviado. Te responderemos pronto.</div>
          ) : (
            <div className="space-y-2">
              <textarea
                className="w-full rounded-xl px-4 py-3 text-sm resize-none outline-none text-white placeholder:text-slate-500"
                style={{background:"rgba(15,23,42,0.7)", border:"1px solid rgba(148,163,184,0.2)"}}
                rows={3}
                placeholder="Escribe tu pregunta o comentario..."
                value={msgText}
                onChange={e=>setMsgText(e.target.value)}
              />
              <button
                onClick={sendMsg}
                disabled={msgSending || !msgText.trim()}
                className="w-full py-3 rounded-xl font-black text-sm disabled:opacity-40 transition-all active:scale-95"
                style={{background:"#f59e0b", color:"#000"}}
              >{msgSending ? "Enviando..." : "Enviar mensaje 📨"}</button>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-slate-600 pb-4">Royal Services · Seguimiento de orden</div>
      </div>
    </div>
  );
}

// ── GUEST FORM ────────────────────────────────────────────────────
function GuestForm({ token }) {
  const [step, setStep] = useState("loading");
  const [sending, setSending] = useState(false);
  const [trackId, setTrackId] = useState(null);
  const [producto, setProducto] = useState("cuenta"); // "cuenta" | "estipendio"
  const [plataforma, setPlataforma] = useState("AMAZON");
  const [plataformaEstipendio, setPlataformaEstipendio] = useState("");
  const [form, setForm] = useState({ nombre:"", telefono:"", ciudad:"", telefonoRef:"", usoCara:"", tieneCelular:"", tieneCupo:"", reclamoEstipendio:"" });
  const [pidioGarantia, setPidioGarantia] = useState(false);
  const [garantiaCorreo, setGarantiaCorreo] = useState("");
  const [solicitudEspecifica, setSolicitudEspecifica] = useState("");
  const [mensajeCliente, setMensajeCliente] = useState("");
  const [fotoSelfie, setFotoSelfie] = useState([]);
  const [fotoAbono, setFotoAbono] = useState([]);
  const [fotoCuentaInfo, setFotoCuentaInfo] = useState([]); // captura de info de cuenta (estipendio)
  const [fotoPlaca, setFotoPlaca] = useState([]);
  const [fotoRegistro, setFotoRegistro] = useState([]);
  const [fotoVin, setFotoVin] = useState([]);
  const selfieRef = React.useRef();
  const abonoRef = React.useRef();
  const cuentaInfoRef = React.useRef();
  const placaRef = React.useRef();
  const registroRef = React.useRef();
  const vinRef = React.useRef();
  const isEstipendio = producto === "estipendio";
  const needsCar = !isEstipendio && (plataforma === "UBER" || plataforma === "LYFT");
  const carRequired = !isEstipendio && plataforma === "LYFT";

  useEffect(() => {
    fetch("/api/guest/" + token).then(r=>r.json())
      .then(d=>{ if(d.expired||d.error) setStep("expired"); else setStep("form"); })
      .catch(()=>setStep("expired"));
  }, [token]);

  // Comprime imagen antes de guardarla: alta calidad para descargar pero
  // manejable para subir/cargar (ver compressImageHQ arriba).
  const compressImage = (file) => compressImageHQ(file);

  const handleFiles = (files, setter, max=1) => {
    Array.from(files).filter(f=>f.type.startsWith("image/")).forEach(async file=>{
      const compressed = await compressImage(file);
      setter(prev => prev.length < max ? [...prev, compressed] : prev);
    });
  };

  const SinglePhoto = ({ label, value, setter, refEl, required, maxPhotos=1 }) => (
    <div>
      <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">{label}{required&&" *"}</label>
      {value.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-2">
          {value.map((src,i)=>(
            <div key={i} className="relative w-24 h-24 shrink-0">
              <img src={src} alt="" className="w-full h-full object-cover rounded-xl border border-slate-600"/>
              <button onClick={()=>setter(v=>v.filter((_,j)=>j!==i))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">×</button>
            </div>
          ))}
        </div>
      )}
      {value.length < maxPhotos && (
        <div onClick={()=>refEl.current?.click()} className="border-2 border-dashed border-slate-600 rounded-xl px-4 py-5 text-center cursor-pointer hover:border-amber-400 transition-all">
          <div className="text-2xl mb-1">📷</div>
          <div className="text-xs text-slate-400">{value.length>0 ? `Agregar foto (${value.length}/${maxPhotos})` : "Toca para subir foto"}</div>
        </div>
      )}
      <input ref={refEl} type="file" accept="image/*" className="hidden" onChange={e=>handleFiles(e.target.files,setter,maxPhotos)}/>
    </div>
  );

  const isAmazon = !isEstipendio && plataforma === "AMAZON";
  const amazonNeedsCiudad = isAmazon && form.tieneCupo === "No";

  const submit = async () => {
    if (isEstipendio) {
      if (!form.nombre.trim() || !form.telefono.trim() || !plataformaEstipendio || !form.reclamoEstipendio || fotoCuentaInfo.length===0) return;
    } else {
      const ciudadReq = isAmazon ? (amazonNeedsCiudad ? form.ciudad.trim() : true) : form.ciudad.trim();
      if (!form.nombre.trim() || !form.telefono.trim() || !ciudadReq || fotoSelfie.length===0) return;
      if (carRequired && (fotoPlaca.length===0||fotoRegistro.length===0||fotoVin.length===0)) return;
    }
    setSending(true);
    try {
      let notas, allFotos, platEnviar;
      if (isEstipendio) {
        const notasParts = [
          "PRODUCTO: ESTIPENDIO",
          "App a cobrar: "+plataformaEstipendio,
          "¿Ha reclamado estipendio antes? "+(form.reclamoEstipendio||"No indicado"),
          "Cuenta California: Sí",
          "WhatsApp: "+form.telefono,
          form.telefonoRef ? "Tel. referencia: "+form.telefonoRef : ""
        ].filter(Boolean);
        notas = notasParts.join(" | ");
        allFotos = [...fotoCuentaInfo];
        platEnviar = "ESTIPENDIO";
      } else {
        const notasParts = ["¿Cara en app antes? "+(form.usoCara||"No indicado")];
        if (isAmazon) {
          notasParts.push("¿Ya tiene cupo? "+(form.tieneCupo||"No indicado"));
          if (form.tieneCupo === "No" && form.ciudad.trim()) notasParts.push("Ciudad ideal para cupo: "+form.ciudad);
        } else {
          notasParts.push("¿Celular para formatear? "+(form.tieneCelular||"No indicado"));
          notasParts.push("Ciudad/Estado: "+form.ciudad);
        }
        notasParts.push("WhatsApp: "+form.telefono);
        notasParts.push("Tel. referencia: "+(form.telefonoRef||"N/A"));
        notas = notasParts.join(" | ");
        allFotos = [...fotoSelfie, ...fotoAbono, ...fotoPlaca, ...fotoRegistro, ...fotoVin];
        platEnviar = plataforma;
      }
      const httpRes = await fetch("/api/guest/"+token+"/pedido",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ cliente:form.nombre.trim(), plataforma: platEnviar, notas, fotos:allFotos, solicitudEspecifica: (!isEstipendio && plataforma==="OTROS") ? solicitudEspecifica : "", pidioGarantia: isEstipendio ? false : pidioGarantia, garantiaCorreo: isEstipendio ? "" : garantiaCorreo, mensajeCliente: mensajeCliente.trim() })
      });
      // Acepta cualquier respuesta 2xx como éxito
      if (httpRes.ok) {
        const res2 = await httpRes.json().catch(()=>({}));
        if (res2.trackId) setTrackId(res2.trackId);
        setStep("done"); return;
      }
      // Si el servidor devuelve JSON con ok/success, también aceptar
      const res = await httpRes.json().catch(()=>({}));
      if (res.ok || res.success || res.id) { if (res.trackId) setTrackId(res.trackId); setStep("done"); return; }
      // Si llegamos aquí, hubo un error del servidor
      alert("Hubo un error al enviar. Por favor intenta de nuevo.");
    } catch(err) {
      alert("Error de conexión. Verifica tu internet e intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const canSubmit = isEstipendio
    ? (form.nombre.trim() && form.telefono.trim() && plataformaEstipendio && form.reclamoEstipendio && fotoCuentaInfo.length>0)
    : (form.nombre.trim() && form.telefono.trim() && form.usoCara && fotoSelfie.length>0
        && (!pidioGarantia || garantiaCorreo.trim())
        && (!carRequired || (fotoPlaca.length>0&&fotoRegistro.length>0&&fotoVin.length>0))
        && (isAmazon
          ? (form.tieneCupo && (form.tieneCupo === "Sí" || form.ciudad.trim()))
          : needsCar
            ? form.ciudad.trim()
            : (form.tieneCelular && form.ciudad.trim())));

  if (step==="loading") return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><div className="text-white text-xl font-black animate-pulse">Verificando...</div></div>;
  if (step==="expired") return <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4"><div className="text-center space-y-4"><div className="text-6xl">⏰</div><div className="text-2xl font-black text-white">Link expirado</div><div className="text-slate-400 text-sm">Este link ya no es válido. Pide uno nuevo.</div></div></div>;
  if (step==="done") return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div initial={{scale:0.8,opacity:0}} animate={{scale:1,opacity:1}} className="text-center space-y-4 max-w-sm">
        <div className="text-7xl">✅</div>
        <div className="text-3xl font-black text-white">¡Solicitud recibida!</div>
        <div className="text-slate-300 text-sm leading-relaxed">Nos pondremos en contacto contigo pronto por WhatsApp.</div>
        {trackId && (
          <div className="rounded-2xl bg-slate-800 border border-slate-600 p-5 space-y-3 text-left">
            <div className="text-emerald-400 font-black text-sm">🔍 Tu link de seguimiento</div>
            <div className="text-xs text-slate-300 leading-relaxed">Guarda este link para ver el estado de tu orden:</div>
            <div className="rounded-xl bg-slate-900 px-3 py-2.5 text-xs text-amber-300 font-mono break-all select-all">
              {window.location.origin}/seguimiento/{trackId}
            </div>
            <button onClick={()=>navigator.clipboard.writeText(window.location.origin+'/seguimiento/'+trackId)}
              className="w-full py-3 rounded-xl bg-emerald-500 text-black font-black text-sm hover:bg-emerald-400 active:scale-95 transition-all">
              📋 Copiar link
            </button>
          </div>
        )}
        <div className="rounded-2xl bg-amber-500/20 border border-amber-400/30 px-6 py-4 text-amber-300 text-sm font-medium">
          ⏳ Los servicios a veces toman un par de días en prepararse por la alta demanda. ¡Gracias por tu paciencia!
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 py-8 px-4" style={{fontFamily:"'Inter',system-ui,sans-serif"}}>
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center"><div className="text-4xl mb-2">📦</div><div className="text-2xl font-black text-white">Royal — Solicitud</div><div className="text-slate-400 text-sm mt-1">Llena el formulario para continuar</div></div>
        <div className="rounded-2xl bg-amber-500/20 border border-amber-400/30 px-5 py-4 text-amber-300 text-sm leading-relaxed">⏳ <strong>Aviso:</strong> Los servicios a veces toman un par de días en prepararse por la alta demanda.</div>

        {/* ── SELECTOR DE PRODUCTO ── */}
        <div>
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2 block">¿Qué producto necesitas? *</label>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={()=>setProducto("cuenta")}
              className={"rounded-2xl border-2 p-4 text-left transition-all " + (producto==="cuenta" ? "border-amber-400 bg-amber-500/10" : "border-slate-700 bg-slate-800/50 hover:border-slate-500")}>
              <div className="text-2xl mb-1">📱</div>
              <div className={"font-black text-sm " + (producto==="cuenta" ? "text-amber-400" : "text-white")}>Cuenta</div>
              <div className="text-slate-400 text-xs mt-0.5">Activación de cuenta nueva</div>
            </button>
            <button type="button" onClick={()=>setProducto("estipendio")}
              className={"rounded-2xl border-2 p-4 text-left transition-all " + (producto==="estipendio" ? "border-emerald-400 bg-emerald-500/10" : "border-slate-700 bg-slate-800/50 hover:border-slate-500")}>
              <div className="text-2xl mb-1">💵</div>
              <div className={"font-black text-sm " + (producto==="estipendio" ? "text-emerald-400" : "text-white")}>Estipendio</div>
              <div className="text-slate-400 text-xs mt-0.5">Cobro de estipendio</div>
            </button>
          </div>
        </div>

        {/* ── NOMBRE (siempre) ── */}
        <div>
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Nombre completo *</label>
          <input className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" placeholder="Tu nombre" value={form.nombre} onChange={e=>setForm({...form,nombre:e.target.value})}/>
        </div>

        {/* ══════════════════════════════════════════════
            FORMULARIO ESTIPENDIO
        ══════════════════════════════════════════════ */}
        {isEstipendio && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 px-4 py-3 text-emerald-300 text-xs leading-relaxed">
              💵 <strong>Estipendio:</strong> La cuenta debe ser de California. Solo necesitas la captura de info de la cuenta.
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">1. App para cobrar el estipendio *</label>
              <select className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-emerald-400" value={plataformaEstipendio} onChange={e=>setPlataformaEstipendio(e.target.value)}>
                <option value="">Selecciona la app...</option>
                {["AMAZON","DOORDASH","UBER","INSTACART","SPARK","LYFT","OTROS"].map(p=><option key={p}>{p}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">2. ¿Ha reclamado estipendio antes? *</label>
              <select className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-emerald-400" value={form.reclamoEstipendio} onChange={e=>setForm({...form,reclamoEstipendio:e.target.value})}>
                <option value="">Selecciona...</option>
                <option>Sí</option>
                <option>No</option>
              </select>
            </div>

            <SinglePhoto label="3. Captura de la información de la cuenta *" value={fotoCuentaInfo} setter={setFotoCuentaInfo} refEl={cuentaInfoRef} required maxPhotos={3}/>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">4. Número WhatsApp para contactarte *</label>
              <input type="tel" className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-emerald-400" placeholder="+1 (555) 000-0000" value={form.telefono} onChange={e=>setForm({...form,telefono:e.target.value})}/>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">5. Número de quien te refirió (opcional)</label>
              <input type="tel" className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-emerald-400" placeholder="+1 (555) 000-0000" value={form.telefonoRef} onChange={e=>setForm({...form,telefonoRef:e.target.value})}/>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            FORMULARIO CUENTA (original)
        ══════════════════════════════════════════════ */}
        {!isEstipendio && (
          <div className="space-y-4">
            <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Plataforma *</label>
              <select className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" value={plataforma} onChange={e=>setPlataforma(e.target.value)}>
                {["AMAZON","DOORDASH","UBER","LYFT","VARIAS APP","OTROS"].map(p=><option key={p}>{p}</option>)}</select></div>
            <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">1. ¿Ha puesto la cara en la app antes? *</label>
              <select className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" value={form.usoCara} onChange={e=>setForm({...form,usoCara:e.target.value})}>
                <option value="">Selecciona...</option><option>Sí</option><option>No</option></select></div>
            {isAmazon ? (<>
              <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">2. ¿Ya tiene cupo de Amazon? *</label>
                <select className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" value={form.tieneCupo} onChange={e=>setForm({...form,tieneCupo:e.target.value,ciudad:""})}>
                  <option value="">Selecciona...</option><option>Sí</option><option>No</option></select></div>
              {form.tieneCupo === "No" && (
                <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">3. ¿En qué ciudad le gustaría el cupo? *</label>
                  <input className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" placeholder="Ej: Houston, Texas" value={form.ciudad} onChange={e=>setForm({...form,ciudad:e.target.value})}/></div>
              )}
              <SinglePhoto label={`${form.tieneCupo === "No" ? "4" : "3"}. Foto selfie de tu cara`} value={fotoSelfie} setter={setFotoSelfie} refEl={selfieRef} required maxPhotos={3}/>
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">{form.tieneCupo === "No" ? "5" : "4"}. Captura de pantalla — $50 de abono <span className="text-slate-500 font-normal normal-case">(opcional)</span></label>
                {fotoAbono.length > 0
                  ? <div className="relative"><img src={fotoAbono[0]} alt="" className="w-full h-32 object-cover rounded-xl border border-slate-600"/><button onClick={()=>setFotoAbono([])} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white text-sm font-bold flex items-center justify-center">×</button></div>
                  : <div onClick={()=>abonoRef.current?.click()} className="border-2 border-dashed border-slate-600 rounded-xl px-4 py-5 text-center cursor-pointer hover:border-amber-400 transition-all">
                      <div className="text-2xl mb-1">📷</div><div className="text-xs text-slate-400">Toca para subir foto (opcional)</div>
                    </div>
                }
                <input ref={abonoRef} type="file" accept="image/*" className="hidden" onChange={e=>handleFiles(e.target.files,setFotoAbono)}/>
              </div>
              <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">{form.tieneCupo === "No" ? "6" : "5"}. Número WhatsApp para contactarte *</label>
                <input type="tel" className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" placeholder="+1 (555) 000-0000" value={form.telefono} onChange={e=>setForm({...form,telefono:e.target.value})}/></div>
              <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">{form.tieneCupo === "No" ? "7" : "6"}. Número de quien te refirió (opcional)</label>
                <input type="tel" className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" placeholder="+1 (555) 000-0000" value={form.telefonoRef} onChange={e=>setForm({...form,telefonoRef:e.target.value})}/></div>
            </>) : (<>
              <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">2. ¿Tiene un celular para formatear? *</label>
                <select className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" value={form.tieneCelular} onChange={e=>setForm({...form,tieneCelular:e.target.value})}>
                  <option value="">Selecciona...</option><option>Sí</option><option>No</option></select></div>
              <SinglePhoto label="3. Foto selfie de tu cara" value={fotoSelfie} setter={setFotoSelfie} refEl={selfieRef} required maxPhotos={3}/>
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">4. Captura de pantalla — $50 de abono <span className="text-slate-500 font-normal normal-case">(opcional)</span></label>
                {fotoAbono.length > 0
                  ? <div className="relative"><img src={fotoAbono[0]} alt="" className="w-full h-32 object-cover rounded-xl border border-slate-600"/><button onClick={()=>setFotoAbono([])} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white text-sm font-bold flex items-center justify-center">×</button></div>
                  : <div onClick={()=>abonoRef.current?.click()} className="border-2 border-dashed border-slate-600 rounded-xl px-4 py-5 text-center cursor-pointer hover:border-amber-400 transition-all">
                      <div className="text-2xl mb-1">📷</div><div className="text-xs text-slate-400">Toca para subir foto (opcional)</div>
                    </div>
                }
                <input ref={abonoRef} type="file" accept="image/*" className="hidden" onChange={e=>handleFiles(e.target.files,setFotoAbono)}/>
              </div>
              <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">5. Número WhatsApp para contactarte *</label>
                <input type="tel" className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" placeholder="+1 (555) 000-0000" value={form.telefono} onChange={e=>setForm({...form,telefono:e.target.value})}/></div>
              <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">6. Ciudad y estado del servicio *</label>
                <input className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" placeholder="Ej: Houston, Texas" value={form.ciudad} onChange={e=>setForm({...form,ciudad:e.target.value})}/></div>
              <div><label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">7. Número de quien te refirió (opcional)</label>
                <input type="tel" className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400" placeholder="+1 (555) 000-0000" value={form.telefonoRef} onChange={e=>setForm({...form,telefonoRef:e.target.value})}/></div>
            </>)}
            {plataforma === "OTROS" && (
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">8. Describe lo que necesitas (PQR) *</label>
                <textarea className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400 resize-none" rows={4} placeholder="Explica detalladamente tu solicitud o reclamo..." value={solicitudEspecifica} onChange={e=>setSolicitudEspecifica(e.target.value)}/>
              </div>
            )}
            <div className="rounded-2xl border p-4 space-y-3" style={{background:"rgba(16,185,129,0.07)", borderColor:"rgba(16,185,129,0.25)"}}>
              <div className="flex items-start gap-3">
                <div className="text-2xl shrink-0">🛡️</div>
                <div>
                  <div className="text-emerald-400 font-black text-sm">Garantía incluida</div>
                  <div className="text-slate-300 text-xs mt-0.5 leading-relaxed">
                    Tu servicio tiene <strong className="text-white">{plataforma === "LYFT" ? "17" : "20"} días de garantía</strong> a partir de la activación.{" "}
                    Si el producto presenta algún defecto dentro de ese período, puedes reportarlo aquí para que le demos seguimiento.
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer" onClick={()=>setPidioGarantia(v=>!v)}>
                <div className={"w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all shrink-0 "+(pidioGarantia?"bg-rose-500 border-rose-500":"border-slate-500 bg-slate-800")}>
                  {pidioGarantia && <span className="text-white text-xs font-black">✓</span>}
                </div>
                <div>
                  <div className="text-white font-bold text-sm">⚠️ Reportar producto defectuoso</div>
                  <div className="text-slate-400 text-xs">Marca esto solo si tu producto ya presentó un fallo</div>
                </div>
              </label>
              {pidioGarantia && (
                <div className="space-y-2 pt-1">
                  <div className="rounded-xl bg-rose-500/10 border border-rose-400/30 px-3 py-2 text-rose-300 text-xs">
                    ⚠️ Al reportar, nuestro equipo revisará tu caso y se pondrá en contacto contigo para resolverlo dentro del período de garantía.
                  </div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Correo de la cuenta con el problema *</label>
                  <input type="email" className="w-full rounded-xl bg-slate-800 border border-slate-500 px-4 py-3 text-white text-sm outline-none focus:border-rose-400" placeholder="tucorreo@email.com" value={garantiaCorreo} onChange={e=>setGarantiaCorreo(e.target.value)}/>
                </div>
              )}
            </div>
            {needsCar && (
              <div className="rounded-2xl bg-slate-700/50 border border-slate-600 p-4 space-y-4">
                <div className="text-amber-400 font-black text-sm">🚗 {plataforma} — Fotos del vehículo {carRequired ? "(obligatorio)" : "(opcional)"}</div>
                <SinglePhoto label="Foto de la placa" value={fotoPlaca} setter={setFotoPlaca} refEl={placaRef} required={carRequired}/>
                <SinglePhoto label="Foto del registro del carro" value={fotoRegistro} setter={setFotoRegistro} refEl={registroRef} required={carRequired}/>
                <SinglePhoto label="Foto del VIN del carro" value={fotoVin} setter={setFotoVin} refEl={vinRef} required={carRequired}/>
              </div>
            )}
          </div>
        )}

        {/* Mensaje libre (siempre visible) */}
        <div className="rounded-2xl bg-slate-800/60 border border-slate-600 p-4 space-y-2">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block">💬 Mensaje para nosotros <span className="text-slate-600 font-normal normal-case">(opcional)</span></label>
          <textarea
            className="w-full rounded-xl bg-slate-800 border border-slate-600 px-4 py-3 text-white text-sm outline-none focus:border-amber-400 resize-none"
            rows={3} maxLength={200}
            placeholder="¿Algo que quieras comentarnos antes de enviar tu solicitud?"
            value={mensajeCliente} onChange={e=>setMensajeCliente(e.target.value.slice(0,200))}
          />
          <div className="text-right text-xs text-slate-500">{mensajeCliente.length}/200</div>
        </div>
        <motion.button whileTap={{scale:0.96}} onClick={submit} disabled={sending||!canSubmit}
          className={"w-full py-4 rounded-2xl text-base font-black disabled:opacity-50 transition-colors " + (isEstipendio ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-amber-500 hover:bg-amber-400 text-black")}>
          {sending?"Enviando...":"Enviar solicitud →"}
        </motion.button>
        {!canSubmit && <div className="text-center text-xs text-slate-500">Completa todos los campos obligatorios (*)</div>}
        <div className="text-center text-xs text-slate-600 pb-4">Royal Services · Formulario seguro</div>
      </div>
    </div>
  );
}



// ── INBOX PANEL ───────────────────────────────────────────────────
function InboxPanel({ onClose, onAccept, onReject }) {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('nuevas');
  const [expandedId, setExpandedId] = useState(null); // preview completo
  const [fotosExpandida, setFotosExpandida] = useState({}); // fotos cargadas por id
  const [inboxLightbox, setInboxLightbox] = useState(null); // {fotos, i}

  const load = () => {
    setLoading(true);
    apiFetch('/api/pedidos').then(d => {
      setSolicitudes(d||[]);
      setLoading(false);
    }).catch(()=>setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const [procesando, setProcesando] = useState({});

  // Cargar fotos de un pedido bajo demanda al expandir
  const cargarFotos = async (id) => {
    if (fotosExpandida[id]) return; // ya cargadas
    const fotos = await apiFetch('/api/pedidos/'+id+'/fotos').catch(()=>[]);
    setFotosExpandida(prev=>({...prev,[id]: fotos||[]}));
  };

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    cargarFotos(id);
  };

  const aceptar = async (s) => {
    setProcesando(p=>({...p,[s.id]:'aceptando'}));
    try {
      const { fotos, ...sinFotos } = s;
      await apiFetch('/api/pedidos/'+s.id, { method:'PUT', body:JSON.stringify({...sinFotos, bandeja: null}) });
      onAccept(s);
      setSolicitudes(prev => prev.map(x => x.id===s.id ? {...x, bandeja: null} : x));
      setExpandedId(null);
    } catch(e) {
      alert('Error al aceptar, intenta de nuevo');
    } finally {
      setProcesando(p=>({...p,[s.id]:null}));
    }
  };

  const rechazar = async (id) => {
    setProcesando(p=>({...p,[id]:'rechazando'}));
    try {
      const s = solicitudes.find(x=>x.id===id);
      const { fotos, ...sinFotos } = s||{id};
      await apiFetch('/api/pedidos/'+id, { method:'PUT', body:JSON.stringify({...sinFotos, bandeja: 'archivada'}) });
      setSolicitudes(prev => prev.map(x => x.id===id ? {...x, bandeja:'archivada'} : x));
      if (expandedId === id) setExpandedId(null);
      onReject && onReject(id);
    } catch(e) {
      alert('Error al archivar, intenta de nuevo');
    } finally {
      setProcesando(p=>({...p,[id]:null}));
    }
  };

  // Clasificar: guest = vino del formulario externo; interno = lo creó un admin/user interno
  const esGuest = s => s.userName === 'Cliente (link)' || s.origen === 'guest';
  const esNueva = s => s.bandeja === 'nueva' && s.bandeja !== 'archivada';

  // Nuevas: tanto las de clientes externos como las que crearon sub-admins/users
  const nuevas = solicitudes.filter(s => esNueva(s));
  const archivadas = solicitudes.filter(s => s.bandeja === 'archivada');
  const lista = tab === 'nuevas' ? nuevas : archivadas;

  return (
    <motion.div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <motion.div className="w-full max-w-lg bg-white rounded-[28px] overflow-hidden shadow-2xl flex flex-col"
        style={{maxHeight:"90vh"}} initial={{scale:0.9,y:20}} animate={{scale:1,y:0}}>
        <div className="bg-slate-900 px-6 py-5 flex items-center justify-between shrink-0">
          <div>
            <div className="text-white font-black text-lg">📥 Bandeja de entrada</div>
            {nuevas.length > 0 && <div className="text-slate-400 text-xs mt-0.5">{nuevas.length} solicitud{nuevas.length!==1?"es":""} sin revisar</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
        </div>
        <div className="flex border-b border-slate-100 shrink-0">
          {[['nuevas','📬 Nuevas',nuevas.length],['archivadas','📁 Archivadas',archivadas.length]].map(([t,l,count])=>(
            <button key={t} onClick={()=>setTab(t)}
              className={"flex-1 py-3 text-sm font-bold transition-all flex items-center justify-center gap-2 "+(tab===t?'text-slate-900 border-b-2 border-slate-900':'text-slate-400')}>
              {l}
              {count>0&&<span className={"px-1.5 py-0.5 rounded-full text-xs font-black "+(t==='nuevas'?'bg-red-500 text-white':'bg-slate-100 text-slate-500')}>{count}</span>}
            </button>
          ))}
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {loading && <div className="text-center text-slate-400 py-8 text-sm">Cargando...</div>}
          {!loading && lista.length === 0 && (
            <div className="text-center py-12 space-y-2">
              <div className="text-4xl">{tab==='nuevas'?'✅':'📭'}</div>
              <div className="text-slate-500 text-sm font-medium">{tab==='nuevas'?'Todo revisado':'Sin archivadas'}</div>
            </div>
          )}
          {lista.map(s => {
            const pc = PC[s.plataforma] || PC["OTROS"];
            const fecha = new Date(s.createdAt).toLocaleDateString('es-MX',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'});
            const esExterno = esGuest(s);
            const isExpanded = expandedId === s.id;
            const fotosDelPedido = fotosExpandida[s.id] || [];
            const sc = SC[s.estado] || {};

            return (
              <div key={s.id} className="rounded-2xl border-2 border-slate-100 bg-white overflow-hidden shadow-sm">
                {/* Cabecera siempre visible */}
                <div className={"px-4 py-3 flex items-center gap-3 "+(pc.light)}>
                  <span className="text-2xl">{pc.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-slate-900 truncate">{s.cliente}</div>
                    <TituloChip order={s} className="mt-0.5"/>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={"px-2 py-0.5 rounded-full font-bold "+pc.bg+" text-white text-xs"}>{s.plataforma}</span>
                      <span className={"px-1.5 py-0.5 rounded-full text-xs font-bold "+(esExterno?'bg-violet-100 text-violet-700':'bg-blue-100 text-blue-700')}>
                        {esExterno ? '🔗 Cliente externo' : '👤 '+s.userName}
                      </span>
                      <span className="text-slate-400">· {fecha}</span>
                    </div>
                  </div>
                  {/* Botón ver detalles */}
                  <button onClick={()=>toggleExpand(s.id)}
                    className={"shrink-0 px-3 py-1.5 rounded-xl text-xs font-black transition-all active:scale-95 "+(isExpanded?'bg-slate-900 text-white':'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                    {isExpanded ? '▲ Ocultar' : '▼ Ver todo'}
                  </button>
                </div>

                {/* Resumen rápido siempre visible */}
                {s.notas && !isExpanded && (
                  <div className="px-4 py-2 text-xs text-slate-600 bg-slate-50 border-t border-slate-100 line-clamp-2">{s.notas}</div>
                )}

                {/* ── DETALLE EXPANDIDO ── */}
                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {/* Datos básicos organizados */}
                    <div className="px-4 py-3 bg-slate-50 grid grid-cols-2 gap-x-4 gap-y-2">
                      <div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-0.5">Estado</div>
                        <span className={"px-2 py-0.5 rounded-full text-xs font-bold "+(sc.bg||'bg-slate-200')+" "+(sc.text||'text-slate-700')}>{sc.emoji} {s.estado}</span>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-0.5">Prioridad</div>
                        <span className={"text-xs font-bold "+(s.prioridad==='Alta'?'text-red-600':s.prioridad==='Media'?'text-amber-600':'text-slate-500')}>{s.prioridad==='Alta'?'🔥':s.prioridad==='Media'?'⚡':'—'} {s.prioridad}</span>
                      </div>
                      {s.entrega && (
                        <div>
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-0.5">Entrega</div>
                          <div className="text-xs font-bold text-slate-700">{new Date(s.entrega+'T12:00').toLocaleDateString('es-MX',{day:'numeric',month:'short',year:'numeric'})}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-0.5">Plataforma</div>
                        <div className="text-xs font-bold text-slate-700">{pc.emoji} {s.plataforma}</div>
                      </div>
                    </div>

                    {/* Notas del sistema */}
                    {s.notas && (
                      <div className="px-4 py-3 border-t border-slate-100">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">📋 Notas</div>
                        <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{s.notas}</div>
                      </div>
                    )}

                    {/* Mensaje del cliente */}
                    {s.mensajeCliente && (
                      <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
                        <div className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-1">💬 Mensaje del cliente</div>
                        <div className="text-sm text-amber-900 leading-relaxed">{s.mensajeCliente}</div>
                      </div>
                    )}

                    {/* Solicitud específica */}
                    {s.solicitudEspecifica && (
                      <div className="px-4 py-3 bg-violet-50 border-t border-violet-100">
                        <div className="text-xs font-bold text-violet-600 uppercase tracking-wide mb-1">📝 Solicitud específica</div>
                        <div className="text-sm text-violet-900 leading-relaxed">{s.solicitudEspecifica}</div>
                      </div>
                    )}

                    {/* Garantía */}
                    {s.pidioGarantia ? (
                      <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-100">
                        <div className="text-xs font-bold text-emerald-600 uppercase tracking-wide mb-1">🛡️ Solicita garantía</div>
                        {s.garantiaCorreo && <div className="text-xs text-emerald-800">Correo: <strong>{s.garantiaCorreo}</strong></div>}
                      </div>
                    ) : null}

                    {/* Fotos cargadas bajo demanda */}
                    {fotosDelPedido.length > 0 && (
                      <div className="px-4 py-3 border-t border-slate-100">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">📷 Fotos ({fotosDelPedido.length})</div>
                        <div className="flex flex-wrap gap-2">
                          {fotosDelPedido.map((src,i)=>(
                            <img key={i} src={src} alt="" onClick={()=>setInboxLightbox({fotos:fotosDelPedido,i})}
                              className="w-16 h-16 object-cover rounded-xl border border-slate-200 cursor-zoom-in hover:opacity-80"/>
                          ))}
                        </div>
                      </div>
                    )}
                    {fotosDelPedido.length === 0 && isExpanded && (
                      <div className="px-4 py-2 border-t border-slate-100">
                        <div className="text-xs text-slate-400 italic">Sin fotos adjuntas</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Acciones */}
                {tab === 'nuevas' && (
                  <div className="px-4 py-3 flex gap-2 border-t border-slate-100">
                    <button onClick={()=>!procesando[s.id]&&aceptar(s)} disabled={!!procesando[s.id]}
                      className="flex-1 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-black hover:bg-emerald-600 active:scale-95 transition-all disabled:opacity-60">
                      {procesando[s.id]==='aceptando' ? '⏳ Aceptando...' : '✅ Aceptar → Pedidos'}
                    </button>
                    <button onClick={()=>!procesando[s.id]&&rechazar(s.id)} disabled={!!procesando[s.id]}
                      className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-500 text-sm font-bold hover:bg-red-50 hover:text-red-500 active:scale-95 transition-all disabled:opacity-60">
                      {procesando[s.id]==='rechazando' ? '⏳' : '🗑'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
      <AnimatePresence>{inboxLightbox&&<Lightbox fotos={inboxLightbox.fotos} startIndex={inboxLightbox.i} onClose={()=>setInboxLightbox(null)}/>}</AnimatePresence>
    </motion.div>
  );
}

// ── GARANTIA MODAL ────────────────────────────────────────────────
function GarantiaModal({ order, onClose, onSave }) {
  const [form, setForm] = useState({
    garantiaActiva: order.garantiaActiva||false,
    garantiaUsuario: order.garantiaUsuario||'',
    garantiaPassword: order.garantiaPassword||'',
    garantiaFecha: order.garantiaFecha|| new Date().toISOString().split('T')[0],
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await apiFetch('/api/pedidos/'+order.id+'/garantia', {method:'PUT', body:JSON.stringify(form)});
    setSaving(false);
    onSave(form);
    onClose();
  };

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{background:"rgba(0,0,0,0.7)"}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <motion.div className="w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] shadow-2xl overflow-hidden"
        initial={{y:60,opacity:0}} animate={{y:0,opacity:1}} exit={{y:60,opacity:0}} transition={{type:"spring",damping:22}}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="text-lg font-black text-slate-900">🛡️ Garantía</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 text-slate-400 font-bold text-sm flex items-center justify-center">✕</button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <div className={"w-12 h-6 rounded-full transition-all relative "+(form.garantiaActiva?"bg-emerald-500":"bg-slate-200")}
              onClick={()=>setForm({...form,garantiaActiva:!form.garantiaActiva})}>
              <div className={"absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all "+(form.garantiaActiva?"left-6":"left-0.5")}/>
            </div>
            <span className="font-bold text-slate-900 text-sm">Garantía activa</span>
          </label>
          {form.garantiaActiva && (
            <>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Fecha de activación</label>
                <input type="date" className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                  value={form.garantiaFecha} onChange={e=>setForm({...form,garantiaFecha:e.target.value})}/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Usuario de la app</label>
                <input className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                  placeholder="usuario123" value={form.garantiaUsuario} onChange={e=>setForm({...form,garantiaUsuario:e.target.value})}/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Contraseña de la app</label>
                <input className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                  placeholder="••••••••" value={form.garantiaPassword} onChange={e=>setForm({...form,garantiaPassword:e.target.value})}/>
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-amber-700 text-xs">
                ⏳ El contador de 20 días empieza desde la fecha de activación que pongas arriba.
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm">Cancelar</button>
          <button onClick={save} disabled={saving} className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-bold text-sm disabled:opacity-50">
            {saving?"Guardando...":"Guardar"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── LINKS PANEL ───────────────────────────────────────────────────
function LinksPanel({ onClose }) {
  const [links, setLinks]   = useState([]);
  const [horas, setHoras]   = useState(24);
  const [copied, setCopied] = useState(null);
  const load = () => apiFetch("/api/links").then(d=>setLinks(d||[]));
  useEffect(()=>{ load(); },[]);
  const crear = async () => { const r=await apiFetch("/api/links",{method:"POST",body:JSON.stringify({horas})}); if(r?.ok) load(); };
  const eliminar = async (id) => { await apiFetch("/api/links/"+id,{method:"DELETE"}); load(); };
  const copyLink = (token) => { navigator.clipboard.writeText(window.location.origin+"/solicitud/"+token); setCopied(token); setTimeout(()=>setCopied(null),2000); };
  const isExpired = (e) => new Date(e) < new Date();
  return (
    <motion.div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
      <motion.div className="w-full max-w-lg bg-white rounded-[28px] overflow-hidden shadow-2xl flex flex-col" style={{maxHeight:"90vh"}} initial={{scale:0.9,y:20}} animate={{scale:1,y:0}}>
        <div className="bg-slate-900 px-6 py-5 flex items-center justify-between shrink-0">
          <div className="text-white font-black text-lg">🔗 Links de Solicitud</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
        </div>
        <div className="overflow-y-auto p-6 space-y-5 flex-1">
          <div className="rounded-2xl border-2 border-slate-200 p-5 space-y-3">
            <div className="font-black text-slate-900">Generar nuevo link</div>
            <div className="flex gap-3">
              <select className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none" value={horas} onChange={e=>setHoras(Number(e.target.value))}>
                <option value={1}>1 hora</option><option value={6}>6 horas</option><option value={24}>24 horas</option><option value={48}>48 horas</option><option value={168}>7 días</option>
              </select>
              <button onClick={crear} className="px-5 py-2.5 rounded-xl bg-slate-900 text-white font-black text-sm hover:bg-slate-800 active:scale-95 transition-all">Crear</button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="font-black text-slate-900">Links ({links.length})</div>
            {links.length===0&&<div className="text-slate-400 text-sm">No hay links creados aún</div>}
            {links.map(l=>{
              const expired=isExpired(l.expiresAt);
              const url=window.location.origin+"/solicitud/"+l.token;
              const exp=new Date(l.expiresAt).toLocaleString("es-MX",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});
              return (
                <div key={l.id} className={"rounded-xl border px-4 py-3 space-y-2 "+(expired?"bg-slate-50 border-slate-200 opacity-60":"bg-emerald-50 border-emerald-200")}>
                  <div className="flex items-center justify-between">
                    <span className={"text-xs font-bold "+(expired?"text-slate-400":"text-emerald-700")}>{expired?"⛔ Expirado":"✅ Activo"} · {exp}</span>
                    <button onClick={()=>eliminar(l.id)} className="text-red-400 hover:text-red-600 text-xs font-bold">Eliminar</button>
                  </div>
                  <div className="flex gap-2">
                    <input readOnly value={url} className="flex-1 rounded-lg bg-white border border-slate-200 px-3 py-1.5 text-xs text-slate-500 outline-none"/>
                    <button onClick={()=>copyLink(l.token)} className={"px-3 py-1.5 rounded-lg text-xs font-bold transition-all "+(copied===l.token?"bg-emerald-500 text-white":"bg-slate-200 text-slate-700 hover:bg-slate-300")}>{copied===l.token?"¡Copiado!":"Copiar"}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function LoginScreen({ onLogin, notice = "" }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const handleLogin = async () => {
    if (!username || !password) return;
    setLoading(true); setError("");
    const data = await fetch("/api/login", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ username, password })
    }).then(r=>r.json());
    setLoading(false);
    if (data.error) { setError(data.error); return; }
    setToken(data.token);
    onLogin(data.user);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}}
        className="w-full max-w-sm bg-white rounded-[32px] overflow-hidden shadow-2xl">
        <div className="bg-slate-900 p-8 text-center">
          <div className="text-5xl mb-3">📦</div>
          <div className="text-2xl font-black text-white">Royal Pedidos</div>
          <div className="text-slate-400 text-sm mt-1">Ingresa con tu cuenta</div>
        </div>
        <div className="p-8 space-y-4">
          {notice && !error && <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-amber-800 text-sm font-medium">{notice}</div>}
          {error && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm font-medium">{error}</div>}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Usuario</label>
            <input className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              placeholder="tu_usuario" value={username} onChange={e=>setUsername(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleLogin()} autoCapitalize="none" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Contraseña</label>
            <input type="password" className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleLogin()} />
          </div>
          <motion.button whileTap={{scale:0.96}} onClick={handleLogin} disabled={loading}
            className="w-full py-4 rounded-2xl bg-slate-900 text-white font-black text-base disabled:opacity-50">
            {loading ? "Entrando..." : "Entrar →"}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}

function AdminPanel({ onClose, currentUser }) {
  const [users, setUsers]           = useState([]);
  const [unowned, setUnowned]       = useState([]);
  const [form, setForm]             = useState({ nombre:"", username:"", password:"", rol:"user" });
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  // ── Config Google Sheets ──
  const [sheetsCfg, setSheetsCfg]   = useState({ webhook:"", secret:"", conectado:false });
  const [sheetsSaving, setSheetsSaving] = useState(false);
  const [sheetsMsg, setSheetsMsg]   = useState("");
  const esSuperAdmin = currentUser?.isSuperAdmin;

  const load = () => {
    apiFetch("/api/usuarios").then(d => setUsers(d || []));
    apiFetch("/api/pedidos/sin-dueno").then(d => setUnowned(d || []));
    if (currentUser?.isSuperAdmin) {
      apiFetch("/api/config/sheets").then(d => { if (d && !d.error) setSheetsCfg(d); }).catch(()=>{});
    }
  };
  useEffect(()=>{ load(); },[]);

  const guardarSheets = async () => {
    setSheetsSaving(true); setSheetsMsg("");
    const r = await apiFetch("/api/config/sheets", { method:"PUT", body:JSON.stringify({ webhook: sheetsCfg.webhook.trim(), secret: sheetsCfg.secret }) });
    setSheetsSaving(false);
    if (r?.error) { setSheetsMsg("❌ " + r.error); return; }
    setSheetsMsg("✅ Guardado. Tu Google Sheet ya está conectado.");
    setSheetsCfg(c => ({ ...c, conectado: !!c.webhook.trim() }));
  };

  const sincronizarTodo = async () => {
    if (!window.confirm("Esto enviará TODOS tus pedidos al Google Sheet. ¿Continuar?")) return;
    setSheetsMsg("⏳ Sincronizando todos los pedidos...");
    const r = await apiFetch("/api/config/sheets/sync-all", { method:"POST" });
    if (r?.error) { setSheetsMsg("❌ " + r.error); return; }
    setSheetsMsg("✅ Enviando " + r.total + " pedidos al Sheet (puede tardar un momento).");
  };

  // ── Prueba REAL de conexión: el backend manda una fila de prueba al
  //    webhook y nos dice si la hoja respondió de verdad. Esto define todo.
  const [sheetsTest, setSheetsTest]     = useState(null); // { ok, status, body, error, diag }
  const [sheetsTesting, setSheetsTesting] = useState(false);
  const probarConexion = async () => {
    if (!sheetsCfg.webhook.trim()) { setSheetsMsg("❌ Primero pega el link /exec de tu Apps Script."); return; }
    setSheetsTesting(true); setSheetsTest(null); setSheetsMsg("🧪 Probando conexión con tu hoja...");
    let r;
    try {
      r = await apiFetch("/api/config/sheets/test", {
        method: "POST",
        body: JSON.stringify({ webhook: sheetsCfg.webhook.trim(), secret: sheetsCfg.secret })
      });
    } catch (e) {
      r = { ok:false, error:"No se pudo contactar al servidor: " + (e?.message||e) };
    }
    setSheetsTesting(false);
    const status = r?.status;
    const body = (r?.body || "").toString();
    let diag;
    if (r?.ok) {
      diag = { tone:"ok", msg:"✅ ¡Conectó! Tu hoja respondió correctamente. Busca la fila de prueba «🧪 PRUEBA CONEXIÓN» en el Sheet — si está ahí, todo funciona." };
    } else if (status == null) {
      diag = { tone:"err", msg:"❌ El servidor no pudo alcanzar la URL. Revisa que el link termine en /exec y esté bien pegado.\n(" + (r?.error || "sin detalle") + ")" };
    } else if (/accounts\.google\.com|ServiceLogin|Sign in|<html/i.test(body) || status === 302) {
      diag = { tone:"err", msg:"❌ La URL pidió iniciar sesión en vez de ejecutar el script. En Apps Script ve a Implementar → Administrar implementaciones y deja «Ejecutar como: Yo» y «Quién tiene acceso: Cualquier usuario». Vuelve a desplegar y pega la URL /exec nueva." };
    } else if (status === 404) {
      diag = { tone:"err", msg:"❌ La URL no existe (404). Casi seguro re-desplegaste el script y el /exec cambió. Copia la URL nueva desde «Administrar implementaciones» y pégala aquí." };
    } else if (status === 401 || status === 403 || /secret|clave|unauthorized|forbidden/i.test(body)) {
      diag = { tone:"err", msg:"❌ El script rechazó la petición (HTTP " + status + "). La clave secreta no coincide con la del Apps Script." };
    } else {
      diag = { tone:"err", msg:"❌ La hoja respondió con error (HTTP " + status + "). Respuesta: " + (body ? body.slice(0,200) : (r?.error || "sin detalle")) };
    }
    setSheetsTest({ ...r, diag });
    setSheetsMsg("");
  };

  const createUser = async () => {
    setError(""); setSuccess("");
    if (!form.nombre || !form.username || !form.password) return setError("Llena todos los campos");
    if (form.password.length < 6) return setError("La contraseña debe tener al menos 6 caracteres");
    const r = await apiFetch("/api/usuarios", { method:"POST", body:JSON.stringify(form) });
    if (r.error) return setError(r.error);
    setSuccess("Usuario @"+form.username+" creado ✅");
    setForm({ nombre:"", username:"", password:"", rol:"user" });
    load();
  };

  const deleteUser = async (id, uname) => {
    if (id === currentUser.id) return setError("No puedes eliminarte a ti mismo");
    if (!window.confirm("¿Eliminar a @"+uname+"?")) return;
    await apiFetch("/api/usuarios/"+id, { method:"DELETE" });
    load();
  };

  const changePassword = async (id) => {
    const np = window.prompt("Nueva contraseña (mínimo 6 caracteres):");
    if (!np || np.length < 6) return setError("Contraseña muy corta");
    const r = await apiFetch("/api/usuarios/"+id+"/password", { method:"PUT", body:JSON.stringify({ password: np }) });
    if (r?.ok) setSuccess("Contraseña actualizada ✅");
    else setError("Error al actualizar");
  };

  return (
    <motion.div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
      <motion.div className="w-full max-w-lg bg-white rounded-[28px] overflow-hidden shadow-2xl flex flex-col"
        style={{maxHeight:"90vh"}} initial={{scale:0.9,y:20}} animate={{scale:1,y:0}}>
        <div className="bg-slate-900 px-6 py-5 flex items-center justify-between shrink-0">
          <div className="text-white font-black text-lg">👑 Panel de Admin</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
        </div>
        <div className="overflow-y-auto p-6 space-y-6 flex-1">
          <div className="rounded-2xl border-2 border-slate-200 p-5 space-y-3">
            <div className="font-black text-slate-900">Crear nuevo usuario</div>
            {error   && <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-red-700 text-sm">{error}</div>}
            {success && <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-700 text-sm">{success}</div>}
            <input className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="Nombre completo" value={form.nombre} onChange={e=>setForm({...form,nombre:e.target.value})} />
            <input className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="Usuario (sin espacios)" value={form.username} onChange={e=>setForm({...form,username:e.target.value.toLowerCase().replace(/\s/g,"")})} autoCapitalize="none" />
            <input type="password" className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="Contraseña (mín. 6 caracteres)" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} />
            <select className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none" value={form.rol} onChange={e=>setForm({...form,rol:e.target.value})}>
              <option value="user">Usuario normal</option>
              <option value="admin">Administrador</option>
            </select>
            <button onClick={createUser} className="w-full py-3 rounded-xl bg-slate-900 text-white font-black text-sm hover:bg-slate-800 active:scale-95 transition-all">Crear usuario</button>
          </div>
          <div className="space-y-2">
            <div className="font-black text-slate-900">Usuarios ({users.length})</div>
            {users.map(u=>(
              <div key={u.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <div>
                  <div className="font-bold text-slate-900 text-sm">{u.nombre}</div>
                  <div className="text-xs text-slate-500">@{u.username} · {u.rol==="admin"?"👑 Admin":"👤 Usuario"}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={()=>changePassword(u.id)} className="text-blue-400 hover:text-blue-600 text-xs font-bold transition-colors">🔑 Pass</button>
                  {u.id !== currentUser.id && (
                    <button onClick={()=>deleteUser(u.id, u.username)} className="text-red-400 hover:text-red-600 text-xs font-bold transition-colors">Eliminar</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ── GOOGLE SHEETS (solo superadmin) ── */}
          {esSuperAdmin && (
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">📊</span>
                <div className="font-black text-slate-900">Conectar Google Sheet</div>
                {sheetsCfg.conectado && <span className="ml-auto px-2 py-0.5 rounded-full bg-emerald-500 text-white text-xs font-black">Conectado</span>}
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Pega aquí el link de tu Apps Script (termina en <span className="font-mono">/exec</span>). Cada pedido nuevo o editado se escribirá solo en tu hoja, en el orden de tus columnas.
              </p>
              {sheetsMsg && <div className="rounded-xl bg-white border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700">{sheetsMsg}</div>}
              <div>
                <label className="text-xs font-bold text-slate-400 mb-1 block">Link del Apps Script (/exec)</label>
                <input className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400 font-mono"
                  placeholder="https://script.google.com/macros/s/.../exec"
                  value={sheetsCfg.webhook} onChange={e=>setSheetsCfg({...sheetsCfg, webhook:e.target.value})}/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-400 mb-1 block">Clave secreta (opcional, debe coincidir con el script)</label>
                <input className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400"
                  placeholder="ej: royal2024" value={sheetsCfg.secret} onChange={e=>setSheetsCfg({...sheetsCfg, secret:e.target.value})}/>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={guardarSheets} disabled={sheetsSaving}
                  className="flex-1 min-w-[140px] py-2.5 rounded-xl bg-emerald-600 text-white font-black text-sm hover:bg-emerald-700 active:scale-95 transition-all disabled:opacity-50">
                  {sheetsSaving ? "Guardando..." : "Guardar conexión"}
                </button>
                <button onClick={probarConexion} disabled={sheetsTesting}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 text-white font-black text-sm hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50">
                  {sheetsTesting ? "Probando..." : "🧪 Probar conexión"}
                </button>
                {sheetsCfg.conectado && (
                  <button onClick={sincronizarTodo}
                    className="px-4 py-2.5 rounded-xl bg-slate-900 text-white font-black text-sm hover:bg-slate-800 active:scale-95 transition-all">
                    🔄 Sincronizar todo
                  </button>
                )}
              </div>
              {sheetsTest?.diag && (
                <div className={"rounded-xl px-3 py-2.5 text-sm font-medium whitespace-pre-line border-2 "+(sheetsTest.diag.tone==="ok"?"bg-emerald-50 border-emerald-300 text-emerald-800":"bg-red-50 border-red-300 text-red-800")}>
                  {sheetsTest.diag.msg}
                  {sheetsTest.status!=null && <div className="mt-1 text-xs opacity-70">HTTP {sheetsTest.status}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function Celebration({ onDone }) {
  useEffect(()=>{ const t=setTimeout(onDone,2500); return ()=>clearTimeout(t); },[onDone]);
  const pts = Array.from({length:30},(_,i)=>({
    id:i,x:(Math.random()-.5)*600,y:-(Math.random()*400+80),
    r:Math.random()*720,s:Math.random()*14+6,
    c:["#FF9900","#FF3008","#FF00BF","#7C3AED","#10b981","#f59e0b","#3b82f6"][i%7]
  }));
  return (
    <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center overflow-hidden">
      {pts.map(p=>(
        <motion.div key={p.id} className="absolute rounded-sm"
          style={{width:p.s,height:p.s,background:p.c,top:"50%",left:"50%"}}
          initial={{x:0,y:0,rotate:0,opacity:1}} animate={{x:p.x,y:p.y,rotate:p.r,opacity:0}} transition={{duration:1.5,ease:"easeOut"}}/>
      ))}
      <motion.div className="relative text-center" initial={{scale:0,y:50}} animate={{scale:[0,1.3,1],y:0}} transition={{duration:.5}}>
        <div className="text-8xl mb-3">🎉</div>
        <div className="text-5xl font-black text-white drop-shadow-2xl">¡LISTO!</div>
        <div className="text-2xl text-white/80 mt-1">Sigue así 💪</div>
      </motion.div>
    </div>
  );
}

function FocusMode({ onVerCliente, order, onClose, onDone, onRework, onChat, onEdit, onGarantia, onRentas, onPin, pinnedId, isAdmin, onSetStatus, navigableOrders, onNavigate, onCancel, onTrackLink, onRevokeTrackLink, onSetTrackExpiry, onReasign, onRemove, onPostpone, isPostponed }) {
  const p = PC[order.plataforma];
  const d = days(order.entrega);
  const [lightbox, setLightbox] = useState(null);
  const [showStateMenu, setShowStateMenu] = useState(false);
  const sc = SC[order.estado]||{};
  const [fotos, setFotos] = useState(order.fotos||[]);
  const [loadingFotos, setLoadingFotos] = useState(false);
  const [errorFotos, setErrorFotos] = useState(false);
  const [imgError, setImgError] = useState({}); // fotos que fallaron al mostrarse
  const [reoptim, setReoptim] = useState(null); // null | 'trabajando' | 'listo' | 'nada'
  // Re-optimiza las fotos YA guardadas de este pedido (las viejas y pesadas)
  const reoptimizarFotos = async () => {
    if (!fotos || fotos.length === 0) return;
    setReoptim('trabajando');
    try {
      const nuevas = [];
      let cambio = false;
      for (const f of fotos) {
        const opt = await recompressDataUrl(f);
        if (opt && opt !== f) cambio = true;
        nuevas.push(opt || f);
      }
      if (!cambio) { setReoptim('nada'); setTimeout(()=>setReoptim(null), 2500); return; }
      await apiFetch('/api/pedidos/' + order.id, { method:'PUT', body: JSON.stringify({ ...order, fotos: nuevas }) });
      setFotos(nuevas);
      setImgError({});
      setReoptim('listo');
      setTimeout(()=>setReoptim(null), 2500);
    } catch {
      setReoptim(null);
      alert('No se pudieron optimizar las fotos. Intenta de nuevo.');
    }
  };
  const fetchFotosFocus = (intento=0) => {
    setLoadingFotos(true); setErrorFotos(false);
    apiFetch('/api/pedidos/' + order.id + '/fotos')
      .then(f => { if (Array.isArray(f)) setFotos(f); setLoadingFotos(false); })
      .catch(() => {
        // Reintenta solo una vez para recuperarse de fallas pasajeras de red
        if (intento < 1) { setTimeout(() => fetchFotosFocus(intento + 1), 800); }
        else { setLoadingFotos(false); setErrorFotos(true); }
      });
  };
  useEffect(() => { fetchFotosFocus(0); }, [order.id]);

  // ESC para cerrar, flechas para navegar
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); }
      else if (e.key === 'ArrowRight' && navigableOrders && onNavigate) {
        const idx = navigableOrders.findIndex(o => o.id === order.id);
        if (idx !== -1 && idx < navigableOrders.length - 1) onNavigate(navigableOrders[idx + 1].id);
      } else if (e.key === 'ArrowLeft' && navigableOrders && onNavigate) {
        const idx = navigableOrders.findIndex(o => o.id === order.id);
        if (idx > 0) onNavigate(navigableOrders[idx - 1].id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [order.id, onClose, navigableOrders, onNavigate]);

  // Calcular índice actual y si hay siguiente/anterior
  const navIdx = navigableOrders ? navigableOrders.findIndex(o => o.id === order.id) : -1;
  const hasPrev = navIdx > 0;
  const hasNext = navigableOrders && navIdx !== -1 && navIdx < navigableOrders.length - 1;

  // ── Vista combinada: fases (arriba) + info (izq) + checklist y acciones (der) ──
  const [masAbierto, setMasAbierto] = useState(false);
  const [verCliente, setVerCliente] = useState(false);
  const [verEstados, setVerEstados] = useState(false);
  useEffect(() => { setMasAbierto(false); setVerCliente(false); setVerEstados(false); }, [order.id]);

  // Línea de fases (los estados de pausa se pintan sobre la fase en la que ocurren)
  const FASES = [
    { e: "Pendiente Revisión",   l: "Revisión" },
    { e: "Aceptado",             l: "Aceptado" },
    { e: "En Producción",        l: "Producción" },
    { e: "Pendiente Aprobación", l: "Aprobación" },
    { e: "Terminado",            l: "Terminado" },
    { e: "VERI FACIAL",          l: "Veri facial" },
    { e: "Entregado",            l: "Entregado" },
  ];
  const ALIAS_FASE = { "Rehacer": "En Producción", "Esperando Cliente": "Pendiente Aprobación", "Background Check": "Pendiente Aprobación" };
  const esCierreMalo = ESTADOS_TERMINALES_NEG.includes(order.estado);
  const faseIdx = FASES.findIndex(f => f.e === (ALIAS_FASE[order.estado] || order.estado));
  const enPausa = !!ALIAS_FASE[order.estado];

  // Checklist: lo que le falta al pedido para cerrarlo
  const nExcel = CAMPOS_EXCEL.filter(f => String(order[f.k] ?? "").trim() !== "").length;
  const precioNum = parseFloat(order.precio) || 0;
  const saldoNum = parseFloat(order.saldo);
  const pagado = order.estadoPago === "al-corriente" || (precioNum > 0 && !isNaN(saldoNum) && saldoNum <= 0.01);
  const abrirEditar = () => { onClose(); setTimeout(() => onEdit(order.id), 200); };
  const checklist = [
    { k: "fotos",  l: "Fotos",                ok: fotos && fotos.length > 0, extra: fotos?.length ? fotos.length : "", accion: isAdmin && onEdit ? { t: "Subir", f: abrirEditar } : null },
    { k: "excel",  l: "Datos del Excel",      ok: nExcel > 0, extra: nExcel ? nExcel + "/" + CAMPOS_EXCEL.length : "", accion: isAdmin && onEdit ? { t: "Llenar", f: abrirEditar } : null },
    ...(isAdmin ? [
      { k: "precio", l: "Precio",             ok: precioNum > 0, extra: precioNum > 0 ? "$" + precioNum : "", accion: onEdit ? { t: "Poner", f: abrirEditar } : null },
      { k: "pago",   l: "Pago",               ok: pagado, extra: pagado ? "al corriente" : (precioNum > 0 && !isNaN(saldoNum) ? "debe $" + saldoNum : ""), accion: onRentas ? { t: "Cobrar", f: () => { onClose(); setTimeout(() => onRentas(order.id), 200); } } : null },
    ] : []),
  ];
  const faltan = checklist.filter(c => !c.ok).length;

  // Botón principal según la fase
  const principal = order.estado === "VERI FACIAL"
    ? { t: "✅ Veri facial OK — entregar", c: "bg-emerald-500 hover:bg-emerald-600" }
    : order.estado === "Terminado"
      ? { t: "🤳 Enviar a veri facial", c: "bg-cyan-500 hover:bg-cyan-600" }
      : { t: "✅ Terminé esto", c: "bg-emerald-500 hover:bg-emerald-600" };

  const ItemMas = ({ icon, children, onClick, danger, activo }) => (
    <button onClick={onClick}
      className={"w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-left transition-colors " + (danger ? "text-red-600 hover:bg-red-50" : activo ? "bg-amber-50 text-amber-800 font-bold" : "text-slate-700 hover:bg-slate-50")}>
      <span className="w-5 text-center shrink-0">{icon}</span><span className="flex-1">{children}</span>
    </button>
  );
  const Grupo = ({ titulo, children }) => (
    <div>
      <div className="px-3 pt-2 pb-1 text-[11px] font-black text-slate-400 uppercase tracking-wide">{titulo}</div>
      {children}
    </div>
  );

  return (
    <motion.div className="fixed inset-0 z-40 flex items-stretch sm:items-center justify-center sm:p-4"
      style={{ background: "rgba(0,0,0,0.85)" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div className="w-full max-w-6xl bg-white sm:rounded-[28px] shadow-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: "100dvh" }}
        initial={{ scale: .96, y: 30 }} animate={{ scale: 1, y: 0 }} transition={{ type: "spring", damping: 22 }}>

        {/* ── Barra superior: navegación + app + cerrar ── */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-1">
            {navigableOrders && navigableOrders.length > 1 && (<>
              <button onClick={() => hasPrev && onNavigate(navigableOrders[navIdx - 1].id)} disabled={!hasPrev}
                className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 font-black disabled:opacity-30 hover:bg-slate-200" title="Anterior (←)">←</button>
              <span className="text-xs font-bold text-slate-400 px-2 tabular-nums">{navIdx + 1} de {navigableOrders.length}</span>
              <button onClick={() => hasNext && onNavigate(navigableOrders[navIdx + 1].id)} disabled={!hasNext}
                className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 font-black disabled:opacity-30 hover:bg-slate-200" title="Siguiente (→)">→</button>
            </>)}
          </div>
          <div className="flex items-center gap-2">
            <span className={"px-3 py-1.5 rounded-full text-white text-xs font-black " + p.bg}>{p.emoji} {order.plataforma}</span>
            <button onClick={onClose} title="Cerrar (ESC)" className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold hover:bg-slate-200">✕</button>
          </div>
        </div>

        {/* ── Línea de fases ── */}
        <div className="px-4 sm:px-6 pt-3 pb-3 border-b border-slate-100 shrink-0">
          {esCierreMalo ? (
            <div className={"rounded-xl px-4 py-2.5 text-sm font-black text-center " + sc.bg + " " + sc.text}>{sc.emoji} Pedido cerrado: {order.estado}</div>
          ) : (<>
            <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${FASES.length}, minmax(0,1fr))` }}>
              {FASES.map((f, i) => {
                const hecho = i < faseIdx, actual = i === faseIdx;
                return (
                  <button key={f.e} type="button" disabled={!isAdmin || !onSetStatus || actual}
                    onClick={() => onSetStatus(order.id, f.e)}
                    title={isAdmin ? "Mover a " + f.e : f.e}
                    className="group flex flex-col gap-1.5 text-left disabled:cursor-default">
                    <div className={"h-2 rounded-full transition-all " + (actual ? (order.estado === "Rehacer" ? "bg-rose-500" : enPausa ? "bg-amber-400" : "bg-blue-500") : hecho ? "bg-emerald-400" : "bg-slate-200 group-hover:bg-slate-300")}/>
                    <span className={"text-[11px] leading-tight truncate " + (actual ? "font-black text-slate-900" : hecho ? "text-emerald-700 font-bold" : "text-slate-400")}>
                      {hecho ? "✓ " : ""}{f.l}
                    </span>
                  </button>
                );
              })}
            </div>
            {enPausa && <div className="mt-2 text-xs font-bold text-amber-700">{sc.emoji} {order.estado}</div>}
          </>)}
        </div>

        {/* ── Cuerpo en dos columnas (en celular se apila) ── */}
        <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">

          {/* IZQUIERDA: la info del pedido */}
          <div className="p-4 sm:p-6 space-y-4 lg:overflow-y-auto lg:border-r border-slate-100">
            <div>
              <div className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight">{order.cliente}</div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <TituloChip order={order} className="text-sm px-3 py-1"/>
                <span className={"px-3 py-1 rounded-full text-xs font-black " + sc.bg + " " + sc.text}>{sc.emoji} {order.estado}</span>
                {d !== null && d <= 1 && order.estado !== "Entregado" && order.estado !== "Terminado" && (
                  <motion.span animate={{ scale: [1, 1.06, 1] }} transition={{ repeat: Infinity, duration: 1 }}
                    className="px-3 py-1 rounded-full bg-red-100 text-red-700 font-black text-xs">
                    🚨 {d === 0 ? "Entrega hoy" : d < 0 ? "Vencido hace " + Math.abs(d) + "d" : "Entrega mañana"}
                  </motion.span>
                )}
                {order.chatPendiente && <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-black text-xs">💬 Cliente esperando</span>}
                {isPostponed && <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-black text-xs">⌛ Aplazado</span>}
              </div>
            </div>

            {order.notas && <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-4 text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{order.notas}</div>}
            {order.mensajeCliente && <div className="rounded-2xl bg-orange-50 border border-orange-200 p-3 text-sm text-orange-900">💬 {order.mensajeCliente}</div>}

            {/* Fotos */}
            {loadingFotos && <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">{[0, 1, 2, 3].map(i => <div key={i} className="aspect-square rounded-xl bg-slate-100 animate-pulse"/>)}</div>}
            {!loadingFotos && errorFotos && (
              <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                <span className="text-slate-500 text-sm">No se pudieron cargar las fotos</span>
                <button onClick={() => fetchFotosFocus(0)} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-bold">🔄 Reintentar</button>
              </div>
            )}
            {!loadingFotos && !errorFotos && fotos && fotos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {fotos.map((src, i) => (
                  <div key={i} className="relative aspect-square cursor-zoom-in" onClick={() => !imgError[i] && setLightbox(i)}>
                    {imgError[i] ? (
                      <div className="w-full h-full rounded-xl border border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1">
                        <span className="text-slate-300 text-lg">🖼️</span>
                        <button onClick={e => { e.stopPropagation(); setImgError(pp => ({ ...pp, [i]: false })); }} className="text-[10px] text-slate-500 font-bold underline">reintentar</button>
                      </div>
                    ) : (
                      <img src={src} alt="" loading="lazy" decoding="async" onError={() => setImgError(pp => ({ ...pp, [i]: true }))}
                        className="w-full h-full object-cover rounded-xl border border-slate-200 hover:opacity-85 transition-opacity"/>
                    )}
                  </div>
                ))}
              </div>
            )}
            {isAdmin && !loadingFotos && !errorFotos && fotos && fotos.length > 0 && (() => {
              const pesadas = fotos.filter(f => bytesDeDataUrl(f) > 1600000).length;
              const hayError = Object.values(imgError).some(Boolean);
              if (pesadas === 0 && !hayError && reoptim !== 'listo' && reoptim !== 'nada') return null;
              return (
                <button onClick={reoptimizarFotos} disabled={reoptim === 'trabajando'}
                  className={"w-full py-2 rounded-xl text-xs font-bold disabled:opacity-60 " + (reoptim === 'listo' ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800 hover:bg-amber-200")}>
                  {reoptim === 'trabajando' ? '⏳ Optimizando fotos...' : reoptim === 'listo' ? '✅ Fotos optimizadas' : reoptim === 'nada' ? 'Las fotos ya estaban optimizadas' : `🗜 Optimizar ${pesadas} foto${pesadas !== 1 ? 's' : ''} pesada${pesadas !== 1 ? 's' : ''}`}
                </button>
              );
            })()}
            <AnimatePresence>{lightbox !== null && <Lightbox fotos={fotos} startIndex={lightbox} onClose={() => setLightbox(null)}/>}</AnimatePresence>

            <DatosExcel order={order}/>

            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <button type="button" onClick={() => setVerCliente(v => !v)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
                <span>👁 Lo que ve el cliente</span><span>{verCliente ? "▲" : "▼"}</span>
              </button>
              {verCliente && <div className="bg-slate-900 p-4"><F1TrackBar estado={order.estado} createdAt={order.createdAt} stateChangedAt={order.stateChangedAt}/></div>}
            </div>
          </div>

          {/* DERECHA: checklist + qué hacer */}
          <div className="p-4 sm:p-6 space-y-4 lg:overflow-y-auto bg-slate-50/60">
            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-black text-slate-400 uppercase tracking-wide">Lo que falta</span>
                <span className={"text-[11px] font-black " + (faltan ? "text-amber-600" : "text-emerald-600")}>{faltan ? `${checklist.length - faltan} de ${checklist.length} listo` : "✓ Todo listo"}</span>
              </div>
              <div className="space-y-1.5">
                {checklist.map(c => (
                  <div key={c.k} className={"flex items-center gap-2.5 rounded-xl px-3 py-2.5 bg-white border " + (c.ok ? "border-slate-100" : "border-amber-200")}>
                    <span className={"w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-black shrink-0 " + (c.ok ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-700")}>{c.ok ? "✓" : "!"}</span>
                    <span className={"text-sm font-bold flex-1 " + (c.ok ? "text-slate-500" : "text-slate-800")}>{c.l}{c.extra ? <span className="font-medium text-slate-400"> · {c.extra}</span> : null}</span>
                    {!c.ok && c.accion && (
                      <button onClick={c.accion.f} className="px-2.5 py-1 rounded-lg bg-slate-900 text-white text-xs font-black active:scale-95">{c.accion.t}</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Siguiente paso */}
            <div className="space-y-2">
              <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide">Siguiente paso</div>
              {!esCierreMalo && order.estado !== "Entregado" && (
                <motion.button whileTap={{ scale: .97 }} onClick={() => onDone(order.id)}
                  className={"w-full py-5 rounded-2xl text-white text-xl font-black shadow-lg transition-colors " + principal.c}>
                  {principal.t}
                </motion.button>
              )}
              {order.estado === "VERI FACIAL" && (
                <div className="rounded-2xl border-2 border-red-100 bg-red-50 p-3">
                  <div className="text-xs font-black text-red-600 text-center mb-2">❌ ¿Falló la veri facial?</div>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => onRework(order.id)} className="py-2.5 rounded-xl bg-rose-100 text-rose-700 text-xs font-black">🔄 Rehacer</button>
                    {isAdmin && onCancel ? <button onClick={() => { onCancel(order.id); onClose(); }} className="py-2.5 rounded-xl bg-slate-200 text-slate-600 text-xs font-black">🚫 Cancelar</button> : <div/>}
                    {isAdmin && onSetStatus ? <button onClick={() => { onSetStatus(order.id, "Intento Fallido"); onClose(); }} className="py-2.5 rounded-xl bg-red-200 text-red-800 text-xs font-black">⛔ Fallido</button> : <div/>}
                  </div>
                </div>
              )}
              {(esCierreMalo || order.estado === "Entregado") && (
                <div className="rounded-2xl bg-slate-100 text-slate-500 text-sm font-bold text-center py-4">Este pedido ya está cerrado.</div>
              )}

              {/* 3 secundarios fijos */}
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => onRework(order.id)} className="py-3 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs font-black hover:bg-rose-50 hover:border-rose-200 flex flex-col items-center gap-1">
                  <span className="text-lg">🔄</span>Rehacer
                </button>
                {onPostpone ? (
                  <button onClick={() => onPostpone(order.id)} className={"py-3 rounded-xl border text-xs font-black flex flex-col items-center gap-1 " + (isPostponed ? "bg-amber-400 border-amber-400 text-amber-950" : "bg-white border-slate-200 text-slate-700 hover:bg-amber-50")}>
                    <span className="text-lg">{isPostponed ? "⬆️" : "⌛"}</span>{isPostponed ? "Traer de vuelta" : "Aplazar"}
                  </button>
                ) : <div/>}
                {isAdmin && onEdit ? (
                  <button onClick={abrirEditar} className="py-3 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs font-black hover:bg-blue-50 flex flex-col items-center gap-1">
                    <span className="text-lg">✏️</span>Editar
                  </button>
                ) : <div/>}
              </div>
            </div>

            {/* Más opciones */}
            {isAdmin && (
              <div className="rounded-2xl bg-white border border-slate-200">
                <button onClick={() => setMasAbierto(v => !v)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-black text-slate-700">
                  <span>Más opciones</span><span className="text-slate-400">{masAbierto ? "▲" : "▼"}</span>
                </button>
                {masAbierto && (
                  <div className="px-1 pb-2 border-t border-slate-100">
                    <Grupo titulo="Cliente">
                      {onVerCliente && order.clienteId && <ItemMas icon="👤" onClick={() => onVerCliente(order.clienteId)}>Ficha del cliente</ItemMas>}
                      {onChat && <ItemMas icon="💬" activo={order.chatPendiente} onClick={() => onChat(order.id)}>{order.chatPendiente ? "Quitar chat pendiente" : "Marcar chat pendiente"}</ItemMas>}
                      {onTrackLink && <ItemMas icon="🔗" onClick={() => onTrackLink(order.id)}>Copiar link de seguimiento</ItemMas>}
                      {onSetTrackExpiry && <ItemMas icon="📅" onClick={() => { const f = window.prompt('Fecha de expiración del link (AAAA-MM-DD)\nVacío = sin expiración:'); if (f === null) return; onSetTrackExpiry(order.id, f.trim() || null); }}>Expiración del link</ItemMas>}
                      {onRevokeTrackLink && <ItemMas icon="🗑" onClick={() => onRevokeTrackLink(order.id)}>Revocar link</ItemMas>}
                    </Grupo>
                    <Grupo titulo="Dinero">
                      {onRentas && <ItemMas icon="💰" onClick={() => { onClose(); setTimeout(() => onRentas(order.id), 200); }}>Pagos{order.estadoPago === "debe" ? " · 🚨 debe" : ""}</ItemMas>}
                      {onGarantia && <ItemMas icon="🛡️" activo={order.garantiaActiva} onClick={() => { onClose(); setTimeout(() => onGarantia(order.id), 200); }}>{order.garantiaActiva ? "Garantía activa" : "Activar garantía"}</ItemMas>}
                    </Grupo>
                    <Grupo titulo="Gestión">
                      {onSetStatus && <ItemMas icon="🔀" onClick={() => setVerEstados(v => !v)}>Cambiar a otro estado {verEstados ? "▲" : "▼"}</ItemMas>}
                      {verEstados && onSetStatus && (
                        <div className="mx-2 my-1 rounded-xl border border-slate-200 overflow-hidden max-h-64 overflow-y-auto">
                          <EstadoMenuItems current={order.estado} onPick={est => { onSetStatus(order.id, est); setVerEstados(false); }}/>
                        </div>
                      )}
                      {onPin && <ItemMas icon="📌" activo={pinnedId === order.id} onClick={() => onPin(order.id)}>{pinnedId === order.id ? "Quitar de arriba" : "Fijar arriba"}</ItemMas>}
                      {onReasign && <div className="grid grid-cols-2 px-2 py-1"><ReasignDropdown order={order} onReasign={onReasign}/></div>}
                      {onCancel && <ItemMas icon="🚫" danger onClick={() => { if (window.confirm('¿Cancelar este pedido? Quedará archivado y podrás reactivarlo después.')) { onCancel(order.id); onClose(); } }}>Cancelar pedido</ItemMas>}
                      {onRemove && <ItemMas icon="🗑" danger onClick={() => { if (window.confirm('¿Eliminar este pedido de forma permanente? No se puede deshacer.')) { onRemove(order.id); onClose(); } }}>Eliminar pedido</ItemMas>}
                    </Grupo>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function PhotoUpload({ fotos=[], onChange }) {
  const fileRef = useRef();
  const [procesando, setProcesando] = useState(0);
  const handleFiles = useCallback((files) => {
    const imgs = Array.from(files).filter(f=>f.type.startsWith("image/"));
    if (imgs.length === 0) return;
    setProcesando(p => p + imgs.length);
    imgs.forEach(async file => {
      try {
        // Comprime a alta calidad pero manejable: evita que fotos de celular
        // de varios MB tarden en subir/cargar o fallen por tamaño.
        const dataUrl = await compressImageHQ(file);
        if (dataUrl) onChange(prev=>[...(prev||[]), dataUrl]);
      } catch {
        const reader = new FileReader();
        reader.onload = e => onChange(prev=>[...(prev||[]), e.target.result]);
        reader.readAsDataURL(file);
      } finally {
        setProcesando(p => Math.max(0, p - 1));
      }
    });
  }, [onChange]);
  const handleDrop = useCallback(e=>{ e.preventDefault(); handleFiles(e.dataTransfer.files); },[handleFiles]);
  // FIX: usa splice en lugar de filter para no tener bug con fotos duplicadas
  const remove = useCallback((i) => {
    onChange(prev => {
      const next = [...(prev||[])];
      next.splice(i, 1);
      return next;
    });
  }, [onChange]);
  return (
    <div className="space-y-2">
      {fotos&&fotos.length>0&&(
        <div className="grid grid-cols-3 gap-2">
          {fotos.map((src,i)=>(
            <div key={i} className="relative">
              <img src={src} alt="" className="w-full h-20 object-cover rounded-xl border border-slate-200"/>
              {/* FIX: botón siempre visible — group-hover no funciona en móvil/touch */}
              <button
                onClick={e=>{ e.stopPropagation(); remove(i); }}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-black flex items-center justify-center shadow-md active:scale-90 transition-transform"
              >✕</button>
            </div>
          ))}
        </div>
      )}
      <div onDrop={handleDrop} onDragOver={e=>e.preventDefault()} onClick={()=>fileRef.current?.click()}
        className="border-2 border-dashed border-slate-200 rounded-xl px-4 py-4 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-all">
        <div className="text-2xl mb-1">{procesando>0?"⏳":"📷"}</div>
        <div className="text-xs text-slate-500 font-medium">{procesando>0 ? `Optimizando ${procesando} foto${procesando!==1?'s':''}...` : "Toca para agregar fotos o arrastra aquí"}</div>
        <div className="text-xs text-slate-400 mt-0.5">Desde galería o cámara</div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e=>handleFiles(e.target.files)}/>
    </div>
  );
}

function EditModal({ order, onSave, onClose, orders }) {
  const [tituloEstado, setTituloEstado] = useState('vacio');
  const [form, setForm] = useState({
    cliente:order.cliente, clienteId:order.clienteId||"", titulo:order.titulo||"", plataforma:order.plataforma, estado:order.estado,
    prioridad:order.prioridad, entrega:order.entrega||"", notas:order.notas||"",
    chatPendiente:order.chatPendiente||false, rehacer:order.rehacer||false, fotos:[], mensajeAdmin:order.mensajeAdmin||'',
    // ── campos del Excel/Sheet ──
    mail:order.mail||"", dato:order.dato||"", telefonoName:order.telefonoName||"",
    dl:order.dl||"", dob:order.dob||"", ssn:order.ssn||"", tipoCuenta:order.tipoCuenta||"",
    creditoDebito:order.creditoDebito||"", precio:order.precio||"", abonado:order.abonado||"",
    saldo:order.saldo||"", pagoHasta:order.pagoHasta||"", documentNumber:order.documentNumber||"",
    report:order.report||"", whyDeactivated:order.whyDeactivated||"", infoDatoFinal:order.infoDatoFinal||""
  });
  const [showCamposExcel, setShowCamposExcel] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingFotos, setLoadingFotos] = useState(true);
  // flag: ¿conocemos el estado real de las fotos del servidor?
  const fotosLoadedRef = useRef(false);
  // flag: ¿el usuario agregó o quitó fotos en esta edición?
  const fotosTouchedRef = useRef(false);
  const setFotos = useCallback(u=>{ fotosTouchedRef.current = true; setForm(p=>({...p,fotos:typeof u==="function"?u(p.fotos):u})); },[]);
  useEffect(() => {
    fotosLoadedRef.current = false;
    apiFetch('/api/pedidos/' + order.id + '/fotos')
      .then(f => {
        if (Array.isArray(f)) {
          setForm(p=>({...p, fotos:f}));
          fotosLoadedRef.current = true;
        }
      })
      .catch(()=>{ fotosLoadedRef.current = true; }) // si falla, permitir guardar igual
      .finally(()=>setLoadingFotos(false));
  }, [order.id]);
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Solo mandamos las fotos si conocemos su estado real (se cargaron)
      // o si el usuario las modificó. Así nunca bloqueamos el guardado y
      // tampoco borramos fotos existentes por accidente.
      const enviarFotos = fotosLoadedRef.current || fotosTouchedRef.current;
      const payload = {...order,...form,cliente:form.cliente.trim(),titulo:(form.titulo||'').trim().replace(/\s+/g,' ')};
      if (!enviarFotos) delete payload.fotos; // dejar intactas las fotos del servidor
      await onSave(payload);
    }
    finally { setSaving(false); }
  };
  return (
    <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{background:"rgba(0,0,0,0.6)"}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <motion.div className="w-full sm:max-w-lg bg-white sm:rounded-[28px] rounded-t-[28px] shadow-2xl overflow-hidden flex flex-col"
        style={{maxHeight:"90vh"}} initial={{y:60,opacity:0}} animate={{y:0,opacity:1}} exit={{y:60,opacity:0}} transition={{type:"spring",damping:22}}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-slate-100 shrink-0">
          <div className="text-lg font-black text-slate-900">✏️ Editar pedido</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 text-slate-400 font-bold text-sm flex items-center justify-center hover:bg-slate-200">✕</button>
        </div>
        <div className="overflow-y-auto px-6 py-4 space-y-3 flex-1">
          <ClientePicker value={form.cliente} clienteId={form.clienteId}
            onChange={({cliente,clienteId})=>setForm(f=>({...f,cliente,clienteId}))}
            onPrefill={d=>setForm(f=>({...f, telefonoName:f.telefonoName||d.telefonoName, mail:f.mail||d.mail}))}/>
          <TituloPedidoInput value={form.titulo} onChange={v=>setForm(f=>({...f,titulo:v}))}
            cliente={form.cliente} clienteId={form.clienteId} pedidoId={order.id} orders={orders} onEstado={setTituloEstado}/>
          <div className="grid grid-cols-2 gap-3">
            <select className="rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none font-bold" value={form.plataforma} onChange={e=>setForm({...form,plataforma:e.target.value})}>{PLATAFORMAS.map(p=><option key={p}>{p}</option>)}</select>
            <select className="rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" value={form.prioridad} onChange={e=>setForm({...form,prioridad:e.target.value})}>{PRIORIDADES.map(p=><option key={p}>{p}</option>)}</select>
            <select className="rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" value={form.estado} onChange={e=>setForm({...form,estado:e.target.value})}>
              {ESTADOS_GRUPOS.map(g=>(
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((s,i)=><option key={s} value={s}>{g.flujo?(i+1)+". ":""}{s}</option>)}
                </optgroup>
              ))}
            </select>
            <input type="date" className="rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" value={form.entrega} onChange={e=>setForm({...form,entrega:e.target.value})}/>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer font-medium"><input type="checkbox" className="w-4 h-4" checked={form.chatPendiente} onChange={e=>setForm({...form,chatPendiente:e.target.checked})}/>💬 Chat pendiente</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer font-medium"><input type="checkbox" className="w-4 h-4" checked={form.rehacer} onChange={e=>setForm({...form,rehacer:e.target.checked})}/>🔄 Rehacer</label>
          </div>
          <textarea className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none resize-none" rows={2} placeholder="Notas..." value={form.notas} onChange={e=>setForm({...form,notas:e.target.value})}/>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">💬 Mensaje para el cliente (visible en seguimiento)</label>
            <textarea className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none resize-none" rows={2} placeholder="Ej: Tu orden está en proceso, te avisamos en 24hrs..." value={form.mensajeAdmin} onChange={e=>setForm({...form,mensajeAdmin:e.target.value})}/>
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">📷 Fotos de la orden</div>
            {loadingFotos
              ? <div className="grid grid-cols-3 gap-2">{[0,1,2].map(i=><div key={i} className="w-full h-16 rounded-xl bg-slate-100 animate-pulse"/>)}</div>
              : <PhotoUpload fotos={form.fotos} onChange={setFotos}/>
            }
          </div>

          {/* ── CAMPOS DEL EXCEL (plegable) ── */}
          <div>
            <button type="button" onClick={()=>setShowCamposExcel(v=>!v)}
              className="w-full flex items-center justify-between rounded-xl bg-emerald-50 border-2 border-emerald-200 px-4 py-3 text-emerald-800 font-bold text-sm hover:bg-emerald-100 transition-all">
              <span>📊 Datos para el Excel {showCamposExcel ? "(ocultar)" : ""}</span>
              <span>{showCamposExcel ? "▲" : "▼"}</span>
            </button>
            {showCamposExcel && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                {[
                  {k:"mail",label:"Mail",type:"email"},
                  {k:"telefonoName",label:"Teléfono o Name"},
                  {k:"dato",label:"Dato"},
                  {k:"dl",label:"DL (licencia)"},
                  {k:"dob",label:"DOB (nacimiento)"},
                  {k:"ssn",label:"SSN"},
                  // Desplegables iguales a la validación del Google Sheet:
                  {k:"tipoCuenta",label:"Type",options:EXCEL_TYPE},
                  {k:"creditoDebito",label:"Crédito/Débito",options:EXCEL_CREDITO_DEBITO},
                  // Precio y Abonado enlazados: el Saldo se calcula solo.
                  {k:"precio",label:"Precio ($)",type:"number",num:true},
                  {k:"abonado",label:"Abonado ($)",type:"number",num:true},
                  {k:"saldo",label:"Saldo (auto: Precio − Abonado)",type:"number",readOnly:true},
                  {k:"pagoHasta",label:"Pago hasta",type:"date"},
                  {k:"documentNumber",label:"Document Number"},
                  {k:"report",label:"Report"},
                  {k:"whyDeactivated",label:"Why deactivated"},
                  {k:"infoDatoFinal",label:"Info dato final"},
                ].map(f=>(
                  <div key={f.k} className={f.k==="infoDatoFinal"||f.k==="whyDeactivated"?"sm:col-span-2":""}>
                    <label className="text-xs font-bold text-slate-400 mb-1 block">{f.label}</label>
                    {f.options ? (
                      <select className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-400 bg-white"
                        value={form[f.k]} onChange={e=>setForm({...form,[f.k]:e.target.value})}>
                        <option value="">— Sin definir —</option>
                        {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                        {form[f.k] && !f.options.includes(form[f.k]) && <option value={form[f.k]}>{form[f.k]} (valor viejo)</option>}
                      </select>
                    ) : (
                      <input type={f.type||"text"} readOnly={!!f.readOnly}
                        className={"w-full rounded-lg border-2 px-3 py-2 text-sm outline-none "+(f.readOnly?"border-slate-200 bg-slate-100 text-slate-500 font-bold":"border-slate-200 focus:border-emerald-400")}
                        value={form[f.k]}
                        onChange={e=>{
                          const v = e.target.value;
                          if (!f.num) { setForm({...form,[f.k]:v}); return; }
                          // Al tocar Precio o Abonado, el Saldo se recalcula automáticamente
                          setForm(p=>{
                            const nf = {...p, [f.k]: v};
                            const pr = parseFloat(nf.precio);
                            const ab = parseFloat(nf.abonado)||0;
                            nf.saldo = isNaN(pr) ? "" : String(Math.max(0, +(pr-ab).toFixed(2)));
                            return nf;
                          });
                        }}/>
                    )}
                  </div>
                ))}
                <div className="sm:col-span-2 text-[11px] text-slate-400">💡 El Saldo se calcula solo (Precio − Abonado). Los pagos que registres en “💰 Rentas/Pagos” también actualizan Abonado y Saldo automáticamente, aquí y en el Google Sheet.</div>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 shrink-0">
          <button onClick={onClose} disabled={saving} className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 active:scale-95 transition-all">Cancelar</button>
          <button onClick={handleSave} disabled={saving||tituloEstado==='ocupado'} className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 active:scale-95 transition-all disabled:opacity-60">
            {saving ? <span className="flex items-center justify-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>Guardando...</span> : '💾 Guardar'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ReasignDropdown({ order, onReasign }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(()=>{ if(open) apiFetch('/api/usuarios').then(u=>setUsers(u||[])); },[open]);
  return (
    <div className="col-span-2 relative">
      <button onClick={()=>setOpen(v=>!v)} className="w-full py-2 rounded-xl text-xs font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
        👥 {order.asignadoNombre?'Asignado: '+order.asignadoNombre:'Reasignar pedido'}
      </button>
      {open&&(
        <div className="absolute bottom-full mb-1 left-0 right-0 bg-white rounded-xl shadow-xl border border-slate-200 z-30 overflow-y-auto max-h-48">
          <div className="px-3 py-2 text-xs font-bold text-slate-400 border-b border-slate-100">Asignar a:</div>
          {users.map(u=>(
            <button key={u.id} onClick={()=>{ onReasign(order.id,u.id,u.nombre); setOpen(false); }}
              className={"w-full text-left px-3 py-2 text-sm hover:bg-slate-50 "+(order.asignadoA===u.id?"font-black text-indigo-700":"text-slate-700")}>
              {u.nombre} <span className="text-xs text-slate-400">@{u.username}</span>
            </button>
          ))}
          <button onClick={()=>{ onReasign(order.id,'',''); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-50 border-t border-slate-100">✕ Sin asignar</button>
        </div>
      )}
    </div>
  );
}

// Menú de estados ORDENADO: primero el flujo paso a paso (numerado),
// después los cierres por fallo. Se usa igual en las tarjetas y en FocusMode.
function EstadoMenuItems({ current, onPick }) {
  return (<>
    {ESTADOS_GRUPOS.map(g => (
      <div key={g.label}>
        <div className="px-3 py-1.5 text-[10px] font-black text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100">{g.flujo ? "📈 " : "🛑 "}{g.label}</div>
        {g.items.map((est, i) => {
          const sc = SC[est] || {};
          const isActive = current === est;
          return (
            <button key={est} onClick={() => onPick(est)}
              className={"w-full text-left px-3 py-2.5 flex items-center gap-2 text-sm transition-colors " + (isActive ? "bg-slate-100 font-black" : "hover:bg-slate-50 font-medium")}>
              {g.flujo && <span className="text-[10px] font-black text-slate-300 w-4 text-right shrink-0">{i + 1}.</span>}
              <span className={"px-2 py-0.5 rounded-full text-xs font-bold " + sc.bg + " " + sc.text}>{sc.emoji}</span>
              <span className={isActive ? "text-slate-900" : "text-slate-700"}>{est}</span>
              {isActive && <span className="ml-auto text-slate-400 text-xs">← actual</span>}
            </button>
          );
        })}
      </div>
    ))}
  </>);
}

function StatusSelector({ order, onCycle, isAdmin, onSetStatus }) {
  const [open, setOpen] = useState(false);
  const s = SC[order.estado] || {};
  if (!isAdmin) return (
    <span className={"shrink-0 px-2.5 py-1 rounded-full text-xs font-bold cursor-default "+s.bg+" "+s.text}>{s.emoji} {order.estado}</span>
  );
  return (
    <div className="relative shrink-0">
      <button onClick={()=>setOpen(v=>!v)}
        className={"px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 "+s.bg+" "+s.text}>
        {s.emoji} {order.estado} <span className="opacity-70">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden min-w-[220px] max-h-[60vh] overflow-y-auto">
          <div className="px-3 py-2 text-xs font-black text-slate-400 uppercase tracking-wide border-b border-slate-100">Cambiar estado</div>
          <EstadoMenuItems current={order.estado} onPick={est=>{ onSetStatus(order.id, est); setOpen(false); }}/>
        </div>
      )}
    </div>
  );
}

// ── DATOS PARA EL EXCEL (solo lectura, copiable) ──────────────────
// Muestra los campos del Sheet SIN tener que abrir "Editar pedido".
// - Tocar un valor lo copia.
// - "Copiar todo" copia la lista Etiqueta: valor.
// - "Copiar fila Sheet" copia una fila lista para pegar en el Google Sheet
//   en el orden exacto de columnas A→U.
const CAMPOS_EXCEL = [
  { k:"mail",           label:"Mail" },
  { k:"telefonoName",   label:"Teléfono o Name" },
  { k:"dato",           label:"Dato" },
  { k:"dl",             label:"DL" },
  { k:"dob",            label:"DOB" },
  { k:"ssn",            label:"SSN" },
  { k:"tipoCuenta",     label:"Type" },
  { k:"creditoDebito",  label:"Crédito/Débito" },
  { k:"precio",         label:"Precio" },
  { k:"abonado",        label:"Abonado" },
  { k:"saldo",          label:"Saldo" },
  { k:"pagoHasta",      label:"Pago hasta" },
  { k:"documentNumber", label:"Document Number" },
  { k:"report",         label:"Report" },
  { k:"whyDeactivated", label:"Why deactivated" },
  { k:"infoDatoFinal",  label:"Info dato final" },
];

function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);
  } catch (e) {}
  // Fallback para navegadores/contextos sin clipboard API
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand('copy'); document.body.removeChild(ta);
  } catch (e) {}
  return Promise.resolve();
}

function DatosExcel({ order, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [copiado, setCopiado] = useState(null);
  const marca = (id) => { setCopiado(id); setTimeout(() => setCopiado(c => c === id ? null : c), 1200); };

  const val = (k) => (order[k] == null ? "" : String(order[k]));
  const llenos = CAMPOS_EXCEL.filter(f => val(f.k).trim() !== "");

  const copiarValor = (f) => { copyToClipboard(val(f.k)); marca(f.k); };
  const copiarTodo = () => {
    copyToClipboard(llenos.map(f => `${f.label}: ${val(f.k)}`).join("\n"));
    marca('__all__');
  };
  // Fila en el ORDEN EXACTO del Google Sheet (A→U), separada por TAB para que
  // al pegar caiga una celda por columna.
  const copiarFilaSheet = () => {
    const fecha = (order.createdAt || '').split(' ')[0] || (order.createdAt || '').split('T')[0] || '';
    const fila = [
      fecha, val('mail'), order.cliente || '', val('dato'), order.notas || '',
      val('telefonoName'), val('dl'), val('dob'), val('ssn'), order.estado || '',
      order.plataforma || '', val('tipoCuenta'), val('creditoDebito'), val('precio'),
      val('abonado'), val('saldo'), val('pagoHasta'), val('documentNumber'),
      val('report'), val('whyDeactivated'), val('infoDatoFinal'),
    ].join("\t");
    copyToClipboard(fila);
    marca('__row__');
  };

  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-emerald-800 font-bold text-xs hover:bg-emerald-100 transition-all">
        <span>📊 Datos para el Excel {llenos.length > 0 ? `(${llenos.length})` : "(vacío)"}</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2">
          {llenos.length === 0 ? (
            <div className="text-xs text-slate-400 italic">Este pedido todavía no tiene datos de Excel.</div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-1">
                {llenos.map(f => (
                  <button key={f.k} type="button" onClick={() => copiarValor(f)} title="Tocar para copiar"
                    className="group flex items-center gap-2 text-left rounded-lg bg-white border border-slate-200 px-2.5 py-1.5 hover:border-emerald-300 active:scale-[0.99] transition-all">
                    <span className="text-[10px] font-black uppercase tracking-wide text-slate-400 w-24 shrink-0">{f.label}</span>
                    <span className="text-xs text-slate-800 font-medium break-all flex-1">{val(f.k)}</span>
                    <span className={"text-[10px] font-bold shrink-0 " + (copiado === f.k ? "text-emerald-600" : "text-slate-300 group-hover:text-slate-500")}>{copiado === f.k ? "✓ copiado" : "copiar"}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={copiarTodo}
                  className="flex-1 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-bold hover:bg-slate-700 active:scale-95 transition-all">
                  {copiado === '__all__' ? "✓ Copiado" : "📋 Copiar todo"}
                </button>
                <button type="button" onClick={copiarFilaSheet}
                  title="Copia una fila lista para pegar en el Google Sheet (columnas A→U)"
                  className="flex-1 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-500 active:scale-95 transition-all">
                  {copiado === '__row__' ? "✓ Copiado" : "📊 Copiar fila Sheet"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, onFocus, onCycle, onChat, onRework, onDone, onRemove, onNotes, onEdit, onTrackLink, onRevokeTrackLink, onSetTrackExpiry, onGarantia, onRentas, onReasign, onPin, pinnedId, isAdmin, onSetStatus, nextTaskId, onReactivar }) {
  const [cardLightbox, setCardLightbox] = useState(null);
  const [expanded,setExpanded] = useState(false);
  const [editing,setEditing]   = useState(false);
  const [note,setNote]         = useState(order.notas);
  const [fotos, setFotos] = useState(order.fotos||[]);
  const [thumbSrc, setThumbSrc] = useState(null);
  const [thumbCount, setThumbCount] = useState(0);
  const fotosLoaded = useRef(false);
  const thumbLoaded = useRef(false);
  const [loadingFotos, setLoadingFotos] = useState(false);
  const [errorFotos, setErrorFotos] = useState(false);
  const fetchFotosCard = () => {
    setLoadingFotos(true); setErrorFotos(false);
    apiFetch('/api/pedidos/' + order.id + '/fotos')
      .then(f => { if (Array.isArray(f)) { setFotos(f); if(f.length>0){setThumbSrc(f[0]);setThumbCount(f.length);} } setLoadingFotos(false); })
      .catch(() => { setLoadingFotos(false); setErrorFotos(true); });
  };
  // ── ARREGLO DE VELOCIDAD ────────────────────────────────────────
  // ANTES: aquí había un useEffect que descargaba TODAS las fotos del pedido
  // al montar CADA tarjeta, solo para pintar una miniatura de 64px. Con 25
  // pedidos en pantalla eran 25 descargas de varios MB cada una = la app se
  // arrastraba.
  // AHORA: no se descarga nada hasta que el usuario abre la tarjeta. La lista
  // carga instantánea y las fotos solo bajan cuando de verdad se van a ver.
  const handleExpand = () => {
    const abriendo = !expanded;
    setExpanded(v => !v);
    if (abriendo && !fotosLoaded.current) {
      fotosLoaded.current = true;
      thumbLoaded.current = true;
      fetchFotosCard();
    }
  };
  const s=SC[order.estado]||{};
  const d=days(order.entrega);
  const done = order.estado==="Entregado" || order.estado==="Terminado" || order.estado==="Intento Fallido";
  const fullyDone = order.estado==="Entregado" || order.estado==="Intento Fallido";
  const archived=isArchived(order);
  const urgent=!done&&!archived&&order.estado!=="Terminado"&&d!==null&&d<=1;
  const isNextTask = nextTaskId && order.id === nextTaskId;
  return (
    <motion.div layout initial={{opacity:0}} animate={{opacity:fullyDone?.5:1}} exit={{opacity:0}}
      className={"rounded-2xl border-2 overflow-hidden "+(isNextTask?"border-emerald-400 bg-emerald-950 shadow-[0_0_0_4px_rgba(52,211,153,0.25),0_8px_32px_rgba(52,211,153,0.2)]":urgent?"border-rose-400 bg-white":"border-slate-100 bg-white")}>
      {isNextTask&&<div className="h-1 bg-emerald-400"/>}
      {!isNextTask&&urgent&&<motion.div animate={{opacity:[1,.3,1]}} transition={{repeat:Infinity,duration:.9}} className="h-1 bg-gradient-to-r from-rose-500 to-orange-400"/>}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className={"font-black truncate "+(isNextTask?"text-emerald-100":"text-slate-900")}>{order.cliente}</div>
            <TituloChip order={order} tono={isNextTask?"verde":"claro"} className="mt-1"/>
          </div>
          {isNextTask&&<span className="shrink-0 px-2 py-0.5 rounded-full bg-emerald-400 text-emerald-950 text-xs font-black">▶ AHORA</span>}
          <StatusSelector order={order} onCycle={onCycle} isAdmin={isAdmin} onSetStatus={onSetStatus}/>
        </div>
        {order.userName&&<div className={"text-xs mb-2 "+(isNextTask?"text-emerald-400":"text-slate-400")}>👤 Creado por: {order.userName}{order.asignadoNombre&&<span className={"ml-2 px-2 py-0.5 rounded-full font-bold "+(isNextTask?"bg-emerald-800 text-emerald-200":"bg-violet-100 text-violet-700")}>→ {order.asignadoNombre}</span>}</div>}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {order.prioridad==="Alta"&&!archived&&<span className={"px-2 py-0.5 rounded-full text-xs font-bold "+(isNextTask?"bg-red-500 text-white":"bg-red-100 text-red-700")}>🔥 Alta</span>}
          {order.chatPendiente&&!archived&&<span className={"px-2 py-0.5 rounded-full text-xs font-bold animate-pulse "+(isNextTask?"bg-orange-400 text-orange-950":"bg-orange-100 text-orange-700")}>💬 Chat</span>}
          {order.rehacer&&!archived&&<span className={"px-2 py-0.5 rounded-full text-xs font-bold "+(isNextTask?"bg-rose-500 text-white":"bg-rose-100 text-rose-700")}>🔄 Rehacer</span>}
          {d!==null&&!archived&&<span className={"px-2 py-0.5 rounded-full text-xs font-bold "+(isNextTask?(d<0?"bg-red-500 text-white":d<=1?"bg-orange-400 text-orange-950":"bg-emerald-800 text-emerald-200"):(d<0?"bg-red-100 text-red-700":d<=1?"bg-orange-100 text-orange-700":"bg-slate-100 text-slate-500"))}>{d<0?"⚠️ "+Math.abs(d)+"d":d===0?"🚨 Hoy":"📅 "+d+"d"}</span>}
          {fotos&&fotos.length>0&&expanded&&<span className={"px-2 py-0.5 rounded-full text-xs font-bold "+(isNextTask?"bg-emerald-800 text-emerald-200":"bg-blue-50 text-blue-600")}>🖼 {fotos.length}</span>}
        </div>
        {/* Miniatura de la primera foto */}
        {thumbSrc&&!expanded&&(
          <div className="mb-2 flex items-center gap-2 cursor-pointer" onClick={()=>onFocus(order.id)}>
            <div className="relative w-14 h-14 rounded-xl overflow-hidden border-2 border-slate-200 shrink-0 shadow-sm">
              <img src={thumbSrc} alt="" className="w-full h-full object-cover"/>
              {thumbCount>1&&(
                <div className="absolute bottom-0 right-0 bg-black/60 text-white text-xs font-black px-1 rounded-tl-lg leading-tight">+{thumbCount-1}</div>
              )}
            </div>
            <div className={"text-xs font-medium "+(isNextTask?"text-emerald-400":"text-slate-400")}>
              {thumbCount} foto{thumbCount!==1?"s":""} adjunta{thumbCount!==1?"s":""}
            </div>
          </div>
        )}
        {!fullyDone&&<button onClick={()=>onFocus(order.id)} className={"w-full py-3 rounded-xl text-sm font-black active:scale-95 transition-all mb-2 "+(isNextTask?"bg-emerald-400 text-emerald-950 hover:bg-emerald-300":"bg-slate-900 text-white hover:bg-slate-700")}>▶ EMPEZAR</button>}
        {fullyDone&&onReactivar&&(
          <button onClick={()=>{ if(window.confirm('¿Volver a trabajar este pedido? Regresará a la cola como "Pendiente Revisión".')) onReactivar(order.id); }}
            className="w-full py-3 rounded-xl text-sm font-black active:scale-95 transition-all mb-2 bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-200">
            ↩ Trabajar de nuevo
          </button>
        )}
        <button onClick={handleExpand} className={"w-full text-xs py-0.5 "+(isNextTask?"text-emerald-600 hover:text-emerald-300":"text-slate-400 hover:text-slate-600")}>{expanded?"▲ cerrar":"▼ notas y acciones"}</button>
        <AnimatePresence>
          {expanded&&(
            <motion.div initial={{height:0,opacity:0}} animate={{height:"auto",opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
              <div className="pt-3 space-y-2">
                {editing
                  ?<textarea autoFocus rows={3} value={note} onChange={e=>setNote(e.target.value)}
                      onBlur={()=>{setEditing(false);onNotes(order.id,note);}}
                      className="w-full rounded-xl border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm resize-none outline-none"/>
                  :<div onClick={()=>!fullyDone&&setEditing(true)}
                      className={"rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600 min-h-[48px] "+(!fullyDone?"cursor-text hover:bg-amber-50":"")+" transition-colors"}>
                      {order.notas||React.createElement("span",{className:"text-slate-300 italic"},"Sin notas — toca para añadir")}
                    </div>
                }
                {loadingFotos&&(
                  <div className="grid grid-cols-3 gap-1.5">
                    {[0,1,2].map(i=><div key={i} className="w-full h-16 rounded-xl bg-slate-100 animate-pulse"/>)}
                  </div>
                )}
                {!loadingFotos&&errorFotos&&(
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-xs">No se cargaron las fotos</span>
                    <button onClick={fetchFotosCard} className="px-3 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200 active:scale-95 transition-all">🔄 Reintentar</button>
                  </div>
                )}
                {!loadingFotos&&!errorFotos&&fotos&&fotos.length>0&&(
                  <div className="space-y-1.5">
                    <div className="text-xs font-bold text-slate-400">📷 Fotos del cliente ({fotos.length})</div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {fotos.map((src,i)=>(
                        <div key={i} className="relative group cursor-pointer" onClick={()=>setCardLightbox(i)}>
                          <img src={src} alt="" className="w-full h-16 object-cover rounded-xl border border-slate-200 hover:opacity-80 transition-opacity"/>
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl bg-black/20">
                            <span className="text-white text-lg drop-shadow-lg">🔍</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <AnimatePresence>{cardLightbox!==null&&<Lightbox fotos={fotos} startIndex={cardLightbox} onClose={()=>setCardLightbox(null)}/>}</AnimatePresence>
                  </div>
                )}
                {order.mensajeCliente&&(
                  <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                    <span className="text-amber-500 text-sm shrink-0">💬</span>
                    <div className="text-xs text-amber-800 font-medium leading-relaxed">{order.mensajeCliente}</div>
                  </div>
                )}
                {/* Datos del Excel: visibles y copiables SIN entrar a editar */}
                <DatosExcel order={order} />
                <div className="grid grid-cols-2 gap-1.5">
                  {isAdmin&&onPin&&!fullyDone&&<button onClick={()=>onPin(order.id)}
                    className={"py-2 rounded-xl text-xs font-bold col-span-2 transition-all "+(pinnedId===order.id?"bg-amber-400 text-black":"bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-700")}>
                    {pinnedId===order.id?"📌 Fijada como tarea principal — toca para quitar":"📌 Fijar como tarea principal"}
                  </button>}
                  <button onClick={()=>onEdit(order.id)} className="py-2 rounded-xl text-xs font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 col-span-2">✏️ Editar pedido</button>
                  {isAdmin&&<button onClick={()=>onTrackLink(order.id)} className="py-2 rounded-xl text-xs font-bold bg-violet-50 text-violet-700 hover:bg-violet-100 col-span-2">🔍 Copiar link de seguimiento</button>}
                  {isAdmin&&<button onClick={()=>onRevokeTrackLink&&onRevokeTrackLink(order.id)} className="py-2 rounded-xl text-xs font-bold bg-red-50 text-red-600 hover:bg-red-100">🗑 Revocar link</button>}
                  {isAdmin&&<button onClick={()=>{ const f=window.prompt('Fecha de expiración (AAAA-MM-DD), ej: 2025-03-22\nDejar vacío = sin expiración:'); if(f===null) return; onSetTrackExpiry&&onSetTrackExpiry(order.id, f.trim()||null); }} className="py-2 rounded-xl text-xs font-bold bg-amber-50 text-amber-700 hover:bg-amber-100">📅 Expirar día 22</button>}
                  {isAdmin&&<button onClick={()=>onGarantia(order.id)} className={"py-2 rounded-xl text-xs font-bold col-span-2 "+(order.garantiaActiva?"bg-emerald-100 text-emerald-700 hover:bg-emerald-200":"bg-slate-100 text-slate-600 hover:bg-slate-200")}>🛡️ {order.garantiaActiva?"Garantía activa":"Activar garantía"}</button>}
                  {isAdmin&&<button onClick={()=>onRentas(order.id)} className={"py-2 rounded-xl text-xs font-bold col-span-2 "+(order.estadoPago==="debe"?"bg-red-100 text-red-700":order.estadoPago==="al-corriente"?"bg-emerald-100 text-emerald-700":"bg-slate-100 text-slate-600")+" hover:opacity-80"}>💰 {order.estadoPago==="debe"?"🚨 Debe":order.estadoPago==="al-corriente"?"✅ Al corriente":"Registrar pago"}</button>}
                  {isAdmin&&<button onClick={()=>onChat(order.id)} className={"py-2 rounded-xl text-xs font-bold transition-all "+(order.chatPendiente?"bg-orange-100 text-orange-700":"bg-slate-100 text-slate-500 hover:bg-slate-200")}>💬 Chat</button>}
                  {isAdmin&&<button onClick={()=>onRework(order.id)} className="py-2 rounded-xl text-xs font-bold bg-rose-100 text-rose-700 hover:bg-rose-200">🔄 Rehacer</button>}
                  {order.estado==="Terminado"&&isAdmin&&<button onClick={()=>onDone(order.id)} className="py-2 rounded-xl text-xs font-bold bg-cyan-100 text-cyan-700 hover:bg-cyan-200 col-span-2">🤳 Enviar a Veri Facial</button>}
                  {order.estado==="VERI FACIAL"&&isAdmin&&(<>
                    <button onClick={()=>onDone(order.id)} className="py-2 rounded-xl text-xs font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-200">✅ Pasó → Entregar</button>
                    <button onClick={()=>onRework(order.id)} className="py-2 rounded-xl text-xs font-bold bg-rose-100 text-rose-700 hover:bg-rose-200">❌ Falló → Rehacer</button>
                  </>)}
                  {!done&&order.estado!=="VERI FACIAL"&&<button onClick={()=>onDone(order.id)} className="py-2 rounded-xl text-xs font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 col-span-2">🏁 Terminado</button>}
                  {isAdmin&&<button onClick={()=>onRemove(order.id)} className="py-1.5 rounded-xl text-xs text-slate-300 hover:text-red-400 col-span-2">🗑 Eliminar</button>}
                  {isAdmin&&onReasign&&<ReasignDropdown order={order} onReasign={onReasign}/>}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ── DRAG & DROP ORDER (persisted per plataforma) ──────────────────
function loadGroupOrder(plataforma) {
  try { return JSON.parse(localStorage.getItem("royal_order_"+plataforma)||"null"); } catch { return null; }
}
function saveGroupOrder(plataforma, ids) {
  localStorage.setItem("royal_order_"+plataforma, JSON.stringify(ids));
}

function Group({ plataforma, orders, handlers, pinnedId, isAdmin, nextTaskId, forceShowArchived=false }) {
  const p=PC[plataforma];
  // Archivados (entregados >6h) no cuentan como activos ni disparan alertas
  const active=orders.filter(o=>o.estado!=="Entregado"&&o.estado!=="Intento Fallido"&&o.estado!=="Esperando Cliente"&&o.estado!=="Background Check"&&!isArchived(o));
  const done=orders.filter(o=>o.estado==="Entregado" && !isArchived(o));
  const archived=orders.filter(o=>isArchived(o));
  const hasU=active.some(o=>{const d=days(o.entrega);return d!==null&&d<=1;});
  const hasR=active.some(o=>o.rehacer);
  const hasC=active.some(o=>o.chatPendiente);
  const [col,setCol]=useState(active.length===0);
  const [showArchived,setShowArchived]=useState(false);
  // Al buscar, mostrar archivados que coincidan y abrir el grupo automáticamente
  const verArchivados = showArchived || forceShowArchived;
  const colapsado = col && !forceShowArchived;

  // Orden personalizado: empieza desde localStorage, luego mantiene en estado
  const [orderedIds, setOrderedIds] = useState(() => {
    const saved = loadGroupOrder(plataforma);
    if (saved) {
      // Mezclar: primero los guardados que aún existen, luego los nuevos al final
      const existing = saved.filter(id => orders.some(o=>o.id===id));
      const newOnes  = orders.map(o=>o.id).filter(id=>!saved.includes(id));
      return [...existing, ...newOnes];
    }
    return orders.map(o=>o.id);
  });

  // Si llegan pedidos nuevos del servidor, agregarlos al final del orden
  useEffect(() => {
    setOrderedIds(prev => {
      const existing = prev.filter(id => orders.some(o=>o.id===id));
      const newOnes  = orders.map(o=>o.id).filter(id=>!prev.includes(id));
      return [...existing, ...newOnes];
    });
  }, [orders]);

  const sortedOrders = useMemo(() => {
    const map = Object.fromEntries(orders.map(o=>[o.id,o]));
    const all = orderedIds.map(id=>map[id]).filter(Boolean);
    // Activos normales: respetan orden manual (drag & drop)
    const activos    = all.filter(o => o.estado !== "Terminado" && o.estado !== "Entregado" && o.estado !== "Intento Fallido" && o.estado !== "Esperando Cliente" && o.estado !== "Background Check" && !isArchived(o));
    // Esperando Cliente: al fondo de los activos (en pausa, el balón está en su cancha)
    const esperando  = all.filter(o => o.estado === "Esperando Cliente" && !isArchived(o));
    // Intento Fallido: después de esperando (terminales, solo se cancelan)
    const fallidos   = all.filter(o => o.estado === "Intento Fallido" && !isArchived(o));
    // Terminados: al fondo de los activos, los más antiguos primero
    const terminados = all.filter(o => o.estado === "Terminado" && !isArchived(o))
                          .sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
    // Entregados recientes: antes de los archivados, los más antiguos primero
    const entregados = all.filter(o => o.estado === "Entregado" && !isArchived(o))
                          .sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
    // Archivados (entregados >6h): al final del todo
    const archivados = all.filter(o => isArchived(o))
                          .sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
    return [...activos, ...esperando, ...fallidos, ...terminados, ...entregados, ...archivados];
  }, [orderedIds, orders]);

  // Drag state
  const dragId   = useRef(null);
  const dragOver = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [overIdx,  setOverIdx]  = useState(null);

  const onDragStart = (e, id) => {
    dragId.current = id;
    setDragging(id);
    e.dataTransfer.effectAllowed = "move";
    // Imagen fantasma transparente para que no se vea el clon feo del browser
    const ghost = document.createElement("div");
    ghost.style.opacity = "0";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(()=>document.body.removeChild(ghost), 0);
  };
  const onDragEnter = (e, id) => {
    dragOver.current = id;
    const idx = sortedOrders.findIndex(o=>o.id===id);
    setOverIdx(idx);
  };
  const onDragEnd = () => {
    if (dragId.current && dragOver.current && dragId.current !== dragOver.current) {
      setOrderedIds(prev => {
        const next = [...prev];
        const from = next.indexOf(dragId.current);
        const to   = next.indexOf(dragOver.current);
        if (from !== -1 && to !== -1) {
          next.splice(from, 1);
          next.splice(to, 0, dragId.current);
        }
        saveGroupOrder(plataforma, next);
        return next;
      });
    }
    dragId.current = null;
    dragOver.current = null;
    setDragging(null);
    setOverIdx(null);
  };

  if(orders.length===0) return null;
  return (
    <div className={"rounded-[24px] border-2 overflow-hidden "+p.border+" "+p.light}>
      <button onClick={()=>setCol(v=>!v)} className="w-full flex items-center justify-between px-5 py-4 hover:opacity-80 transition-opacity">
        <div className="flex items-center gap-3">
          <div className={"w-10 h-10 rounded-2xl "+p.bg+" flex items-center justify-center text-white text-lg shadow-sm"}>{p.emoji}</div>
          <div className="text-left">
            <div className={"text-base font-black "+p.text+" uppercase tracking-wide"}>{plataforma}</div>
            <div className="text-xs text-slate-400">{active.length} activos · {done.length} entregados{archived.length>0?" · "+archived.length+" archivados":""}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasR&&<span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-600 text-xs font-bold">🔄</span>}
          {hasC&&<span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 text-xs font-bold animate-pulse">💬</span>}
          {hasU&&<motion.span animate={{scale:[1,1.1,1]}} transition={{repeat:Infinity,duration:.8}} className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-bold">🚨</motion.span>}
          <span className={"w-8 h-8 rounded-xl "+p.bg+" text-white font-black text-sm flex items-center justify-center"}>{active.length}</span>
          <span className="text-slate-400 text-sm">{colapsado?"▼":"▲"}</span>
        </div>
      </button>
      <AnimatePresence>
        {!colapsado&&(
          <motion.div initial={{height:0}} animate={{height:"auto"}} exit={{height:0}} className="overflow-hidden">
            <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {(verArchivados ? sortedOrders : sortedOrders.filter(o=>!isArchived(o))).map((o,i)=>(
                <div
                  key={o.id}
                  draggable={!isArchived(o)}
                  onDragStart={e=>!isArchived(o)&&onDragStart(e,o.id)}
                  onDragEnter={e=>!isArchived(o)&&onDragEnter(e,o.id)}
                  onDragOver={e=>e.preventDefault()}
                  onDragEnd={onDragEnd}
                  style={{
                    opacity: dragging===o.id ? 0.35 : (isArchived(o) ? 0.6 : 1),
                    outline: overIdx===i && dragging!==o.id ? "2px dashed "+p.color : "none",
                    outlineOffset: "2px",
                    borderRadius: "16px",
                    transition: "opacity 0.15s, outline 0.1s",
                    cursor: isArchived(o) ? "default" : "grab",
                  }}
                >
                  <OrderCard order={o} isAdmin={isAdmin} pinnedId={pinnedId} nextTaskId={nextTaskId} {...handlers}/>
                </div>
              ))}
            </div>
            {archived.length>0&&!forceShowArchived&&(
              <div className="px-4 pb-4 -mt-1">
                <button onClick={()=>setShowArchived(v=>!v)}
                  className={"w-full py-2.5 rounded-xl text-xs font-bold active:scale-95 transition-all "+(showArchived?"bg-slate-200 text-slate-600 hover:bg-slate-300":"bg-slate-50 text-slate-400 hover:bg-slate-100 border border-dashed border-slate-200")}>
                  {showArchived?"▲ Ocultar archivados":"📦 Ver "+archived.length+" archivado"+(archived.length!==1?"s":"")+" (para abrir o reasignar)"}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── COBROS PAGE (pantalla independiente de cobros) ────────────────

// ══════════════════════════════════════════════════════════════════
// FINANZAS PAGE — Vista completa independiente
// ══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
//  FINANZAS · tema claro/oscuro + gráficas propias (sin librerías)
//  Todo el módulo de dinero usa estas variables, así sigue el modo que
//  elijas con 🌙/☀️ sin duplicar estilos.
// ═══════════════════════════════════════════════════════════════════
const FIN_CSS = `
.fin{--fin-bg:#0a0a0f;--fin-head:rgba(10,10,15,.95);--fin-panel:#0f172a;--fin-card:rgba(255,255,255,.04);
--fin-t1:#fff;--fin-t2:rgba(255,255,255,.8);--fin-t3:rgba(255,255,255,.45);--fin-t4:rgba(255,255,255,.28);
--fw-3:rgba(255,255,255,.03);--fw-4:rgba(255,255,255,.04);--fw-5:rgba(255,255,255,.05);--fw-6:rgba(255,255,255,.06);--fw-7:rgba(255,255,255,.07);
--fw-8:rgba(255,255,255,.08);--fw-10:rgba(255,255,255,.1);--fw-12:rgba(255,255,255,.12);--fw-14:rgba(255,255,255,.14);
--fw-30:rgba(255,255,255,.3);--fw-35:rgba(255,255,255,.35);--fw-40:rgba(255,255,255,.4);--fw-50:rgba(255,255,255,.5);--fw-70:rgba(255,255,255,.7);
--fc-green:#34d399;--fc-green2:#10b981;--fc-red:#f87171;--fc-red2:#ef4444;--fc-amber:#fbbf24;--fc-amber2:#f59e0b;
--fc-indigo:#a5b4fc;--fc-blue:#60a5fa;--fc-orange:#fb923c;--fc-violet:#a78bfa;--fc-track:rgba(255,255,255,.07);
background:var(--fin-bg);color:var(--fin-t1)}
.fin.claro{--fin-bg:#f1f5f9;--fin-head:rgba(255,255,255,.94);--fin-panel:#fff;--fin-card:#fff;
--fin-t1:#0f172a;--fin-t2:#334155;--fin-t3:#64748b;--fin-t4:#94a3b8;
--fw-3:#fff;--fw-4:#fff;--fw-5:#fff;--fw-6:rgba(15,23,42,.05);--fw-7:rgba(15,23,42,.06);
--fw-8:rgba(15,23,42,.10);--fw-10:rgba(15,23,42,.10);--fw-12:rgba(15,23,42,.13);--fw-14:rgba(15,23,42,.16);
--fw-30:rgba(15,23,42,.45);--fw-35:rgba(15,23,42,.5);--fw-40:rgba(15,23,42,.55);--fw-50:rgba(15,23,42,.6);--fw-70:rgba(15,23,42,.75);
--fc-green:#059669;--fc-green2:#059669;--fc-red:#dc2626;--fc-red2:#dc2626;--fc-amber:#b45309;--fc-amber2:#d97706;
--fc-indigo:#4338ca;--fc-blue:#2563eb;--fc-orange:#ea580c;--fc-violet:#7c3aed;--fc-track:rgba(15,23,42,.07)}
.fin .fin-t1{color:var(--fin-t1)}.fin .fin-t2{color:var(--fin-t2)}.fin .fin-t3{color:var(--fin-t3)}.fin .fin-t4{color:var(--fin-t4)}
.fin .fin-hover-t1:hover{color:var(--fin-t1)}.fin .fin-hover-bg:hover{background:var(--fw-10)}
.fin .fin-border{border-color:var(--fw-10)}.fin .fin-bgw10{background:var(--fw-10)}.fin .fin-bgw5{background:var(--fw-6)}
.fin input,.fin select,.fin textarea{color:var(--fin-t1)}.fin select option{background:var(--fin-panel);color:var(--fin-t1)}
.fin ::placeholder{color:var(--fin-t4)}
.fin.claro [style*="--fw-4"],.fin.claro [style*="--fin-card"]{box-shadow:0 1px 2px rgba(15,23,42,.06)}
.fin.claro [class*="text-red-3"],.fin.claro [class*="text-red-4"],.fin.claro [class*="text-rose-3"],.fin.claro [class*="text-rose-4"]{color:#dc2626}
.fin.claro [class*="text-amber-2"],.fin.claro [class*="text-amber-3"],.fin.claro [class*="text-amber-4"],.fin.claro [class*="text-yellow-3"]{color:#b45309}
.fin.claro [class*="text-emerald-3"],.fin.claro [class*="text-emerald-4"],.fin.claro [class*="text-green-3"],.fin.claro [class*="text-green-4"]{color:#047857}
.fin.claro [class*="text-indigo-3"],.fin.claro [class*="text-blue-3"],.fin.claro [class*="text-blue-4"],.fin.claro [class*="text-sky-3"]{color:#1d4ed8}
.fin.claro [class*="text-violet-3"],.fin.claro [class*="text-purple-3"]{color:#6d28d9}
.fin.claro [class*="text-orange-3"],.fin.claro [class*="text-orange-4"]{color:#c2410c}
.fin.claro .text-zinc-200{color:#3f3f46}
.fin .fin-dot{animation:finPulse 2s infinite}@keyframes finPulse{0%,100%{opacity:1}50%{opacity:.3}}
`;

function FinCard({ titulo, extra, children, className = '', onClick }) {
  return (
    <div onClick={onClick} className={"rounded-2xl p-4 " + (onClick ? "cursor-pointer active:scale-[0.99] transition-transform " : "") + className}
      style={{ background: 'var(--fin-card)', border: '1px solid var(--fw-8)' }}>
      {(titulo || extra) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="fin-t1 font-black text-sm">{titulo}</div>{extra}
        </div>
      )}
      {children}
    </div>
  );
}

// Barras dobles por semana: entró (verde) vs gastos (rojo)
function FinBarrasSemana({ semanas, fmt }) {
  const [sel, setSel] = useState(null);
  const max = Math.max(1, ...semanas.flatMap(s => [s.entro, s.salio]));
  const foco = semanas[sel ?? semanas.length - 1];
  return (
    <div>
      <div className="flex items-end gap-1.5 sm:gap-3 h-40">
        {semanas.map((s, i) => {
          const activa = sel === null ? i === semanas.length - 1 : sel === i;
          return (
            <div key={i} className="flex-1 h-full flex items-end justify-center gap-0.5 sm:gap-1 cursor-pointer"
              onMouseEnter={() => setSel(i)} onMouseLeave={() => setSel(null)} onClick={() => setSel(i)}
              title={`${s.label}: entró ${fmt(s.entro)} · gastos ${fmt(s.salio)}`}>
              {[['entro', 'var(--fc-green2)'], ['salio', 'var(--fc-red2)']].map(([k, c]) => (
                <div key={k} className="flex-1 max-w-[22px] rounded-t-md transition-all duration-500"
                  style={{ height: (s[k] / max * 100) + '%', minHeight: s[k] ? 3 : 0, background: c, opacity: activa ? 1 : .4 }}/>
              ))}
            </div>
          );
        })}
      </div>
      <div className="flex gap-1.5 sm:gap-3 mt-1.5" style={{ borderTop: '1px solid var(--fw-8)', paddingTop: 6 }}>
        {semanas.map((s, i) => (
          <div key={i} className={"flex-1 text-center text-[10px] truncate " + ((sel ?? semanas.length - 1) === i ? 'fin-t1 font-black' : 'fin-t4')}>{s.label}</div>
        ))}
      </div>
      {foco && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs">
          <span className="fin-t3">Semana del {foco.label}</span>
          <span style={{ color: 'var(--fc-green)' }} className="font-black">↑ {fmt(foco.entro)}</span>
          <span style={{ color: 'var(--fc-red)' }} className="font-black">↓ {fmt(foco.salio)}</span>
          <span className="fin-t1 font-black">= {fmt(foco.entro - foco.salio)}</span>
        </div>
      )}
    </div>
  );
}

// Una sola barra repartida en partes (ej. deuda por antigüedad)
function FinBarraApilada({ partes, fmt, onPick }) {
  const total = partes.reduce((s, p) => s + p.valor, 0);
  return (
    <div>
      <div className="flex h-4 rounded-full overflow-hidden" style={{ background: 'var(--fc-track)' }}>
        {total > 0 && partes.filter(p => p.valor > 0).map(p => (
          <div key={p.k} title={p.label + ': ' + fmt(p.valor)} className="h-full transition-all duration-500"
            style={{ width: (p.valor / total * 100) + '%', background: p.color }}/>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
        {partes.map(p => (
          <button key={p.k} type="button" onClick={() => onPick && onPick(p)} className="flex items-center gap-2 text-left text-xs rounded-lg px-1 py-0.5 fin-hover-bg">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color }}/>
            <span className="fin-t3 flex-1 truncate">{p.label}</span>
            <span className="fin-t1 font-black">{fmt(p.valor)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Barras horizontales (ranking)
function FinHBarras({ filas, fmt, color = 'var(--fc-red)', onPick, vacio = 'Sin datos' }) {
  if (!filas.length) return <div className="fin-t4 text-xs text-center py-6">{vacio}</div>;
  const max = Math.max(1, ...filas.map(f => f.valor));
  return (
    <div className="space-y-2">
      {filas.map(f => (
        <button key={f.label} type="button" onClick={() => onPick && onPick(f)} className="w-full text-left group">
          <div className="flex items-center justify-between gap-2 text-xs mb-1">
            <span className="fin-t2 font-bold truncate">{f.label}{f.sub ? <span className="fin-t4 font-medium"> · {f.sub}</span> : null}</span>
            <span className="font-black shrink-0" style={{ color }}>{fmt(f.valor)}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--fc-track)' }}>
            <div className="h-full rounded-full transition-all duration-500 group-hover:opacity-80" style={{ width: (f.valor / max * 100) + '%', background: color }}/>
          </div>
        </button>
      ))}
    </div>
  );
}

// Modal para juntar deudores bajo un mismo "super cliente"
function SuperClientesModal({ nombres, mapping, onSave, onClose }) {
  const [draft, setDraft] = useState(() => {
    const d = {}; nombres.forEach(n => { d[n] = mapping[n] || ''; }); return d;
  });
  const setName = (n, v) => setDraft(prev => ({ ...prev, [n]: v }));
  const superExistentes = [...new Set(Object.values(draft).map(v => (v||'').trim()).filter(Boolean))];
  const guardar = () => {
    const next = {};
    Object.entries(draft).forEach(([n, v]) => { const s = (v||'').trim(); if (s && s !== n) next[n] = s; });
    onSave(next); onClose();
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" style={{background:'rgba(0,0,0,0.65)'}} onClick={onClose}>
      <motion.div initial={{y:60,opacity:0}} animate={{y:0,opacity:1}} onClick={e=>e.stopPropagation()}
        className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl flex flex-col" style={{background:'var(--fin-panel)', maxHeight:'85vh', border:'1px solid var(--fw-10)'}}>
        <div className="p-4 flex items-center justify-between shrink-0" style={{borderBottom:'1px solid var(--fw-8)'}}>
          <div>
            <div className="fin-t1 font-black text-lg">👑 Super Clientes</div>
            <div className="fin-t3 text-xs mt-0.5">Escribe el mismo grupo en varios para juntarlos como un solo deudor</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full fin-t3 fin-hover-t1 flex items-center justify-center shrink-0" style={{background:'var(--fw-8)'}}>✕</button>
        </div>
        <div className="p-4 overflow-y-auto space-y-2.5">
          {nombres.length === 0 && <div className="fin-t3 text-sm text-center py-8">No hay deudores para agrupar todavía.</div>}
          {nombres.map(n => (
            <div key={n} className="rounded-xl p-3" style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
              <div className="fin-t2 text-sm font-bold mb-1.5 truncate">{n}</div>
              <input value={draft[n]} onChange={e=>setName(n, e.target.value)}
                placeholder="Nombre del grupo (vacío = queda solo)"
                className="w-full rounded-lg px-3 py-2 text-sm fin-t1 outline-none" style={{background:'var(--fw-6)', border:'1px solid var(--fw-12)'}}/>
              {superExistentes.filter(s => s !== (draft[n]||'').trim()).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {superExistentes.filter(s => s !== (draft[n]||'').trim()).map(s => (
                    <button key={s} onClick={()=>setName(n, s)} className="px-2 py-0.5 rounded-full text-[11px] font-bold text-amber-200" style={{background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.3)'}}>👑 {s}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="p-4 shrink-0" style={{borderTop:'1px solid var(--fw-8)'}}>
          <button onClick={guardar} className="w-full py-3 rounded-xl font-black text-white active:scale-95 transition-all" style={{background:'#f59e0b'}}>Guardar agrupación</button>
        </div>
      </motion.div>
    </div>
  );
}
function FinanzasPage({ orders, usuarios=[], onBack, onOpenRentas, onReasignar, onSavePedido, onEliminarPedido, onCambiarEstadoPedido, oscuro=true, seccionInicial='inicio' }) {
  const [seccion, setSeccion] = useState(seccionInicial);
  const [movTipo, setMovTipo] = useState('todo');      // todo | in | out
  const [movPeriodo, setMovPeriodo] = useState('mes'); // semana | mes | 30 | todo
  const [movMetodo, setMovMetodo] = useState('');
  const [actualizado, setActualizado] = useState(Date.now());
  const [ahora, setAhora] = useState(Date.now());
  const [finData, setFinData] = useState(null);
  const [extras, setExtras] = useState([]);
  const [semanales, setSemanales] = useState([]);
  const [creditos, setCreditos] = useState([]);
  const [cartera, setCartera] = useState([]);
  const [rentasDirectas, setRentasDirectas] = useState([]);
  const [gastos, setGastos] = useState([]);
  // ── REPORTE REAL DE CARTERA ──────────────────────────────────────
  // Viene de /api/reporte/cartera: junta pedidos + cartera + rentas + los
  // campos del Excel para mostrar TODOS los pedidos, no solo los que alguien
  // registró a mano en la tabla cartera.
  const [reporte, setReporte] = useState(null);
  const [repFiltro, setRepFiltro] = useState('con-saldo'); // con-saldo|vencidos|sin-precio|saldados|todos
  const [repOrden, setRepOrden] = useState('antiguo');     // antiguo|monto|cliente
  const [repExp, setRepExp] = useState(null);              // id del pedido expandido
  // ── MANTENIMIENTO: corregir estados de pago mal marcados ─────────
  const [mant, setMant] = useState(null);        // vista previa del servidor
  const [mantAbierto, setMantAbierto] = useState(false);
  const [mantTrabajando, setMantTrabajando] = useState(false);
  const [mantHecho, setMantHecho] = useState(null);
  const abrirMantenimiento = async () => {
    setMantAbierto(true); setMant(null); setMantHecho(null);
    const r = await apiFetch('/api/mantenimiento/estados-pago').catch(() => null);
    setMant(r && !r.error ? r : { error: (r && r.error) || 'No se pudo revisar' });
  };
  const aplicarMantenimiento = async () => {
    setMantTrabajando(true);
    const r = await apiFetch('/api/mantenimiento/estados-pago', { method:'POST' }).catch(() => null);
    setMantTrabajando(false);
    if (r && r.ok) { setMantHecho({ corregidos: r.corregidos }); await loadAll(); }
    else alert('No se pudo aplicar: ' + ((r && r.error) || 'error desconocido'));
  };
  const deshacerMantenimiento = async () => {
    if (!window.confirm('¿Regresar los pedidos a como estaban antes de la corrección?')) return;
    setMantTrabajando(true);
    const r = await apiFetch('/api/mantenimiento/estados-pago/deshacer', { method:'POST' }).catch(() => null);
    setMantTrabajando(false);
    if (r && r.ok) { setMantHecho(null); setMantAbierto(false); await loadAll(); }
    else alert('No se pudo deshacer: ' + ((r && r.error) || 'error desconocido'));
  };
  // ── PONER PRECIO A LOS PEDIDOS QUE NO LO TIENEN ──────────────────
  const [precAbierto, setPrecAbierto] = useState(false);
  const [precData, setPrecData] = useState(null);
  const [precPlat, setPrecPlat] = useState('TODAS');
  const [precSel, setPrecSel] = useState([]);      // ids seleccionados
  const [precLote, setPrecLote] = useState('');    // monto a aplicar en bloque
  const [precValores, setPrecValores] = useState({}); // id -> texto escrito
  const [precGuardados, setPrecGuardados] = useState({}); // id -> monto guardado
  const [precTrabajando, setPrecTrabajando] = useState(false);

  const abrirPrecios = async () => {
    setPrecAbierto(true); setPrecData(null); setPrecSel([]); setPrecLote('');
    setPrecValores({}); setPrecGuardados({}); setPrecPlat('TODAS');
    const r = await apiFetch('/api/pedidos/sin-precio').catch(() => null);
    setPrecData(r && !r.error ? r : { error: (r && r.error) || 'No se pudo cargar' });
  };

  // Lista visible según el filtro de plataforma, sin los ya guardados
  const precVisibles = (precData?.pedidos || [])
    .filter(p => !precGuardados[p.id])
    .filter(p => precPlat === 'TODAS' || p.plataforma === precPlat)
    .filter(p => coincide(p.cliente, p.titulo, p.plataforma, p.folio, p.notas));

  // Sugerencias según la plataforma elegida
  const precSugerencias = precPlat !== 'TODAS'
    ? (precData?.sugerencias?.porPlataforma?.[precPlat] || precData?.sugerencias?.global || [])
    : (precData?.sugerencias?.global || []);

  const guardarPrecioUno = async (id, valor) => {
    const n = parseFloat(String(valor).replace(/[^0-9.]/g, ''));
    if (!(n > 0)) return;
    const r = await apiFetch('/api/pedidos/' + id + '/precio', {
      method: 'PUT', body: JSON.stringify({ precio: String(n) })
    }).catch(() => null);
    if (r && r.ok) setPrecGuardados(prev => ({ ...prev, [id]: r.precio }));
    else alert('No se pudo guardar: ' + ((r && r.error) || 'error'));
  };

  const aplicarLote = async () => {
    const n = parseFloat(String(precLote).replace(/[^0-9.]/g, ''));
    if (!(n > 0) || precSel.length === 0) return;
    setPrecTrabajando(true);
    const r = await apiFetch('/api/pedidos-precio-lote', {
      method: 'PUT', body: JSON.stringify({ ids: precSel, precio: String(n) })
    }).catch(() => null);
    setPrecTrabajando(false);
    if (r && r.ok) {
      const nuevos = {};
      precSel.forEach(id => { nuevos[id] = r.precio; });
      setPrecGuardados(prev => ({ ...prev, ...nuevos }));
      setPrecSel([]); setPrecLote('');
    } else alert('No se pudo aplicar: ' + ((r && r.error) || 'error'));
  };

  const cerrarPrecios = async () => {
    setPrecAbierto(false);
    if (Object.keys(precGuardados).length > 0) await loadAll(); // refrescar el reporte
  };

  // ── REGISTRAR PAGO SIN SALIRSE DE FINANZAS ───────────────────────
  // Antes, el botón de cobrar llamaba a onOpenRentas, que CIERRA esta pantalla
  // y abre el modal en la página principal → te sacaba y tenías que volver a
  // entrar a Finanzas → Reporte. Ahora el modal se abre aquí encima.
  const [rentasLocal, setRentasLocal] = useState(null);
  const cerrarRentasLocal = async () => { setRentasLocal(null); await loadAll(); };

  // ── LLEGAR AL PEDIDO DESDE FINANZAS ──────────────────────────────
  // Abre el MISMO editor de pedidos que el hub principal. Todo lo que
  // cambies aquí (precio, datos, notas, estado → DESACTIVADA, etc.) se
  // guarda en el pedido real, así que se ve igual en el hub de pedidos.
  const [pedidoEditId, setPedidoEditId] = useState(null);
  const [estadoMenuId, setEstadoMenuId] = useState(null);
  const [rentaAbiertaId, setRentaAbiertaId] = useState(null); // panel de acciones de una renta semanal
  const pedidoReal = (id) => orders.find(o => o.id === id);
  const abrirPedido = (id) => {
    if (!onSavePedido) return;
    if (!pedidoReal(id)) { alert('No encontré este pedido en la lista. Toca ↻ arriba y vuelve a intentar.'); return; }
    setEstadoMenuId(null);
    setPedidoEditId(id);
  };
  const guardarPedidoDesdeFinanzas = async (updated) => {
    await onSavePedido(updated);   // guarda en el pedido real (hub + servidor)
    setPedidoEditId(null);
    await loadAll();               // refresca cartera, reporte y rentas
  };
  const cambiarEstadoDesdeFinanzas = async (id, estado) => {
    if (!onCambiarEstadoPedido) return;
    setEstadoMenuId(null);
    await onCambiarEstadoPedido(id, estado);
    await loadAll();
  };
  const eliminarPedidoDesdeFinanzas = async (id) => {
    if (!onEliminarPedido) return;
    const o = pedidoReal(id);
    if (!window.confirm('¿Eliminar el pedido' + (o?.cliente ? ' de ' + o.cliente : '') + ' de forma permanente?\nSe borra también del hub de pedidos. No se puede deshacer.')) return;
    await onEliminarPedido(id);
    setCarteraExp(null); setRepExp(null); setEstadoMenuId(null);
    await loadAll();
  };
  // Barra de acciones sobre el pedido real (se usa en Cartera, Reporte y Rentas)
  const renderAccionesPedido = (id) => {
    const o = pedidoReal(id);
    if (!o || !onSavePedido) return null;
    const sc = SC[o.estado] || {};
    return (
      <div className="rounded-xl p-2.5 space-y-2" style={{background:'rgba(59,130,246,0.08)', border:'1px solid rgba(59,130,246,0.25)'}}>
        <div className="flex items-center justify-between gap-2">
          <div className="fin-t3 text-xs font-bold">📦 Pedido</div>
          <span className={"px-2 py-0.5 rounded-full text-[10px] font-black "+(sc.bg||'bg-slate-200')+" "+(sc.text||'text-slate-700')}>{sc.emoji} {o.estado}</span>
        </div>
        <button onClick={()=>abrirPedido(id)}
          className="w-full py-2.5 rounded-lg font-black text-xs text-white active:scale-95 transition-transform"
          style={{background:'#3b82f6'}}>
          ✏️ Abrir pedido completo · editar info, precio y datos
        </button>
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <button onClick={()=>setEstadoMenuId(estadoMenuId===id?null:id)}
              className="w-full py-2 rounded-lg text-xs font-black fin-t2 active:scale-95 transition-transform"
              style={{background:'var(--fw-8)', border:'1px solid var(--fw-12)'}}>
              🔀 Cambiar estado ▾
            </button>
            {estadoMenuId===id && (
              <div className="absolute left-0 bottom-full mb-1 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden min-w-[230px] max-h-[55vh] overflow-y-auto">
                <EstadoMenuItems current={o.estado} onPick={est=>cambiarEstadoDesdeFinanzas(id, est)}/>
              </div>
            )}
          </div>
          <button onClick={()=>cambiarEstadoDesdeFinanzas(id, 'DESACTIVADA')} disabled={o.estado==='DESACTIVADA'}
            className="w-full py-2 rounded-lg text-xs font-black text-zinc-200 active:scale-95 transition-transform disabled:opacity-30"
            style={{background:'rgba(82,82,91,0.5)', border:'1px solid rgba(161,161,170,0.3)'}}>
            🔌 Desactivada
          </button>
        </div>
        {onEliminarPedido && (
          <button onClick={()=>eliminarPedidoDesdeFinanzas(id)}
            className="w-full py-1.5 rounded-lg text-[11px] font-bold text-red-400/70 hover:text-red-400 active:scale-95 transition-transform">
            🗑 Eliminar pedido
          </button>
        )}
      </div>
    );
  };

  // ── EDITAR EL PRECIO DESDE EL REPORTE ────────────────────────────
  const [editPrecio, setEditPrecio] = useState({});   // id -> texto escrito
  const [precioSaving, setPrecioSaving] = useState(null);
  const guardarPrecioReporte = async (id) => {
    const n = parseFloat(String(editPrecio[id] ?? '').replace(/[^0-9.]/g, ''));
    if (!(n > 0)) return;
    setPrecioSaving(id);
    const r = await apiFetch('/api/pedidos/' + id + '/precio', {
      method: 'PUT', body: JSON.stringify({ precio: String(n) })
    }).catch(() => null);
    setPrecioSaving(null);
    if (r && r.ok) {
      setEditPrecio(p => { const c = { ...p }; delete c[id]; return c; });
      await loadAll();
    } else alert('No se pudo guardar el precio: ' + ((r && r.error) || 'error'));
  };

  // ── DAR DE BAJA / REACTIVAR UNA DEUDA ────────────────────────────
  // Para los que no pagan ni cobrándoles: en vez de "borrar" (que no quitaba la
  // deuda porque el precio del pedido la regeneraba), se CIERRA el cobro. El
  // pedido sale de deudores, deja de sumar, y queda el motivo registrado para
  // poder auditarlo. Se puede reactivar después. Usa el endpoint del server que
  // además anula la fila de cartera si existía.
  const cerrarCobro = async (id) => {
    const motivo = window.prompt('¿Por qué se da de baja esta deuda?\n(ej: no paga, incobrable, cliente perdido)', 'No paga');
    if (motivo === null) return; // canceló el prompt
    const r = await apiFetch('/api/pedidos/' + id + '/cerrar-cobro', {
      method: 'POST', body: JSON.stringify({ motivo: (motivo || '').trim() || 'No se cobra' })
    }).catch(() => null);
    if (r && r.ok) { setCarteraExp(null); await loadAll(); }
    else alert('No se pudo dar de baja: ' + ((r && r.error) || 'error'));
  };
  const reabrirCobro = async (id) => {
    const r = await apiFetch('/api/pedidos/' + id + '/reabrir-cobro', { method: 'POST' }).catch(() => null);
    if (r && r.ok) await loadAll();
    else alert('No se pudo reactivar el cobro: ' + ((r && r.error) || 'error'));
  };

  // Días desde el último pago (o null si nunca pagó). Base de las alertas de renta.
  const diasSinPagar = (ultimoPago) => ultimoPago
    ? Math.floor((Date.now() - new Date(ultimoPago + 'T12:00').getTime()) / 86400000)
    : null;
  // Cuántas rentas (directas + semanales) están atrasadas (7+ días o sin pagos).
  // Se usa para el badge de alerta del encabezado.
  const rentasAtrasadasCount = [...rentasDirectas, ...semanales].filter(c => {
    if (!(parseFloat(c.montoSemanal || 0) > 0)) return false;
    const d = diasSinPagar(c.ultimoPago);
    return d === null || d >= 7;
  }).length;

  const [periodo, setPeriodo] = useState('semana'); // 'semana' | 'mes'
  const [gastoForm, setGastoForm] = useState({ monto:'', categoria:'Cuentas', descripcion:'', metodo:'Zelle', fecha: new Date().toISOString().split('T')[0] });
  const [gastoSaving, setGastoSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [abonoForm, setAbonoForm] = useState({});
  const [abonoSaving, setAbonoSaving] = useState(null);
  const [expDeudor, setExpDeudor] = useState(null); // nombre del deudor expandido
  const [carteraExp, setCarteraExp] = useState(null); // id del pedido expandido dentro de un deudor
  const [ordenDeudas, setOrdenDeudas] = useState('antiguo'); // 'antiguo' | 'monto'
  // ── SUPER CLIENTES ────────────────────────────────────────────────
  // Mapa { nombreDeCliente: "Nombre del Super Cliente" }. Junta las deudas de
  // varios pedidos/nombres bajo un mismo deudor. Se guarda en el servidor
  // (sobrevive sesión) con respaldo en el dispositivo.
  const [superClientes, setSuperClientes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('royal_super_clientes') || '{}'); } catch { return {}; }
  });
  const [showSuperModal, setShowSuperModal] = useState(false);
  useEffect(() => {
    apiFetch('/api/me/prefs').then(p => {
      if (p && p.superClientes && typeof p.superClientes === 'object') setSuperClientes(p.superClientes);
    }).catch(()=>{});
  }, []);
  const persistSuper = (next) => {
    setSuperClientes(next);
    localStorage.setItem('royal_super_clientes', JSON.stringify(next));
    apiFetch('/api/me/prefs', { method:'PUT', body: JSON.stringify({ superClientes: next }) }).catch(()=>{});
  };
  const superOf = (nombre) => (superClientes[(nombre||'').trim()] || (nombre||'').trim());
  // Enlaza una deuda con su pedido real para poder ver correo, datos, notas
  const pedidoDe = (pedidoId) => pedidoId ? orders.find(o => o.id === pedidoId) : null;
  const copiar = (txt) => { try { navigator.clipboard.writeText(txt); } catch {} };
  // ── BÚSQUEDA en finanzas ──────────────────────────────────────────
  const [finSearch, setFinSearch] = useState('');
  const coincide = (...campos) => {
    const q = finSearch.trim().toLowerCase();
    if (!q) return true;
    return campos.some(c => (c||'').toString().toLowerCase().includes(q));
  };
  // ── EXPORTAR a Excel (CSV que abre directo en Excel/Sheets) ───────
  const descargarCSV = (nombreArchivo, filas) => {
    const esc = v => {
      const s = (v==null ? '' : String(v)).replace(/"/g,'""');
      return /[",\n;]/.test(s) ? '"'+s+'"' : s;
    };
    const csv = '\uFEFF' + filas.map(r => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const hoy = new Date().toISOString().split('T')[0];
    a.href = url; a.download = nombreArchivo.replace('.csv','') + '_' + hoy + '.csv';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(()=>URL.revokeObjectURL(url), 1000);
  };
  const [extraForm, setExtraForm] = useState({ monto:'', metodo:'Zelle', descripcion:'', fechaPago: new Date().toISOString().split('T')[0] });
  const [extraSaving, setExtraSaving] = useState(false);
  const [rentaForm, setRentaForm] = useState({ cliente:'', plataforma:'OTROS', montoSemanal:'', notas:'' });
  const [editandoRenta, setEditandoRenta] = useState(null);
  const [showNuevaRenta, setShowNuevaRenta] = useState(false);
  const [savingRenta, setSavingRenta] = useState(false);
  const [clientesBusqueda, setClientesBusqueda] = useState('');
  const [clientesAsignado, setClientesAsignado] = useState('');
  const [reasignandoCliente, setReasignandoCliente] = useState(null); // nombre del cliente cuyas órdenes se están reasignando
  const [reasignandoSaving, setReasignandoSaving] = useState(false);
  const [clienteExp, setClienteExp] = useState(null);
  const [fichaFinId, setFichaFinId] = useState(null); // ficha de cliente abierta desde Finanzas // nombre del cliente cuyos pedidos están desplegados en la sección Clientes

  const reasignarPedidosCliente = async (clienteNombre, userId, userName) => {
    if (!onReasignar) return;
    setReasignandoSaving(true);
    const pedidosCliente = orders.filter(o => o.cliente === clienteNombre);
    for (const o of pedidosCliente) {
      await onReasignar(o.id, userId, userName);
    }
    setReasignandoSaving(false);
    setReasignandoCliente(null);
  };

  const loadAll = async (silencioso = false) => {
    if (silencioso !== true) setLoading(true);
    const [fin, ext, sem, cred, cart, rentas, gas, rep] = await Promise.all([
      apiFetch('/api/finanzas'),
      apiFetch('/api/pagos-extras'),
      apiFetch('/api/pedidos-semanales'),
      apiFetch('/api/pedidos-credito'),
      apiFetch('/api/cartera'),
      apiFetch('/api/rentas-directas'),
      apiFetch('/api/gastos'),
      apiFetch('/api/reporte/cartera').catch(() => null),
    ]);
    setFinData(fin);
    setExtras(ext || []);
    setSemanales(sem || []);
    setCreditos(cred || []);
    setCartera(cart || []);
    setRentasDirectas(rentas || []);
    setGastos(gas || []);
    setReporte(rep && !rep.error ? rep : null);
    setActualizado(Date.now()); setAhora(Date.now());
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const fmtDate = d => d ? new Date(d + 'T12:00').toLocaleDateString('es-MX', { day:'numeric', month:'short', year:'numeric' }) : '—';
  const fmtMoney = n => '$' + (parseFloat(n) || 0).toFixed(0);

  const submitExtra = async () => {
    if (!extraForm.monto || !extraForm.descripcion.trim()) return;
    setExtraSaving(true);
    await apiFetch('/api/pagos-extras', { method:'POST', body: JSON.stringify(extraForm) });
    setExtraForm({ monto:'', metodo:'Zelle', descripcion:'', fechaPago: new Date().toISOString().split('T')[0] });
    const ext = await apiFetch('/api/pagos-extras');
    setExtras(ext || []);
    setExtraSaving(false);
  };
  const deleteExtra = async id => {
    await apiFetch('/api/pagos-extras/' + id, { method:'DELETE' });
    setExtras(prev => prev.filter(e => e.id !== id));
  };

  const handleAbonoCartera = async entrada => {
    const abono = parseFloat(abonoForm[entrada.id] || 0);
    if (!abono) return;
    setAbonoSaving(entrada.id);
    const r = await apiFetch('/api/cartera/' + entrada.id + '/abonar', { method:'PUT', body: JSON.stringify({ abono, metodo:'Zelle' }) });
    await apiFetch('/api/pedidos/' + entrada.pedidoId + '/rentas', { method:'POST', body: JSON.stringify({
      monto: abono, metodo:'Zelle', fechaPago: new Date().toISOString().split('T')[0], notas:'Abono cartera', proximoPago:''
    })});
    setAbonoSaving(null);
    setAbonoForm(prev => ({ ...prev, [entrada.id]: '' }));
    const cart = await apiFetch('/api/cartera');
    setCartera(cart || []);
  };
  const deleteCartera = async id => {
    if (!window.confirm('¿Eliminar esta deuda de la cartera?')) return;
    await apiFetch('/api/cartera/' + id, { method:'DELETE' });
    setCartera(prev => prev.filter(c => c.id !== id));
  };

  const saveRenta = async () => {
    if (!rentaForm.cliente || !rentaForm.montoSemanal) return;
    setSavingRenta(true);
    if (editandoRenta) {
      await apiFetch('/api/rentas-directas/' + editandoRenta, { method:'PUT', body: JSON.stringify(rentaForm) });
    } else {
      await apiFetch('/api/rentas-directas', { method:'POST', body: JSON.stringify(rentaForm) });
    }
    setRentaForm({ cliente:'', plataforma:'OTROS', montoSemanal:'', notas:'' });
    setShowNuevaRenta(false); setEditandoRenta(null);
    const rentas = await apiFetch('/api/rentas-directas');
    setRentasDirectas(rentas || []);
    setSavingRenta(false);
  };
  const deleteRentaDirecta = async id => {
    if (!window.confirm('¿Eliminar esta renta y todo su historial?')) return;
    await apiFetch('/api/rentas-directas/' + id, { method:'DELETE' });
    setRentasDirectas(prev => prev.filter(r => r.id !== id));
  };

  // ── GASTOS ───────────────────────────────────────────────────────
  const submitGasto = async () => {
    if (!gastoForm.monto || !gastoForm.descripcion.trim()) return;
    setGastoSaving(true);
    await apiFetch('/api/gastos', { method:'POST', body: JSON.stringify(gastoForm) });
    setGastoForm({ monto:'', categoria: gastoForm.categoria, descripcion:'', metodo: gastoForm.metodo, fecha: new Date().toISOString().split('T')[0] });
    const gas = await apiFetch('/api/gastos');
    setGastos(gas || []);
    // recarga finanzas para refrescar los totales de ganancia neta
    apiFetch('/api/finanzas').then(f=>setFinData(f));
    setGastoSaving(false);
  };
  const deleteGasto = async id => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    await apiFetch('/api/gastos/' + id, { method:'DELETE' });
    setGastos(prev => prev.filter(g => g.id !== id));
    apiFetch('/api/finanzas').then(f=>setFinData(f));
  };
  const CATEGORIAS_GASTO = ['Cuentas','Teléfonos','Suscripciones','Trabajadores','Publicidad','Otros'];

  // ── Datos calculados ─────────────────────────────────────────────
  const totalCarteraPendiente = cartera.filter(c=>c.estado!=='saldado').reduce((s,c)=>s+Math.max(0,parseFloat(c.montoTotal)-parseFloat(c.montoPagado)),0);
  const totalCarteraDeudores = cartera.filter(c=>c.estado!=='saldado').length;

  // Deudas unificadas: cartera (entregas sin cobrar) + créditos (pagos a plazos) = todo es deuda
  const totalCreditoPendiente = creditos.reduce((s,c)=>{
    const pagado = parseFloat(c.totalPagado||0);
    const total  = parseFloat(c.totalCredito||0);
    return s + Math.max(0, total - pagado);
  }, 0);
  // La deuda "de verdad" sale del reporte reconciliado (precio real del pedido +
  // cartera + recibos), no de la tabla cartera cruda. Así el badge del header, la
  // pantalla de Ventas y la de Cartera muestran EXACTAMENTE el mismo número y los
  // precios no se quedan pegados en montos viejos escritos a mano. Si el reporte
  // aún no cargó, caemos al cálculo crudo para no mostrar $0.
  const totalDeudas = reporte
    ? reporte.resumen.porCobrar
    : (totalCarteraPendiente + totalCreditoPendiente);
  const totalDeudores = reporte
    ? reporte.resumen.deudores
    : (totalCarteraDeudores + creditos.filter(c=>{
        const pagado = parseFloat(c.totalPagado||0);
        const total  = parseFloat(c.totalCredito||0);
        return total > 0 && pagado < total;
      }).length);

  // Estado de cuenta por cliente (desde API)
  const porCliente = finData?.porCliente || [];

  // ── Ingresos / gastos / ganancia neta según el periodo elegido ───
  const ingresosPeriodo = periodo==='semana'
    ? (finData?.pagosSemana||0)+(finData?.extrasSemana||0)
    : (finData?.pagosMes||0)+(finData?.extrasMes||0);
  const gastosPeriodo   = periodo==='semana' ? (finData?.gastosSemana||0) : (finData?.gastosMes||0);
  const gananciaPeriodo = ingresosPeriodo - gastosPeriodo;
  const labelPeriodo    = periodo==='semana' ? 'esta semana' : 'este mes';
  // Por cobrar (recurrentes que vencen pronto + semanales atrasados)
  const totalPorCobrar = (finData?.vencenProximo||[]).reduce((s,v)=>s+parseFloat(v.monto||0),0);

  // ── REPORTE: filtro, orden y exportación ─────────────────────────
  const BUCKET_CFG = {
    '0-7':   { label:'Al día (0-7 días)',  color:'var(--fc-green)', emoji:'🟢' },
    '8-15':  { label:'8-15 días',          color:'var(--fc-amber)', emoji:'🟡' },
    '16-30': { label:'16-30 días',         color:'var(--fc-orange)', emoji:'🟠' },
    '31+':   { label:'Más de 30 días',     color:'var(--fc-red)', emoji:'🔴' },
  };
  const EC_CFG = {
    'abierta':    { label:'Sin cobrar', bg:'rgba(248,113,113,0.15)', color:'var(--fc-red)' },
    'parcial':    { label:'Abonado',    bg:'rgba(251,191,36,0.15)',  color:'var(--fc-amber)' },
    'saldada':    { label:'Pagado',     bg:'rgba(52,211,153,0.15)',  color:'var(--fc-green)' },
    'sin-precio': { label:'Sin precio', bg:'rgba(148,163,184,0.15)', color:'#94a3b8' },
    'anulada':    { label:'No se cobra',bg:'rgba(148,163,184,0.12)', color:'#64748b' },
    'en-proceso': { label:'En proceso · aún no cobra', bg:'rgba(96,165,250,0.15)', color:'var(--fc-blue)' },
    'desactivada':{ label:'Desactivada · no es deuda', bg:'rgba(113,113,122,0.2)', color:'#a1a1aa' },
  };

  const repPedidos = (() => {
    if (!reporte) return [];
    let lista = reporte.pedidos.filter(p => !p.esRentaDirecta);
    // cobroCerrado = ya se revisó y se decidió no cobrarlo. No es deuda.
    // fueraCartera = en proceso (aún no se entrega) o DESACTIVADA → no es deuda.
    if (repFiltro === 'con-saldo')  lista = lista.filter(p => !p.cobroCerrado && !p.fueraCartera && p.saldo > 0.01 && p.total > 0);
    if (repFiltro === 'vencidos')   lista = lista.filter(p => !p.cobroCerrado && !p.fueraCartera && p.saldo > 0.01 && p.total > 0 && (p.diasSinPago ?? 0) > 30);
    if (repFiltro === 'en-proceso') lista = lista.filter(p => p.fueraCartera === 'en-proceso');
    if (repFiltro === 'desactivadas') lista = lista.filter(p => p.fueraCartera === 'desactivada');
    if (repFiltro === 'sin-precio') lista = lista.filter(p => p.estadoCobro === 'sin-precio' && !p.fueraCartera);
    if (repFiltro === 'saldados')   lista = lista.filter(p => p.estadoCobro === 'saldada');
    if (repFiltro === 'anuladas')   lista = lista.filter(p => p.cobroCerrado);
    lista = lista.filter(p => coincide(p.cliente, p.plataforma, p.contacto, p.mail, p.folio));
    const orden = {
      antiguo: (a,b) => (b.diasSinPago ?? 0) - (a.diasSinPago ?? 0),
      monto:   (a,b) => b.saldo - a.saldo,
      cliente: (a,b) => a.cliente.localeCompare(b.cliente),
    }[repOrden];
    return [...lista].sort(orden);
  })();

  const exportarReporte = () => {
    if (!reporte) return;
    const encabezados = ['Folio','Cliente','Plataforma','Estado del pedido','Tipo de cobro',
      'Fecha del pedido','Fecha de entrega','Total','Pagado','Saldo','Estado de cobro',
      '# de pagos','Último pago','Días sin pagar','Método','Contacto','Correo',
      'Responsable','Notas'];
    const filas = repPedidos.map(p => [
      p.folio, p.cliente, p.plataforma, p.estadoPedido, p.tipoCobro,
      (p.createdAt||'').split(' ')[0].split('T')[0], p.entrega,
      p.total.toFixed(2), p.pagado.toFixed(2), p.saldo.toFixed(2),
      (EC_CFG[p.estadoCobro]||{}).label || p.estadoCobro,
      p.numPagos, p.ultimoPago, p.diasSinPago ?? '',
      p.metodo, p.contacto, p.mail, p.responsable,
      (p.notas||'').replace(/\s+/g,' ').slice(0,200),
    ]);
    descargarCSV('reporte_cartera', [encabezados, ...filas]);
  };

  // ── DATOS PARA EL TABLERO (se recalculan solos con cada actualización) ──
  const fFecha = d => d ? new Date(String(d).slice(0, 10) + 'T12:00') : null;
  // Libro único de movimientos: pagos de pedidos + pagos sueltos + gastos
  const movimientos = useMemo(() => {
    const ins = (finData?.historial || []).map(r => ({
      key: 'p' + r.id, tipo: 'in', fuente: 'Pago de pedido', fecha: String(r.fechaPago || '').slice(0, 10),
      monto: parseFloat(r.monto) || 0, quien: r.cliente || '—', plataforma: r.plataforma || '',
      detalle: r.notas || '', metodo: r.metodo || '', pedidoId: r.pedidoId || r.pedido_id || null,
    }));
    const ext = extras.map(e => ({
      key: 'e' + e.id, tipo: 'in', fuente: 'Ingreso suelto', fecha: String(e.fechaPago || '').slice(0, 10),
      monto: parseFloat(e.monto) || 0, quien: e.descripcion || '—', plataforma: '', detalle: '', metodo: e.metodo || '', pedidoId: null,
    }));
    const gas = gastos.map(g => ({
      key: 'g' + g.id, tipo: 'out', fuente: g.categoria || 'Gasto', fecha: String(g.fecha || '').slice(0, 10),
      monto: parseFloat(g.monto) || 0, quien: g.descripcion || '—', plataforma: '', detalle: g.categoria || '', metodo: g.metodo || '', pedidoId: null,
    }));
    return [...ins, ...ext, ...gas].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [finData, extras, gastos]);

  // Últimas 8 semanas (lunes a domingo): cuánto entró y cuánto salió
  const semanas = useMemo(() => {
    const lunes = d => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); x.setHours(12, 0, 0, 0); return x; };
    const base = lunes(new Date());
    const arr = Array.from({ length: 8 }, (_, i) => {
      const ini = new Date(base); ini.setDate(ini.getDate() - 7 * (7 - i));
      return { ini, label: ini.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }), entro: 0, salio: 0 };
    });
    movimientos.forEach(m => {
      const f = fFecha(m.fecha); if (!f) return;
      const t = lunes(f).getTime();
      const s = arr.find(a => Math.abs(a.ini.getTime() - t) < 3 * 3600000);
      if (s) { if (m.tipo === 'in') s.entro += m.monto; else s.salio += m.monto; }
    });
    return arr;
  }, [movimientos]);

  // Deuda real (misma fuente que "Te deben")
  const deudasActivas = useMemo(() => (reporte?.pedidos || []).filter(p =>
    !p.esRentaDirecta && !p.cobroCerrado && !p.fueraCartera && p.saldo > 0.01 && p.total > 0), [reporte]);
  const deudaPorPlataforma = useMemo(() => {
    const m = {};
    deudasActivas.forEach(p => { const k = p.plataforma || 'OTROS'; m[k] = m[k] || { label: k, valor: 0, n: 0 }; m[k].valor += p.saldo; m[k].n++; });
    return Object.values(m).map(x => ({ ...x, sub: x.n + ' ped.' })).sort((a, b) => b.valor - a.valor).slice(0, 8);
  }, [deudasActivas]);
  const vencido30 = reporte ? (reporte.buckets?.['31+'] || 0) : 0;

  // Cómo te pagaron este mes
  const metodosMes = useMemo(() => {
    const mes = new Date().toISOString().slice(0, 7);
    const m = {};
    movimientos.filter(x => x.tipo === 'in' && (x.fecha || '').startsWith(mes)).forEach(x => {
      const k = x.metodo || 'Otro'; m[k] = m[k] || { label: k, valor: 0, n: 0 }; m[k].valor += x.monto; m[k].n++;
    });
    return Object.values(m).map(x => ({ ...x, sub: x.n + ' pagos' })).sort((a, b) => b.valor - a.valor);
  }, [movimientos]);

  // Para cobrar ahora: rentas atrasadas + los que más deben
  const rentasAtrasadas = useMemo(() => [
    ...rentasDirectas.map(c => ({ ...c, _tipo: 'renta' })), ...semanales.map(c => ({ ...c, _tipo: 'semanal' })),
  ].filter(c => parseFloat(c.montoSemanal || 0) > 0)
    .map(c => ({ ...c, _dias: diasSinPagar(c.ultimoPago) }))
    .filter(c => c._dias === null || c._dias >= 7)
    .sort((a, b) => (b._dias ?? 999) - (a._dias ?? 999)), [rentasDirectas, semanales]);

  // ── TIEMPO REAL: se actualiza solo cada 45 s y al volver a la pestaña ──
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') loadAll(true); }, 45000);
    const tick = setInterval(() => setAhora(Date.now()), 5000);
    const alVolver = () => { if (document.visibilityState === 'visible') loadAll(true); };
    window.addEventListener('focus', alVolver);
    document.addEventListener('visibilitychange', alVolver);
    return () => { clearInterval(t); clearInterval(tick); window.removeEventListener('focus', alVolver); document.removeEventListener('visibilitychange', alVolver); };
  }, []); // eslint-disable-line
  const haceCuanto = (() => {
    const s = Math.max(0, Math.round((ahora - actualizado) / 1000));
    return s < 10 ? 'ahora' : s < 60 ? 'hace ' + s + ' s' : 'hace ' + Math.round(s / 60) + ' min';
  })();

  // Reporte completo en un Excel (usa la deuda reconciliada, no la tabla cruda)
  const descargarReporteCompleto = () => {
    const rows = []; const push = (...r) => rows.push(r);
    push('REPORTE DE FINANZAS', new Date().toLocaleString('es-MX'));
    push('');
    push('== RESUMEN (' + labelPeriodo + ') ==');
    push('Ingresos', ingresosPeriodo.toFixed(2));
    push('Gastos', gastosPeriodo.toFixed(2));
    push('Ganancia neta', gananciaPeriodo.toFixed(2));
    push('Te deben (total)', totalDeudas.toFixed(2));
    push('Te deben con más de 30 días', vencido30.toFixed(2));
    push('');
    push('== QUIÉN TE DEBE (por pedido) ==');
    push('Deudor (grupo)', 'Cliente', 'Folio', 'Plataforma', 'Tipo de cobro', 'Total', 'Pagado', 'Falta', 'Días sin pagar', 'Último pago', 'Correo', 'Contacto');
    [...deudasActivas].sort((a, b) => b.saldo - a.saldo).forEach(p => push(superOf((p.cliente || '').trim()), p.cliente || '', p.folio || '', p.plataforma || '', p.tipoCobro || '', p.total.toFixed(2), p.pagado.toFixed(2), p.saldo.toFixed(2), p.diasSinPago ?? '', p.ultimoPago || '', p.mail || '', p.contacto || ''));
    push('');
    push('== RENTAS ==');
    push('Cliente', 'Plataforma', 'Renta semanal', 'Total cobrado', 'Último pago');
    [...rentasDirectas, ...semanales].forEach(c => push(c.cliente || '', c.plataforma || '', parseFloat(c.montoSemanal || 0).toFixed(2), parseFloat(c.totalPagado || 0).toFixed(2), c.ultimoPago || ''));
    push('');
    push('== MOVIMIENTOS ==');
    push('Fecha', 'Tipo', 'Concepto', 'Quién / qué', 'Plataforma', 'Método', 'Monto');
    movimientos.forEach(m => push(m.fecha, m.tipo === 'in' ? 'Entrada' : 'Salida', m.fuente, m.quien, m.plataforma, m.metodo, (m.tipo === 'in' ? '' : '-') + m.monto.toFixed(2)));
    descargarCSV('reporte_finanzas_completo', rows);
  };

  // ── MOVIMIENTOS: filtros ──
  const movFiltrados = useMemo(() => {
    const hoy = new Date();
    const desde = movPeriodo === 'semana' ? (() => { const x = new Date(hoy); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x.toISOString().slice(0, 10); })()
      : movPeriodo === 'mes' ? hoy.toISOString().slice(0, 8) + '01'
      : movPeriodo === '30' ? new Date(hoy.getTime() - 30 * 86400000).toISOString().slice(0, 10) : '';
    return movimientos.filter(m =>
      (movTipo === 'todo' || m.tipo === movTipo) &&
      (!desde || (m.fecha || '') >= desde) &&
      (!movMetodo || m.metodo === movMetodo) &&
      coincide(m.quien, m.plataforma, m.metodo, m.fuente, m.detalle));
  }, [movimientos, movTipo, movPeriodo, movMetodo, finSearch]); // eslint-disable-line
  const movMetodos = useMemo(() => [...new Set(movimientos.map(m => m.metodo).filter(Boolean))].sort(), [movimientos]);

  const seccionesNav = [
    { id:'inicio',      emoji:'📊', label:'Resumen' },
    { id:'deudas',      emoji:'💸', label:'Te deben', match:['deudas','reporte'] },
    { id:'movimientos', emoji:'📜', label:'Movimientos', match:['movimientos','historial','extras'] },
    { id:'rentas',      emoji:'🏠', label:'Rentas' },
    { id:'gastos',      emoji:'➖', label:'Gastos' },
    { id:'clientes',    emoji:'👥', label:'Clientes' },
  ];

  // ── DETALLE DE COBRO (compartido entre Reporte y Cartera) ─────────
  // Un solo bloque para gestionar una deuda: datos del pedido, avisos de
  // discrepancia, editar el PRECIO REAL del pedido y registrar un pago. Se usa
  // idéntico en las dos pantallas para que se sientan iguales y nada se
  // desincronice. Es una función que devuelve JSX (no un componente) a propósito:
  // así los <input> no se re-montan en cada tecla y no pierden el foco.
  const renderDetalleCobro = (p) => {
    const esCredito = p.tipoCobro === 'credito';
    return (
      <div className="px-3 pb-3 pt-1 space-y-2 border-t" style={{borderColor:'var(--fw-6)'}}>
        {/* Datos del pedido para poder cobrar sin salir de aquí */}
        {[['📞 Contacto', p.contacto], ['✉️ Correo', p.mail], ['🗂 Dato', p.dato],
          ['📅 Pedido', (p.createdAt||'').split(' ')[0].split('T')[0]],
          ['🚚 Entrega', p.entrega], ['💵 Último pago', p.ultimoPago ? `${p.ultimoPago} (${p.metodo||'—'})` : 'Nunca ha pagado'],
          ['🔁 Tipo de cobro', p.tipoCobro], ['👤 Responsable', p.responsable],
        ].filter(([,v]) => v).map(([k,v]) => (
          <div key={k} className="flex items-start gap-2 text-xs">
            <span className="fin-t4 shrink-0 w-28">{k}</span>
            <span className="fin-t2 font-medium flex-1 break-words">{v}</span>
            <button onClick={()=>copiar(String(v))} className="fin-t4 fin-hover-t1 shrink-0">📋</button>
          </div>
        ))}
        {p.notas && (
          <div className="rounded-xl p-2 text-xs fin-t3" style={{background:'var(--fw-4)'}}>
            <span className="fin-t4 font-bold">Notas: </span>{p.notas}
          </div>
        )}
        {/* Discrepancias entre las fuentes de datos */}
        {(() => {
          const f = p._fuentes;
          const avisos = [];
          if (f.carteraTotal > 0 && f.precioExcel > 0 && Math.abs(f.carteraTotal - f.precioExcel) > 0.01)
            avisos.push(`Cartera dice ${fmtMoney(f.carteraTotal)} pero el precio del pedido dice ${fmtMoney(f.precioExcel)}`);
          if (f.carteraPagado > 0 && f.rentasSuma > 0 && Math.abs(f.carteraPagado - f.rentasSuma) > 0.01)
            avisos.push(`Cartera registra ${fmtMoney(f.carteraPagado)} pagado pero los recibos suman ${fmtMoney(f.rentasSuma)}`);
          if (!p.tieneCartera && p.saldo > 0.01)
            avisos.push('Esta deuda no está en la tabla de cartera. Solo se ve aquí.');
          if (!avisos.length) return null;
          return (
            <div className="rounded-xl p-2 space-y-1" style={{background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.2)'}}>
              {avisos.map((a,i) => <div key={i} className="text-amber-300/80 text-xs">⚠️ {a}</div>)}
            </div>
          );
        })()}
        {/* Cambiar el precio sin salir de aquí. En créditos a plazos el total lo
            fija el plan de pagos, así que ahí no se edita el precio suelto. */}
        {esCredito ? (
          <div className="rounded-xl p-2.5 text-xs fin-t3" style={{background:'rgba(59,130,246,0.08)', border:'1px solid rgba(59,130,246,0.2)'}}>
            💳 Crédito a plazos · total {fmtMoney(p.total)}. El monto lo define el plan de pagos; registra cada abono abajo.
          </div>
        ) : (
          <div className="rounded-xl p-2.5" style={{background:'var(--fw-4)'}}>
            <div className="fin-t4 text-xs font-bold mb-1.5">💵 Precio del pedido</div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 fin-t3 text-xs font-black">$</span>
                <input
                  value={editPrecio[p.id] ?? (p.total > 0 ? String(p.total) : '')}
                  onChange={e=>setEditPrecio(v=>({...v,[p.id]:e.target.value}))}
                  onKeyDown={e=>{ if(e.key==='Enter') guardarPrecioReporte(p.id); }}
                  inputMode="decimal" placeholder="0"
                  className="w-full rounded-lg pl-6 pr-2 py-2 text-sm fin-t1 font-black outline-none"
                  style={{background:'var(--fw-7)', border:'1px solid var(--fw-14)'}}/>
              </div>
              <button onClick={()=>guardarPrecioReporte(p.id)}
                disabled={precioSaving===p.id || editPrecio[p.id]===undefined || !editPrecio[p.id]}
                className="px-3 py-2 rounded-lg font-black text-xs text-emerald-950 disabled:opacity-30 active:scale-95 transition-all"
                style={{background:'#34d399'}}>
                {precioSaving===p.id ? '⏳' : 'Guardar'}
              </button>
            </div>
            {p.pagado > 0 && (
              <div className="fin-t4 text-xs mt-1.5">
                Ya pagó {fmtMoney(p.pagado)}. Al cambiar el precio se recalcula lo que debe.
              </div>
            )}
          </div>
        )}

        <button onClick={()=>setRentasLocal(p.id)}
          className="w-full py-2.5 rounded-xl font-black text-xs fin-t1 active:scale-95 transition-transform"
          style={{background:'var(--fw-10)'}}>
          💵 Registrar un pago
        </button>

        {/* Llegar al pedido real: editar, cambiar estado, desactivar, eliminar */}
        {renderAccionesPedido(p.id)}

        {/* Dar de baja la deuda (no paga ni cobrándole) o reactivarla */}
        {p.cobroCerrado ? (
          <div className="rounded-xl p-2.5 space-y-2" style={{background:'rgba(148,163,184,0.08)', border:'1px solid rgba(148,163,184,0.2)'}}>
            <div className="fin-t3 text-xs">🚫 Dada de baja{p.cobroCerradoMotivo?`: ${p.cobroCerradoMotivo}`:''}. No cuenta como deuda.</div>
            <button onClick={()=>reabrirCobro(p.id)}
              className="w-full py-2 rounded-lg text-xs font-black fin-t2 active:scale-95 transition-transform"
              style={{background:'var(--fw-8)', border:'1px solid var(--fw-12)'}}>
              ↩️ Reactivar cobro
            </button>
          </div>
        ) : (
          <button onClick={()=>cerrarCobro(p.id)}
            className="w-full py-2 rounded-xl text-xs font-black text-red-300 active:scale-95 transition-transform"
            style={{background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)'}}>
            🚫 No cobrar · dar de baja esta deuda
          </button>
        )}
      </div>
    );
  };

  if (loading) return (
    <div className={"fin min-h-screen flex items-center justify-center "+(oscuro?'':'claro')}>
      <style>{FIN_CSS}</style>
      <div className="text-center">
        <div className="text-5xl mb-4 animate-pulse">💰</div>
        <div className="fin-t1 font-black text-lg">Cargando finanzas...</div>
      </div>
    </div>
  );

  return (
    <div className={"fin min-h-screen flex flex-col "+(oscuro?'':'claro')} style={{fontFamily:"'DM Sans', system-ui, sans-serif"}}>
      <style>{FIN_CSS}</style>

      {/* ── HEADER ─────────────────────────────────────────────── */}
      <div className="sticky top-0 z-20 px-4 py-3 flex items-center gap-3 border-b fin-border"
           style={{background:'var(--fin-head)', backdropFilter:'blur(20px)'}}>
        <button onClick={onBack}
          className="w-9 h-9 rounded-xl flex items-center justify-center fin-t3 fin-hover-t1 fin-hover-bg transition-all active:scale-95 shrink-0">
          ←
        </button>
        <div className="flex-1 min-w-0">
          <div className="fin-t1 font-black text-base leading-tight">💰 Finanzas</div>
          <div className="fin-t3 text-xs flex items-center gap-1.5 whitespace-nowrap overflow-hidden">
            <span className="fin-dot w-1.5 h-1.5 rounded-full" style={{background:'var(--fc-green)'}}/>
            En vivo · actualizado {haceCuanto}
            {finData && <span className="hidden sm:inline"> · {fmtMoney(finData.totalRecaudado)} recaudado en total</span>}
          </div>
        </div>
        {totalDeudas > 0.01 && (
          <button onClick={()=>setSeccion('deudas')}
            className="hidden sm:inline-flex px-3 py-1.5 rounded-full text-xs font-black bg-red-500 text-white">
            💸 Te deben {fmtMoney(totalDeudas)}
          </button>
        )}
        {rentasAtrasadasCount > 0 && (
          <button onClick={()=>setSeccion('rentas')}
            className="hidden sm:inline-flex px-3 py-1.5 rounded-full text-xs font-black text-amber-950"
            style={{background:'#fbbf24'}}>
            ⏰ {rentasAtrasadasCount} renta{rentasAtrasadasCount>1?'s':''}
          </button>
        )}
        <button onClick={()=>loadAll(true)} title="Actualizar ahora" className="w-9 h-9 rounded-xl flex items-center justify-center fin-t3 fin-hover-t1 fin-hover-bg transition-all active:scale-95">
          ↻
        </button>
      </div>

      {/* ── NAV TABS ────────────────────────────────────────────── */}
      <div className="flex overflow-x-auto border-b fin-border shrink-0 sm:justify-center" style={{scrollbarWidth:'none'}}>
        {seccionesNav.map(s => {
          const activo = (s.match || [s.id]).includes(seccion);
          const badge = s.id==='deudas' && totalDeudas>0.01 ? fmtMoney(totalDeudas)
            : s.id==='rentas' && rentasAtrasadasCount>0 ? String(rentasAtrasadasCount) : null;
          return (
            <button key={s.id} onClick={()=>setSeccion(s.id)}
              className={"px-4 py-3 text-xs font-black whitespace-nowrap transition-all shrink-0 flex items-center gap-1.5 " +
                (activo ? "fin-t1 border-b-2 border-emerald-400" : "fin-t3 fin-hover-t1")}>
              {s.emoji} {s.label}
              {badge && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-black" style={{background:s.id==='deudas'?'rgba(239,68,68,0.15)':'rgba(245,158,11,0.18)', color:s.id==='deudas'?'var(--fc-red)':'var(--fc-amber)'}}>{badge}</span>}
            </button>
          );
        })}
      </div>

      {/* ── BÚSQUEDA (en secciones con clientes/deudas/rentas) ────── */}
      {(seccion==='deudas'||seccion==='reporte') && (
        <div className="px-4 pt-3 shrink-0">
          <div className="mx-auto flex gap-1 p-1 rounded-xl w-fit" style={{background:'var(--fw-6)'}}>
            {[['deudas','👤 Por cliente'],['reporte','📦 Por pedido']].map(([k,l])=>(
              <button key={k} onClick={()=>setSeccion(k)} className={"px-4 py-1.5 rounded-lg text-xs font-black transition-all "+(seccion===k?'fin-t1':'fin-t3')}
                style={seccion===k?{background:'var(--fin-card)', boxShadow:'0 1px 2px rgba(0,0,0,.15)'}:undefined}>{l}</button>
            ))}
          </div>
        </div>
      )}
      {(seccion==='deudas'||seccion==='rentas'||seccion==='reporte'||seccion==='movimientos') && (
        <div className="px-4 pt-3 pb-1 shrink-0">
          <div className="relative max-w-[1120px] mx-auto">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 fin-t4 text-sm">🔍</span>
            <input value={finSearch} onChange={e=>setFinSearch(e.target.value)}
              placeholder="Buscar por nombre o plataforma..."
              className="w-full rounded-xl pl-9 pr-9 py-2.5 text-sm fin-t1 outline-none"
              style={{background:'var(--fw-6)', border:'1px solid var(--fw-12)'}}/>
            {finSearch && <button onClick={()=>setFinSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 fin-t3 fin-hover-t1 text-sm">✕</button>}
          </div>
        </div>
      )}

      {/* ── CONTENIDO ───────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">

        {/* ── REPORTE DE CARTERA (todos los pedidos, con datos reales) ─── */}
        {seccion==='reporte' && (
          <div className="p-4 space-y-4 max-w-6xl mx-auto">
            {!reporte ? (
              <div className="rounded-2xl p-6 text-center" style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
                <div className="text-3xl mb-2">📋</div>
                <div className="fin-t3 text-sm font-bold">No se pudo cargar el reporte</div>
                <button onClick={loadAll} className="mt-3 px-4 py-2 rounded-xl text-xs font-black fin-t1" style={{background:'var(--fw-10)'}}>Reintentar</button>
              </div>
            ) : (
              <>
                {/* Totales grandes */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="rounded-2xl p-4" style={{background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.25)'}}>
                    <div className="text-red-400 font-black text-2xl">{fmtMoney(reporte.resumen.porCobrar)}</div>
                    <div className="fin-t3 text-xs mt-0.5 font-bold">Por cobrar</div>
                    <div className="fin-t4 text-xs">{reporte.resumen.pedidosConSaldo} pedidos · {reporte.resumen.deudores} clientes</div>
                  </div>
                  <div className="rounded-2xl p-4" style={{background:'rgba(52,211,153,0.1)', border:'1px solid rgba(52,211,153,0.25)'}}>
                    <div className="text-emerald-400 font-black text-2xl">{fmtMoney(reporte.resumen.cobrado)}</div>
                    <div className="fin-t3 text-xs mt-0.5 font-bold">Cobrado</div>
                    <div className="fin-t4 text-xs">de {fmtMoney(reporte.resumen.facturado)} facturado</div>
                  </div>
                </div>

                {/* Alerta: pedidos sin precio = deuda invisible */}
                {reporte.resumen.sinPrecio > 0 && (
                  <button onClick={abrirPrecios}
                    className="w-full rounded-2xl p-3 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
                    style={{background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.3)'}}>
                    <div className="text-2xl">⚠️</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-amber-300 font-black text-sm">{reporte.resumen.sinPrecio} pedidos sin precio</div>
                      <div className="fin-t3 text-xs">No se pueden cobrar porque nadie les puso monto. Toca para ponerles precio.</div>
                    </div>
                    <div className="fin-t4">›</div>
                  </button>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {/* Antigüedad de la deuda */}
                <div className="rounded-2xl p-4" style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
                  <div className="fin-t1 font-black text-sm mb-3">📅 Antigüedad de la deuda</div>
                  <div className="space-y-2">
                    {Object.entries(BUCKET_CFG).map(([k, cfg]) => {
                      const monto = reporte.buckets[k] || 0;
                      const cant  = reporte.bucketsCount[k] || 0;
                      const max   = Math.max(...Object.values(reporte.buckets), 1);
                      return (
                        <div key={k}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="fin-t2 font-bold">{cfg.emoji} {cfg.label}</span>
                            <span className="font-black" style={{color:cfg.color}}>{fmtMoney(monto)} <span className="fin-t4 font-medium">({cant})</span></span>
                          </div>
                          <div className="h-2 rounded-full overflow-hidden" style={{background:'var(--fw-6)'}}>
                            <div className="h-full rounded-full transition-all" style={{width: (monto/max*100)+'%', background:cfg.color}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="fin-t4 text-xs mt-3 leading-relaxed">
                    Días desde el último pago (o desde que se creó el pedido si nunca ha pagado).
                  </div>
                </div>

                {/* Top deudores */}
                {reporte.porCliente.length > 0 && (
                  <div className="rounded-2xl p-4" style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
                    <div className="fin-t1 font-black text-sm mb-3">🔝 Quién debe más</div>
                    <div className="space-y-1.5">
                      {reporte.porCliente.slice(0, 8).map(c => (
                        <div key={c.cliente} className="flex items-center gap-2 text-xs">
                          <div className="flex-1 min-w-0 truncate fin-t2 font-bold">{c.cliente}</div>
                          <div className="fin-t4 shrink-0">{c.pedidos}p · {c.masViejo}d</div>
                          <div className="font-black shrink-0 w-20 text-right" style={{color: c.masViejo>30?'var(--fc-red)':'var(--fc-amber)'}}>{fmtMoney(c.saldo)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                </div>
                {/* Botón exportar */}
                <button onClick={exportarReporte}
                  className="w-full py-3 rounded-2xl font-black text-sm text-emerald-950 active:scale-95 transition-transform"
                  style={{background:'#34d399'}}>
                  ⬇ Descargar Excel ({repPedidos.length} pedidos)
                </button>

                {/* Herramienta: corregir estados de pago mal marcados */}
                <button onClick={abrirMantenimiento}
                  className="w-full py-2.5 rounded-2xl font-black text-xs fin-t2 active:scale-95 transition-transform"
                  style={{background:'var(--fw-6)', border:'1px solid var(--fw-12)'}}>
                  🔧 Revisar pedidos mal marcados como "pagado"
                </button>

                {/* Filtros */}
                <div className="flex gap-1.5 overflow-x-auto pb-1" style={{scrollbarWidth:'none'}}>
                  {[['con-saldo','Con saldo'],['vencidos','+30 días'],['sin-precio','Sin precio'],['saldados','Pagados'],['en-proceso','En proceso'],['desactivadas','Desactivadas'],['anuladas','No se cobran'],['todos','Todos']].map(([id,l])=>(
                    <button key={id} onClick={()=>setRepFiltro(id)}
                      className={"px-3 py-1.5 rounded-full text-xs font-black whitespace-nowrap shrink-0 transition-all "+(repFiltro===id?'text-emerald-950':'fin-t3')}
                      style={{background: repFiltro===id ? '#34d399' : 'var(--fw-6)'}}>{l}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="fin-t4 text-xs font-bold shrink-0">Ordenar:</span>
                  {[['antiguo','Más viejo'],['monto','Mayor monto'],['cliente','Nombre']].map(([id,l])=>(
                    <button key={id} onClick={()=>setRepOrden(id)}
                      className={"px-2.5 py-1 rounded-lg text-xs font-bold transition-all "+(repOrden===id?'fin-t1':'fin-t4')}
                      style={{background: repOrden===id ? 'var(--fw-12)' : 'transparent'}}>{l}</button>
                  ))}
                </div>

                {/* Lista */}
                {repPedidos.length === 0 ? (
                  <div className="rounded-2xl p-8 text-center" style={{background:'var(--fw-3)'}}>
                    <div className="text-3xl mb-2">🎉</div>
                    <div className="fin-t3 text-sm font-bold">Nada en este filtro</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-start">
                    {repPedidos.map(p => {
                      const ec = EC_CFG[p.estadoCobro] || EC_CFG['abierta'];
                      const bcfg = p.bucket ? BUCKET_CFG[p.bucket] : null;
                      const abierto = repExp === p.id;
                      return (
                        <div key={p.id} className="rounded-2xl overflow-hidden"
                             style={{background:'var(--fw-4)', border:'1px solid '+(p.bucket==='31+'?'rgba(248,113,113,0.3)':'var(--fw-8)')}}>
                          <button onClick={()=>setRepExp(abierto?null:p.id)} className="w-full p-3 text-left">
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                <div className="font-black fin-t1 text-sm truncate">{p.cliente || '(sin nombre)'}</div>
                                <TituloChip order={p} tono="oscuro" className="shrink min-w-0"/>
                              </div>
                              <span className="px-2 py-0.5 rounded-full text-xs font-black shrink-0" style={{background:ec.bg, color:ec.color}}>{ec.label}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
                              <span className="fin-t4">#{p.folio}</span>
                              <span className="fin-t3 font-bold">{p.plataforma}</span>
                              <span className="fin-t4">·</span>
                              <span className="fin-t4">{p.estadoPedido}</span>
                              {bcfg && <span className="font-bold" style={{color:bcfg.color}}>{bcfg.emoji} {p.diasSinPago}d</span>}
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              <div><span className="fin-t4">Total </span><span className="fin-t2 font-black">{fmtMoney(p.total)}</span></div>
                              <div><span className="fin-t4">Pagado </span><span className="text-emerald-400 font-black">{fmtMoney(p.pagado)}</span></div>
                              <div className="ml-auto"><span className="fin-t4">Debe </span><span className="font-black" style={{color: p.saldo>0.01?'var(--fc-red)':'var(--fc-green)'}}>{fmtMoney(p.saldo)}</span></div>
                            </div>
                          </button>

                          {abierto && renderDetalleCobro(p)}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="fin-t4 text-xs text-center leading-relaxed pt-2 pb-4">
                  Reporte generado {reporte.generadoEn ? new Date(reporte.generadoEn).toLocaleString('es-MX') : ''}.<br/>
                  Los montos se arman con el precio del pedido, la cartera y los recibos de pago.
                </div>
              </>
            )}
          </div>
        )}

        {/* ── RESUMEN: el tablero de la plata ─── */}
        {seccion==='inicio' && finData && (
          <div className="p-4 sm:p-6 space-y-4 max-w-6xl mx-auto">

            {/* Fila 1: cuánto te deben + cómo vas */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <button onClick={()=>setSeccion('deudas')} className="text-left rounded-3xl p-5 active:scale-[0.99] transition-transform"
                style={{background:'rgba(239,68,68,0.10)', border:'1px solid rgba(239,68,68,0.30)'}}>
                <div className="fin-t3 text-xs font-black uppercase tracking-wide">💸 Te deben</div>
                <div className="font-black text-5xl mt-1 tabular-nums" style={{color:'var(--fc-red)'}}>{fmtMoney(totalDeudas)}</div>
                <div className="fin-t2 text-sm mt-2">{totalDeudores} cliente{totalDeudores!==1?'s':''} · {deudasActivas.length} pedido{deudasActivas.length!==1?'s':''}</div>
                {vencido30 > 0 && <div className="text-xs font-black mt-1" style={{color:'var(--fc-red)'}}>🔴 {fmtMoney(vencido30)} llevan más de 30 días</div>}
                {reporte?.resumen?.sinPrecio > 0 && <div className="text-xs font-bold mt-1" style={{color:'var(--fc-amber)'}}>⚠️ {reporte.resumen.sinPrecio} pedidos sin precio (no suman)</div>}
                <div className="fin-t1 text-xs font-black mt-3">Ver quién me debe →</div>
              </button>

              <div className="lg:col-span-2 rounded-3xl p-5" style={{background:'var(--fin-card)', border:'1px solid var(--fw-8)'}}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="fin-t3 text-xs font-black uppercase tracking-wide">Ganancia neta · {labelPeriodo}</div>
                  <div className="flex gap-1 p-1 rounded-xl" style={{background:'var(--fw-6)'}}>
                    {[{id:'semana',l:'Semana'},{id:'mes',l:'Mes'}].map(t=>(
                      <button key={t.id} onClick={()=>setPeriodo(t.id)}
                        className={"px-3 py-1 rounded-lg text-xs font-black transition-all "+(periodo===t.id?"fin-t1":"fin-t3")}
                        style={periodo===t.id?{background:'var(--fin-card)', boxShadow:'0 1px 2px rgba(0,0,0,.15)'}:undefined}>{t.l}</button>
                    ))}
                  </div>
                </div>
                <div className="font-black text-5xl mt-1 tabular-nums" style={{color: gananciaPeriodo>=0?'var(--fc-green)':'var(--fc-red)'}}>{fmtMoney(gananciaPeriodo)}</div>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {[
                    {l:'Entró', v:ingresosPeriodo, c:'var(--fc-green)', go:()=>{setMovTipo('in');setMovPeriodo(periodo);setSeccion('movimientos');}},
                    {l:'Gastos', v:gastosPeriodo, c:'var(--fc-red)', go:()=>{setMovTipo('out');setMovPeriodo(periodo);setSeccion('movimientos');}},
                    {l:'Rentas atrasadas', v:rentasAtrasadas.reduce((s,c)=>s+(parseFloat(c.montoSemanal)||0),0), c:'var(--fc-amber)', go:()=>setSeccion('rentas')},
                  ].map(k=>(
                    <button key={k.l} onClick={k.go} className="text-left rounded-2xl p-3 fin-hover-bg transition-colors" style={{background:'var(--fw-6)'}}>
                      <div className="fin-t3 text-[11px] font-bold">{k.l}</div>
                      <div className="font-black text-lg tabular-nums" style={{color:k.c}}>{fmtMoney(k.v)}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Fila 2: gráficas principales */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FinCard titulo="📈 Entró vs gastos · 8 semanas"
                extra={<div className="flex items-center gap-3 text-[11px] fin-t3"><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background:'var(--fc-green2)'}}/>Entró</span><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background:'var(--fc-red2)'}}/>Gastos</span></div>}>
                <FinBarrasSemana semanas={semanas} fmt={fmtMoney}/>
              </FinCard>
              <FinCard titulo="⏳ Te deben, por antigüedad" extra={<span className="fin-t3 text-xs font-bold">{fmtMoney(totalDeudas)}</span>}>
                {reporte ? (
                  <FinBarraApilada fmt={fmtMoney}
                    partes={Object.entries(BUCKET_CFG).map(([k,c])=>({k, label:c.label, valor:reporte.buckets?.[k]||0, color:{'0-7':'var(--fc-green2)','8-15':'var(--fc-amber2)','16-30':'var(--fc-orange)','31+':'var(--fc-red2)'}[k]}))}
                    onPick={p=>{ setRepFiltro(p.k==='31+'?'vencidos':'con-saldo'); setSeccion('reporte'); }}/>
                ) : <div className="fin-t4 text-xs">Sin datos de cartera</div>}
                <div className="fin-t4 text-[11px] mt-3">Días desde el último pago. Toca un tramo para ver esos pedidos.</div>
                {reporte?.resumen?.facturado > 0 && (() => {
                  const fac = reporte.resumen.facturado, cob = reporte.resumen.cobrado, pct = Math.min(100, Math.round(cob / fac * 100));
                  return (
                    <div className="mt-4 pt-4" style={{borderTop:'1px solid var(--fw-8)'}}>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="fin-t2 font-bold">De todo lo que vendiste, ya cobraste</span>
                        <span className="font-black" style={{color:'var(--fc-green)'}}>{pct}%</span>
                      </div>
                      <div className="h-3 rounded-full overflow-hidden" style={{background:'var(--fc-track)'}}>
                        <div className="h-full rounded-full transition-all duration-500" style={{width:pct+'%', background:'var(--fc-green2)'}}/>
                      </div>
                      <div className="flex justify-between text-[11px] fin-t3 mt-1.5"><span>Cobrado {fmtMoney(cob)}</span><span>Vendido {fmtMoney(fac)}</span></div>
                    </div>
                  );
                })()}
              </FinCard>
            </div>

            {/* Fila 3: dónde está la deuda y cómo te pagan */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FinCard titulo="📱 Te deben, por plataforma">
                <FinHBarras filas={deudaPorPlataforma} fmt={fmtMoney} color="var(--fc-red)" vacio="Nadie te debe 🎉"
                  onPick={f=>{ setFinSearch(f.label); setSeccion('reporte'); setRepFiltro('con-saldo'); }}/>
              </FinCard>
              <FinCard titulo="💳 Cómo te pagaron este mes">
                <FinHBarras filas={metodosMes} fmt={fmtMoney} color="var(--fc-green)" vacio="Aún no hay pagos este mes"
                  onPick={f=>{ setMovMetodo(f.label); setMovTipo('in'); setMovPeriodo('mes'); setSeccion('movimientos'); }}/>
              </FinCard>
            </div>

            {/* Fila 4: qué hacer ahora + últimos movimientos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FinCard titulo="🎯 Cobrar ahora" extra={<span className="fin-t3 text-xs font-bold">{rentasAtrasadas.length + Math.min(5,(reporte?.porCliente||[]).length)}</span>}>
                <div className="space-y-1.5">
                  {rentasAtrasadas.slice(0,4).map(c=>(
                    <div key={'r'+c._tipo+c.id} className="flex items-center gap-2 rounded-xl px-3 py-2" style={{background:'rgba(245,158,11,0.10)'}}>
                      <span className="text-sm">🏠</span>
                      <div className="flex-1 min-w-0">
                        <div className="fin-t1 text-sm font-bold truncate">{c.cliente||'(sin nombre)'}</div>
                        <div className="text-[11px]" style={{color:'var(--fc-amber)'}}>{c._dias===null?'Nunca ha pagado':c._dias+' días sin pagar'} · {fmtMoney(c.montoSemanal)}/sem</div>
                      </div>
                      <button onClick={()=>setRentasLocal(c.id)} className="px-3 py-1.5 rounded-lg text-xs font-black text-white shrink-0" style={{background:'#10b981'}}>Cobrar</button>
                    </div>
                  ))}
                  {(reporte?.porCliente||[]).slice(0,5).map(c=>(
                    <div key={'d'+c.cliente} className="flex items-center gap-2 rounded-xl px-3 py-2" style={{background:'var(--fw-6)'}}>
                      <span className="text-sm">👤</span>
                      <div className="flex-1 min-w-0">
                        <div className="fin-t1 text-sm font-bold truncate">{c.cliente}</div>
                        <div className="fin-t3 text-[11px]">{c.pedidos} pedido{c.pedidos!==1?'s':''} · el más viejo hace {c.masViejo} días</div>
                      </div>
                      <span className="font-black text-sm tabular-nums shrink-0" style={{color:c.masViejo>30?'var(--fc-red)':'var(--fc-amber)'}}>{fmtMoney(c.saldo)}</span>
                      <button onClick={()=>{ setFinSearch(c.cliente); setSeccion('deudas'); }} className="px-3 py-1.5 rounded-lg text-xs font-black fin-t1 shrink-0" style={{background:'var(--fw-12)'}}>Ver</button>
                    </div>
                  ))}
                  {rentasAtrasadas.length===0 && !(reporte?.porCliente||[]).length && <div className="fin-t4 text-sm text-center py-6">Nada pendiente 🎉</div>}
                </div>
              </FinCard>

              <FinCard titulo="📜 Últimos movimientos" extra={<button onClick={()=>{setMovTipo('todo');setMovPeriodo('todo');setMovMetodo('');setSeccion('movimientos');}} className="text-xs font-black" style={{color:'var(--fc-blue)'}}>Ver todos →</button>}>
                <div className="space-y-1">
                  {movimientos.slice(0,8).map(m=>(
                    <div key={m.key} className="flex items-center gap-2 py-1.5" style={{borderBottom:'1px solid var(--fw-6)'}}>
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black shrink-0" style={{background:m.tipo==='in'?'rgba(16,185,129,0.15)':'rgba(239,68,68,0.15)', color:m.tipo==='in'?'var(--fc-green)':'var(--fc-red)'}}>{m.tipo==='in'?'↑':'↓'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="fin-t1 text-sm font-bold truncate">{m.quien}</div>
                        <div className="fin-t4 text-[11px] truncate">{m.fuente}{m.metodo?' · '+m.metodo:''} · {fmtDate(m.fecha)}</div>
                      </div>
                      <span className="font-black text-sm tabular-nums shrink-0" style={{color:m.tipo==='in'?'var(--fc-green)':'var(--fc-red)'}}>{m.tipo==='in'?'+':'−'}{fmtMoney(m.monto)}</span>
                    </div>
                  ))}
                  {movimientos.length===0 && <div className="fin-t4 text-sm text-center py-6">Aún no hay movimientos</div>}
                </div>
              </FinCard>
            </div>

            {/* Acciones */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button onClick={()=>setSeccion('extras')} className="py-3.5 rounded-2xl font-black text-sm active:scale-95 transition-all"
                style={{background:'rgba(16,185,129,0.15)', color:'var(--fc-green)', border:'1px solid rgba(16,185,129,0.3)'}}>➕ Registrar ingreso</button>
              <button onClick={()=>setSeccion('gastos')} className="py-3.5 rounded-2xl font-black text-sm active:scale-95 transition-all"
                style={{background:'rgba(239,68,68,0.12)', color:'var(--fc-red)', border:'1px solid rgba(239,68,68,0.3)'}}>➖ Registrar gasto</button>
              <button onClick={descargarReporteCompleto} className="py-3.5 rounded-2xl font-black text-sm text-white active:scale-95 transition-all"
                style={{background:'#059669'}}>📊 Reporte completo (Excel)</button>
            </div>
          </div>
        )}

        {/* ── MOVIMIENTOS: la trazabilidad de la plata ─── */}
        {seccion==='movimientos' && (() => {
          const entro = movFiltrados.filter(m=>m.tipo==='in').reduce((s,m)=>s+m.monto,0);
          const salio = movFiltrados.filter(m=>m.tipo==='out').reduce((s,m)=>s+m.monto,0);
          const dias = [];
          movFiltrados.forEach(m=>{ const d=dias[dias.length-1]; if(d && d.fecha===m.fecha) d.items.push(m); else dias.push({fecha:m.fecha, items:[m]}); });
          const chip = (activo) => "px-3 py-1.5 rounded-lg text-xs font-black transition-all "+(activo?"fin-t1":"fin-t3");
          const chipSt = (activo) => activo?{background:'var(--fin-card)', boxShadow:'0 1px 2px rgba(0,0,0,.15)'}:undefined;
          return (
            <div className="p-4 sm:p-6 space-y-4 max-w-6xl mx-auto">
              <div className="grid grid-cols-3 gap-3">
                {[{l:'Entró',v:entro,c:'var(--fc-green)'},{l:'Salió',v:salio,c:'var(--fc-red)'},{l:'Neto',v:entro-salio,c:(entro-salio)>=0?'var(--fc-green)':'var(--fc-red)'}].map(k=>(
                  <div key={k.l} className="rounded-2xl p-4" style={{background:'var(--fin-card)', border:'1px solid var(--fw-8)'}}>
                    <div className="fin-t3 text-xs font-bold">{k.l}</div>
                    <div className="font-black text-2xl tabular-nums" style={{color:k.c}}>{fmtMoney(k.v)}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-1 p-1 rounded-xl" style={{background:'var(--fw-6)'}}>
                  {[['todo','Todo'],['in','↑ Entradas'],['out','↓ Salidas']].map(([k,l])=>(
                    <button key={k} onClick={()=>setMovTipo(k)} className={chip(movTipo===k)} style={chipSt(movTipo===k)}>{l}</button>
                  ))}
                </div>
                <div className="flex gap-1 p-1 rounded-xl" style={{background:'var(--fw-6)'}}>
                  {[['semana','Semana'],['mes','Mes'],['30','30 días'],['todo','Todo']].map(([k,l])=>(
                    <button key={k} onClick={()=>setMovPeriodo(k)} className={chip(movPeriodo===k)} style={chipSt(movPeriodo===k)}>{l}</button>
                  ))}
                </div>
                <select value={movMetodo} onChange={e=>setMovMetodo(e.target.value)} className="rounded-xl px-3 py-2 text-xs font-bold outline-none"
                  style={{background:'var(--fw-6)', border:'1px solid var(--fw-10)'}}>
                  <option value="">Todos los métodos</option>{movMetodos.map(m=><option key={m}>{m}</option>)}
                </select>
                <button onClick={()=>{
                  const rows=[['Fecha','Tipo','Concepto','Quién / qué','Plataforma','Método','Monto','Notas']];
                  movFiltrados.forEach(m=>rows.push([m.fecha, m.tipo==='in'?'Entrada':'Salida', m.fuente, m.quien, m.plataforma, m.metodo, (m.tipo==='in'?'':'-')+m.monto.toFixed(2), m.detalle]));
                  descargarCSV('movimientos', rows);
                }} className="ml-auto px-3 py-2 rounded-xl text-xs font-black" style={{background:'rgba(16,185,129,0.15)', color:'var(--fc-green)'}}>📊 Excel</button>
              </div>

              {dias.length===0 ? (
                <div className="fin-t4 text-sm text-center py-16">No hay movimientos con estos filtros.</div>
              ) : (
                <div className="space-y-4">
                  {dias.map(d=>{
                    const neto = d.items.reduce((s,m)=>s+(m.tipo==='in'?m.monto:-m.monto),0);
                    return (
                      <div key={d.fecha||'sin'}>
                        <div className="flex items-center justify-between px-1 mb-1.5">
                          <span className="fin-t3 text-xs font-black uppercase tracking-wide">{d.fecha ? new Date(d.fecha+'T12:00').toLocaleDateString('es-MX',{weekday:'long', day:'numeric', month:'long'}) : 'Sin fecha'}</span>
                          <span className="text-xs font-black tabular-nums" style={{color:neto>=0?'var(--fc-green)':'var(--fc-red)'}}>{neto>=0?'+':'−'}{fmtMoney(Math.abs(neto))}</span>
                        </div>
                        <div className="rounded-2xl overflow-hidden" style={{background:'var(--fin-card)', border:'1px solid var(--fw-8)'}}>
                          {d.items.map((m,i)=>{
                            const ped = m.pedidoId ? pedidoReal(m.pedidoId) : null;
                            return (
                              <div key={m.key} className="flex items-center gap-3 px-4 py-3" style={i?{borderTop:'1px solid var(--fw-6)'}:undefined}>
                                <span className="w-8 h-8 rounded-xl flex items-center justify-center text-sm font-black shrink-0" style={{background:m.tipo==='in'?'rgba(16,185,129,0.15)':'rgba(239,68,68,0.15)', color:m.tipo==='in'?'var(--fc-green)':'var(--fc-red)'}}>{m.tipo==='in'?'↑':'↓'}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="fin-t1 text-sm font-bold truncate">{m.quien}</div>
                                  <div className="fin-t4 text-xs truncate">{m.fuente}{m.plataforma?' · '+m.plataforma:''}{m.metodo?' · '+m.metodo:''}{m.detalle&&m.tipo==='in'?' · '+m.detalle:''}</div>
                                </div>
                                {m.pedidoId && (
                                  <button onClick={()=>setRentasLocal(m.pedidoId)} title="Ver todos los pagos de este pedido" className="px-2.5 py-1.5 rounded-lg text-[11px] font-black fin-t2 shrink-0" style={{background:'var(--fw-10)'}}>🧾 Pagos</button>
                                )}
                                {ped && onSavePedido && (
                                  <button onClick={()=>abrirPedido(m.pedidoId)} title="Abrir el pedido" className="px-2.5 py-1.5 rounded-lg text-[11px] font-black fin-t2 shrink-0" style={{background:'var(--fw-10)'}}>📦</button>
                                )}
                                {!m.pedidoId && m.tipo==='in' && m.fuente==='Pago de pedido' && (
                                  <button onClick={()=>{ setFinSearch(m.quien); setSeccion('reporte'); setRepFiltro('todos'); }} title="Buscar sus pedidos" className="px-2.5 py-1.5 rounded-lg text-[11px] font-black fin-t2 shrink-0" style={{background:'var(--fw-10)'}}>🔍</button>
                                )}
                                <span className="font-black tabular-nums shrink-0 w-20 text-right" style={{color:m.tipo==='in'?'var(--fc-green)':'var(--fc-red)'}}>{m.tipo==='in'?'+':'−'}{fmtMoney(m.monto)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="fin-t4 text-[11px] text-center">{movFiltrados.length} movimientos. Cada pago de pedido te deja ver su historial (🧾) o abrir el pedido (📦).</div>
            </div>
          );
        })()}

        {/* ── GASTOS ─── */}
        {seccion==='gastos' && (
          <div className="p-4 space-y-4 max-w-6xl mx-auto">
            {/* Totales de gastos */}
            <div className="grid grid-cols-3 gap-3 pt-2">
              {[
                { label:'Esta semana', val: fmtMoney(finData?.gastosSemana||0) },
                { label:'Este mes',    val: fmtMoney(finData?.gastosMes||0) },
                { label:'Total',       val: fmtMoney(finData?.gastosTotal||0) },
              ].map(k => (
                <div key={k.label} className="rounded-2xl p-4 text-center" style={{background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)'}}>
                  <div className="font-black text-lg text-red-400">{k.val}</div>
                  <div className="fin-t3 text-xs mt-0.5">{k.label}</div>
                </div>
              ))}
            </div>

            {/* Formulario nuevo gasto */}
            <div className="rounded-2xl p-4 space-y-3" style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
              <div className="fin-t1 font-black text-sm">➖ Registrar gasto</div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" inputMode="decimal" placeholder="Monto $" value={gastoForm.monto}
                  onChange={e=>setGastoForm(f=>({...f,monto:e.target.value}))}
                  className="px-3 py-2.5 rounded-xl fin-bgw5 border fin-border fin-t1 placeholder-white/30 text-sm focus:outline-none focus:border-emerald-400"/>
                <select value={gastoForm.categoria} onChange={e=>setGastoForm(f=>({...f,categoria:e.target.value}))}
                  className="px-3 py-2.5 rounded-xl fin-bgw5 border fin-border fin-t1 text-sm focus:outline-none focus:border-emerald-400">
                  {CATEGORIAS_GASTO.map(c=><option key={c} value={c} className="bg-slate-800">{c}</option>)}
                </select>
              </div>
              <input type="text" placeholder="¿En qué fue? (ej: iPhone para cuenta de Juan)" value={gastoForm.descripcion}
                onChange={e=>setGastoForm(f=>({...f,descripcion:e.target.value}))}
                className="w-full px-3 py-2.5 rounded-xl fin-bgw5 border fin-border fin-t1 placeholder-white/30 text-sm focus:outline-none focus:border-emerald-400"/>
              <div className="grid grid-cols-2 gap-2">
                <select value={gastoForm.metodo} onChange={e=>setGastoForm(f=>({...f,metodo:e.target.value}))}
                  className="px-3 py-2.5 rounded-xl fin-bgw5 border fin-border fin-t1 text-sm focus:outline-none focus:border-emerald-400">
                  {['Zelle','Cash','Tarjeta','Transferencia','Otro'].map(m=><option key={m} value={m} className="bg-slate-800">{m}</option>)}
                </select>
                <input type="date" value={gastoForm.fecha} onChange={e=>setGastoForm(f=>({...f,fecha:e.target.value}))}
                  className="px-3 py-2.5 rounded-xl fin-bgw5 border fin-border fin-t1 text-sm focus:outline-none focus:border-emerald-400"/>
              </div>
              <button onClick={submitGasto} disabled={gastoSaving||!gastoForm.monto||!gastoForm.descripcion.trim()}
                className="w-full py-3 rounded-xl font-black text-sm transition-all disabled:opacity-40"
                style={{background:'#ef4444', color:'var(--fin-t1)'}}>
                {gastoSaving?'Guardando...':'Guardar gasto'}
              </button>
            </div>

            {/* Lista de gastos */}
            <div className="rounded-2xl overflow-hidden" style={{border:'1px solid var(--fw-8)'}}>
              <div className="px-4 py-3 flex items-center justify-between" style={{background:'var(--fw-4)'}}>
                <div className="fin-t1 font-black text-sm">Gastos registrados</div>
                {gastos.length>0 && (
                  <button onClick={()=>{
                    const rows = [['Fecha','Categoría','Descripción','Método','Monto']];
                    gastos.forEach(g=>rows.push([g.fecha?new Date(g.fecha+'T12:00').toLocaleDateString('es-MX'):'', g.categoria||'', g.descripcion||'', g.metodo||'', parseFloat(g.monto||0).toFixed(2)]));
                    descargarCSV('gastos', rows);
                  }} className="px-2.5 py-1.5 rounded-lg text-xs font-black text-emerald-300 active:scale-95 transition-all" style={{background:'rgba(16,185,129,0.15)', border:'1px solid rgba(16,185,129,0.3)'}}>📊 Excel</button>
                )}
              </div>
              {gastos.length===0 ? (
                <div className="px-4 py-6 text-center fin-t4 text-sm">Aún no hay gastos registrados</div>
              ) : gastos.map(g => (
                <div key={g.id} className="px-4 py-3 flex items-center justify-between border-t fin-border">
                  <div className="min-w-0">
                    <div className="fin-t1 font-bold text-sm truncate">{g.descripcion}</div>
                    <div className="fin-t3 text-xs">{g.categoria} · {g.metodo} · {fmtDate(g.fecha)}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-red-400 font-black">−{fmtMoney(g.monto)}</div>
                    <button onClick={()=>deleteGasto(g.id)} className="fin-t4 hover:text-red-400 text-sm">🗑</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ESTADO DE CUENTA POR CLIENTE ─── */}
        {seccion==='clientes' && (()=>{
          // Personas asignadas únicas extraídas de los pedidos
          const personasAsignadas = [...new Set(
            orders.map(o => o.asignadoNombre).filter(Boolean)
          )].sort();

          // Pedidos filtrados por persona asignada seleccionada
          const pedidosFiltrados = clientesAsignado
            ? orders.filter(o => o.asignadoNombre === clientesAsignado)
            : orders;

          // Agrupar por nombre del cliente (del pedido), respetando filtro de asignado
          // Construimos el resumen desde los pedidos directamente para poder filtrar por asignado
          const resumenPorCliente = (() => {
            const map = {};
            pedidosFiltrados.forEach(o => {
              const key = o.cliente || '(sin nombre)';
              if (!map[key]) map[key] = { cliente: key, asignadoNombre: o.asignadoNombre || '', totalPedidos: 0, totalPagado: 0, totalFacturado: 0, ultimoPago: null, plataformas: new Set() };
              map[key].totalPedidos++;
              map[key].totalPagado += parseFloat(o.totalPagado || 0);
              map[key].totalFacturado += parseFloat(o.precio || o.totalCredito || 0);
              map[key].plataformas.add(o.plataforma);
              if (o.ultimoPago && (!map[key].ultimoPago || o.ultimoPago > map[key].ultimoPago)) map[key].ultimoPago = o.ultimoPago;
            });
            return Object.values(map).sort((a,b) => b.totalPagado - a.totalPagado);
          })();

          // Si hay filtro de asignado, usar resumen construido desde pedidos; si no, usar el de la API
          const listaBase = clientesAsignado ? resumenPorCliente : (porCliente.length > 0 ? porCliente : resumenPorCliente);

          // Filtro por texto
          const listaFinal = clientesBusqueda.trim()
            ? listaBase.filter(c => c.cliente.toLowerCase().includes(clientesBusqueda.toLowerCase()))
            : listaBase;

          // Totales del grupo seleccionado
          const totalGrupo = listaFinal.reduce((s,c) => s + parseFloat(c.totalPagado||0), 0);
          const pedidosGrupo = listaFinal.reduce((s,c) => s + (c.totalPedidos||0), 0);

          return (
            <div className="p-4 space-y-3 max-w-6xl mx-auto pt-4">

              {/* Buscador */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 fin-t4 text-sm">🔍</span>
                <input
                  className="w-full rounded-xl pl-9 pr-4 py-2.5 text-sm font-medium fin-t1 outline-none"
                  style={{background:'var(--fw-7)', border:'1px solid var(--fw-10)'}}
                  placeholder="Buscar por nombre de cliente..."
                  value={clientesBusqueda}
                  onChange={e => setClientesBusqueda(e.target.value)}
                />
                {clientesBusqueda && (
                  <button onClick={()=>setClientesBusqueda('')} className="absolute right-3 top-1/2 -translate-y-1/2 fin-t4 fin-hover-t1 text-sm">✕</button>
                )}
              </div>

              {/* Filtro por persona asignada */}
              {personasAsignadas.length > 0 && (
                <div className="space-y-1.5">
                  <div className="fin-t4 text-xs uppercase tracking-widest font-bold px-1">Filtrar por persona</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={()=>setClientesAsignado('')}
                      className="px-3 py-1.5 rounded-full text-xs font-black transition-all active:scale-95"
                      style={clientesAsignado==='' ? {background:'#10b981', color:'var(--fin-t1)'} : {background:'var(--fw-8)', color:'var(--fw-50)', border:'1px solid var(--fw-10)'}}>
                      👥 Todos
                    </button>
                    {personasAsignadas.map(p => (
                      <button key={p}
                        onClick={()=>setClientesAsignado(clientesAsignado===p ? '' : p)}
                        className="px-3 py-1.5 rounded-full text-xs font-black transition-all active:scale-95"
                        style={clientesAsignado===p ? {background:'#6366f1', color:'var(--fin-t1)'} : {background:'var(--fw-8)', color:'var(--fw-50)', border:'1px solid var(--fw-10)'}}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Resumen del grupo */}
              {(clientesAsignado || clientesBusqueda) && listaFinal.length > 0 && (
                <div className="rounded-2xl p-4 flex items-center justify-between"
                  style={{background:'rgba(99,102,241,0.1)', border:'1px solid rgba(99,102,241,0.25)'}}>
                  <div>
                    <div className="text-indigo-300 font-black text-sm">
                      {clientesAsignado ? `📊 Cuenta de ${clientesAsignado}` : `🔍 Resultados`}
                    </div>
                    <div className="fin-t3 text-xs mt-0.5">{pedidosGrupo} pedido{pedidosGrupo!==1?'s':''} · {listaFinal.length} cliente{listaFinal.length!==1?'s':''}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-emerald-400 font-black text-xl">{fmtMoney(totalGrupo)}</div>
                    <div className="fin-t4 text-xs">total cobrado</div>
                  </div>
                </div>
              )}

              {/* Lista de clientes */}
              {listaFinal.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="text-5xl mb-4">🔎</div>
                  <div className="fin-t1 font-black text-lg">Sin resultados</div>
                  <div className="fin-t3 text-sm mt-1">Prueba con otro nombre o filtro</div>
                </div>
              ) : (
                <>
                  <button onClick={()=>{
                    const rows = [['Cliente','Asignado a','Pedidos','Facturado','Pagado','Pendiente','Plataformas','Último pago']];
                    listaFinal.forEach(c=>{
                      const fac = parseFloat(c.totalFacturado||0), pag = parseFloat(c.totalPagado||0);
                      const plats = c.plataformas ? [...c.plataformas].join(' / ') : '';
                      rows.push([c.cliente||'', c.asignadoNombre||'', c.totalPedidos||0, fac.toFixed(2), pag.toFixed(2), Math.max(0,fac-pag).toFixed(2), plats, c.ultimoPago?new Date(c.ultimoPago).toLocaleDateString('es-MX'):'']);
                    });
                    descargarCSV('clientes'+(clientesAsignado?'_'+clientesAsignado:''), rows);
                  }} className="w-full py-2.5 rounded-xl text-xs font-black fin-t1 active:scale-95 transition-all mb-1" style={{background:'rgba(16,185,129,0.15)', border:'1px solid rgba(16,185,129,0.3)'}}>
                    📊 Descargar reporte de clientes (Excel)
                  </button>
                  <div className="fin-t4 text-xs uppercase tracking-widest font-bold px-1">
                    {listaFinal.length} cliente{listaFinal.length!==1?'s':''}
                    {clientesAsignado ? ` vendido${listaFinal.length!==1?'s':''} a ${clientesAsignado}` : ' con actividad'}
                  </div>
                  {listaFinal.map(c => {
                    const totalPagado    = parseFloat(c.totalPagado || 0);
                    const totalFacturado = parseFloat(c.totalFacturado || 0);
                    const pct = totalFacturado > 0 ? Math.min(100, Math.round(totalPagado / totalFacturado * 100)) : 100;
                    const esReasignando  = reasignandoCliente === c.cliente;
                    return (
                      <div key={c.cliente} className="rounded-2xl p-4 space-y-3"
                        style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="fin-t1 font-black text-base truncate">{c.cliente}</div>
                            <div className="flex items-center gap-2 flex-wrap mt-0.5">
                              <span className="fin-t3 text-xs">{c.totalPedidos} pedido{c.totalPedidos!==1?'s':''}</span>
                              {c.asignadoNombre ? (
                                <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                                  style={{background:'rgba(99,102,241,0.2)', color:'var(--fc-indigo)'}}>
                                  👤 {c.asignadoNombre}
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                                  style={{background:'var(--fw-6)', color:'var(--fw-30)'}}>
                                  Sin asignar
                                </span>
                              )}
                              {c.ultimoPago && (
                                <span className="fin-t4 text-xs">Último pago: {new Date(c.ultimoPago+'T12:00').toLocaleDateString('es-MX',{day:'numeric',month:'short'})}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-start gap-2 shrink-0">
                            <div className="text-right">
                              <div className="text-emerald-400 font-black text-lg">{fmtMoney(totalPagado)}</div>
                              <div className="fin-t4 text-xs">pagado</div>
                            </div>
                            {onReasignar && (
                              <button
                                onClick={()=>setReasignandoCliente(esReasignando ? null : c.cliente)}
                                className="w-7 h-7 rounded-lg flex items-center justify-center text-sm transition-all active:scale-95 mt-0.5"
                                style={esReasignando ? {background:'rgba(99,102,241,0.3)', color:'var(--fc-indigo)'} : {background:'var(--fw-7)', color:'var(--fw-30)'}}
                                title="Cambiar asignado">
                                ✏️
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Selector de reasignación inline */}
                        {esReasignando && (
                          <div className="rounded-xl p-3 space-y-2" style={{background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.2)'}}>
                            <div className="text-xs font-black text-indigo-300 uppercase tracking-wide">Asignar {c.totalPedidos} pedido{c.totalPedidos!==1?'s':''} a...</div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={()=>reasignarPedidosCliente(c.cliente, '', '')}
                                disabled={reasignandoSaving}
                                className="px-3 py-1.5 rounded-full text-xs font-black transition-all active:scale-95 disabled:opacity-40"
                                style={{background:'var(--fw-8)', color:'var(--fw-50)', border:'1px solid var(--fw-10)'}}>
                                ❌ Sin asignar
                              </button>
                              {usuarios.map(u => (
                                <button key={u.id}
                                  onClick={()=>reasignarPedidosCliente(c.cliente, u.id, u.nombre)}
                                  disabled={reasignandoSaving}
                                  className="px-3 py-1.5 rounded-full text-xs font-black transition-all active:scale-95 disabled:opacity-40"
                                  style={c.asignadoNombre===u.nombre ? {background:'#6366f1', color:'var(--fin-t1)'} : {background:'rgba(99,102,241,0.15)', color:'var(--fc-indigo)', border:'1px solid rgba(99,102,241,0.3)'}}>
                                  {reasignandoSaving ? 'Guardando...' : '👤 '+u.nombre}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {totalFacturado > 0 && (
                          <div>
                            <div className="flex justify-between text-xs mb-1.5">
                              <span className="fin-t3">Pagado: <strong className="fin-t2">{fmtMoney(totalPagado)}</strong></span>
                              <span className="fin-t3">Facturado: <strong className="fin-t2">{fmtMoney(totalFacturado)}</strong></span>
                            </div>
                            <div className="h-1.5 rounded-full overflow-hidden" style={{background:'var(--fw-8)'}}>
                              <div className="h-full rounded-full transition-all" style={{width:pct+'%', background: pct===100?'#10b981':'#3b82f6'}}/>
                            </div>
                          </div>
                        )}

                        {/* ── VER Y ABRIR LOS PEDIDOS DE ESTE CLIENTE ──────────────
                            Antes desde Clientes solo veías el resumen: no había forma
                            de entrar al pedido. Ahora se despliegan TODOS los pedidos
                            del cliente (de cualquier plataforma/fuente) y cada uno abre
                            el mismo editor completo que el hub, con estado, precio,
                            datos, etc. Así el pedido es "integral" también aquí. */}
                        {(() => {
                          const pedidosCliente = orders
                            .filter(o => (o.cliente || '(sin nombre)') === c.cliente)
                            .filter(o => !clientesAsignado || o.asignadoNombre === clientesAsignado)
                            .sort((a,b) => new Date(b.createdAt||b.entrega||0) - new Date(a.createdAt||a.entrega||0));
                          if (pedidosCliente.length === 0) return null;
                          const abierto = clienteExp === c.cliente;
                          const cliId = pedidosCliente.find(o => o.clienteId)?.clienteId;
                          return (
                            <div className="pt-1 space-y-2">
                              {cliId && (
                                <button onClick={()=>setFichaFinId(cliId)}
                                  className="w-full py-2.5 rounded-xl text-xs font-black fin-t1 active:scale-95 transition-all"
                                  style={{background:'rgba(14,165,233,0.2)', border:'1px solid rgba(14,165,233,0.4)'}}>
                                  👤 Ficha del cliente · todas sus fuentes, deuda y pagos
                                </button>
                              )}
                              <button onClick={()=>setClienteExp(abierto ? null : c.cliente)}
                                className="w-full py-2.5 rounded-xl text-xs font-black fin-t1 active:scale-95 transition-all flex items-center justify-center gap-2"
                                style={{background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)'}}>
                                📦 {abierto ? 'Ocultar' : 'Ver y abrir'} pedidos ({pedidosCliente.length}) {abierto ? '▲' : '▼'}
                              </button>
                              {abierto && (
                                <div className="mt-2 space-y-3">
                                  {pedidosCliente.map(o => {
                                    const sc = SC[o.estado] || {};
                                    return (
                                      <div key={o.id} className="rounded-xl p-2.5 space-y-2"
                                        style={{background:'var(--fw-3)', border:'1px solid var(--fw-7)'}}>
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="min-w-0">
                                            <div className="fin-t2 text-xs font-bold truncate">{o.plataforma || 'Sin plataforma'}</div>
                                            <div className="fin-t4 text-[11px]">
                                              {o.precio ? fmtMoney(o.precio) : 'sin precio'}
                                              {o.createdAt ? ' · '+new Date(o.createdAt).toLocaleDateString('es-MX',{day:'numeric',month:'short',year:'2-digit'}) : ''}
                                            </div>
                                          </div>
                                          <span className={"px-2 py-0.5 rounded-full text-[10px] font-black shrink-0 "+(sc.bg||'bg-slate-200')+" "+(sc.text||'text-slate-700')}>{sc.emoji} {o.estado}</span>
                                        </div>
                                        {renderAccionesPedido(o.id)}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })()}

        {/* ── DEUDAS UNIFICADAS (Cartera + Créditos) ─── */}
        {seccion==='deudas' && (
          <div className="p-4 space-y-4 max-w-6xl mx-auto">
            {(() => {
              // ── CARTERA reorganizada ──────────────────────────────────
              // Antes esta pantalla leía la tabla `cartera` cruda, donde el precio
              // quedaba CONGELADO en lo que alguien escribió a mano → se veían
              // montos viejos ("se quedan pegados") y no se podía tocar el precio
              // real del pedido. Ahora sale de la MISMA fuente reconciliada que la
              // pantalla Reporte (/api/reporte/cartera), agrupada por cliente. El
              // precio siempre es el real, se edita igual que en Reporte y cada
              // cambio refresca todo (loadAll), así que nada se desincroniza.
              const norm = s => (s || '').trim();
              const activos = (reporte?.pedidos || []).filter(p => !p.esRentaDirecta);
              // Deuda de verdad: con saldo, con precio y sin cobro cerrado.
              // En proceso y DESACTIVADAS no son deudores (se quedan en su pedido).
              const conSaldoTodos = activos.filter(p => !p.cobroCerrado && !p.fueraCartera && p.saldo > 0.01 && p.total > 0);
              // Lo que se muestra respeta el buscador de arriba.
              const conSaldo = conSaldoTodos.filter(p => coincide(p.cliente, p.plataforma, superOf(norm(p.cliente)), p.mail, p.folio));

              // Totales, de la misma fuente (no de la tabla cruda).
              const totalDeudaVis   = Math.round(conSaldoTodos.reduce((s,p)=>s+p.saldo,0)*100)/100;
              const totalCobradoVis = Math.round(activos.filter(p=>p.total>0).reduce((s,p)=>s+p.pagado,0)*100)/100;

              // Agrupar por super cliente (lo único que Cartera hace y Reporte no).
              const map = {};
              conSaldo.forEach(p => {
                const real = norm(p.cliente) || '(sin nombre)';
                const k = superOf(real);
                const g = map[k] || (map[k] = { nombre:k, items:[], miembros:new Set(), esSuper: !!superClientes[real], total:0, diasViejo:0 });
                g.items.push(p);
                g.miembros.add(real);
                if (superClientes[real]) g.esSuper = true;
                g.total += p.saldo;
                g.diasViejo = Math.max(g.diasViejo, p.diasSinPago ?? 0);
              });
              let grupos = Object.values(map).map(g => ({ ...g, total: Math.round(g.total*100)/100 }));
              grupos.sort((a,b) => ordenDeudas === 'monto' ? (b.total - a.total) : (b.diasViejo - a.diasViejo));

              // Histórico de pagados, misma fuente.
              const saldados = activos
                .filter(p => p.estadoCobro === 'saldada')
                .filter(p => coincide(p.cliente, p.plataforma, superOf(norm(p.cliente)), p.mail, p.folio))
                .sort((a,b) => (b.diasSinPago ?? 0) - (a.diasSinPago ?? 0));

              return (
                <>
                  {/* Totales */}
                  <div className="grid grid-cols-3 gap-3 pt-2">
                    {[
                      { label:'Total deuda', val: fmtMoney(totalDeudaVis),   color:'var(--fc-red2)' },
                      { label:'Cobrado',     val: fmtMoney(totalCobradoVis), color:'var(--fc-green2)' },
                      { label:'Deudores',    val: grupos.length,             color:'var(--fc-amber2)' },
                    ].map(k => (
                      <div key={k.label} className="rounded-2xl p-3 text-center" style={{background:'var(--fw-5)', border:'1px solid var(--fw-8)'}}>
                        <div className="font-black text-xl" style={{color:k.color}}>{k.val}</div>
                        <div className="fin-t3 text-xs mt-0.5">{k.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Si el reporte no cargó, avisamos en vez de mostrar $0 en silencio */}
                  {!reporte && (
                    <div className="rounded-2xl p-4 text-center fin-t3 text-sm font-bold" style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
                      No se pudo cargar la cartera reconciliada. Toca ↻ arriba para reintentar.
                    </div>
                  )}

                  {/* Exportar (misma info que ves, desde la fuente real) */}
                  {conSaldoTodos.length > 0 && (
                    <button onClick={()=>{
                      const rows = [['Deudor (grupo)','Nombre en el pedido','Plataforma','Tipo de cobro','Correo','Datos','Titular','Total','Pagado','Falta','Días sin pagar','Notas']];
                      [...conSaldoTodos].sort((a,b)=>(b.diasSinPago??0)-(a.diasSinPago??0)).forEach(p=>{
                        rows.push([superOf(norm(p.cliente)), p.cliente||'', p.plataforma||'', p.tipoCobro||'', p.mail||'', p.dato||'', p.contacto||'', p.total.toFixed(2), p.pagado.toFixed(2), p.saldo.toFixed(2), p.diasSinPago??'', (p.notas||'').replace(/\s+/g,' ').slice(0,200)]);
                      });
                      descargarCSV('cartera_deudores', rows);
                    }} className="w-full py-2.5 rounded-xl text-xs font-black fin-t1 active:scale-95 transition-all flex items-center justify-center gap-2" style={{background:'rgba(16,185,129,0.15)', border:'1px solid rgba(16,185,129,0.3)'}}>
                      📊 Descargar cartera ({conSaldoTodos.length}) en Excel
                    </button>
                  )}

                  {/* Sin deudas */}
                  {reporte && conSaldoTodos.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="text-5xl mb-4">🎉</div>
                      <div className="fin-t1 font-black text-xl">¡Sin deudas pendientes!</div>
                      <div className="fin-t3 text-sm mt-2">Todos los clientes están al corriente</div>
                    </div>
                  )}

                  {/* Sin resultados de búsqueda */}
                  {reporte && conSaldoTodos.length > 0 && grupos.length === 0 && (
                    <div className="text-center fin-t3 text-sm py-10">Sin deudores que coincidan con la búsqueda.</div>
                  )}

                  {/* ── DEUDORES agrupados ── */}
                  {grupos.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between px-1 mb-2">
                        <div className="fin-t3 text-xs uppercase tracking-widest font-bold">👥 Deudores ({grupos.length})</div>
                        <div className="flex items-center gap-2">
                          <button onClick={()=>setShowSuperModal(true)} className="px-2.5 py-1 rounded-lg text-xs font-bold text-amber-300 active:scale-95 transition-all" style={{background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.3)'}}>👑 Super clientes</button>
                          <div className="flex rounded-lg overflow-hidden" style={{border:'1px solid var(--fw-10)'}}>
                            <button onClick={()=>setOrdenDeudas('antiguo')} className={"px-2.5 py-1 text-xs font-bold transition-all "+(ordenDeudas==='antiguo'?'fin-t1':'fin-t3')} style={{background: ordenDeudas==='antiguo'?'rgba(239,68,68,0.25)':'transparent'}}>⏱ Más viejo</button>
                            <button onClick={()=>setOrdenDeudas('monto')} className={"px-2.5 py-1 text-xs font-bold transition-all "+(ordenDeudas==='monto'?'fin-t1':'fin-t3')} style={{background: ordenDeudas==='monto'?'rgba(239,68,68,0.25)':'transparent'}}>💵 Más deuda</button>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
                      {grupos.map(g => {
                        const abierto = expDeudor === g.nombre;
                        return (
                          <div key={g.nombre} className="rounded-2xl mb-3 overflow-hidden" style={{background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.25)'}}>
                            <button onClick={()=>setExpDeudor(abierto ? null : g.nombre)} className="w-full flex items-center justify-between gap-3 p-4 text-left active:opacity-80 transition-opacity">
                              <div className="min-w-0">
                                <div className="fin-t1 font-black text-base truncate">{(g.esSuper||g.miembros.size>1)&&'👑 '}{g.nombre}</div>
                                <div className="fin-t3 text-xs mt-0.5">
                                  {g.items.length} {g.items.length===1?'pedido':'pedidos'}
                                  {g.diasViejo > 0 && <span className={g.diasViejo>=14?'text-red-400 font-bold':''}> · hace {g.diasViejo} {g.diasViejo===1?'día':'días'}</span>}
                                </div>
                                {g.miembros.size>1 && <div className="text-amber-300/70 text-[11px] mt-0.5 truncate">incluye: {[...g.miembros].join(', ')}</div>}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <div className="text-right">
                                  <div className="text-red-400 font-black text-xl leading-none">{fmtMoney(g.total)}</div>
                                  <div className="fin-t4 text-[10px] uppercase tracking-wide mt-0.5">te debe</div>
                                </div>
                                <span className="fin-t4 text-sm">{abierto?'▲':'▼'}</span>
                              </div>
                            </button>

                            {abierto && (
                              <div className="px-3 pb-3" style={{borderTop:'1px solid var(--fw-8)'}}>
                                {[...g.items].sort((a,b)=>(b.diasSinPago??0)-(a.diasSinPago??0)).map(p => {
                                  const ec = EC_CFG[p.estadoCobro] || EC_CFG['abierta'];
                                  const bcfg = p.bucket ? BUCKET_CFG[p.bucket] : null;
                                  const abiertoP = carteraExp === p.id;
                                  return (
                                    <div key={p.id} className="rounded-2xl overflow-hidden mt-2.5"
                                         style={{background:'rgba(0,0,0,0.18)', border:'1px solid '+(p.bucket==='31+'?'rgba(248,113,113,0.3)':'var(--fw-8)')}}>
                                      <button onClick={()=>setCarteraExp(abiertoP?null:p.id)} className="w-full p-3 text-left">
                                        <div className="flex items-center gap-2 mb-1.5">
                                          <div className="fin-t2 text-sm font-bold truncate flex-1">{(PC[p.plataforma]||PC['OTROS']).emoji} {p.plataforma||'—'}</div>
                                          <span className="px-2 py-0.5 rounded-full text-xs font-black shrink-0" style={{background:ec.bg, color:ec.color}}>{ec.label}</span>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
                                          <span className="fin-t4">#{p.folio}</span>
                                          <span className="fin-t4">{p.estadoPedido}</span>
                                          {bcfg && <span className="font-bold" style={{color:bcfg.color}}>{bcfg.emoji} {p.diasSinPago}d</span>}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs">
                                          <div><span className="fin-t4">Total </span><span className="fin-t2 font-black">{fmtMoney(p.total)}</span></div>
                                          <div><span className="fin-t4">Pagado </span><span className="text-emerald-400 font-black">{fmtMoney(p.pagado)}</span></div>
                                          <div className="ml-auto"><span className="fin-t4">Debe </span><span className="font-black" style={{color: p.saldo>0.01?'var(--fc-red)':'var(--fc-green)'}}>{fmtMoney(p.saldo)}</span></div>
                                        </div>
                                      </button>
                                      {abiertoP && renderDetalleCobro(p)}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  )}

                  {/* Pagados (histórico) */}
                  {saldados.length > 0 && (
                    <div>
                      <div className="fin-t3 text-xs uppercase tracking-widest font-bold px-1 mb-2">
                        ✅ Pagados ({saldados.length})
                      </div>
                      {saldados.slice(0,60).map(p=>(
                        <div key={p.id} className="rounded-2xl p-3 flex items-center justify-between mb-2"
                          style={{background:'rgba(16,185,129,0.06)', border:'1px solid rgba(16,185,129,0.15)'}}>
                          <div className="min-w-0">
                            <div className="fin-t2 font-bold text-sm truncate">{p.cliente || '(sin nombre)'}</div>
                            <div className="fin-t4 text-xs">{(PC[p.plataforma]||PC['OTROS']).emoji} {p.plataforma} · {fmtMoney(p.total)}</div>
                          </div>
                          <span className="text-emerald-400 text-xs font-black shrink-0">✅ Pagado</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ── RENTAS DIRECTAS ─── */}
        {seccion==='rentas' && (() => {
          // ── RENTAS unificadas ─────────────────────────────────────
          // Antes había DOS conceptos separados y confusos: "Rentas directas" y
          // "Semanales" (pedidos con cobro semanal), y la pestaña de Semanales ni
          // siquiera estaba en el menú → esos pedidos no se veían en ningún lado.
          // Aquí se juntan en una sola lista, ordenada por quién está más atrasado,
          // con alertas arriba. Cada tarjeta dice de qué tipo es.
          const norm = s => (s || '').trim();
          const lista = [
            ...rentasDirectas.map(c => ({ ...c, _tipo:'directa' })),
            ...semanales.map(c => ({ ...c, _tipo:'semanal' })),
          ].filter(c => coincide(c.cliente, c.plataforma));

          const conDias = lista.map(c => {
            const dias = diasSinPagar(c.ultimoPago);
            const overdue = (parseFloat(c.montoSemanal||0) > 0) && (dias===null || dias>=7);
            const pronto  = !overdue && dias!==null && dias>=5;
            return { ...c, _dias:dias, _overdue:overdue, _pronto:pronto };
          }).sort((a,b) => {
            const da = a._dias===null ? 999 : a._dias;
            const db = b._dias===null ? 999 : b._dias;
            return db - da;
          });

          const atrasadas   = conDias.filter(c => c._overdue);
          const porVencer   = conDias.filter(c => c._pronto);
          const semanalTotal = Math.round(conDias.reduce((s,c)=>s+parseFloat(c.montoSemanal||0),0));
          const totalActivas = rentasDirectas.length + semanales.length;

          return (
            <div className="p-4 space-y-4 max-w-6xl mx-auto">
              <div className="flex items-center justify-between pt-2">
                <div className="fin-t3 text-xs">{totalActivas} rentas · {fmtMoney(semanalTotal)}/sem</div>
                <button onClick={()=>{setRentaForm({cliente:'',plataforma:'OTROS',montoSemanal:'',notas:''});setEditandoRenta(null);setShowNuevaRenta(v=>!v)}}
                  className="px-4 py-2 rounded-xl text-xs font-black text-white active:scale-95 transition-all"
                  style={{background:'#8b5cf6'}}>
                  + Nueva renta
                </button>
              </div>

              {/* ── ALERTAS de cobro ── */}
              {(atrasadas.length > 0 || porVencer.length > 0) && (
                <div className="rounded-2xl p-4 space-y-2" style={{background: atrasadas.length? 'rgba(239,68,68,0.1)':'rgba(245,158,11,0.1)', border:'1px solid '+(atrasadas.length?'rgba(239,68,68,0.3)':'rgba(245,158,11,0.3)')}}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {atrasadas.length > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-black bg-red-500/20 text-red-300">🚨 {atrasadas.length} atrasada{atrasadas.length>1?'s':''}</span>}
                    {porVencer.length > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-black bg-amber-500/20 text-amber-300">⏰ {porVencer.length} por vencer</span>}
                    <span className="fin-t3 text-xs ml-auto">7+ días = atrasada</span>
                  </div>
                  {atrasadas.slice(0,4).map(c => (
                    <div key={'al'+c._tipo+c.id} className="flex items-center gap-2 text-xs">
                      <span className="fin-t2 font-bold truncate flex-1">{c.cliente || '(sin nombre)'}</span>
                      <span className="text-red-300 shrink-0">{c._dias===null?'sin pagos':c._dias+'d'}</span>
                      <button onClick={()=>setRentasLocal(c.id)} className="px-2 py-0.5 rounded-lg text-[11px] font-black text-white shrink-0" style={{background:'#10b981'}}>Cobrar</button>
                    </div>
                  ))}
                  {atrasadas.length > 4 && <div className="fin-t4 text-xs">y {atrasadas.length-4} más abajo…</div>}
                </div>
              )}

              {/* Formulario de nueva renta directa */}
              <AnimatePresence>
                {showNuevaRenta && (
                  <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
                    <div className="rounded-2xl p-4 space-y-3" style={{background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.25)'}}>
                      <div className="fin-t1 font-black text-sm">{editandoRenta ? 'Editar renta' : 'Nueva renta directa'}</div>
                      <input className="w-full rounded-xl px-4 py-3 text-sm font-bold fin-t1 outline-none"
                        style={{background:'var(--fw-8)', border:'1px solid var(--fw-10)'}}
                        placeholder="Nombre del cliente *" value={rentaForm.cliente} onChange={e=>setRentaForm({...rentaForm,cliente:e.target.value})}/>
                      <div className="grid grid-cols-2 gap-3">
                        <select className="rounded-xl px-3 py-3 text-sm font-bold fin-t1 outline-none"
                          style={{background:'var(--fw-8)', border:'1px solid var(--fw-10)'}}
                          value={rentaForm.plataforma} onChange={e=>setRentaForm({...rentaForm,plataforma:e.target.value})}>
                          {PLATAFORMAS.map(p=><option key={p}>{p}</option>)}
                        </select>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 fin-t4 font-bold">$</span>
                          <input type="number" placeholder="Monto semanal *"
                            className="w-full rounded-xl pl-7 pr-3 py-3 text-sm font-bold fin-t1 outline-none"
                            style={{background:'var(--fw-8)', border:'1px solid var(--fw-10)'}}
                            value={rentaForm.montoSemanal} onChange={e=>setRentaForm({...rentaForm,montoSemanal:e.target.value})}/>
                        </div>
                      </div>
                      <input className="w-full rounded-xl px-4 py-3 text-sm fin-t1 outline-none"
                        style={{background:'var(--fw-8)', border:'1px solid var(--fw-10)'}}
                        placeholder="Notas (opcional)" value={rentaForm.notas} onChange={e=>setRentaForm({...rentaForm,notas:e.target.value})}/>
                      <div className="flex gap-2">
                        <button onClick={()=>{setShowNuevaRenta(false);setEditandoRenta(null);}} className="flex-1 py-2.5 rounded-xl text-sm font-bold fin-t3 fin-hover-t1 transition-all" style={{background:'var(--fw-5)'}}>Cancelar</button>
                        <button onClick={saveRenta} disabled={savingRenta||!rentaForm.cliente||!rentaForm.montoSemanal}
                          className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-40 active:scale-95 transition-all"
                          style={{background:'#8b5cf6'}}>
                          {savingRenta ? 'Guardando...' : '💾 Guardar'}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {totalActivas===0 && !showNuevaRenta && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="text-5xl mb-4">🏠</div>
                  <div className="fin-t1 font-black text-lg">Sin rentas aún</div>
                  <div className="fin-t3 text-sm mt-1">Agrega clientes con renta fija semanal</div>
                </div>
              )}

              {totalActivas>0 && (
                <button onClick={()=>{
                  const rows = [['Cliente','Tipo','Plataforma','Renta semanal','Total cobrado','Último pago','Días sin pagar','Notas']];
                  conDias.forEach(c=>{
                    rows.push([c.cliente||'', c._tipo==='directa'?'Renta directa':'Semanal', c.plataforma||'', parseFloat(c.montoSemanal||0).toFixed(2), parseFloat(c.totalPagado||0).toFixed(2), c.ultimoPago?new Date(c.ultimoPago+'T12:00').toLocaleDateString('es-MX'):'', c._dias===null?'':c._dias, c.notas||'']);
                  });
                  descargarCSV('rentas', rows);
                }} className="w-full py-2.5 rounded-xl text-xs font-black fin-t1 active:scale-95 transition-all" style={{background:'rgba(16,185,129,0.15)', border:'1px solid rgba(16,185,129,0.3)'}}>
                  📊 Descargar reporte de rentas (Excel)
                </button>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
              {/* Lista unificada, más atrasadas primero */}
              {conDias.map(c => {
                const dias = c._dias, overdue = c._overdue, pronto = c._pronto;
                const esDirecta = c._tipo === 'directa';
                const borderColor = overdue?'rgba(239,68,68,0.3)':pronto?'rgba(245,158,11,0.3)':'var(--fw-8)';
                const bgColor = overdue?'rgba(239,68,68,0.06)':pronto?'rgba(245,158,11,0.06)':'var(--fw-3)';
                return (
                  <div key={c._tipo+c.id} className="rounded-2xl p-4 space-y-2" style={{background:bgColor, border:'1px solid '+borderColor}}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="fin-t1 font-black text-sm truncate">{c.cliente || '(sin nombre)'}</div>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-black shrink-0" style={esDirecta?{background:'rgba(139,92,246,0.2)',color:'#c4b5fd'}:{background:'rgba(56,189,248,0.2)',color:'#7dd3fc'}}>
                            {esDirecta?'renta':'semanal'}
                          </span>
                        </div>
                        <div className="fin-t3 text-xs mt-0.5">{(PC[c.plataforma]||PC['OTROS']).emoji} {c.plataforma} · {fmtMoney(c.montoSemanal)}/sem · cobrado: <strong className="fin-t3">{fmtMoney(c.totalPagado)}</strong></div>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <button onClick={()=>setRentasLocal(c.id)}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-black text-white active:scale-95 transition-all"
                          style={{background:'#10b981'}}>
                          💰
                        </button>
                        {esDirecta && (
                          <button onClick={()=>{setRentaForm({cliente:c.cliente,plataforma:c.plataforma,montoSemanal:c.montoSemanal,notas:c.notas||''});setEditandoRenta(c.id);setShowNuevaRenta(true);}}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-black fin-t3 fin-hover-t1 active:scale-95 transition-all"
                            style={{background:'var(--fw-8)'}}>✏️</button>
                        )}
                        {esDirecta && (
                          <button onClick={()=>deleteRentaDirecta(c.id)}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-black text-red-400/60 hover:text-red-400 active:scale-95 transition-all"
                            style={{background:'var(--fw-5)'}}>🗑</button>
                        )}
                        {/* Rentas semanales = pedidos reales → se abren para editar/desactivar/eliminar */}
                        {!esDirecta && pedidoReal(c.id) && (
                          <button onClick={()=>{ setEstadoMenuId(null); setRentaAbiertaId(rentaAbiertaId===c.id?null:c.id); }}
                            title="Editar, cambiar estado, desactivar o eliminar"
                            className="px-2.5 py-1.5 rounded-lg text-xs font-black fin-t3 fin-hover-t1 active:scale-95 transition-all"
                            style={{background: rentaAbiertaId===c.id ? 'rgba(59,130,246,0.35)' : 'var(--fw-8)'}}>✏️</button>
                        )}
                      </div>
                    </div>
                    {!esDirecta && rentaAbiertaId===c.id && (
                      <div className="pt-1">
                        {renderAccionesPedido(c.id)}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className={"text-xs font-black px-2.5 py-1 rounded-full " + (overdue?'bg-red-500/20 text-red-400':pronto?'bg-amber-500/20 text-amber-400':'fin-bgw10 fin-t3')}>
                        {dias===null?'⚠️ Sin pagos':overdue?'🚨 '+dias+'d sin pagar':pronto?'⏰ '+dias+'d sin pagar':'✅ '+dias+'d sin pagar'}
                      </span>
                      {c.ultimoPago && <div className="fin-t4 text-xs">Último: {fmtDate(c.ultimoPago)}</div>}
                    </div>
                    {c.notas && <div className="fin-t4 text-xs italic">{c.notas}</div>}
                  </div>
                );
              })}
              </div>
            </div>
          );
        })()}

        {/* ── EXTRAS ─── */}
        {seccion==='extras' && (
          <div className="p-4 space-y-4 max-w-6xl mx-auto">
            {/* Form */}
            <div className="rounded-2xl p-4 space-y-3 pt-4" style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
              <div className="fin-t1 font-black text-sm">Registrar pago extra</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 fin-t4 font-bold">$</span>
                  <input type="number" placeholder="Monto *"
                    className="w-full rounded-xl pl-7 pr-3 py-2.5 text-sm font-bold fin-t1 outline-none"
                    style={{background:'var(--fw-8)', border:'1px solid var(--fw-10)'}}
                    value={extraForm.monto} onChange={e=>setExtraForm({...extraForm,monto:e.target.value})}/>
                </div>
                <select className="rounded-xl px-3 py-2.5 text-sm font-bold fin-t1 outline-none"
                  style={{background:'var(--fw-8)', border:'1px solid var(--fw-10)'}}
                  value={extraForm.metodo} onChange={e=>setExtraForm({...extraForm,metodo:e.target.value})}>
                  {['Zelle','Cash','CashApp','PayPal','Venmo','Otro'].map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
              <input className="w-full rounded-xl px-4 py-2.5 text-sm fin-t1 outline-none"
                style={{background:'var(--fw-8)', border:'1px solid var(--fw-10)'}}
                placeholder="Descripción *" value={extraForm.descripcion} onChange={e=>setExtraForm({...extraForm,descripcion:e.target.value})}/>
              <input type="date" className="w-full rounded-xl px-4 py-2.5 text-sm fin-t1 outline-none"
                style={{background:'var(--fw-8)', border:'1px solid var(--fw-10)'}}
                value={extraForm.fechaPago} onChange={e=>setExtraForm({...extraForm,fechaPago:e.target.value})}/>
              <button onClick={submitExtra} disabled={extraSaving||!extraForm.monto||!extraForm.descripcion.trim()}
                className="w-full py-3 rounded-xl text-sm font-black text-white disabled:opacity-40 active:scale-95 transition-all"
                style={{background:'#10b981'}}>
                {extraSaving?'Guardando...':'💾 Registrar'}
              </button>
            </div>
            {/* Lista */}
            <div className="space-y-2">
              <div className="fin-t3 text-xs uppercase tracking-widest font-bold px-1">Registrados ({extras.length})</div>
              {extras.length===0 && <div className="text-center fin-t4 text-sm py-6">Sin pagos extras aún</div>}
              {extras.map(e => (
                <div key={e.id} className="rounded-xl px-4 py-3 flex items-center justify-between"
                  style={{background:'var(--fw-4)', border:'1px solid var(--fw-7)'}}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-400 font-black">{fmtMoney(e.monto)}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full fin-t3 font-bold" style={{background:'var(--fw-8)'}}>{e.metodo}</span>
                    </div>
                    <div className="fin-t2 text-sm">{e.descripcion}</div>
                    <div className="fin-t4 text-xs">{fmtDate(e.fechaPago)}</div>
                  </div>
                  <button onClick={()=>deleteExtra(e.id)} className="text-red-400/40 hover:text-red-400 text-lg shrink-0 active:scale-95 transition-all ml-3">🗑</button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
      <AnimatePresence>
        {showSuperModal && (
          <SuperClientesModal
            nombres={[...new Set([
              ...cartera.filter(c=>c.estado!=='saldado'),
              ...creditos.filter(c=>parseFloat(c.totalPagado||0)<parseFloat(c.totalCredito||0))
            ].map(c=>(c.cliente||'—').trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b))}
            mapping={superClientes}
            onSave={persistSuper}
            onClose={()=>setShowSuperModal(false)}
          />
        )}
      </AnimatePresence>

      {/* ── VENTANA: corregir estados de pago mal marcados ────────── */}
      <AnimatePresence>
        {mantAbierto && (
          <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{background:'rgba(0,0,0,0.8)'}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            onClick={e=>{ if(e.target===e.currentTarget && !mantTrabajando) setMantAbierto(false); }}>
            <motion.div className="w-full sm:max-w-lg rounded-t-[28px] sm:rounded-[28px] flex flex-col overflow-hidden"
              style={{maxHeight:'88vh', background:'#14141c', border:'1px solid var(--fw-10)'}}
              initial={{y:80,opacity:0}} animate={{y:0,opacity:1}} exit={{y:80,opacity:0}} transition={{type:'spring',damping:22}}>

              <div className="px-5 py-4 shrink-0" style={{borderBottom:'1px solid var(--fw-8)'}}>
                <div className="flex items-center justify-between">
                  <div className="fin-t1 font-black text-base">🔧 Corregir estados de pago</div>
                  {!mantTrabajando && <button onClick={()=>setMantAbierto(false)} className="fin-t3 fin-hover-t1 text-xl">✕</button>}
                </div>
              </div>

              <div className="overflow-y-auto flex-1 p-5 space-y-3">
                {!mant && (
                  <div className="py-12 text-center">
                    <div className="text-3xl mb-3 animate-pulse">🔍</div>
                    <div className="fin-t3 text-sm font-bold">Revisando tus pedidos...</div>
                  </div>
                )}

                {mant && mant.error && (
                  <div className="rounded-2xl p-4 text-center" style={{background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)'}}>
                    <div className="text-red-400 font-bold text-sm">{mant.error}</div>
                    <div className="fin-t3 text-xs mt-1">Esta herramienta es solo para el administrador principal.</div>
                  </div>
                )}

                {/* Ya se aplicó */}
                {mantHecho && (
                  <div className="rounded-2xl p-5 text-center" style={{background:'rgba(52,211,153,0.1)', border:'1px solid rgba(52,211,153,0.3)'}}>
                    <div className="text-4xl mb-2">✅</div>
                    <div className="text-emerald-400 font-black text-lg">{mantHecho.corregidos} pedidos corregidos</div>
                    <div className="fin-t3 text-xs mt-2 leading-relaxed">
                      Ya aparecen como "debe" en toda la app y puedes empezar a cobrarlos.
                    </div>
                    <button onClick={deshacerMantenimiento} disabled={mantTrabajando}
                      className="mt-4 px-4 py-2 rounded-xl text-xs font-black fin-t3 disabled:opacity-40"
                      style={{background:'var(--fw-8)'}}>
                      ↩ Deshacer, déjalo como estaba
                    </button>
                  </div>
                )}

                {/* Vista previa */}
                {mant && !mant.error && !mantHecho && (
                  <>
                    <div className="rounded-2xl p-4" style={{background:'var(--fw-4)', border:'1px solid var(--fw-8)'}}>
                      <div className="fin-t3 text-xs leading-relaxed">
                        El bug viejo marcaba un pedido como <span className="fin-t1 font-bold">"pagado"</span> con
                        cualquier pago, aunque fuera un abono chico. Estos pedidos dicen que están pagados
                        pero <span className="fin-t1 font-bold">el cliente todavía debe</span>.
                      </div>
                    </div>

                    {mant.malMarcados === 0 ? (
                      <div className="rounded-2xl p-8 text-center" style={{background:'rgba(52,211,153,0.08)'}}>
                        <div className="text-4xl mb-2">🎉</div>
                        <div className="text-emerald-400 font-black">Todo está correcto</div>
                        <div className="fin-t3 text-xs mt-1">Revisé {mant.revisados} pedidos. No hay nada que corregir.</div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="rounded-2xl p-4 text-center" style={{background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.3)'}}>
                            <div className="text-amber-400 font-black text-2xl">{mant.malMarcados}</div>
                            <div className="fin-t3 text-xs mt-0.5">pedidos mal marcados</div>
                          </div>
                          <div className="rounded-2xl p-4 text-center" style={{background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)'}}>
                            <div className="text-red-400 font-black text-2xl">{fmtMoney(mant.deudaEscondida)}</div>
                            <div className="fin-t3 text-xs mt-0.5">deuda escondida</div>
                          </div>
                        </div>

                        <div className="fin-t3 text-xs font-bold pt-1">Qué va a cambiar:</div>
                        <div className="space-y-1.5">
                          {mant.cambios.slice(0, 50).map(c => (
                            <div key={c.id} className="rounded-xl p-2.5 flex items-center gap-2"
                                 style={{background:'var(--fw-4)'}}>
                              <div className="flex-1 min-w-0">
                                <div className="fin-t1 text-xs font-bold truncate">{c.cliente}</div>
                                <div className="fin-t4 text-xs">
                                  {c.plataforma} · pagó {fmtMoney(c.pagado)} de {fmtMoney(c.total)}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="fin-t4 text-xs line-through">{c.estadoAnterior}</span>
                                <span className="fin-t4 text-xs">→</span>
                                <span className="px-2 py-0.5 rounded-full text-xs font-black"
                                      style={{background: c.estadoNuevo==='debe' ? 'rgba(248,113,113,0.2)' : 'rgba(52,211,153,0.2)',
                                              color: c.estadoNuevo==='debe' ? 'var(--fc-red)' : 'var(--fc-green)'}}>
                                  {c.estadoNuevo}
                                </span>
                              </div>
                            </div>
                          ))}
                          {mant.cambios.length > 50 && (
                            <div className="fin-t4 text-xs text-center py-2">... y {mant.cambios.length - 50} más</div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Botones */}
              {mant && !mant.error && !mantHecho && mant.malMarcados > 0 && (
                <div className="p-4 shrink-0 space-y-2" style={{borderTop:'1px solid var(--fw-8)'}}>
                  <button onClick={aplicarMantenimiento} disabled={mantTrabajando}
                    className="w-full py-3.5 rounded-2xl font-black text-sm text-emerald-950 active:scale-95 transition-transform disabled:opacity-50"
                    style={{background:'#34d399'}}>
                    {mantTrabajando ? '⏳ Corrigiendo...' : `✓ Corregir los ${mant.malMarcados} pedidos`}
                  </button>
                  <div className="fin-t4 text-xs text-center leading-relaxed">
                    Solo cambia la etiqueta de pago. No borra pagos, precios, fotos ni nada más.<br/>
                    Se puede deshacer.
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ── VENTANA: ponerle precio a los pedidos que no tienen ───── */}
      <AnimatePresence>
        {precAbierto && (
          <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{background:'rgba(0,0,0,0.85)'}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <motion.div className="w-full sm:max-w-lg rounded-t-[28px] sm:rounded-[28px] flex flex-col overflow-hidden"
              style={{maxHeight:'92vh', height:'92vh', background:'#14141c', border:'1px solid var(--fw-10)'}}
              initial={{y:80,opacity:0}} animate={{y:0,opacity:1}} exit={{y:80,opacity:0}} transition={{type:'spring',damping:22}}>

              {/* Encabezado con progreso */}
              <div className="px-5 py-4 shrink-0" style={{borderBottom:'1px solid var(--fw-8)'}}>
                <div className="flex items-center justify-between mb-2">
                  <div className="fin-t1 font-black text-base">💵 Ponerles precio</div>
                  <button onClick={cerrarPrecios} className="fin-t3 fin-hover-t1 text-xl">✕</button>
                </div>
                {precData && !precData.error && (
                  <>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="fin-t3">
                        {Object.keys(precGuardados).length} de {precData.total} listos
                      </span>
                      <span className="text-emerald-400 font-black">
                        {precData.total - Object.keys(precGuardados).length} faltan
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{background:'var(--fw-8)'}}>
                      <div className="h-full rounded-full transition-all"
                           style={{width: (precData.total ? Object.keys(precGuardados).length/precData.total*100 : 0)+'%', background:'#34d399'}}/>
                    </div>
                  </>
                )}
              </div>

              {precData && !precData.error && precData.total > 0 && (
                <>
                  {/* Filtro por plataforma */}
                  <div className="px-4 pt-3 shrink-0">
                    <div className="flex gap-1.5 overflow-x-auto pb-2" style={{scrollbarWidth:'none'}}>
                      {['TODAS', ...Object.keys(precData.porPlataforma)].map(pl => {
                        const n = pl==='TODAS'
                          ? precData.pedidos.filter(p=>!precGuardados[p.id]).length
                          : precData.pedidos.filter(p=>!precGuardados[p.id] && p.plataforma===pl).length;
                        if (n === 0 && pl !== 'TODAS') return null;
                        return (
                          <button key={pl} onClick={()=>{ setPrecPlat(pl); setPrecSel([]); }}
                            className={"px-3 py-1.5 rounded-full text-xs font-black whitespace-nowrap shrink-0 "+(precPlat===pl?'text-emerald-950':'fin-t3')}
                            style={{background: precPlat===pl ? '#34d399' : 'var(--fw-6)'}}>
                            {pl} ({n})
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Selección en bloque: la forma rápida */}
                  <div className="px-4 pb-2 shrink-0">
                    <div className="rounded-2xl p-3" style={{background:'rgba(52,211,153,0.07)', border:'1px solid rgba(52,211,153,0.2)'}}>
                      <div className="flex items-center justify-between mb-2">
                        <button onClick={()=>{
                            const ids = precVisibles.map(p=>p.id);
                            setPrecSel(precSel.length===ids.length ? [] : ids);
                          }}
                          className="text-xs font-black text-emerald-400">
                          {precSel.length===precVisibles.length && precVisibles.length>0 ? '☑ Quitar selección' : `☐ Seleccionar los ${precVisibles.length} de aquí`}
                        </button>
                        {precSel.length>0 && <span className="fin-t3 text-xs font-bold">{precSel.length} elegidos</span>}
                      </div>

                      {/* Precios que sueles cobrar → un toque */}
                      {precSugerencias.length>0 && (
                        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-1" style={{scrollbarWidth:'none'}}>
                          <span className="fin-t4 text-xs font-bold shrink-0 self-center pr-1">Usas:</span>
                          {precSugerencias.map(s => (
                            <button key={s.monto} onClick={()=>setPrecLote(String(s.monto))}
                              className="px-2.5 py-1 rounded-lg text-xs font-black shrink-0"
                              style={{background: String(s.monto)===precLote ? '#34d399' : 'var(--fw-8)',
                                      color: String(s.monto)===precLote ? '#052e1a' : 'var(--fw-70)'}}>
                              ${s.monto} <span className="opacity-50 font-medium">×{s.veces}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 fin-t3 text-sm font-black">$</span>
                          <input value={precLote} onChange={e=>setPrecLote(e.target.value)}
                            inputMode="decimal" placeholder="Monto"
                            className="w-full rounded-xl pl-7 pr-3 py-2.5 text-sm fin-t1 font-bold outline-none"
                            style={{background:'var(--fw-6)', border:'1px solid var(--fw-12)'}}/>
                        </div>
                        <button onClick={aplicarLote}
                          disabled={precTrabajando || precSel.length===0 || !precLote}
                          className="px-4 py-2.5 rounded-xl font-black text-xs text-emerald-950 disabled:opacity-30 active:scale-95 transition-all whitespace-nowrap"
                          style={{background:'#34d399'}}>
                          {precTrabajando ? '⏳' : `Aplicar a ${precSel.length}`}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Lista */}
                  <div className="overflow-y-auto flex-1 px-4 pb-4 space-y-2">
                    {precVisibles.length===0 ? (
                      <div className="py-16 text-center">
                        <div className="text-4xl mb-2">🎉</div>
                        <div className="text-emerald-400 font-black">
                          {Object.keys(precGuardados).length>0 ? '¡Listo por aquí!' : 'Nada en este filtro'}
                        </div>
                        <div className="fin-t3 text-xs mt-1">
                          {precPlat!=='TODAS' ? 'Prueba otra plataforma arriba' : 'Ya les pusiste precio a todos'}
                        </div>
                      </div>
                    ) : precVisibles.map(p => {
                      const sel = precSel.includes(p.id);
                      return (
                        <div key={p.id} className="rounded-2xl p-3"
                             style={{background: sel?'rgba(52,211,153,0.08)':'var(--fw-4)',
                                     border:'1px solid '+(sel?'rgba(52,211,153,0.35)':'var(--fw-8)')}}>
                          <div className="flex items-start gap-2.5">
                            <button onClick={()=>setPrecSel(s => sel ? s.filter(x=>x!==p.id) : [...s,p.id])}
                              className="w-6 h-6 rounded-lg shrink-0 mt-0.5 flex items-center justify-center text-xs font-black"
                              style={{background: sel?'#34d399':'var(--fw-8)', color:'#052e1a'}}>
                              {sel ? '✓' : ''}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="fin-t1 font-bold text-sm truncate">{p.cliente}</div>
                              <div className="fin-t4 text-xs">
                                {p.plataforma}{p.tipoCuenta ? ' · '+p.tipoCuenta : ''} · {(p.createdAt||'').split(' ')[0].split('T')[0]}
                              </div>
                              {p.notas && <div className="fin-t4 text-xs truncate mt-0.5">{p.notas}</div>}
                            </div>
                            <div className="relative w-24 shrink-0">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 fin-t3 text-xs font-black">$</span>
                              <input
                                value={precValores[p.id] ?? ''}
                                onChange={e=>setPrecValores(v=>({...v,[p.id]:e.target.value}))}
                                onBlur={e=>{ if(e.target.value) guardarPrecioUno(p.id, e.target.value); }}
                                onKeyDown={e=>{ if(e.key==='Enter'){ e.target.blur(); } }}
                                inputMode="decimal" placeholder="0"
                                className="w-full rounded-xl pl-6 pr-2 py-2 text-sm fin-t1 font-black outline-none text-right"
                                style={{background:'var(--fw-7)', border:'1px solid var(--fw-14)'}}/>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {precData && precData.error && (
                <div className="p-5"><div className="rounded-2xl p-4 text-center" style={{background:'rgba(248,113,113,0.1)'}}>
                  <div className="text-red-400 font-bold text-sm">{precData.error}</div>
                </div></div>
              )}
              {!precData && (
                <div className="py-16 text-center">
                  <div className="text-3xl mb-3 animate-pulse">💵</div>
                  <div className="fin-t3 text-sm font-bold">Cargando pedidos...</div>
                </div>
              )}
              {precData && !precData.error && precData.total===0 && (
                <div className="py-16 text-center px-5">
                  <div className="text-4xl mb-2">🎉</div>
                  <div className="text-emerald-400 font-black">Todos tienen precio</div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ── MODAL DE PAGOS, AQUÍ MISMO ────────────────────────────
          Se abre encima de Finanzas en vez de mandarte a la página
          principal. Al cerrarlo se refrescan los números del reporte. */}
      <AnimatePresence>
        {rentasLocal && (
          <RentasModal
            order={orders.find(o => o.id === rentasLocal)
                   || (reporte?.pedidos || []).find(p => p.id === rentasLocal)
                   || { id: rentasLocal, cliente: '', plataforma: '' }}
            onClose={cerrarRentasLocal}
            onUpdate={() => {}}
          />
        )}
      </AnimatePresence>

      {/* ── EDITOR DEL PEDIDO, AQUÍ MISMO ───────────────────────────
          Es el mismo editor del hub. Guarda sobre el pedido real, así
          que el cambio se ve también en la pantalla de pedidos. */}
      <AnimatePresence>
        <AnimatePresence>{fichaFinId && (
          <ClienteFichaModal clienteId={fichaFinId} isAdmin={true} onClose={()=>setFichaFinId(null)} onChanged={loadAll}
            onOpenPedido={p=>{ setFichaFinId(null); if (p.esRentaDirecta) setRentasLocal(p.id); else abrirPedido(p.id); }}/>
        )}</AnimatePresence>
        {pedidoEditId && pedidoReal(pedidoEditId) && (
          <EditModal
            order={pedidoReal(pedidoEditId)}
            orders={orders}
            onSave={guardarPedidoDesdeFinanzas}
            onClose={()=>setPedidoEditId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}



function CobrosPage({ orders, onBack, onOpenRentas }) {
  const [tab, setTab] = useState('urgente'); // urgente | todos | historial
  const [busqueda, setBusqueda] = useState('');
  const [finData, setFinData] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);

  useEffect(() => {
    apiFetch('/api/finanzas').then(d => setFinData(d));
  }, []);

  useEffect(() => {
    if (tab === 'historial' && historial.length === 0) {
      setLoadingHist(true);
      apiFetch('/api/finanzas').then(d => {
        setHistorial(d?.historial || []);
        setLoadingHist(false);
      });
    }
  }, [tab]);

  const now = Date.now();

  // Clasificar cada pedido con tipo de cobro activo
  const cobros = orders
    .filter(o => !isArchived(o))
    .flatMap(o => {
      const items = [];
      if (o.tipoCobro === 'semanal' && o.montoSemanal) {
        const dias = o.ultimoPago
          ? Math.floor((now - new Date(o.ultimoPago + 'T12:00').getTime()) / 86_400_000)
          : 999;
        items.push({ ...o, _tipo: 'semanal', _dias: dias, _urgencia: dias >= 7 ? 'vencido' : dias >= 5 ? 'pronto' : 'ok' });
      }
      if (o.tipoCobro === 'credito' && o.totalCredito) {
        const pagado = parseFloat(o.totalPagado || 0);
        const total = parseFloat(o.totalCredito || 0);
        const falta = total - pagado;
        if (falta > 0) {
          const pct = Math.round((pagado / total) * 100);
          items.push({ ...o, _tipo: 'credito', _falta: falta, _pagado: pagado, _total: total, _pct: pct, _urgencia: pagado === 0 ? 'vencido' : 'pronto' });
        }
      }
      return items;
    });

  const urgentes = cobros.filter(o => o._urgencia === 'vencido').sort((a, b) => (b._dias || 0) - (a._dias || 0));
  const proximos = cobros.filter(o => o._urgencia === 'pronto').sort((a, b) => (b._dias || 0) - (a._dias || 0));
  const alDia    = cobros.filter(o => o._urgencia === 'ok');

  const listaTodos = [...urgentes, ...proximos, ...alDia];
  const listaFiltrada = busqueda.trim()
    ? listaTodos.filter(o => o.cliente.toLowerCase().includes(busqueda.toLowerCase()))
    : listaTodos;

  const listaTab = tab === 'urgente' ? [...urgentes, ...proximos] : listaFiltrada;

  const totalPendiente = cobros.reduce((s, o) => {
    if (o._tipo === 'semanal') return s + parseFloat(o.montoSemanal || 0);
    if (o._tipo === 'credito') return s + (o._falta || 0);
    return s;
  }, 0);

  const fmtDate = d => d ? new Date(d + 'T12:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : '—';
  const fmtMoney = v => '$' + parseFloat(v || 0).toFixed(0);

  return (
    <div className="min-h-screen" style={{ background: 'var(--fin-bg)', fontFamily: "'Inter','DM Sans',system-ui,sans-serif" }}>

      {/* HEADER */}
      <div style={{ background: 'var(--fin-bg)', borderBottom: '1px solid var(--fw-6)' }}>
        <div className="max-w-2xl mx-auto px-4 pt-8 pb-6">
          <div className="flex items-center gap-4 mb-6">
            <button onClick={onBack}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 fin-hover-t1 transition-colors"
              style={{ background: 'var(--fw-6)', border: '1px solid var(--fw-10)' }}>
              ←
            </button>
            <div>
              <div className="fin-t1 font-black text-xl">💸 Cobros</div>
              <div className="text-slate-500 text-xs">Centro de cobranza</div>
            </div>
          </div>

          {/* MÉTRICAS GRANDES */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Vencidos', val: urgentes.length, color: urgentes.length > 0 ? 'var(--fc-red2)' : '#22c55e', bg: urgentes.length > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.08)', border: urgentes.length > 0 ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.2)', emoji: urgentes.length > 0 ? '🚨' : '✅' },
              { label: 'Esta semana', val: proximos.length, color: 'var(--fc-amber2)', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.2)', emoji: '⏰' },
              { label: 'Por cobrar', val: fmtMoney(totalPendiente), color: 'var(--fc-violet)', bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.2)', emoji: '💰' },
            ].map((m, i) => (
              <div key={i} className="rounded-2xl p-3 text-center"
                style={{ background: m.bg, border: `1px solid ${m.border}` }}>
                <div className="text-lg mb-0.5">{m.emoji}</div>
                <div className="font-black text-lg" style={{ color: m.color }}>{m.val}</div>
                <div className="text-xs" style={{ color: 'var(--fw-35)' }}>{m.label}</div>
              </div>
            ))}
          </div>

          {/* TABS */}
          <div className="flex gap-1 p-1 rounded-2xl" style={{ background: 'var(--fw-5)' }}>
            {[
              ['urgente', `🚨 Urgentes (${urgentes.length + proximos.length})`],
              ['todos', `📋 Todos (${listaTodos.length})`],
              ['historial', '📈 Historial'],
            ].map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)}
                className="flex-1 py-2 rounded-xl text-xs font-bold transition-all"
                style={tab === t
                  ? { background: 'var(--fw-12)', color: 'var(--fin-t1)' }
                  : { color: 'var(--fw-40)' }}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">

        {/* BÚSQUEDA en tab todos */}
        {tab === 'todos' && (
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">🔍</span>
            <input
              className="w-full rounded-2xl pl-10 pr-4 py-3 text-sm font-medium outline-none"
              style={{ background: 'var(--fw-7)', border: '1px solid var(--fw-10)', color: 'var(--fin-t1)' }}
              placeholder="Buscar cliente..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
          </div>
        )}

        {/* HISTORIAL */}
        {tab === 'historial' && (
          <div className="space-y-2">
            {loadingHist && <div className="text-center py-12 text-slate-500 text-sm">Cargando...</div>}
            {!loadingHist && historial.length === 0 && <div className="text-center py-12 text-slate-500 text-sm">Sin pagos registrados aún</div>}
            {/* resumen mensual del backend */}
            {finData && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { label: 'Total recaudado', val: fmtMoney(finData.totalRecaudado), color: 'var(--fc-green)' },
                  { label: 'Este mes', val: fmtMoney(finData.pagosMes), color: 'var(--fc-blue)' },
                  { label: 'Hoy', val: fmtMoney(finData.pagosHoy), color: 'var(--fc-amber)' },
                ].map((s, i) => (
                  <div key={i} className="rounded-2xl p-4" style={{ background: 'var(--fw-5)', border: '1px solid var(--fw-8)' }}>
                    <div className="font-black text-xl" style={{ color: s.color }}>{s.val}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--fw-40)' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}
            {historial.map(r => (
              <div key={r.id} className="rounded-2xl flex items-center justify-between px-4 py-3"
                style={{ background: 'var(--fw-5)', border: '1px solid var(--fw-7)' }}>
                <div>
                  <div className="font-bold fin-t1 text-sm">{r.cliente}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'var(--fw-35)' }}>
                    {r.plataforma} · {r.metodo} · {fmtDate(r.fechaPago)}
                  </div>
                  {r.notas && <div className="text-xs italic mt-0.5" style={{ color: 'var(--fw-30)' }}>{r.notas}</div>}
                </div>
                <div className="font-black text-lg" style={{ color: 'var(--fc-green)' }}>${r.monto}</div>
              </div>
            ))}
          </div>
        )}

        {/* LISTA DE COBROS */}
        {tab !== 'historial' && (
          <>
            {listaTab.length === 0 && (
              <div className="text-center py-16">
                <div className="text-5xl mb-3">🎉</div>
                <div className="font-black fin-t1 text-xl mb-1">¡Todo cobrado!</div>
                <div className="text-slate-500 text-sm">No hay pagos pendientes urgentes</div>
              </div>
            )}

            {/* Sección VENCIDOS */}
            {tab === 'urgente' && urgentes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 px-1 mb-2">
                  <span className="text-xs font-black uppercase tracking-widest" style={{ color: 'var(--fc-red2)' }}>🚨 Vencidos</span>
                  <span className="text-xs font-black px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--fc-red2)' }}>{urgentes.length}</span>
                </div>
                <div className="space-y-2">
                  {urgentes.map(o => <CobroCard key={o.id + o._tipo} cobro={o} onCobrar={() => onOpenRentas(o.id)} />)}
                </div>
              </div>
            )}

            {/* Sección PRÓXIMOS */}
            {tab === 'urgente' && proximos.length > 0 && (
              <div>
                <div className="flex items-center gap-2 px-1 mb-2 mt-4">
                  <span className="text-xs font-black uppercase tracking-widest" style={{ color: 'var(--fc-amber2)' }}>⏰ Esta semana</span>
                  <span className="text-xs font-black px-2 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--fc-amber2)' }}>{proximos.length}</span>
                </div>
                <div className="space-y-2">
                  {proximos.map(o => <CobroCard key={o.id + o._tipo} cobro={o} onCobrar={() => onOpenRentas(o.id)} />)}
                </div>
              </div>
            )}

            {/* Vista TODOS — lista unificada */}
            {tab === 'todos' && (
              <div className="space-y-2">
                {listaFiltrada.map(o => <CobroCard key={o.id + o._tipo} cobro={o} onCobrar={() => onOpenRentas(o.id)} />)}
              </div>
            )}
          </>
        )}

        {/* ESPACIO INFERIOR */}
        <div className="h-10" />
      </div>
    </div>
  );
}

// Tarjeta individual de cobro para CobrosPage
function CobroCard({ cobro: o, onCobrar }) {
  const pc = PC[o.plataforma] || PC['OTROS'];

  const urgColor = o._urgencia === 'vencido' ? '#ef4444' : o._urgencia === 'pronto' ? '#f59e0b' : '#4ade80';
  const urgBg    = o._urgencia === 'vencido' ? 'rgba(239,68,68,0.08)' : o._urgencia === 'pronto' ? 'rgba(245,158,11,0.08)' : 'rgba(74,222,128,0.06)';
  const urgBdr   = o._urgencia === 'vencido' ? 'rgba(239,68,68,0.2)' : o._urgencia === 'pronto' ? 'rgba(245,158,11,0.2)' : 'rgba(74,222,128,0.15)';

  return (
    <div className="rounded-2xl p-4" style={{ background: urgBg, border: `1px solid ${urgBdr}` }}>
      <div className="flex items-start gap-3">
        {/* Icono plataforma */}
        <div className={'w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 ' + pc.bg}>
          {pc.emoji}
        </div>

        {/* Info principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black fin-t1 text-sm">{o.cliente}</span>
            <TituloChip order={o} tono="oscuro"/>
            {o._urgencia === 'vencido' && (
              <span className="text-xs font-black px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.2)', color: 'var(--fc-red2)' }}>
                {o._tipo === 'semanal' ? (o._dias === 999 ? 'Sin pagos' : `${o._dias}d vencido`) : 'Sin abono'}
              </span>
            )}
            {o._urgencia === 'pronto' && (
              <span className="text-xs font-black px-2 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.2)', color: 'var(--fc-amber2)' }}>
                {o._tipo === 'semanal' ? `${o._dias}d sin pagar` : 'Abono parcial'}
              </span>
            )}
          </div>
          <div className="text-xs mt-0.5 mb-2" style={{ color: 'var(--fw-40)' }}>{o.plataforma}</div>

          {/* Semanal */}
          {o._tipo === 'semanal' && (
            <div className="flex items-center gap-3">
              <span className="font-black text-base" style={{ color: urgColor }}>${o.montoSemanal}/sem</span>
              {o.ultimoPago && (
                <span className="text-xs" style={{ color: 'var(--fw-30)' }}>
                  Último: {new Date(o.ultimoPago + 'T12:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                </span>
              )}
            </div>
          )}

          {/* Crédito con barra de progreso */}
          {o._tipo === 'credito' && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--fw-40)' }}>
                  ${o._pagado.toFixed(0)} / ${o._total.toFixed(0)} pagado
                </span>
                <span className="font-black text-sm" style={{ color: urgColor }}>${o._falta.toFixed(0)} falta</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--fw-10)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: o._pct + '%', background: `linear-gradient(90deg, ${urgColor}88, ${urgColor})` }} />
              </div>
            </div>
          )}
        </div>

        {/* Botón cobrar */}
        <button onClick={onCobrar}
          className="shrink-0 px-4 py-2.5 rounded-xl font-black text-xs transition-all active:scale-95"
          style={{ background: urgColor, color: o._urgencia === 'pronto' ? '#000' : 'var(--fin-t1)' }}>
          💰 Cobrar
        </button>
      </div>
    </div>
  );
}

// ── COBROS PANEL ──────────────────────────────────────────────────
function CobrosPanel({ porCobrar, onClose, onOpenRentas }) {
  const fmtDias = (d) => {
    if (d===999||d===null) return 'Sin pagos registrados';
    if (d===0) return 'Pagó hoy';
    return `${d}d sin pagar`;
  };
  return (
    <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{background:'rgba(0,0,0,0.75)'}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <motion.div className="w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] shadow-2xl flex flex-col"
        style={{maxHeight:'85vh'}} initial={{y:80,opacity:0}} animate={{y:0,opacity:1}} exit={{y:80,opacity:0}} transition={{type:'spring',damping:22}}>
        <div className="bg-slate-900 px-5 py-4 sm:rounded-t-[28px] rounded-t-[28px] shrink-0">
          <div className="flex items-center justify-between mb-1">
            <div className="text-white font-black text-base">💸 Por cobrar</div>
            <button onClick={onClose} className="text-slate-400 hover:text-white text-xl w-8 h-8 flex items-center justify-center">✕</button>
          </div>
          <div className="text-slate-400 text-xs">{porCobrar.length} cliente{porCobrar.length!==1?'s':''} — ordenados por urgencia</div>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {porCobrar.length===0&&<div className="text-center py-12 text-slate-400 text-sm">¡Todo cobrado! 🎉</div>}
          {porCobrar.map(o=>{
            const pc=PC[o.plataforma]||PC['OTROS'];
            const overdue = o._tipo==='semanal' && o._diasSinPago >= 7;
            return (
              <div key={o.id} className={"rounded-2xl border-2 p-4 flex items-center gap-3 "+(overdue?"border-red-300 bg-red-50":"border-amber-200 bg-amber-50")}>
                <div className={"w-10 h-10 rounded-2xl flex items-center justify-center text-xl shrink-0 "+pc.bg}>{pc.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-slate-900 truncate">{o.cliente}</div>
                  <TituloChip order={o} className="mt-0.5"/>
                  <div className="text-xs text-slate-500">{o.plataforma}</div>
                  {o._tipo==='semanal'&&(
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={"text-xs font-bold px-2 py-0.5 rounded-full "+(overdue?"bg-red-200 text-red-800":"bg-amber-200 text-amber-800")}>
                        {overdue?"🚨":"⏰"} {fmtDias(o._diasSinPago)}
                      </span>
                      <span className="text-xs text-slate-500">${o.montoSemanal}/sem</span>
                    </div>
                  )}
                  {o._tipo==='credito'&&(
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-200 text-blue-800">🤝 Crédito</span>
                      <span className="text-xs text-slate-500">${o._falta.toFixed(0)} pendiente</span>
                    </div>
                  )}
                </div>
                <button onClick={()=>onOpenRentas(o.id)}
                  className="shrink-0 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-black hover:bg-slate-700 active:scale-95 transition-all">
                  💰 Cobrar
                </button>
              </div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── ARCHIVADOS PANEL ──────────────────────────────────────────────
function ArchivadosPanel({ orders, onClose, onReactivar, onReasign }) {
  const [search, setSearch] = useState('');
  const [platFilter, setPlatFilter] = useState(null);
  const [sortBy, setSortBy] = useState('reciente'); // 'reciente' | 'antiguo' | 'nombre'

  const fmtDate = (completedAt) => {
    if (!completedAt) return '';
    const d = new Date(completedAt);
    const h = Math.floor((Date.now() - d.getTime()) / 3_600_000);
    if (h < 24) return `hace ${h}h`;
    const days = Math.floor(h / 24);
    if (days < 7) return `hace ${days}d`;
    return d.toLocaleDateString('es-MX', { day:'numeric', month:'short' });
  };

  const platsConPedidos = PLATAFORMAS.filter(p => orders.some(o => o.plataforma === p));

  const filtered = [...orders]
    .filter(o => {
      if (platFilter && o.plataforma !== platFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return o.cliente?.toLowerCase().includes(q) || o.titulo?.toLowerCase().includes(q) || o.plataforma?.toLowerCase().includes(q) || o.notas?.toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'reciente') return new Date(b.completedAt) - new Date(a.completedAt);
      if (sortBy === 'antiguo')  return new Date(a.completedAt) - new Date(b.completedAt);
      if (sortBy === 'nombre')   return (a.cliente||'').localeCompare(b.cliente||'');
      return 0;
    });

  return (
    <motion.div className="fixed inset-0 z-50 flex flex-col bg-zinc-50"
      initial={{opacity:0, x:40}} animate={{opacity:1, x:0}} exit={{opacity:0, x:40}}
      transition={{type:'spring', damping:24}}>

      {/* ── HEADER ── */}
      <div className="bg-zinc-900 px-4 py-4 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-white font-black text-lg">📦 Archivados</div>
            <div className="text-zinc-400 text-xs">{orders.length} pedido{orders.length!==1?'s':''} archivados</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-zinc-700 hover:bg-zinc-600 text-white text-lg flex items-center justify-center transition-all active:scale-95">✕</button>
        </div>

        {/* Búsqueda */}
        <div className="relative mb-3">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">🔍</span>
          <input
            className="w-full rounded-xl bg-zinc-800 border border-zinc-700 pl-8 pr-4 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-zinc-500"
            placeholder="Buscar por nombre, app, notas..."
            value={search} onChange={e=>setSearch(e.target.value)}
          />
          {search && <button onClick={()=>setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">✕</button>}
        </div>

        {/* Filtro por app */}
        {platsConPedidos.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1" style={{scrollbarWidth:'none'}}>
            <button onClick={()=>setPlatFilter(null)}
              className={"shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-all " + (!platFilter ? "bg-white text-zinc-900" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700")}>
              Todas
            </button>
            {platsConPedidos.map(p => {
              const pc = PC[p];
              const cnt = orders.filter(o=>o.plataforma===p).length;
              return (
                <button key={p} onClick={()=>setPlatFilter(platFilter===p?null:p)}
                  className={"shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all " + (platFilter===p ? pc.bg+" text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700")}>
                  {pc.emoji} {p} <span className="bg-black/20 rounded-full px-1.5 font-black">{cnt}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── SORT BAR ── */}
      <div className="bg-white border-b border-zinc-200 px-4 py-2 flex items-center gap-2 shrink-0">
        <span className="text-xs text-zinc-400 font-bold mr-1">Ordenar:</span>
        {[['reciente','🕐 Más reciente'],['antiguo','🕰 Más antiguo'],['nombre','🔤 Nombre']].map(([key,label])=>(
          <button key={key} onClick={()=>setSortBy(key)}
            className={"px-3 py-1 rounded-full text-xs font-bold transition-all " + (sortBy===key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200")}>
            {label}
          </button>
        ))}
        <span className="ml-auto text-xs text-zinc-400 font-medium">{filtered.length} resultado{filtered.length!==1?'s':''}</span>
      </div>

      {/* ── LISTA ── */}
      <div className="overflow-y-auto flex-1 p-4">
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-zinc-400">
            <div className="text-5xl">📭</div>
            <div className="text-sm font-medium">{search || platFilter ? 'Sin resultados para ese filtro' : 'Sin pedidos archivados aún'}</div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(o => {
            const pc = PC[o.plataforma] || PC['OTROS'];
            return (
              <motion.div key={o.id} layout initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}
                className="bg-white rounded-2xl border-2 border-zinc-100 overflow-hidden hover:border-zinc-300 transition-all">
                {/* Color bar */}
                <div className="h-1.5" style={{background: pc.color}}/>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-slate-800 truncate text-base">{o.cliente}</div>
                      <TituloChip order={o} className="mt-0.5"/>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={"px-2 py-0.5 rounded-full text-xs font-bold " + pc.light + " " + pc.text}>{pc.emoji} {o.plataforma}</span>
                      </div>
                    </div>
                    <div className="text-2xl shrink-0">{pc.emoji}</div>
                  </div>

                  {o.notas && (
                    <div className="text-xs text-zinc-500 bg-zinc-50 rounded-xl px-3 py-2 mb-3 line-clamp-2 leading-relaxed">{o.notas}</div>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <div className="text-xs text-zinc-400">
                      {o.estado === "Cancelado" ? "🚫 Cancelado" : o.estado === "Intento Fallido" ? "🚫 Intento fallido" : o.estado === "NOT APPROVED" ? "⛔ No aprobado" : o.estado === "DIRTY BACKGROUND" ? "⚠️ Antecedentes con problemas" : o.estado === "DESACTIVADA" ? "🔌 Desactivada" : "✅ " + fmtDate(o.completedAt)}
                      {o.userName && <span className="ml-2">· {o.userName}</span>}
                    </div>
                    {o.estado === "Cancelado" && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-500">Cancelado</span>}
                    {o.estado === "Intento Fallido" && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-600">Fallido</span>}
                    {o.estado === "NOT APPROVED" && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700">No aprobado</span>}
                    {o.estado === "DIRTY BACKGROUND" && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">Antecedentes</span>}
                    {o.estado === "DESACTIVADA" && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-zinc-200 text-zinc-600">Desactivada</span>}
                    {/* Se ve de un vistazo cuáles ya revisaste y cuáles siguen
                        contando como deuda sin que nadie los haya mirado. */}
                    {o.cobroCerrado && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-500" title="Revisado: no se cobra">💤 No se cobra</span>}
                  </div>
                </div>
                <div className="px-4 pb-4 space-y-2">
                  {onReasign && (
                    <div className="grid grid-cols-2">
                      <ReasignDropdown order={o} onReasign={onReasign}/>
                    </div>
                  )}
                  <button onClick={()=>{onReactivar(o.id); onClose();}}
                    className="w-full py-2 rounded-xl bg-amber-50 text-amber-800 text-xs font-black hover:bg-amber-100 active:scale-95 transition-all border border-amber-200">
                    ↩ Reactivar pedido
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

// ── LIGHTBOX ─────────────────────────────────────────────────────
function Lightbox({ fotos, startIndex=0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  // ── ZOOM ─────────────────────────────────────────────────────────
  // PC: rueda del mouse (hacia donde apuntas), doble clic, arrastrar, teclas + − 0
  // Celular: pellizcar con 2 dedos, doble toque, arrastrar con 1 dedo
  const MIN_Z = 1, MAX_Z = 6;
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  const areaRef = useRef(null);
  const ptrs = useRef(new Map());      // dedos/mouse activos
  const gesto = useRef(null);          // estado del gesto en curso
  const ultimoTap = useRef(0);
  const zoomed = view.s > 1.01;

  const aplicar = useCallback(v => {
    const s = Math.min(MAX_Z, Math.max(MIN_Z, v.s));
    const n = s <= 1.01 ? { s: 1, x: 0, y: 0 } : { s, x: v.x, y: v.y };
    viewRef.current = n; setView(n);
  }, []);
  // Zoom hacia un punto (px,py relativos al centro del área)
  const zoomEn = useCallback((nuevaS, px = 0, py = 0) => {
    const v = viewRef.current;
    const s = Math.min(MAX_Z, Math.max(MIN_Z, nuevaS));
    const f = s / v.s;
    aplicar({ s, x: px - (px - v.x) * f, y: py - (py - v.y) * f });
  }, [aplicar]);
  const reset = useCallback(() => aplicar({ s: 1, x: 0, y: 0 }), [aplicar]);
  const centroRel = (cx, cy) => {
    const r = areaRef.current.getBoundingClientRect();
    return [cx - (r.left + r.width / 2), cy - (r.top + r.height / 2)];
  };

  const prev = useCallback(() => setIdx(i => (i - 1 + fotos.length) % fotos.length), [fotos.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % fotos.length), [fotos.length]);
  useEffect(() => { reset(); }, [idx, reset]); // cada foto empieza sin zoom

  useEffect(() => {
    const handler = e => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") { if (viewRef.current.s > 1.01) reset(); else onClose(); }
      else if (e.key === "+" || e.key === "=") zoomEn(viewRef.current.s * 1.4);
      else if (e.key === "-") zoomEn(viewRef.current.s / 1.4);
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prev, next, onClose, reset, zoomEn]);

  // Rueda del mouse (listener no-pasivo para poder bloquear el scroll de la página)
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const onWheel = e => {
      e.preventDefault();
      const [px, py] = centroRel(e.clientX, e.clientY);
      zoomEn(viewRef.current.s * Math.exp(-e.deltaY * 0.0015), px, py);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomEn]);

  const onPointerDown = e => {
    areaRef.current.setPointerCapture?.(e.pointerId);
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const lista = [...ptrs.current.values()];
    const v = viewRef.current;
    if (lista.length === 2) {
      const [a, b] = lista;
      const [cx, cy] = centroRel((a.x + b.x) / 2, (a.y + b.y) / 2);
      gesto.current = { tipo: "pinch", d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, v0: v, cx, cy, movio: true };
    } else if (lista.length === 1) {
      gesto.current = { tipo: "pan", x0: e.clientX, y0: e.clientY, v0: v, movio: false, sobreImg: e.target.tagName === "IMG" };
    }
  };
  const onPointerMove = e => {
    if (!ptrs.current.has(e.pointerId)) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesto.current;
    if (!g) return;
    if (g.tipo === "pinch" && ptrs.current.size >= 2) {
      const [a, b] = [...ptrs.current.values()];
      const f = Math.hypot(a.x - b.x, a.y - b.y) / g.d0;
      const s = Math.min(MAX_Z, Math.max(MIN_Z, g.v0.s * f));
      const k = s / g.v0.s;
      aplicar({ s, x: g.cx - (g.cx - g.v0.x) * k, y: g.cy - (g.cy - g.v0.y) * k });
    } else if (g.tipo === "pan") {
      const dx = e.clientX - g.x0, dy = e.clientY - g.y0;
      if (Math.abs(dx) + Math.abs(dy) > 6) g.movio = true;
      if (g.v0.s > 1.01) aplicar({ s: g.v0.s, x: g.v0.x + dx, y: g.v0.y + dy });
    }
  };
  const onPointerUp = e => {
    ptrs.current.delete(e.pointerId);
    const g = gesto.current;
    if (ptrs.current.size > 0) {
      // Quedó un dedo tras pellizcar: sigue como arrastre desde ahí
      const [p] = [...ptrs.current.values()];
      gesto.current = { tipo: "pan", x0: p.x, y0: p.y, v0: viewRef.current, movio: true };
      return;
    }
    gesto.current = null;
    if (!g || g.tipo !== "pan") return;
    const dx = e.clientX - g.x0;
    // Sin zoom: deslizar a los lados cambia de foto
    if (g.movio && g.v0.s <= 1.01 && fotos.length > 1 && Math.abs(dx) > 60) { dx < 0 ? next() : prev(); return; }
    if (g.movio) return;
    // Toque / clic sin moverse
    const ahora = Date.now();
    if (ahora - ultimoTap.current < 300) {
      ultimoTap.current = 0;
      if (viewRef.current.s > 1.01) reset();
      else { const [px, py] = centroRel(e.clientX, e.clientY); zoomEn(2.5, px, py); }
      return;
    }
    ultimoTap.current = ahora;
    // Un toque en el fondo (fuera de la foto) y sin zoom: cerrar
    if (!g.sobreImg && viewRef.current.s <= 1.01) {
      setTimeout(() => { if (ultimoTap.current === ahora) onClose(); }, 300);
    }
  };

  const [bajando, setBajando] = useState(false);
  const download = async () => {
    const src = fotos[idx];
    if (!src) return;
    setBajando(true);
    try {
      // fetch→blob funciona igual con data URLs y con URLs http, y es fiable
      // incluso con imágenes grandes (a.href con base64 gigante puede fallar).
      const res = await fetch(src);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "foto-"+(idx+1)+".jpg";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(()=>URL.revokeObjectURL(url), 1500);
    } catch {
      const a = document.createElement("a");
      a.href = src; a.download = "foto-"+(idx+1)+".jpg"; a.click();
    } finally {
      setBajando(false);
    }
  };

  const btn = "w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all";
  return (
    <motion.div
      className="fixed inset-0 z-[999] flex items-center justify-center select-none"
      style={{background:"rgba(0,0,0,0.95)"}}
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    >
      {/* Área de la foto: aquí viven todos los gestos */}
      <div ref={areaRef} className="absolute inset-0 flex items-center justify-center overflow-hidden"
        style={{touchAction:"none", cursor: zoomed ? (gesto.current ? "grabbing" : "grab") : "zoom-in"}}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <motion.img
          key={idx}
          src={fotos[idx]}
          alt=""
          draggable={false}
          initial={{opacity:0}}
          animate={{opacity:1}}
          transition={{duration:0.15}}
          className="max-w-[90vw] max-h-[78vh] rounded-2xl object-contain shadow-2xl"
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`,
            transition: gesto.current ? "none" : "transform 0.15s ease-out",
            borderRadius: zoomed ? 0 : undefined,
          }}
        />
      </div>

      {/* Botones top */}
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        <button onClick={download} disabled={bajando} title="Descargar en calidad completa" className={btn+" text-lg disabled:opacity-50"}>{bajando?"…":"⬇"}</button>
        <button onClick={onClose} title="Cerrar (ESC)" className={btn+" text-xl"}>✕</button>
      </div>

      {/* Contador */}
      {fotos.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 text-white text-sm font-bold z-10 pointer-events-none">
          {idx+1} / {fotos.length}
        </div>
      )}

      {/* Flechas */}
      {fotos.length > 1 && (
        <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl flex items-center justify-center transition-all z-10">‹</button>
      )}
      {fotos.length > 1 && (
        <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl flex items-center justify-center transition-all z-10">›</button>
      )}

      {/* Controles de zoom + miniaturas */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10">
        <div className="flex items-center gap-1 px-2 py-1.5 rounded-full bg-black/60 border border-white/10">
          <button onClick={()=>zoomEn(view.s/1.4)} disabled={!zoomed} title="Alejar (−)" className="w-9 h-9 rounded-full text-white text-xl font-black hover:bg-white/15 disabled:opacity-30">−</button>
          <button onClick={reset} title="Tamaño normal (0)" className="min-w-[56px] h-9 px-2 rounded-full text-white text-xs font-black hover:bg-white/15 tabular-nums">{Math.round(view.s*100)}%</button>
          <button onClick={()=>zoomEn(view.s*1.4)} disabled={view.s>=MAX_Z} title="Acercar (+)" className="w-9 h-9 rounded-full text-white text-xl font-black hover:bg-white/15 disabled:opacity-30">+</button>
        </div>
        {!zoomed && (
          <div className="text-white/40 text-[11px] font-medium text-center px-4">Doble toque, pellizca o usa la rueda para hacer zoom</div>
        )}
        {fotos.length > 1 && !zoomed && (
          <div className="flex gap-2">
            {fotos.map((src,i) => (
              <button key={i} onClick={()=>setIdx(i)}
                className={"w-12 h-12 rounded-xl overflow-hidden border-2 transition-all "+(i===idx?"border-white scale-110":"border-white/20 opacity-50 hover:opacity-80")}>
                <img src={src} alt="" className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}


// ── CARTERA PAGO MODAL (se muestra al entregar un pedido) ─────────
// ── CIERRE DE COBRO ───────────────────────────────────────────────
// Se abre solo cuando marcas un pedido como NOT APPROVED / cancelado /
// desactivado. Antes, ese pedido se archivaba pero su plata seguía viva:
// la deuda se calcula desde el PRECIO del pedido, así que un pedido
// rechazado seguía saliendo en la fía para siempre y nadie lo veía.
//
// Esta pantalla muestra la verdad del dinero de ESE pedido (juntando precio,
// cartera y pagos reales) y te deja terminar el caso aquí mismo:
//   · No debía nada / no se cobra  → sale de deudas (queda el motivo, se puede reabrir)
//   · Me pagó                       → registras el pago
//   · Sí debe                       → se queda en la cartera, a propósito
function CierreCobroModal({ order, onClose, onDatosCambiados }) {
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('Zelle');
  const [nuevoPrecio, setNuevoPrecio] = useState('');
  const [vista, setVista] = useState('resumen'); // resumen | precio | anular | cobrar

  const cargar = useCallback(() => {
    setCargando(true); setError(false);
    apiFetch('/api/pedidos/' + order.id + '/resumen-cobro')
      .then(d => { if (d && !d.error) setResumen(d); else setError(true); })
      .catch(() => setError(true))
      .finally(() => setCargando(false));
  }, [order.id]);
  useEffect(() => { cargar(); }, [cargar]);

  const fmt = n => '$' + (parseFloat(n) || 0).toFixed(2);
  const pc = PC[order.plataforma] || PC['OTROS'];
  const sc = SC[order.estado] || {};

  const anular = async () => {
    setGuardando(true);
    const r = await apiFetch('/api/pedidos/' + order.id + '/cerrar-cobro', {
      method: 'POST',
      body: JSON.stringify({ motivo: motivo.trim() || (order.estado + ' — no se cobra') })
    }).catch(() => null);
    setGuardando(false);
    if (!r?.ok) { alert('No se pudo cerrar el cobro. Revisa la conexión e intenta de nuevo.'); return; }
    setResumen(r.resumen); setVista('resumen');
    onDatosCambiados && onDatosCambiados();
  };

  const reabrir = async () => {
    setGuardando(true);
    const r = await apiFetch('/api/pedidos/' + order.id + '/reabrir-cobro', { method: 'POST' }).catch(() => null);
    setGuardando(false);
    if (!r?.ok) { alert('No se pudo reabrir el cobro.'); return; }
    setResumen(r.resumen);
    onDatosCambiados && onDatosCambiados();
  };

  // Registrar un pago. Sigue el MISMO camino que el resto de la app
  // (cartera + rentas) para no inventar una tercera forma de guardar plata.
  const cobrar = async () => {
    const n = parseFloat(monto);
    if (!n || n <= 0) return;
    setGuardando(true);
    try {
      if (resumen?.carteraId) {
        await apiFetch('/api/cartera/' + resumen.carteraId + '/abonar', {
          method: 'PUT', body: JSON.stringify({ abono: n, metodo })
        });
      } else {
        await apiFetch('/api/cartera', {
          method: 'POST', body: JSON.stringify({
            pedidoId: order.id, cliente: order.cliente, plataforma: order.plataforma,
            montoTotal: resumen?.total > 0 ? resumen.total : n,
            montoPagado: n, metodo, notas: 'Cobrado al cerrar: ' + order.estado
          })
        });
      }
      await apiFetch('/api/pedidos/' + order.id + '/rentas', {
        method: 'POST', body: JSON.stringify({
          monto: n, metodo, fechaPago: new Date().toISOString().split('T')[0],
          notas: 'Pago al cerrar pedido (' + order.estado + ')', proximoPago: ''
        })
      });
    } catch {
      setGuardando(false);
      alert('No se pudo registrar el pago. Revisa la conexión e intenta de nuevo.');
      return;
    }
    setGuardando(false);
    setMonto(''); setVista('resumen');
    cargar();
    onDatosCambiados && onDatosCambiados();
  };

  // Cambiar lo que se le cobra. Un pedido que no se aprueba casi nunca vale lo
  // mismo que uno entregado: el precio guardado es el del trabajo completo.
  // Usa el endpoint que YA sincroniza precio + cartera + estado de pago, para
  // no dejar el monto viejo escondido en otra tabla.
  const guardarPrecio = async () => {
    const n = parseFloat(nuevoPrecio);
    if (!(n > 0)) return;
    setGuardando(true);
    const r = await apiFetch('/api/pedidos/' + order.id + '/precio', {
      method: 'PUT', body: JSON.stringify({ precio: n })
    }).catch(() => null);
    setGuardando(false);
    if (!r?.ok) { alert(r?.error || 'No se pudo cambiar el precio. Revisa la conexión.'); return; }
    setVista('resumen');
    cargar();
    onDatosCambiados && onDatosCambiados();
  };

  const saldo = resumen?.saldo ?? 0;
  const debe = !!resumen && !resumen.cobroCerrado && resumen.total > 0 && saldo > 0.01;
  const sinPrecio = !!resumen && resumen.total <= 0;

  return (
    <motion.div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{background:'rgba(0,0,0,0.88)'}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div className="w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] shadow-2xl overflow-y-auto"
        style={{maxHeight:'92dvh'}}
        initial={{y:100,opacity:0}} animate={{y:0,opacity:1}} exit={{y:100,opacity:0}} transition={{type:'spring',damping:22}}>

        {/* Header */}
        <div className="px-5 py-5" style={{background: pc.color === "#1d1d1d" ? "#0f172a" : pc.color+"22", borderBottom:`3px solid ${pc.color}`}}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-black uppercase tracking-widest text-slate-500">Revisar antes de archivar</div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center text-slate-600 font-bold hover:bg-black/20">✕</button>
          </div>
          <div className="font-black text-2xl text-slate-900 leading-tight">{order.cliente}</div>
          <div className="flex items-center gap-2 mt-1.5">
            <span className={"px-2.5 py-1 rounded-full text-xs font-black "+(sc.bg||'bg-slate-200')+" "+(sc.text||'text-slate-700')}>{sc.emoji} {order.estado}</span>
            <span className="text-xs text-slate-500 font-bold">{pc.emoji} {order.plataforma}</span>
          </div>
        </div>

        {cargando && (
          <div className="p-8 space-y-3">
            <div className="h-4 rounded-full bg-slate-100 animate-pulse"/>
            <div className="h-16 rounded-2xl bg-slate-100 animate-pulse"/>
            <div className="h-10 rounded-xl bg-slate-100 animate-pulse"/>
          </div>
        )}

        {!cargando && error && (
          <div className="p-6 text-center space-y-3">
            <div className="text-3xl">📡</div>
            <div className="text-slate-600 text-sm font-bold">No se pudo leer el estado de cobro de este pedido.</div>
            <div className="text-slate-400 text-xs">El pedido ya cambió de estado y quedó archivado. Solo no pudimos revisar la plata ahora.</div>
            <button onClick={cargar} className="w-full py-3 rounded-xl bg-slate-100 text-slate-600 font-black text-sm active:scale-95 transition-all">🔄 Reintentar</button>
            <button onClick={onClose} className="w-full py-2.5 rounded-xl text-slate-400 font-bold text-sm">Cerrar</button>
          </div>
        )}

        {!cargando && !error && resumen && (
          <div className="p-5 space-y-4">

            {/* ── El dinero de este pedido ── */}
            <div className="rounded-2xl border-2 p-4"
              style={{borderColor: resumen.cobroCerrado ? '#cbd5e1' : debe ? '#fecaca' : '#a7f3d0',
                      background: resumen.cobroCerrado ? '#f8fafc' : debe ? '#fef2f2' : '#ecfdf5'}}>
              {sinPrecio ? (
                <div className="text-center space-y-1">
                  <div className="text-2xl">🏷️</div>
                  <div className="font-black text-slate-700">Este pedido no tiene precio</div>
                  <div className="text-xs text-slate-500">Nunca se le puso monto, así que no genera deuda. Puedes archivarlo tranquilo.</div>
                </div>
              ) : (<>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-lg font-black text-slate-800">{fmt(resumen.total)}</div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase">Se le cobró</div>
                  </div>
                  <div>
                    <div className="text-lg font-black text-emerald-600">{fmt(resumen.pagado)}</div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase">Ya pagó</div>
                  </div>
                  <div>
                    <div className="text-lg font-black" style={{color: debe ? '#dc2626' : '#059669'}}>{fmt(Math.max(0,saldo))}</div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase">Debe</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-black/5 text-center">
                  {resumen.cobroCerrado ? (
                    <div className="text-xs font-black text-slate-500">
                      🚫 Cobro cerrado — fuera de la cartera
                      {resumen.cobroCerradoMotivo && <div className="font-medium text-slate-400 mt-0.5">"{resumen.cobroCerradoMotivo}"</div>}
                      {resumen.cobroCerradoPor && <div className="font-medium text-slate-300 mt-0.5">Por {resumen.cobroCerradoPor}</div>}
                    </div>
                  ) : debe ? (
                    <div className="text-xs font-black text-red-600">🚨 Este pedido sigue contando como deuda en tu cartera</div>
                  ) : (
                    <div className="text-xs font-black text-emerald-700">✅ No queda nada por cobrar</div>
                  )}
                </div>
                {resumen.numPagos > 0 && (
                  <div className="mt-2 text-center text-[10px] text-slate-400 font-bold">
                    {resumen.numPagos} pago{resumen.numPagos!==1?'s':''} registrado{resumen.numPagos!==1?'s':''}
                    {resumen.ultimoPago ? ' · último el ' + resumen.ultimoPago : ''}
                  </div>
                )}
              </>)}
            </div>

            {/* ── Ya cerrado: solo se puede deshacer ── */}
            {resumen.cobroCerrado && (
              <button onClick={reabrir} disabled={guardando}
                className="w-full py-3 rounded-xl bg-slate-100 text-slate-600 font-black text-sm active:scale-95 transition-all disabled:opacity-40">
                {guardando ? 'Un momento...' : '↩️ Me equivoqué — volver a ponerlo en deudas'}
              </button>
            )}

            {/* ── Decisión: solo si de verdad hay algo que decidir ── */}
            {!resumen.cobroCerrado && debe && vista === 'resumen' && (
              <div className="space-y-2">
                <div className="text-xs font-black text-slate-400 uppercase tracking-wide text-center">¿Cuánto le vas a cobrar?</div>
                <button onClick={()=>{ setNuevoPrecio(String(resumen.total)); setVista('precio'); }}
                  className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-black text-sm active:scale-95 transition-all">
                  ✏️ Cambiar el cobro — ahora dice {fmt(resumen.total)}
                </button>
                <button onClick={()=>setVista('anular')}
                  className="w-full py-3.5 rounded-xl bg-slate-900 text-white font-black text-sm active:scale-95 transition-all">
                  🚫 No debía nada — quitar de deudas
                </button>
                <button onClick={()=>setVista('cobrar')}
                  className="w-full py-3.5 rounded-xl bg-emerald-600 text-white font-black text-sm active:scale-95 transition-all">
                  💰 Sí me pagó — registrar {fmt(saldo)}
                </button>
                <button onClick={onClose}
                  className="w-full py-3 rounded-xl bg-amber-50 text-amber-700 font-bold text-sm active:scale-95 transition-all">
                  ⏳ Sí me debe — dejarlo en la cartera y archivar
                </button>
              </div>
            )}

            {/* ── Cambiar lo que se le cobra ── */}
            {!resumen.cobroCerrado && vista === 'precio' && (() => {
              const n = parseFloat(nuevoPrecio);
              const valido = n > 0;
              const nuevoSaldo = valido ? Math.round((n - resumen.pagado) * 100) / 100 : null;
              return (
                <div className="space-y-3">
                  <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800 leading-relaxed">
                    Escribe lo que <b>de verdad</b> se le cobra a este pedido. Se actualiza en todos lados a la vez: el precio, la cartera y lo que debe.
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Nuevo cobro</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-lg">$</span>
                      <input autoFocus type="number" inputMode="decimal" value={nuevoPrecio}
                        onChange={e=>setNuevoPrecio(e.target.value)}
                        className="w-full rounded-xl border-2 border-slate-200 pl-8 pr-3 py-3.5 text-lg font-black outline-none focus:border-blue-400"/>
                    </div>
                  </div>

                  {/* Atajo para el caso más común: se queda con lo que ya pagó */}
                  {resumen.pagado > 0 && (
                    <button onClick={()=>setNuevoPrecio(String(resumen.pagado))}
                      className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-600 font-black text-xs active:scale-95 transition-all">
                      Cobrarle solo lo que ya pagó ({fmt(resumen.pagado)}) → queda en cero
                    </button>
                  )}

                  {/* Qué va a pasar, ANTES de guardar */}
                  {valido && (
                    <div className="rounded-xl px-4 py-3 text-center"
                      style={{background: nuevoSaldo > 0.01 ? '#fef2f2' : nuevoSaldo < -0.01 ? '#fffbeb' : '#ecfdf5',
                              border: '1px solid ' + (nuevoSaldo > 0.01 ? '#fecaca' : nuevoSaldo < -0.01 ? '#fde68a' : '#a7f3d0')}}>
                      {nuevoSaldo > 0.01 ? (
                        <div className="text-xs font-black text-red-600">Quedaría debiendo {fmt(nuevoSaldo)}</div>
                      ) : nuevoSaldo < -0.01 ? (
                        <div className="text-xs font-black text-amber-700">
                          ⚠️ Te pagó {fmt(resumen.pagado)}, más de lo que le vas a cobrar.
                          <div className="font-medium text-amber-600 mt-0.5">Le quedarías debiendo {fmt(Math.abs(nuevoSaldo))} a él. La app no maneja devoluciones: eso lo arreglas por fuera.</div>
                        </div>
                      ) : (
                        <div className="text-xs font-black text-emerald-700">✅ Queda saldado — no debe nada</div>
                      )}
                    </div>
                  )}

                  <button onClick={guardarPrecio} disabled={guardando || !valido}
                    className="w-full py-4 rounded-xl bg-blue-600 text-white font-black text-base active:scale-95 transition-all disabled:opacity-40">
                    {guardando ? 'Guardando...' : '💾 Guardar el nuevo cobro'}
                  </button>
                  <div className="text-center text-[10px] text-slate-400 font-bold leading-relaxed">
                    ¿No le vas a cobrar nada? El cobro no puede ser $0.<br/>Usa <b>"No debía nada — quitar de deudas"</b> en la pantalla anterior.
                  </div>
                  <button onClick={()=>setVista('resumen')} className="w-full py-2.5 rounded-xl text-slate-400 font-bold text-sm">← Volver</button>
                </div>
              );
            })()}

            {/* ── Anular la deuda ── */}
            {!resumen.cobroCerrado && vista === 'anular' && (
              <div className="space-y-3">
                <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600 leading-relaxed">
                  Sale de la cartera y deja de contar como plata por cobrar. <b>No se borra nada</b>: queda guardado con el motivo, y puedes revertirlo desde aquí mismo.
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Motivo</label>
                  <input autoFocus value={motivo} onChange={e=>setMotivo(e.target.value)}
                    placeholder={order.estado + ' — no se cobra'}
                    className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none focus:border-slate-400"/>
                </div>
                <button onClick={anular} disabled={guardando}
                  className="w-full py-4 rounded-xl bg-slate-900 text-white font-black text-base active:scale-95 transition-all disabled:opacity-40">
                  {guardando ? 'Guardando...' : '🚫 Quitar de deudas y archivar'}
                </button>
                <button onClick={()=>setVista('resumen')} className="w-full py-2.5 rounded-xl text-slate-400 font-bold text-sm">← Volver</button>
              </div>
            )}

            {/* ── Registrar el pago ── */}
            {!resumen.cobroCerrado && vista === 'cobrar' && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">¿Cuánto pagó?</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                    <input autoFocus type="number" inputMode="decimal" value={monto} onChange={e=>setMonto(e.target.value)}
                      placeholder={Math.max(0,saldo).toFixed(2)}
                      className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-3 text-sm font-bold outline-none focus:border-emerald-400"/>
                  </div>
                  <button onClick={()=>setMonto(Math.max(0,saldo).toFixed(2))}
                    className="mt-1.5 text-xs font-black text-emerald-600">Pagó todo ({fmt(saldo)})</button>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Método</label>
                  <select value={metodo} onChange={e=>setMetodo(e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none">
                    {['Zelle','Cash','CashApp','PayPal','Venmo','Otro'].map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <button onClick={cobrar} disabled={guardando || !monto}
                  className="w-full py-4 rounded-xl bg-emerald-600 text-white font-black text-base active:scale-95 transition-all disabled:opacity-40">
                  {guardando ? 'Guardando...' : '💰 Registrar pago'}
                </button>
                <button onClick={()=>setVista('resumen')} className="w-full py-2.5 rounded-xl text-slate-400 font-bold text-sm">← Volver</button>
              </div>
            )}

            {/* ── Nada que decidir: se archiva y ya ── */}
            {vista === 'resumen' && (resumen.cobroCerrado || !debe) && (
              <div className="space-y-2">
                {/* Sigue editable: si te equivocaste de monto, aquí lo corriges.
                    Sin esto, en cuanto el saldo llega a cero perdías la salida. */}
                {!resumen.cobroCerrado && !sinPrecio && (
                  <button onClick={()=>{ setNuevoPrecio(String(resumen.total)); setVista('precio'); }}
                    className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-600 font-black text-xs active:scale-95 transition-all">
                    ✏️ Cambiar el cobro ({fmt(resumen.total)})
                  </button>
                )}
                <button onClick={onClose}
                  className="w-full py-4 rounded-xl bg-slate-900 text-white font-black text-base active:scale-95 transition-all">
                  📁 Archivar y guardar
                </button>
              </div>
            )}

            <div className="text-center text-[10px] text-slate-300 font-bold">
              El pedido ya quedó archivado. Puedes reactivarlo desde Archivados.
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function CarteraPagoModal({ order, onClose, onSaldado }) {
  const [tab, setTab] = useState('cobrar');          // 'cobrar' | 'deuda'
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [monto, setMonto] = useState('');            // "Pagó todo": dinero recibido AHORA
  const [totalDeuda, setTotalDeuda] = useState('');  // "Queda debiendo": total que debe
  const [anticipo, setAnticipo] = useState('');
  const [metodo, setMetodo] = useState('Zelle');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  // Fuente ÚNICA de la verdad del dinero de este pedido
  const cargar = useCallback(() => {
    setCargando(true);
    apiFetch('/api/pedidos/' + order.id + '/resumen-cobro')
      .then(d => {
        if (d && !d.error) {
          setResumen(d);
          const falta = Math.max(0, parseFloat(d.saldo) || 0);
          // Rellenamos: en "pagó todo" proponemos lo que falta; si no hay saldo
          // pero sí precio, proponemos el precio.
          setMonto(falta > 0 ? String(falta) : (parseFloat(d.total) > 0 ? String(d.total) : ''));
          setTotalDeuda(parseFloat(d.total) > 0 ? String(d.total) : '');
        }
      })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, [order.id]);
  useEffect(() => { cargar(); }, [cargar]);

  const total  = parseFloat(resumen?.total)  || 0;
  const yaPago = parseFloat(resumen?.pagado) || 0;
  const falta  = Math.max(0, parseFloat(resumen?.saldo) || 0);
  const fmt = n => '$' + (parseFloat(n) || 0).toFixed(2);
  const pc = PC[order.plataforma] || PC['OTROS'];
  const yaSaldado = !!resumen && total > 0 && falta <= 0.01;

  // "Pagó todo": el cliente liquida lo que falta. Deja la cartera saldada y
  // registra SOLO el dinero recibido ahora (no cuenta doble abonos previos).
  const handlePagoCompleto = async () => {
    const recibido = parseFloat(monto) || 0;
    if (recibido <= 0) return;
    const totalPrecio = total > 0 ? total : recibido;
    setSaving(true);
    try {
      await apiFetch('/api/cartera', { method:'POST', body: JSON.stringify({
        pedidoId: order.id, cliente: order.cliente, plataforma: order.plataforma,
        montoTotal: totalPrecio, montoPagado: totalPrecio,
        metodo, notas: notas || 'Pago completo al entregar', estado: 'saldado'
      })});
      await apiFetch('/api/pedidos/'+order.id+'/rentas', { method:'POST', body: JSON.stringify({
        monto: recibido, metodo, fechaPago: new Date().toISOString().split('T')[0],
        notas: notas || 'Pago completo al entregar', proximoPago: ''
      })});
    } catch {
      setSaving(false);
      alert('No se pudo registrar el pago. Revisa la conexión e intenta de nuevo.');
      return;
    }
    setSaving(false);
    onSaldado && onSaldado();
    onClose();
  };

  // "Queda debiendo": fija el total que debe y, si dio anticipo, lo registra.
  const handleDejarDeuda = async () => {
    const totalP = parseFloat(totalDeuda) || 0;
    if (totalP <= 0) return;
    const ant = parseFloat(anticipo) || 0;
    const pagadoTotal = Math.min(yaPago + ant, totalP); // suma lo ya pagado antes
    setSaving(true);
    try {
      await apiFetch('/api/cartera', { method:'POST', body: JSON.stringify({
        pedidoId: order.id, cliente: order.cliente, plataforma: order.plataforma,
        montoTotal: totalP, montoPagado: pagadoTotal,
        metodo, notas: notas || 'Deuda al entregar',
        estado: pagadoTotal >= totalP ? 'saldado' : 'pendiente'
      })});
      if (ant > 0) {
        await apiFetch('/api/pedidos/'+order.id+'/rentas', { method:'POST', body: JSON.stringify({
          monto: ant, metodo, fechaPago: new Date().toISOString().split('T')[0],
          notas: 'Anticipo al entregar', proximoPago: ''
        })});
      }
    } catch {
      setSaving(false);
      alert('No se pudo registrar la deuda. Revisa la conexión e intenta de nuevo.');
      return;
    }
    setSaving(false);
    if (pagadoTotal >= totalP) onSaldado && onSaldado();
    onClose();
  };

  return (
    <motion.div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{background:'rgba(0,0,0,0.85)'}}
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={e=>{if(e.target===e.currentTarget) onClose();}}>
      <motion.div className="w-full sm:max-w-md bg-white sm:rounded-[28px] rounded-t-[28px] shadow-2xl overflow-y-auto"
        style={{maxHeight:'92dvh'}}
        initial={{y:100,opacity:0}} animate={{y:0,opacity:1}} exit={{y:100,opacity:0}} transition={{type:'spring',damping:22}}>

        {/* Header */}
        <div className="px-5 py-5" style={{background: pc.color === "#1d1d1d" ? "#0f172a" : pc.color+"22", borderBottom:`3px solid ${pc.color}`}}>
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-black uppercase tracking-widest" style={{color: pc.color}}>💳 COBRAR PEDIDO ENTREGADO</div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center text-slate-600 font-bold hover:bg-black/20">✕</button>
          </div>
          <div className="font-black text-2xl text-slate-900">{order.cliente}</div>
          <div className="text-sm text-slate-500">{pc.emoji} {order.plataforma}</div>
        </div>

        {/* Cargando */}
        {cargando && (
          <div className="p-6 space-y-3">
            <div className="h-16 rounded-2xl bg-slate-100 animate-pulse"/>
            <div className="h-10 rounded-xl bg-slate-100 animate-pulse"/>
          </div>
        )}

        {/* RESUMEN DE DINERO — lo que faltaba: precio, ya pagó y falta */}
        {!cargando && (
          <div className="px-5 pt-4">
            <div className="grid grid-cols-3 gap-2 rounded-2xl bg-slate-50 border border-slate-200 p-3 text-center">
              <div>
                <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">Precio</div>
                <div className="text-lg font-black text-slate-900">{total > 0 ? fmt(total) : '—'}</div>
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">Ya pagó</div>
                <div className="text-lg font-black text-emerald-600">{fmt(yaPago)}</div>
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">Falta</div>
                <div className={"text-lg font-black "+(falta>0.01?'text-red-600':'text-emerald-600')}>{total>0?fmt(falta):'—'}</div>
              </div>
            </div>
            {total <= 0 && (
              <div className="mt-2 text-[11px] text-amber-600 font-bold text-center">
                ⚠️ Este pedido no tiene precio guardado. Escribe abajo cuánto se cobra.
              </div>
            )}
          </div>
        )}

        {/* Ya está pagado completo */}
        {!cargando && yaSaldado && (
          <div className="p-5">
            <div className="rounded-xl bg-emerald-50 border-2 border-emerald-200 p-4 text-center">
              <div className="text-4xl mb-2">✅</div>
              <div className="font-black text-emerald-700">Ya está pagado completo</div>
              <div className="text-sm text-emerald-600 mt-1">{fmt(total)} cobrado · sin saldo pendiente</div>
            </div>
            <button onClick={onClose} className="w-full mt-3 py-3 rounded-xl bg-slate-900 text-white font-black text-sm active:scale-95 transition-all">Cerrar</button>
          </div>
        )}

        {/* Cobro */}
        {!cargando && !yaSaldado && (
          <>
            <div className="flex border-b border-slate-100 mt-4">
              {[['cobrar','✅ Pagó todo'],['deuda','⏳ Queda debiendo']].map(([t,l])=>(
                <button key={t} onClick={()=>setTab(t)} className={"flex-1 py-3 text-sm font-black transition-all "+(tab===t?'text-slate-900 border-b-2 border-slate-900':'text-slate-400')}>
                  {l}
                </button>
              ))}
            </div>

            <div className="p-5 space-y-3">
              {tab === 'cobrar' && (<>
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 font-medium">
                  ✅ El cliente pagó lo que faltaba. Ya llené el monto ({fmt(falta > 0 ? falta : total)}); ajústalo si hace falta y cierra el caso.
                </div>
                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Monto recibido ahora *</label>
                  <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                    <input type="number" className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-3 text-sm outline-none focus:border-emerald-400 font-bold" placeholder="0.00" value={monto} onChange={e=>setMonto(e.target.value)}/></div></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Método</label>
                    <select className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" value={metodo} onChange={e=>setMetodo(e.target.value)}>
                      {['Zelle','Cash','CashApp','PayPal','Venmo','Otro'].map(m=><option key={m}>{m}</option>)}</select></div>
                  <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Nota</label>
                    <input className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" placeholder="Opcional" value={notas} onChange={e=>setNotas(e.target.value)}/></div>
                </div>
                <button onClick={handlePagoCompleto} disabled={saving||!monto} className="w-full py-4 rounded-xl bg-emerald-600 text-white font-black text-base disabled:opacity-40 active:scale-95 transition-all shadow-lg">
                  {saving?'Guardando...':'✅ Pago completo — Cerrar caso'}
                </button>
                <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-500 font-bold text-sm active:scale-95 transition-all">
                  Saltar (registrar después)
                </button>
              </>)}

              {tab === 'deuda' && (<>
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 font-medium">
                  ⏳ El cliente queda debiendo. Queda registrado en tu cartera hasta que se salde.
                </div>
                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Monto total que debe *</label>
                  <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                    <input type="number" className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-3 text-sm outline-none focus:border-amber-400 font-bold" placeholder="0.00" value={totalDeuda} onChange={e=>setTotalDeuda(e.target.value)}/></div></div>
                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">¿Pagó algo de anticipo? (opcional)</label>
                  <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                    <input type="number" className="w-full rounded-xl border-2 border-slate-200 pl-7 pr-3 py-3 text-sm outline-none focus:border-amber-400 font-bold" placeholder="0.00" value={anticipo} onChange={e=>setAnticipo(e.target.value)}/></div></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Método pref.</label>
                    <select className="w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-sm outline-none" value={metodo} onChange={e=>setMetodo(e.target.value)}>
                      {['Zelle','Cash','CashApp','PayPal','Venmo','Otro'].map(m=><option key={m}>{m}</option>)}</select></div>
                </div>
                <button onClick={handleDejarDeuda} disabled={saving||!totalDeuda} className="w-full py-4 rounded-xl bg-amber-500 text-white font-black text-base disabled:opacity-40 active:scale-95 transition-all shadow-lg">
                  {saving?'Guardando...':'📋 Registrar deuda en cartera'}
                </button>
                <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-500 font-bold text-sm active:scale-95 transition-all">
                  Saltar (registrar después)
                </button>
              </>)}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── INVENTARIO (cuentas / cupos) ──────────────────────────────────
function InventarioModal({ onClose, isAdmin }) {
  const INV_TYPES=["CUPO","ESPERAR","GARANTIA","PUNTO","RENTA","VENTA"];
  const INV_EMAILS=["ROYALFLEX.APP","CAIN333","ROYALAMAZON","TIGRILLO766","PURELYMAIL","OUTLOOK","GMAIL"];
  const INV_CREDDEB=["NO PAGO","CREDITO","DEBITO"];
  const INV_DISPS=["COLA DE ESPERA","START","APLICADO","DISPONIBLE","RESERVADO","FIADO","VENDIDO","DESACTIVADA","SUSPENDIDA"];
  const INV_ORIGENES=["Propio","Comprado"];
  const INV_STATES=["ALABAMA","ALASKA","ARIZONA","ARKANSAS","CALIFORNIA","COLORADO","CONNECTICUT","DELAWARE","DISTRICT OF COLUMBIA","FLORIDA","GEORGIA","HAWAII","IDAHO","ILLINOIS","INDIANA","IOWA","KANSAS","KENTUCKY","LOUISIANA","MAINE","MARYLAND","MASSACHUSETTS","MICHIGAN","MINNESOTA","MISSISSIPPI","MISSOURI","MONTANA","NEBRASKA","NEVADA","NEW HAMPSHIRE","NEW JERSEY","NEW MEXICO","NEW YORK","NORTH CAROLINA","NORTH DAKOTA","OHIO","OKLAHOMA","OREGON","PENNSYLVANIA","RHODE ISLAND","SOUTH CAROLINA","SOUTH DAKOTA","TENNESSEE","TEXAS","UTAH","VERMONT","VIRGINIA","WASHINGTON","WEST VIRGINIA","WISCONSIN","WYOMING"];
  const DEF={fecha:new Date().toISOString().slice(0,10),mail:"",zip:"",ciudad:"",estadoCupo:"",nombreCupo:"",contrasena:"Royal777",cuentaCorreo:"ROYALFLEX.APP",platform:"AMAZON",type:"CUPO",credDeb:"NO PAGO",precio:"",disp:"COLA DE ESPERA",notas:"",origen:"Propio",proveedor:"",costo:"",asignadoA:"",dispAt:""};
  // Fases que marcan "fecha disponible": al ENTRAR a una de estas se pone la fecha de hoy
  const DISP_FASES=["DISPONIBLE","APLICADO"];
  const hoyLocal=()=>new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD en hora local
  const stampDisp=(prevDisp,nextDisp,actual)=>(DISP_FASES.includes(nextDisp)&&prevDisp!==nextDisp)?hoyLocal():(actual||'');

  // ── VISTAS RÁPIDAS: cada una es un filtro de un toque sobre la fase (disp) ──
  const VISTAS=[
    {k:'',          l:'Todo',        e:'📋', test:()=>true},
    {k:'disp',      l:'Disponibles', e:'🟢', test:i=>i.disp==='DISPONIBLE'},
    {k:'proceso',   l:'En proceso',  e:'⏳', test:i=>['COLA DE ESPERA','START','APLICADO'].includes(i.disp)},
    {k:'apartadas', l:'Apartadas',   e:'🤝', test:i=>['RESERVADO','FIADO'].includes(i.disp)},
    {k:'vendidas',  l:'Vendidas',    e:'💵', test:i=>i.disp==='VENDIDO'},
    {k:'problemas', l:'Problemas',   e:'⛔', test:i=>['DESACTIVADA','SUSPENDIDA'].includes(i.disp)},
    {k:'stock',     l:'En stock',    e:'🛒', test:i=>i.origen==='Comprado'&&i.disp!=='VENDIDO'},
  ];
  // ── AGRUPAR POR: dimensiones para tabular ──
  const GRUPOS=[
    {k:'',           l:'Sin agrupar'},
    {k:'disp',       l:'Fase'},
    {k:'platform',   l:'Plataforma'},
    {k:'ciudad',     l:'Ciudad'},
    {k:'estadoCupo', l:'Estado'},
    {k:'origen',     l:'Origen'},
    {k:'type',       l:'Tipo'},
  ];
  // ── Columnas: label + clave para ordenar (sin clave = no ordena) ──
  const COLS=[
    {h:'Disponibilidad', k:'disp'},
    {h:'Origen',         k:'origen'},
    {h:'Asignado',       k:'asignadoA'},
    {h:'Fecha',          k:'fecha'},
    {h:'F. Disponible',  k:'dispAt'},
    {h:'Mail',           k:'mail'},
    {h:'Precio',         k:'precio'},
    {h:'Costo',          k:'costo'},
    {h:'Platform',       k:'platform'},
    {h:'Type',           k:'type'},
    {h:'ZIP',            k:'zip'},
    {h:'Ciudad',         k:'ciudad'},
    {h:'Estado',         k:'estadoCupo'},
    {h:'Nombre cupo',    k:'nombreCupo'},
    {h:'Contraseña',     k:''},
    {h:'Cuenta correo',  k:'cuentaCorreo'},
    {h:'Créd/Déb',       k:'credDeb'},
    {h:'Notas',          k:''},
    {h:'',               k:''},
  ];

  const [items,setItems]=useState([]);
  const [loading,setLoading]=useState(true);
  const [q,setQ]=useState("");
  const [fPlat,setFPlat]=useState("");
  const [fDisp,setFDisp]=useState("");
  const [fOrigen,setFOrigen]=useState("");
  const [fCiudad,setFCiudad]=useState("");
  const [fEstado,setFEstado]=useState("");
  const [fDominio,setFDominio]=useState("");
  const [vista,setVista]=useState("");                 // vista rápida activa
  const [groupBy,setGroupBy]=useState("");             // dimensión de agrupado
  const [sortKey,setSortKey]=useState("fecha");        // columna de orden
  const [sortDir,setSortDir]=useState("desc");         // asc | desc
  const [colapsados,setColapsados]=useState(()=>new Set()); // grupos cerrados
  const [form,setForm]=useState(null);
  const [saving,setSaving]=useState(false);
  const [zipMsg,setZipMsg]=useState("");
  const zipTimer=useRef(null);

  const load=()=>{ setLoading(true); apiFetch('/api/inventario').then(d=>setItems(Array.isArray(d)?d:[])).catch(()=>{}).finally(()=>setLoading(false)); };
  useEffect(()=>{ load(); },[]);

  const num=n=>Number(String(n==null?'':n).replace(/[^0-9.]/g,''))||0;
  const money=n=>'$'+num(n).toLocaleString('en-US');
  const dominio=m=>{ const s=String(m||''); const k=s.indexOf('@'); return k>=0?s.slice(k+1).toLowerCase():''; };

  const vistaTest=(VISTAS.find(v=>v.k===vista)||VISTAS[0]).test;

  // ── 1) FILTRAR (incluye la vista rápida) ──
  const filtered=items.filter(i=>{
    if(!vistaTest(i))return false;
    if(fPlat&&i.platform!==fPlat)return false;
    if(fDisp&&i.disp!==fDisp)return false;
    if(fOrigen&&i.origen!==fOrigen)return false;
    if(fCiudad&&(i.ciudad||'')!==fCiudad)return false;
    if(fEstado&&(i.estadoCupo||'')!==fEstado)return false;
    if(fDominio&&dominio(i.mail)!==fDominio)return false;
    if(q){const s=q.toLowerCase();const hay=[i.mail,i.ciudad,i.nombreCupo,i.estadoCupo,i.notas,i.zip,i.cuentaCorreo,i.proveedor,i.asignadoA].join(' ').toLowerCase();if(!hay.includes(s))return false;}
    return true;
  });

  // ── 2) ORDENAR ──
  const sortVal=i=>(sortKey==='precio'||sortKey==='costo')?num(i[sortKey]):String(i[sortKey]||'').toLowerCase();
  const sorted=[...filtered].sort((a,b)=>{
    const va=sortVal(a), vb=sortVal(b);
    if(va<vb)return sortDir==='asc'?-1:1;
    if(va>vb)return sortDir==='asc'?1:-1;
    return 0;
  });
  const toggleSort=k=>{ if(!k)return; if(sortKey===k){setSortDir(d=>d==='asc'?'desc':'asc');}else{setSortKey(k);setSortDir('asc');} };

  // ── 3) AGRUPAR (con subtotales) ──
  const secciones=(()=>{
    if(!groupBy)return[{key:null,rows:sorted}];
    const map=new Map();
    sorted.forEach(i=>{ const g=String(i[groupBy]||'—'); if(!map.has(g))map.set(g,[]); map.get(g).push(i); });
    return [...map.entries()].sort((a,b)=>a[0].localeCompare(b[0])).map(([key,rows])=>({key,rows}));
  })();
  const toggleGrupo=g=>setColapsados(prev=>{const n=new Set(prev); n.has(g)?n.delete(g):n.add(g); return n;});

  // Contadores de las vistas rápidas (sobre todo el inventario)
  const vistaCount=v=>items.filter(v.test).length;

  // Tarjetas resumen (informativas)
  const disp=items.filter(i=>i.disp==='DISPONIBLE');
  const enProceso=items.filter(i=>['COLA DE ESPERA','START','APLICADO'].includes(i.disp));
  const apartadas=items.filter(i=>['RESERVADO','FIADO'].includes(i.disp));
  const vend=items.filter(i=>i.disp==='VENDIDO');
  const problemas=items.filter(i=>['DESACTIVADA','SUSPENDIDA'].includes(i.disp));
  const valorDisp=disp.reduce((s,i)=>s+num(i.precio),0);
  const compStock=items.filter(i=>i.origen==='Comprado'&&i.disp!=='VENDIDO');
  const invertido=compStock.reduce((s,i)=>s+num(i.costo),0);

  const filtrosActivos=(q?1:0)+(fPlat?1:0)+(fDisp?1:0)+(fOrigen?1:0)+(fCiudad?1:0)+(fEstado?1:0)+(fDominio?1:0)+(vista?1:0);
  const limpiar=()=>{ setQ("");setFPlat("");setFDisp("");setFOrigen("");setFCiudad("");setFEstado("");setFDominio("");setVista(""); };

  const exportCSV=()=>{
    const cols=[['fecha','Fecha'],['dispAt','F. Disponible'],['mail','Mail'],['disp','Disponibilidad'],['origen','Origen'],['proveedor','Proveedor'],['asignadoA','Asignado'],['precio','Precio'],['costo','Costo'],['platform','Platform'],['type','Type'],['zip','ZIP'],['ciudad','Ciudad'],['estadoCupo','Estado'],['nombreCupo','Nombre cupo'],['cuentaCorreo','Cuenta correo'],['credDeb','Cred/Deb'],['notas','Notas']];
    const esc=v=>{ const s=String(v==null?'':v); return /[",\n]/.test(s)?'"'+s.replace(/"/g,'""')+'"':s; };
    const csv=cols.map(c=>c[1]).join(',')+'\n'+sorted.map(i=>cols.map(c=>esc(i[c[0]])).join(',')).join('\n');
    const blob=new Blob(['\ufeff'+csv],{type:'text/csv;charset=utf-8;'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a'); a.href=url; a.download='inventario.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const guardar=async()=>{
    if(!form.mail||!form.mail.trim()){alert('El mail es obligatorio');return;}
    setSaving(true);
    const editing=!!form.id;
    const orig=editing?items.find(x=>x.id===form.id):null;
    // Si la fase cambió a DISPONIBLE/APLICADO (o es nueva y ya entra así), marca la fecha de hoy.
    // Si el admin escribió la fecha a mano en el formulario, se respeta.
    const tocoFecha=orig?(form.dispAt||'')!==(orig.dispAt||''):!!form.dispAt;
    const body=tocoFecha?form:{...form,dispAt:stampDisp(orig?orig.disp:'',form.disp,form.dispAt)};
    const r=await apiFetch(editing?('/api/inventario/'+form.id):'/api/inventario',{method:editing?'PUT':'POST',body:JSON.stringify(body)}).catch(()=>null);
    setSaving(false);
    if(!r?.ok){alert(r?.error||'No se pudo guardar');return;}
    setForm(null); load();
  };
  const quitar=async(it)=>{ if(!window.confirm('¿Quitar del inventario '+it.mail+'?'))return; await apiFetch('/api/inventario/'+it.id,{method:'DELETE'}).catch(()=>{}); load(); };
  const cambiarDisp=async(it,val)=>{
    const patch={disp:val,dispAt:stampDisp(it.disp,val,it.dispAt)};
    // arranca el cronómetro de 72h SOLO al ENTRAR a START (no si ya estaba)
    if(val==='START' && it.disp!=='START') patch.startAt=new Date().toISOString();
    // sale de START → el cronómetro se apaga
    if(val!=='START') patch.startAt='';
    const upd={...it,...patch};
    setItems(prev=>prev.map(x=>x.id===it.id?upd:x));
    try{
      if(patch.startAt) localStorage.setItem('royal_start_'+it.id, patch.startAt);
      else localStorage.removeItem('royal_start_'+it.id);
    }catch{}
    await apiFetch('/api/inventario/'+it.id,{method:'PUT',body:JSON.stringify(upd)}).catch(()=>{});
  };
  const asignar=async(it)=>{ const c=window.prompt('¿A qué cliente/pedido asignas '+it.mail+'?',it.asignadoA||''); if(c===null)return; const nd=(c.trim()&&it.disp==='DISPONIBLE')?'RESERVADO':((!c.trim()&&it.disp==='RESERVADO')?'DISPONIBLE':it.disp); const upd={...it,asignadoA:c.trim(),disp:nd,dispAt:stampDisp(it.disp,nd,it.dispAt)}; setItems(prev=>prev.map(x=>x.id===it.id?upd:x)); await apiFetch('/api/inventario/'+it.id,{method:'PUT',body:JSON.stringify(upd)}).catch(()=>{}); };

  const onZip=(v)=>{
    const zip=v.replace(/[^0-9]/g,'').slice(0,5);
    setForm(f=>({...f,zip}));
    if(zipTimer.current) clearTimeout(zipTimer.current);
    if(zip.length!==5){setZipMsg('');return;}
    setZipMsg('buscando…');
    const aplicar=(ciudad,estado,abrev)=>{ setForm(f=>({...f,ciudad,estadoCupo:estado})); setZipMsg('✓ '+ciudad+(abrev?', '+abrev:'')); };
    zipTimer.current=setTimeout(()=>{
      apiFetch('/api/ziplookup/'+zip).then(d=>{
        if(d&&d.ok){ aplicar(d.ciudad,d.estado,d.abrev); return; }
        return fetch('https://api.zippopotam.us/us/'+zip).then(r=>r.ok?r.json():Promise.reject()).then(j=>{
          const p=j.places&&j.places[0]; if(!p) throw 0;
          aplicar((p['place name']||'').toUpperCase(),(p['state']||'').toUpperCase(),p['state abbreviation']||'');
        });
      }).catch(()=>setZipMsg('ZIP no encontrado — escríbelo a mano'));
    },300);
  };

  const ST={
    'COLA DE ESPERA':{c:'bg-slate-100 text-slate-600',e:'🕐'},
    'START':{c:'bg-orange-100 text-orange-700',e:'🔔'},
    'APLICADO':{c:'bg-indigo-100 text-indigo-700',e:'📩'},
    'DISPONIBLE':{c:'bg-emerald-100 text-emerald-700',e:'🟢'},
    'RESERVADO':{c:'bg-amber-100 text-amber-800',e:'🟡'},
    'FIADO':{c:'bg-cyan-100 text-cyan-800',e:'🤝'},
    'VENDIDO':{c:'bg-slate-200 text-slate-700',e:'💵'},
    'DESACTIVADA':{c:'bg-red-100 text-red-700',e:'⛔'},
    'SUSPENDIDA':{c:'bg-rose-100 text-rose-700',e:'⏸️'},
  };
  const pill=d=>(ST[d]||{c:'bg-slate-100 text-slate-600'}).c;
  const emo=d=>(ST[d]||{e:'•'}).e;
  const labelCls="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1 block";
  const inputCls="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-400";
  const Field=(label,key,ph,type)=>(<div><label className={labelCls}>{label}</label><input type={type||'text'} value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} placeholder={ph||''} className={inputCls}/></div>);
  const Sel=(label,key,opts)=>(<div><label className={labelCls}>{label}</label><select value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} className={inputCls}>{opts.map(o=><option key={o}>{o}</option>)}</select></div>);

  // Fila de datos (reutilizable dentro y fuera de grupos)
  const FilaInv=(i)=>(
    <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-3 py-2 whitespace-nowrap">
        <select disabled={!isAdmin} value={i.disp} onChange={e=>cambiarDisp(i,e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-bold">{INV_DISPS.map(d=><option key={d}>{d}</option>)}</select>
        <div className="mt-1"><span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black "+pill(i.disp)}>{emo(i.disp)} {i.disp}</span></div>
        {i.disp==='START' && <StartTimer item={i} onVolverEspera={()=>cambiarDisp(i,'COLA DE ESPERA')}/>}
      </td>
      <td className="px-3 py-2 whitespace-nowrap"><span className={"inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black "+(i.origen==='Comprado'?'bg-violet-100 text-violet-700':'bg-sky-100 text-sky-700')}>{i.origen==='Comprado'?'🛒 '+(i.proveedor||'Comprado'):'🏠 Propio'}</span></td>
      <td className="px-3 py-2 whitespace-nowrap">{i.asignadoA?<span className="text-[11px] font-black text-violet-700">👤 {i.asignadoA}</span>:<span className="text-slate-300">—</span>}</td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{i.fecha}</td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{i.dispAt?(()=>{
        const d=Math.floor((new Date(hoyLocal()+'T12:00')-new Date(i.dispAt+'T12:00'))/86400000);
        const activo=DISP_FASES.includes(i.disp);
        return <div><div className={"font-bold "+(i.disp==='DISPONIBLE'?'text-emerald-700':i.disp==='APLICADO'?'text-indigo-700':'text-slate-400')}>{i.dispAt}</div>{activo&&!isNaN(d)&&<div className="text-[10px] text-slate-400">{d<=0?'hoy':'hace '+d+'d'}</div>}</div>;
      })():<span className="text-slate-300">—</span>}</td>
      <td className="px-3 py-2 whitespace-nowrap font-bold">{i.mail}</td>
      <td className="px-3 py-2 whitespace-nowrap font-black text-emerald-700 tabular-nums">{i.precio?money(i.precio):'—'}</td>
      <td className="px-3 py-2 whitespace-nowrap text-violet-700 tabular-nums">{i.costo?money(i.costo):'—'}</td>
      <td className="px-3 py-2 whitespace-nowrap">🛒 {i.platform}</td>
      <td className="px-3 py-2 whitespace-nowrap">{i.type}</td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{i.zip}</td>
      <td className="px-3 py-2 whitespace-nowrap">{i.ciudad}</td>
      <td className="px-3 py-2 whitespace-nowrap">{i.estadoCupo}</td>
      <td className="px-3 py-2 whitespace-nowrap">{i.nombreCupo}</td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{i.contrasena}</td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{i.cuentaCorreo}</td>
      <td className="px-3 py-2 whitespace-nowrap">{i.credDeb}</td>
      <td className="px-3 py-2 whitespace-nowrap max-w-[160px] truncate">{i.notas}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        {isAdmin&&<div className="flex gap-1">
          <button onClick={()=>asignar(i)} title="Asignar a cliente/pedido" className="border border-slate-200 rounded-lg px-2 py-1 hover:bg-slate-100">👤</button>
          <button onClick={()=>{setForm({...i});setZipMsg('');}} title="Editar" className="border border-slate-200 rounded-lg px-2 py-1 hover:bg-slate-100">✏️</button>
          <button onClick={()=>quitar(i)} title="Eliminar" className="border border-slate-200 rounded-lg px-2 py-1 hover:bg-slate-100">🗑️</button>
        </div>}
      </td>
    </tr>
  );

  return (
    <motion.div className="fixed inset-0 z-[90] flex flex-col bg-slate-100" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 bg-white border-b border-slate-200 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-lg">📦</div>
          <div>
            <div className="font-black text-lg text-slate-900 leading-none">Inventario</div>
            <div className="text-xs text-slate-400 mt-0.5">Cuentas / cupos disponibles en tiempo real</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin&&<button onClick={()=>{setForm({...DEF});setZipMsg('');}} className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-black active:scale-95">＋ Agregar</button>}
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold flex items-center justify-center hover:bg-slate-200">✕</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        {/* ── VISTAS RÁPIDAS ── */}
        <div className="flex flex-wrap gap-2 mb-4">
          {VISTAS.map(v=>{
            const activa=vista===v.k;
            return (
              <button key={v.k||'todo'} onClick={()=>setVista(v.k)}
                className={"px-3 py-2 rounded-xl text-xs font-black transition-all active:scale-95 border-2 "+(activa?"bg-emerald-600 text-white border-emerald-600":"bg-white text-slate-600 border-slate-200 hover:border-emerald-300")}>
                <span className="mr-1">{v.e}</span>{v.l}
                <span className={"ml-2 px-1.5 py-0.5 rounded-full text-[10px] "+(activa?"bg-white/25 text-white":"bg-slate-100 text-slate-500")}>{vistaCount(v)}</span>
              </button>
            );
          })}
        </div>

        {/* ── TARJETAS RESUMEN (informativas) ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {[
            {l:'Totales',n:items.length,c:'text-slate-900'},
            {l:'🟢 Disponibles',n:disp.length,c:'text-emerald-600'},
            {l:'⏳ En proceso',n:enProceso.length,c:'text-orange-600',foot:'cola · start · aplicado'},
            {l:'🤝 Apartadas',n:apartadas.length,c:'text-amber-600',foot:'vendidas: '+vend.length+(problemas.length?' · ⛔ '+problemas.length:'')},
            {l:'Valor disponible',n:money(valorDisp),c:'text-emerald-600'},
            {l:'Comprados stock',n:compStock.length,c:'text-violet-600',foot:'invertido '+money(invertido)},
          ].map((k,idx)=>(
            <div key={idx} className="bg-white border border-slate-200 rounded-2xl p-3">
              <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">{k.l}</div>
              <div className={"text-2xl font-black mt-1 "+k.c}>{k.n}</div>
              {k.foot&&<div className="text-[10px] text-slate-400 mt-1">{k.foot}</div>}
            </div>
          ))}
        </div>

        {/* ── FILTROS ── */}
        <div className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded-2xl p-3 mb-3">
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="🔎 Buscar…" className="flex-1 min-w-[160px] rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-400"/>
          <select value={fDisp} onChange={e=>setFDisp(e.target.value)} className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none"><option value="">Fase: todas</option>{INV_DISPS.map(d=><option key={d}>{d}</option>)}</select>
          <select value={fPlat} onChange={e=>setFPlat(e.target.value)} className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none"><option value="">Plataforma: todas</option>{[...new Set(items.map(i=>i.platform).filter(Boolean))].sort().map(p=><option key={p}>{p}</option>)}</select>
          <select value={fCiudad} onChange={e=>setFCiudad(e.target.value)} className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none"><option value="">Ciudad: todas</option>{[...new Set(items.map(i=>i.ciudad).filter(Boolean))].sort().map(p=><option key={p}>{p}</option>)}</select>
          <select value={fEstado} onChange={e=>setFEstado(e.target.value)} className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none"><option value="">Estado: todos</option>{[...new Set(items.map(i=>i.estadoCupo).filter(Boolean))].sort().map(p=><option key={p}>{p}</option>)}</select>
          <select value={fDominio} onChange={e=>setFDominio(e.target.value)} className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none"><option value="">Dominio: todos</option>{[...new Set(items.map(i=>dominio(i.mail)).filter(Boolean))].sort().map(p=><option key={p}>{p}</option>)}</select>
          <select value={fOrigen} onChange={e=>setFOrigen(e.target.value)} className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none"><option value="">Origen: todos</option>{INV_ORIGENES.map(o=><option key={o}>{o}</option>)}</select>
          {/* Agrupar por */}
          <select value={groupBy} onChange={e=>{setGroupBy(e.target.value);setColapsados(new Set());}} className="rounded-xl border-2 border-emerald-200 bg-emerald-50 text-emerald-800 font-bold px-3 py-2 text-sm outline-none">{GRUPOS.map(g=><option key={g.k} value={g.k}>{g.k?'Agrupar: '+g.l:'Sin agrupar'}</option>)}</select>
          {filtrosActivos>0&&<button onClick={limpiar} className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50">✕ Limpiar ({filtrosActivos})</button>}
          <button onClick={exportCSV} className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50">⬇️ CSV</button>
          <span className="text-xs text-slate-400 ml-auto">{filtered.length} de {items.length}</span>
        </div>

        {/* ── TABLA ── */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" style={{minWidth:'1200px'}}>
              <thead><tr className="bg-slate-50 text-slate-400">
                {COLS.map((col,idx)=>(
                  <th key={idx} onClick={()=>toggleSort(col.k)}
                    className={"text-left font-black uppercase tracking-wide px-3 py-2.5 whitespace-nowrap text-[10px] "+(col.k?"cursor-pointer hover:text-slate-600 select-none":"")}>
                    {col.h}{col.k&&sortKey===col.k?<span className="ml-1 text-emerald-600">{sortDir==='asc'?'▲':'▼'}</span>:null}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {loading&&<tr><td colSpan={19} className="px-3 py-10 text-center text-slate-400">Cargando inventario…</td></tr>}
                {!loading&&filtered.length===0&&<tr><td colSpan={19} className="px-3 py-10 text-center text-slate-400">No hay cuentas con esos filtros.</td></tr>}
                {!loading&&filtered.length>0&&secciones.map((sec,si)=>{
                  if(sec.key===null) return sec.rows.map(FilaInv);
                  const cerrado=colapsados.has(sec.key);
                  const subtotal=sec.rows.reduce((s,i)=>s+num(i.precio),0);
                  return (
                    <React.Fragment key={'g'+si}>
                      <tr className="bg-emerald-50/60 border-t-2 border-emerald-100 cursor-pointer" onClick={()=>toggleGrupo(sec.key)}>
                        <td colSpan={19} className="px-3 py-2">
                          <span className="font-black text-emerald-800 text-[11px] uppercase tracking-wide">{cerrado?'▸':'▾'} {sec.key}</span>
                          <span className="ml-2 text-[11px] text-slate-500">{sec.rows.length} {sec.rows.length===1?'cuenta':'cuentas'}</span>
                          {subtotal>0&&<span className="ml-2 text-[11px] font-black text-emerald-700">· valor {money(subtotal)}</span>}
                        </td>
                      </tr>
                      {!cerrado&&sec.rows.map(FilaInv)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="text-[11px] text-slate-400 mt-3">💾 Se guarda en tu base de datos (tabla aparte, no se cruza con los pedidos). El ZIP autocompleta ciudad/estado (usa internet).</div>
      </div>

      <AnimatePresence>
      {form&&(
        <motion.div className="fixed inset-0 z-[95] flex items-start sm:items-center justify-center p-0 sm:p-4" style={{background:'rgba(15,23,42,0.6)'}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={e=>{if(e.target===e.currentTarget)setForm(null);}}>
        <motion.div className="w-full h-full sm:h-auto sm:max-w-2xl bg-white sm:rounded-[24px] shadow-2xl flex flex-col overflow-hidden sm:max-h-[92vh]" initial={{y:30,opacity:0}} animate={{y:0,opacity:1}} exit={{y:30,opacity:0}} transition={{type:'spring',damping:24}}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
            <div className="font-black text-slate-900">{form.id?'✏️ Editar cuenta':'🆕 Agregar cuenta'}</div>
            <button onClick={()=>setForm(null)} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold flex items-center justify-center hover:bg-slate-200">✕</button>
          </div>
          <div className="overflow-y-auto px-6 py-5 flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Field('Fecha','fecha','','date')}
            {Field('Fecha disponible (auto al pasar a DISPONIBLE/APLICADO)','dispAt','','date')}
            {Field('Mail *','mail','correo@dominio.org')}
            <div>
              <label className={labelCls}>ZIP (autocompleta ciudad/estado)</label>
              <input value={form.zip||''} onChange={e=>onZip(e.target.value)} placeholder="93901" className={inputCls}/>
              <div className={"text-[11px] font-bold mt-1 "+(zipMsg.startsWith('✓')?'text-emerald-600':'text-slate-400')}>{zipMsg}</div>
            </div>
            {Field('Ciudad','ciudad')}
            <div>
              <label className={labelCls}>Estado (A→Z o escríbelo)</label>
              <input list="inv_states" value={form.estadoCupo||''} onChange={e=>setForm(f=>({...f,estadoCupo:e.target.value}))} className={inputCls} placeholder="Elige o escribe…"/>
            </div>
            {Field('Nombre cupo','nombreCupo')}
            {Field('Contraseña','contrasena')}
            {Sel('Cuenta correo','cuentaCorreo',INV_EMAILS)}
            {Sel('Platform','platform',PLATAFORMAS)}
            {Sel('Type','type',INV_TYPES)}
            {Sel('Crédito/Débito','credDeb',INV_CREDDEB)}
            {Field('Precio venta ($)','precio','','number')}
            {Sel('Disponibilidad','disp',INV_DISPS)}
            <div className="sm:col-span-2"><label className={labelCls}>Notas</label><input value={form.notas||''} onChange={e=>setForm(f=>({...f,notas:e.target.value}))} className={inputCls}/></div>
            <div className="sm:col-span-2 text-[11px] font-black text-violet-700 uppercase tracking-wide border-t border-dashed border-slate-200 pt-3">Origen (propio o comprado)</div>
            {Sel('Origen','origen',INV_ORIGENES)}
            {Field('Proveedor (si lo compraste)','proveedor')}
            {Field('Costo de compra ($)','costo','','number')}
            {Field('Asignado a cliente/pedido','asignadoA')}
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
            <button onClick={()=>setForm(null)} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm">Cancelar</button>
            <button onClick={guardar} disabled={saving} className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-black text-sm disabled:opacity-40">{saving?'Guardando…':'💾 Guardar'}</button>
          </div>
        </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
      <datalist id="inv_states">{INV_STATES.map(s=><option key={s} value={s}/>)}</datalist>
    </motion.div>
  );
}

// ── CRONÓMETRO 72H PARA CUENTAS EN "START" ─────────────────────────
// Arranca al marcar START. Al vencer parpadea en rojo pidiendo atención:
// hay que regresar el cupo a COLA DE ESPERA (botón de un toque). Lee el
// arranque de item.startAt (si el backend lo guarda) y, de respaldo, de
// localStorage en este dispositivo.
const START_LIMIT_MS = 72 * 3600 * 1000;
function StartTimer({ item, onVolverEspera }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  let ini = item.startAt;
  if (!ini) { try { ini = localStorage.getItem('royal_start_' + item.id) || ''; } catch {} }
  const start = ini ? new Date(ini).getTime() : null;
  if (!start || isNaN(start)) return null;

  const restante = start + START_LIMIT_MS - now;
  const vencido = restante <= 0;
  const abs = Math.abs(restante);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const horas = restante / 3600000;
  const color = vencido ? '#dc2626' : horas <= 6 ? '#ea580c' : horas <= 24 ? '#d97706' : '#059669';

  if (vencido) return (
    <div className="mt-1 flex items-center gap-1 flex-wrap">
      <motion.span animate={{opacity:[1,0.35,1]}} transition={{repeat:Infinity,duration:0.8}}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-600 text-white text-[10px] font-black">
        ⏰ 72h CUMPLIDAS · +{h}h {m}m
      </motion.span>
      {onVolverEspera && (
        <button onClick={onVolverEspera}
          className="px-2 py-1 rounded-lg bg-red-100 text-red-700 text-[10px] font-black hover:bg-red-200 active:scale-95">
          ↩ A espera
        </button>
      )}
    </div>
  );
  return (
    <div className="mt-1">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black tabular-nums"
        style={{background: color+'22', color}}>
        ⏱ {h}h {String(m).padStart(2,'0')}m {String(s).padStart(2,'0')}s
      </span>
    </div>
  );
}

// ── COLA THUMB — miniatura lazy para vista de lista ──────────────
function ColaThumb({ orderId }) {
  const [src, setSrc] = useState(null);
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    apiFetch('/api/pedidos/' + orderId + '/fotos')
      .then(f => { if (Array.isArray(f) && f.length > 0) setSrc(f[0]); })
      .catch(() => {});
  }, [orderId]);
  if (!src) return null;
  return (
    <div className="shrink-0 w-10 h-10 rounded-xl overflow-hidden border border-slate-200 shadow-sm">
      <img src={src} alt="" className="w-full h-full object-cover"/>
    </div>
  );
}

// ── MODO SOLO HOY ─────────────────────────────────────────────────
// Una tarea a la vez. Meta diaria de 3. Cero muro de pendientes.
// Pensado para no abrumar: solo ves lo que sigue, no todo lo que falta.
const META_DIARIA = 3;
function SoloHoyMode({ task, photo, photoCount, hechas, totalPendientes, onStart, onSkip, onPostpone, onVerTodo, userName }) {
  const metaCumplida = hechas >= META_DIARIA;

  // Sin tareas: día limpio
  if (!task) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}} className="text-center max-w-sm">
          <div className="text-7xl mb-4">🏆</div>
          <div className="text-3xl font-black text-slate-900 mb-2">¡Todo al día!</div>
          <div className="text-slate-500 mb-6">No tienes nada pendiente. Respira, te lo ganaste.</div>
          <button onClick={onVerTodo} className="text-slate-400 text-sm font-bold hover:text-slate-600">Ver lista completa →</button>
        </motion.div>
      </div>
    );
  }

  const p = PC[task.plataforma] || PC["OTROS"];
  const s = SC[task.estado] || {};
  const d = days(task.entrega);

  return (
    <div className="min-h-[75vh] flex flex-col items-center justify-center px-5 py-6">
      {/* Progreso del día: 3 puntitos */}
      <div className="flex flex-col items-center gap-2 mb-8">
        <div className="flex items-center gap-2">
          {Array.from({length:META_DIARIA}).map((_,i)=>(
            <motion.div key={i} initial={false} animate={{scale: i<hechas?1.1:1}}
              className={"w-3 h-3 rounded-full transition-all "+(i<hechas?"bg-emerald-500":"bg-slate-200")}/>
          ))}
        </div>
        <div className="text-xs font-bold text-slate-400">
          {metaCumplida ? "🎉 Meta del día cumplida" : `${hechas} de ${META_DIARIA} hoy`}
        </div>
      </div>

      {/* Mensaje de meta cumplida (suave, no obliga a parar) */}
      {metaCumplida && (
        <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}}
          className="mb-5 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold text-center">
          Ya cumpliste tus 3. Lo demás es bono 💪
        </motion.div>
      )}

      {/* LA tarea */}
      <motion.div key={task.id} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
        className="w-full max-w-md rounded-[32px] overflow-hidden shadow-2xl"
        style={{background:"linear-gradient(135deg,"+p.color+"22 0%,#0f172a 55%)",borderLeft:"6px solid "+p.color}}>
        <div className="bg-slate-900/95 p-7">
          <div className="flex items-center gap-2 mb-5">
            <motion.div animate={{scale:[1,1.2,1]}} transition={{repeat:Infinity,duration:1.6}} className={"w-2.5 h-2.5 rounded-full "+(task._aplazadoFallback?"bg-amber-400":"bg-emerald-400")}/>
            <span className={"text-xs font-black uppercase tracking-widest "+(task._aplazadoFallback?"text-amber-300":"text-emerald-400")}>
              {task._pinned ? "📌 Fijada"
                : task._aplazadoFallback ? "⌛ Solo quedan los que aplazaste"
                : "Tu única tarea ahora"}
            </span>
          </div>

          {photo && (
            <div className="mb-5 rounded-2xl overflow-hidden border border-white/10">
              <img src={photo} alt="" className="w-full h-44 object-cover"/>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className={"inline-flex items-center gap-1.5 px-3 py-1 rounded-full "+p.bg+" text-white text-xs font-black"}>{p.emoji} {task.plataforma}</span>
            <span className={"inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black "+(s.bg||"bg-slate-200")+" "+(s.text||"text-slate-700")}>{s.emoji} {task.estado}</span>
          </div>

          <div className="text-4xl font-black text-white leading-tight mb-1">{task.cliente}</div>
          <div className="mb-3"><TituloChip order={task} tono="oscuro" className="text-sm px-3 py-1"/></div>

          <div className="flex flex-wrap gap-2 mb-4">
            {task.rehacer && <span className="px-3 py-1 rounded-full bg-rose-500 text-white text-xs font-black">🔄 REHACER</span>}
            {task.chatPendiente && <span className="px-3 py-1 rounded-full bg-orange-500 text-white text-xs font-black animate-pulse">💬 CLIENTE ESPERA</span>}
            {d!==null && d<=1 && <span className="px-3 py-1 rounded-full bg-red-600 text-white text-xs font-black">{d===0?"🚨 HOY":"🚨 MAÑANA"}</span>}
          </div>

          {task.notas && <div className="text-slate-300 text-sm mb-5 line-clamp-3">{task.notas}</div>}

          <motion.button whileTap={{scale:0.97}} onClick={()=>onStart(task.id)}
            className="w-full py-5 rounded-2xl text-slate-900 text-xl font-black shadow-xl mb-3"
            style={{background:p.color==="#1d1d1d"?"#ffffff":p.color}}>
            ▶ Empezar esta →
          </motion.button>

          {!task._aplazadoFallback && (
            <button onClick={()=>onSkip(task.id)}
              className="w-full py-2.5 rounded-xl bg-slate-700/60 text-slate-300 text-sm font-bold hover:bg-slate-700 transition-all">
              ⏭ Esta no, muéstrame otra
            </button>
          )}

          {!task._pinned && !task._aplazadoFallback && (
            <button onClick={()=>onPostpone(task.id)}
              title="No vuelve a aparecer arriba hasta que revises todo lo demás"
              className="w-full mt-2 py-2.5 rounded-xl bg-amber-400/15 text-amber-300 text-sm font-bold hover:bg-amber-400/25 transition-all">
              ⌛ Déjalo para el final (revisar el resto primero)
            </button>
          )}

          {task._aplazadoFallback && (
            <button onClick={()=>onPostpone(task.id)}
              title="Quitarlo de aplazados y volver a la cola normal"
              className="w-full py-2.5 rounded-xl bg-amber-400 text-amber-950 text-sm font-bold hover:bg-amber-300 transition-all">
              ⬆️ Traer de vuelta a la cola
            </button>
          )}
        </div>
      </motion.div>

      {/* Pie discreto: cuántas faltan + acceso a lista completa */}
      <div className="mt-7 text-center space-y-2">
        {totalPendientes > 1 && (
          <div className="text-slate-400 text-sm">Hay {totalPendientes} en total, pero no te preocupes por eso ahora.</div>
        )}
        <button onClick={onVerTodo} className="text-slate-400 text-sm font-bold hover:text-slate-600 underline decoration-dotted">
          Ver lista completa
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  CLIENTES · ficha única, selector al crear pedido, y ficha completa
//  ────────────────────────────────────────────────────────────────
//  Un cliente tiene MUCHOS pedidos, cada uno de su propia fuente/plataforma.
//  El pedido guarda `clienteId` (la ficha) + `cliente` (el nombre, copia).
//  El servidor hace el cruce con cartera, pagos y pedidos anteriores.
// ═══════════════════════════════════════════════════════════════════
const normNombre = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
const cliMoney = n => '$' + (parseFloat(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const cliFecha = d => { if (!d) return ''; const x = new Date(String(d).length <= 10 ? d + 'T12:00' : String(d).replace(' ', 'T')); return isNaN(x) ? '' : x.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: '2-digit' }); };

// Caché corta de la lista (la usan el selector y el panel). Se invalida al crear/editar.
// `clientesServidorOk` = false cuando el servidor NO tiene el módulo de clientes
// (server.js viejo o sin reiniciar): /api/clientes devuelve la página HTML en vez
// de la lista. Antes eso fallaba en silencio; ahora se avisa en pantalla.
let _clientesCache = null;
let clientesServidorOk = null;
async function cargarClientes(force = false) {
  if (!force && _clientesCache && Date.now() - _clientesCache.t < 30000) return _clientesCache.data;
  let d = null;
  try { d = await apiFetch('/api/clientes'); } catch (e) { d = null; }
  clientesServidorOk = Array.isArray(d);
  const data = Array.isArray(d) ? d : [];
  _clientesCache = { t: Date.now(), data };
  return data;
}
// Índice de respaldo: si el servidor no responde la lista, se arma con los
// pedidos ya cargados (agrupados por nombre), para que igual puedas elegir
// un cliente existente y no se escriba distinto.
function indiceDesdePedidos(orders) {
  const m = {};
  (orders || []).forEach(o => {
    const k = normNombre(o.cliente); if (!k) return;
    if (!m[k]) m[k] = { id: '', _local: true, nombre: (o.cliente || '').trim(), telefono: '', mail: '', numPedidos: 0, activos: 0, plataformas: [], porPlataforma: [], deuda: 0, pedidosConDeuda: 0, ultimoPedido: '' };
    const e = m[k]; e.numPedidos++;
    if (o.plataforma && !e.plataformas.includes(o.plataforma)) e.plataformas.push(o.plataforma);
    if (!e.telefono && o.telefonoName) e.telefono = o.telefonoName;
    if (!e.mail && o.mail) e.mail = o.mail;
    if (String(o.createdAt || '') > e.ultimoPedido) e.ultimoPedido = String(o.createdAt || '');
  });
  Object.values(m).forEach(e => { e.porPlataforma = e.plataformas.map(pl => ({ plataforma: pl, pedidos: (orders || []).filter(o => normNombre(o.cliente) === normNombre(e.nombre) && o.plataforma === pl).length })); });
  return Object.values(m);
}
function invalidarClientes() { _clientesCache = null; }

// ── SELECTOR DE CLIENTE (nuevo pedido / editar pedido) ─────────────
// Escribes → te sugiere fichas existentes con su historial y deuda.
// Eliges una → el pedido queda colgado de ESA ficha (clienteId).
// Si escribes un nombre que no existe → al guardar se crea la ficha sola.
// ═══════════════════════════════════════════════════════════════════
//  TÍTULO ÚNICO DEL PEDIDO
//  El cliente sigue siendo el dueño (arriba, en grande). El título es el
//  "apodo" de ESE pedido para no confundirlo con los otros del mismo
//  cliente: "Camila ojos claros", "Andrés Calvo #2"...
// ═══════════════════════════════════════════════════════════════════
function folioPedido(o) { return '#' + String(o?.id || '').split('-')[0].slice(0, 6).toUpperCase(); }
function tituloDe(o) { return String(o?.titulo || '').trim() || folioPedido(o); }

function TituloChip({ order, tono = 'claro', className = '' }) {
  const estilos = {
    claro:  'bg-indigo-50 text-indigo-700 border-indigo-200',
    oscuro: 'bg-white/10 text-white border-white/25',
    verde:  'bg-emerald-800 text-emerald-100 border-emerald-600',
  };
  return (
    <span title="Título del pedido" className={"inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md border text-xs font-black " + (estilos[tono] || estilos.claro) + " " + className}>
      <span aria-hidden="true">🏷️</span><span className="truncate">{tituloDe(order)}</span>
    </span>
  );
}

// Campo de título con aviso en vivo si ya existe. `onEstado` recibe
// 'vacio' | 'revisando' | 'libre' | 'ocupado' para bloquear el botón Guardar.
function TituloPedidoInput({ value, onChange, cliente, clienteId, pedidoId, orders, onEstado, className = '' }) {
  const [estado, setEstado] = useState({ tipo: 'vacio' });
  const t = String(value || '').trim().replace(/\s+/g, ' ');
  const nT = normNombre(t);

  // Choque contra los pedidos que ya tengo en pantalla (instantáneo)
  const choqueLocal = useMemo(() => (
    nT ? (orders || []).find(o => o.id !== pedidoId && normNombre(o.titulo) === nT) || null : null
  ), [nT, orders, pedidoId]);

  // Los otros pedidos de ESTE cliente, para ver qué títulos ya tiene
  const hermanos = useMemo(() => {
    const nc = normNombre(cliente);
    if (!nc && !clienteId) return [];
    return (orders || []).filter(o => o.id !== pedidoId &&
      (clienteId ? o.clienteId === clienteId : normNombre(o.cliente) === nc));
  }, [orders, cliente, clienteId, pedidoId]);

  useEffect(() => {
    if (!t) { setEstado({ tipo: 'vacio' }); return; }
    if (choqueLocal) { setEstado({ tipo: 'ocupado', cliente: choqueLocal.cliente }); return; }
    setEstado({ tipo: 'revisando' });
    let vivo = true;
    const h = setTimeout(async () => {
      const r = await apiFetch('/api/pedidos-titulo?t=' + encodeURIComponent(t) + '&excluir=' + encodeURIComponent(pedidoId || '')).catch(() => null);
      if (!vivo) return;
      // Server viejo (sin el endpoint): no bloqueamos, el server lo valida al guardar
      if (!r || typeof r.libre !== 'boolean') setEstado({ tipo: 'libre' });
      else setEstado(r.libre ? { tipo: 'libre' } : { tipo: 'ocupado', cliente: r.ocupadoPor?.cliente || '' });
    }, 350);
    return () => { vivo = false; clearTimeout(h); };
  }, [t, choqueLocal, pedidoId]);

  useEffect(() => { if (onEstado) onEstado(estado.tipo); }, [estado.tipo]); // eslint-disable-line

  // "<cliente> #n" con el siguiente número libre
  const autoNombre = () => {
    const base = String(cliente || '').trim().replace(/\s+/g, ' ') || 'Pedido';
    const usados = new Set((orders || []).filter(o => o.id !== pedidoId).map(o => normNombre(o.titulo)));
    let k = hermanos.length + 1;
    while (usados.has(normNombre(base + ' #' + k))) k++;
    return base + ' #' + k;
  };

  const borde = estado.tipo === 'ocupado' ? 'border-rose-400 bg-rose-50/50'
              : estado.tipo === 'libre'   ? 'border-emerald-400 bg-emerald-50/30'
              : 'border-slate-200 focus:border-slate-400';
  const nombreCliente = String(cliente || '').trim();

  return (
    <div className={className}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" aria-hidden="true">🏷️</span>
        <input maxLength={60}
          className={"w-full rounded-xl border-2 pl-9 pr-24 py-3 text-sm outline-none font-semibold " + borde}
          placeholder="Título del pedido (ej: Camila ojos claros)"
          value={value || ''} onChange={e => onChange(e.target.value)} />
        <button type="button" onClick={() => onChange(autoNombre())}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-black hover:bg-slate-200 active:scale-95">
          Numerar
        </button>
      </div>
      <div className={"mt-1 text-[11px] font-bold " + (estado.tipo === 'ocupado' ? 'text-rose-600' : estado.tipo === 'libre' ? 'text-emerald-700' : 'text-slate-400')}>
        {estado.tipo === 'ocupado' && <>Ese título ya lo tiene otro pedido{estado.cliente ? ` (de ${estado.cliente})` : ''}. Cambia una palabra.</>}
        {estado.tipo === 'revisando' && <>Revisando…</>}
        {estado.tipo === 'libre' && <>Disponible{nombreCliente ? <> · cliente principal: <span className="text-slate-700">{nombreCliente}</span></> : ''}</>}
        {estado.tipo === 'vacio' && <>Vacío = se llama “{autoNombre()}” automáticamente.</>}
      </div>
      {hermanos.length > 0 && (
        <div className="mt-1 text-[11px] text-slate-500">
          Este cliente ya tiene: {hermanos.slice(0, 5).map(tituloDe).join(', ')}{hermanos.length > 5 ? ` y ${hermanos.length - 5} más` : ''}
        </div>
      )}
    </div>
  );
}

function ClientePicker({ value, clienteId, onChange, onPrefill, onVerFicha, orders, className = '' }) {
  const [listaSrv, setListaSrv] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cargado, setCargado] = useState(false);
  const [srvOk, setSrvOk] = useState(true);
  // force=true: al abrir el formulario siempre se trae la lista fresca
  useEffect(() => { cargarClientes(true).then(d => { setListaSrv(d); setSrvOk(clientesServidorOk !== false); setCargado(true); }); }, []);
  const lista = useMemo(() => srvOk ? listaSrv : indiceDesdePedidos(orders), [srvOk, listaSrv, orders]);

  const q = normNombre(value);
  const sugerencias = useMemo(() => {
    if (!q) return [...lista].sort((a, b) => String(b.ultimoPedido || '').localeCompare(String(a.ultimoPedido || ''))).slice(0, 6);
    return lista.filter(c => normNombre(c.nombre).includes(q) || normNombre(c.telefono).includes(q))
      .sort((a, b) => (normNombre(a.nombre).startsWith(q) ? 0 : 1) - (normNombre(b.nombre).startsWith(q) ? 0 : 1))
      .slice(0, 8);
  }, [lista, q]);

  // Ficha activa: la elegida, o la que coincide exacto con lo escrito (el servidor la vinculará igual)
  const elegido = clienteId ? lista.find(c => c.id && c.id === clienteId) : null;
  const exacto = !elegido && q ? lista.find(c => normNombre(c.nombre) === q) : null;
  const activo = elegido || exacto;

  const elegir = c => { onChange({ cliente: c.nombre, clienteId: c.id || '' }); setAbierto(false); };

  return (
    <div className={"relative " + className}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">👤</span>
        <input
          className={"w-full rounded-xl border-2 pl-9 pr-9 py-3 text-sm outline-none font-semibold " + (elegido ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 focus:border-slate-400")}
          placeholder="Cliente * (busca o escribe uno nuevo)"
          value={value}
          onFocus={() => setAbierto(true)}
          onBlur={() => setTimeout(() => setAbierto(false), 180)}
          onChange={e => { onChange({ cliente: e.target.value, clienteId: '' }); setAbierto(true); }}
        />
        {elegido && (
          <button type="button" title="Quitar cliente" onClick={() => onChange({ cliente: '', clienteId: '' })}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-200 text-slate-500 text-xs font-bold">✕</button>
        )}
      </div>

      {abierto && sugerencias.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-[90] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden max-h-72 overflow-y-auto">
          {!q && <div className="px-3 pt-2 pb-1 text-[10px] font-black text-slate-400 uppercase tracking-wide">Clientes recientes</div>}
          {sugerencias.map(c => (
            <button type="button" key={c.id || 'l:' + c.nombre} onMouseDown={e => e.preventDefault()} onClick={() => elegir(c)}
              className={"w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0 " + (c.id === clienteId ? "bg-emerald-50" : "")}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold text-slate-900 text-sm truncate">{c.nombre}</div>
                {c.deuda > 0.01 && <span className="shrink-0 text-[11px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded-full">debe {cliMoney(c.deuda)}</span>}
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {c.numPedidos} pedido{c.numPedidos !== 1 ? 's' : ''}{c.plataformas?.length ? ' · ' + c.plataformas.join(' / ') : ''}{c.telefono ? ' · ' + c.telefono : ''}
              </div>
            </button>
          ))}
        </div>
      )}

      {cargado && !srvOk && (
        <div className="mt-2 rounded-xl px-3 py-2 text-xs bg-amber-50 border border-amber-300 text-amber-900 font-bold">
          ⚠️ El servidor no tiene el módulo de clientes (no se están guardando fichas). Sube el server.js nuevo y reinícialo. Mientras tanto te sugiero clientes con los pedidos que ya tienes.
        </div>
      )}
      {/* Cruce con historial y cartera */}
      {activo ? (
        <div className={"mt-2 rounded-xl p-3 text-xs space-y-2 " + (activo.deuda > 0.01 ? "bg-red-50 border border-red-200" : "bg-slate-50 border border-slate-200")}>
          <div className="flex items-center justify-between gap-2">
            <div className="font-black text-slate-800">
              {elegido ? '✅ Ficha vinculada' : '↪ Se vinculará a la ficha existente'}: {activo.nombre}
            </div>
            {onVerFicha && activo.id && <button type="button" onClick={() => onVerFicha(activo.id)} className="shrink-0 px-2 py-1 rounded-lg bg-slate-900 text-white font-black text-[11px]">Ver ficha</button>}
          </div>
          <div className="text-slate-600">
            {activo.numPedidos} pedido{activo.numPedidos !== 1 ? 's' : ''} anterior{activo.numPedidos !== 1 ? 'es' : ''}
            {activo.activos ? ' · ' + activo.activos + ' en curso' : ''}
            {activo.ultimoPedido ? ' · último ' + cliFecha(activo.ultimoPedido) : ''}
          </div>
          {activo.porPlataforma?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {activo.porPlataforma.map(p => (
                <span key={p.plataforma} className="px-2 py-0.5 rounded-full bg-white border border-slate-200 font-bold text-slate-600">{p.plataforma} ×{p.pedidos}</span>
              ))}
            </div>
          )}
          {activo.deuda > 0.01 && (
            <div className="font-black text-red-700">⚠️ Tiene deuda en cartera: {cliMoney(activo.deuda)} en {activo.pedidosConDeuda} pedido{activo.pedidosConDeuda !== 1 ? 's' : ''}</div>
          )}
          {onPrefill && (activo.telefono || activo.mail) && (
            <button type="button" onClick={() => onPrefill({ telefonoName: activo.telefono || '', mail: activo.mail || '' })}
              className="px-2 py-1 rounded-lg bg-white border border-slate-300 font-bold text-slate-700 text-[11px]">
              📋 Usar sus datos ({[activo.telefono, activo.mail].filter(Boolean).join(' · ')})
            </button>
          )}
        </div>
      ) : (q && cargado && srvOk) ? (
        <div className="mt-2 rounded-xl px-3 py-2 text-xs bg-blue-50 border border-blue-200 text-blue-800 font-bold">✨ Cliente nuevo: se crea su ficha al guardar el pedido.</div>
      ) : null}
    </div>
  );
}

// ── FICHA DEL CLIENTE ──────────────────────────────────────────────
// Todo lo del cliente en un solo lugar: contacto, pedidos de TODAS las
// fuentes, deuda por pedido y por plataforma, pagos, y abrir cualquier pedido.
function ClienteFichaModal({ clienteId, onClose, onOpenPedido, isAdmin, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [editando, setEditando] = useState(false);
  const [f, setF] = useState({ nombre: '', telefono: '', mail: '', notas: '' });
  const [saving, setSaving] = useState(false);
  const [fusion, setFusion] = useState(false);
  const [fusionQ, setFusionQ] = useState('');
  const [todos, setTodos] = useState([]);
  const [filtro, setFiltro] = useState('todos'); // todos | deuda | curso | PLATAFORMA

  const load = useCallback(() => {
    setErr('');
    apiFetch('/api/clientes/' + clienteId).then(d => {
      if (!d || d.error) { setErr(d?.error || 'No se pudo cargar'); return; }
      setData(d);
      setF({ nombre: d.cliente.nombre, telefono: d.cliente.telefono, mail: d.cliente.mail, notas: d.cliente.notas });
    }).catch(() => setErr('Error de conexión'));
  }, [clienteId]);
  useEffect(() => { load(); }, [load]);

  const guardar = async () => {
    setSaving(true);
    const r = await apiFetch('/api/clientes/' + clienteId, { method: 'PUT', body: JSON.stringify(f) }).catch(() => null);
    setSaving(false);
    if (!r?.ok) { alert(r?.error || 'No se pudo guardar'); return; }
    invalidarClientes(); setEditando(false); load(); onChanged && onChanged();
  };
  const abrirFusion = async () => { setFusion(true); setTodos(await cargarClientes(true)); };
  const fusionar = async (otro) => {
    if (!window.confirm(`¿Pasar TODO lo de "${otro.nombre}" (${otro.numPedidos} pedidos) a "${data.cliente.nombre}"?\n\n"${otro.nombre}" desaparece como ficha aparte. Úsalo cuando es la misma persona escrita distinto.`)) return;
    const r = await apiFetch('/api/clientes/' + clienteId + '/fusionar', { method: 'POST', body: JSON.stringify({ deId: otro.id }) }).catch(() => null);
    if (!r?.ok) { alert(r?.error || 'No se pudo fusionar'); return; }
    invalidarClientes(); setFusion(false); setFusionQ(''); load(); onChanged && onChanged();
  };
  const borrar = async () => {
    if (!window.confirm('¿Borrar esta ficha de cliente? (solo se puede si no tiene pedidos)')) return;
    const r = await apiFetch('/api/clientes/' + clienteId, { method: 'DELETE' }).catch(() => null);
    if (!r?.ok) { alert(r?.error || 'No se pudo borrar'); return; }
    invalidarClientes(); onChanged && onChanged(); onClose();
  };

  const res = data?.resumen || {};
  const pedidos = (data?.pedidos || []).filter(p => {
    if (filtro === 'todos') return true;
    if (filtro === 'deuda') return (p.saldo || 0) > 0.01 && !p.fueraCartera;
    if (filtro === 'curso') return !['Entregado', 'Cancelado', 'Intento Fallido', 'NOT APPROVED', 'DIRTY BACKGROUND', 'DESACTIVADA'].includes(p.estado);
    return p.plataforma === filtro;
  });
  const candidatosFusion = todos.filter(c => c.id !== clienteId && (!fusionQ || normNombre(c.nombre).includes(normNombre(fusionQ)))).slice(0, 12);

  return (
    <motion.div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center sm:p-4" style={{ background: 'rgba(15,23,42,0.6)' }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div className="w-full sm:max-w-2xl bg-white sm:rounded-[24px] rounded-t-[24px] shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '92vh' }}
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ type: 'spring', damping: 24 }}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide">👤 Ficha del cliente</div>
            <div className="text-xl font-black text-slate-900 truncate">{data?.cliente?.nombre || (err ? '—' : 'Cargando…')}</div>
            {data && <div className="text-xs text-slate-500 truncate">{[data.cliente.telefono, data.cliente.mail].filter(Boolean).join(' · ') || 'Sin datos de contacto'}</div>}
          </div>
          <div className="flex gap-2 shrink-0">
            {isAdmin && data && !editando && <button onClick={() => setEditando(true)} className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 font-bold">✏️</button>}
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold">✕</button>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
          {err && <div className="rounded-xl bg-red-50 text-red-700 text-sm font-bold p-3">{err}</div>}
          {!data && !err && <div className="text-center text-slate-400 py-10 animate-pulse font-bold">Cargando ficha…</div>}

          {data && editando && (
            <div className="rounded-xl border-2 border-slate-200 p-3 space-y-2">
              {[['nombre', 'Nombre *'], ['telefono', 'Teléfono'], ['mail', 'Mail']].map(([k, l]) => (
                <input key={k} className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400" placeholder={l} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} />
              ))}
              <textarea rows={2} className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none resize-none" placeholder="Notas del cliente" value={f.notas} onChange={e => setF({ ...f, notas: e.target.value })} />
              <div className="text-[11px] text-slate-400">Si cambias el nombre, se actualiza en todos sus pedidos, la cartera y el Excel.</div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditando(false)} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm font-bold">Cancelar</button>
                <button onClick={guardar} disabled={saving || !f.nombre.trim()} className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold disabled:opacity-40">{saving ? 'Guardando…' : 'Guardar'}</button>
              </div>
            </div>
          )}

          {data && !editando && data.cliente.notas && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3">📝 {data.cliente.notas}</div>}

          {data && (
            <>
              {/* Resumen */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-xl bg-slate-50 p-3 text-center"><div className="text-xl font-black text-slate-900">{res.numPedidos || 0}</div><div className="text-[11px] text-slate-500 font-bold">pedidos</div></div>
                <div className="rounded-xl bg-slate-50 p-3 text-center"><div className="text-xl font-black text-slate-900">{res.activos || 0}</div><div className="text-[11px] text-slate-500 font-bold">en curso</div></div>
                {isAdmin && <div className="rounded-xl bg-emerald-50 p-3 text-center"><div className="text-xl font-black text-emerald-700">{cliMoney(res.pagado)}</div><div className="text-[11px] text-emerald-700/70 font-bold">pagado de {cliMoney(res.facturado)}</div></div>}
                {isAdmin && <div className={"rounded-xl p-3 text-center " + (res.deuda > 0.01 ? 'bg-red-50' : 'bg-slate-50')}><div className={"text-xl font-black " + (res.deuda > 0.01 ? 'text-red-600' : 'text-slate-400')}>{cliMoney(res.deuda)}</div><div className="text-[11px] text-slate-500 font-bold">debe ({res.pedidosConDeuda || 0})</div></div>}
              </div>
              {isAdmin && res.enProceso > 0 && <div className="text-xs font-bold text-blue-700 bg-blue-50 rounded-lg px-3 py-2">⏳ {res.enProceso} pedido{res.enProceso !== 1 ? 's' : ''} en proceso{res.porCobrarAlEntregar > 0 ? ` · ${cliMoney(res.porCobrarAlEntregar)} por cobrar al entregar` : ''} — no cuentan como deuda hasta que se entreguen.</div>}
              {isAdmin && res.sinPrecio > 0 && <div className="text-xs font-bold text-amber-700 bg-amber-50 rounded-lg px-3 py-2">⚠️ {res.sinPrecio} pedido{res.sinPrecio !== 1 ? 's' : ''} sin precio — no cuentan en la deuda hasta que les pongas precio.</div>}

              {/* Por fuente / plataforma */}
              {res.porPlataforma?.length > 0 && (
                <div>
                  <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-1.5">Por fuente / plataforma</div>
                  <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {res.porPlataforma.map(p => (
                      <button key={p.plataforma} onClick={() => setFiltro(filtro === p.plataforma ? 'todos' : p.plataforma)}
                        className={"w-full flex items-center justify-between px-3 py-2 text-sm " + (filtro === p.plataforma ? 'bg-blue-50' : 'hover:bg-slate-50')}>
                        <span className="font-bold text-slate-800">{p.plataforma} <span className="text-slate-400 font-medium">×{p.pedidos}</span></span>
                        {isAdmin && <span className="text-xs text-slate-500">{cliMoney(p.pagado)} / {cliMoney(p.facturado)}{p.deuda > 0.01 && <strong className="text-red-600 ml-2">debe {cliMoney(p.deuda)}</strong>}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pedidos */}
              <div>
                <div className="flex items-center justify-between mb-1.5 gap-2">
                  <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide">Pedidos ({pedidos.length})</div>
                  <div className="flex gap-1">
                    {[['todos', 'Todos'], ['curso', 'En curso'], ...(isAdmin ? [['deuda', 'Con deuda']] : [])].map(([k, l]) => (
                      <button key={k} onClick={() => setFiltro(k)} className={"px-2 py-1 rounded-full text-[11px] font-black " + (filtro === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500')}>{l}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  {pedidos.length === 0 && <div className="text-center text-slate-400 text-sm py-4">Sin pedidos en este filtro</div>}
                  {pedidos.map(p => {
                    const sc = SC[p.estado] || {};
                    return (
                      <div key={p.id} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-black text-slate-900 text-sm truncate">🏷️ {tituloDe(p)}</div>
                            <div className="text-xs font-bold text-slate-600">{p.plataforma}{p.fuente !== 'Pedido' && <span className="ml-1.5 text-[10px] font-bold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">{p.fuente}</span>}</div>
                            <div className="text-[11px] text-slate-500">{cliFecha(p.createdAt)}{p.responsable ? ' · ' + p.responsable : ''} · #{String(p.id).split('-')[0].toUpperCase()}</div>
                          </div>
                          <span className={"shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black " + (sc.bg || 'bg-slate-200') + " " + (sc.text || 'text-slate-700')}>{sc.emoji} {p.estado}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-2">
                          {isAdmin ? (
                            <div className="text-xs text-slate-600">
                              {p.total > 0 ? <>{cliMoney(p.pagado)} / {cliMoney(p.total)}</> : <span className="text-amber-600 font-bold">sin precio</span>}
                              {p.saldo > 0.01 && <strong className="text-red-600 ml-2">debe {cliMoney(p.saldo)}</strong>}
                              {p.estadoCobro === 'saldada' && <span className="text-emerald-600 font-bold ml-2">✓ saldado</span>}
                              {p.estadoCobro === 'anulada' && <span className="text-zinc-500 font-bold ml-2">cobro cerrado</span>}
                              {p.fueraCartera === 'en-proceso' && p.saldoPendiente > 0.01 && <span className="text-blue-600 font-bold ml-2">cobra {cliMoney(p.saldoPendiente)} al entregar</span>}
                              {p.fueraCartera === 'desactivada' && <span className="text-zinc-500 font-bold ml-2">desactivada · no es deuda</span>}
                            </div>
                          ) : <div />}
                          {onOpenPedido && (
                            <button onClick={() => onOpenPedido(p)} className="shrink-0 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-black active:scale-95">
                              {p.esRentaDirecta ? '💰 Abrir renta' : '✏️ Abrir pedido'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagos */}
              {isAdmin && data.pagos?.length > 0 && (
                <div>
                  <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-1.5">Últimos pagos</div>
                  <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {data.pagos.slice(0, 15).map(g => (
                      <div key={g.id} className="flex items-center justify-between px-3 py-2 text-xs">
                        <span className="text-slate-600">{cliFecha(g.fechaPago)} · {g.plataforma} · {g.metodo}{g.notas ? ' · ' + g.notas : ''}</span>
                        <strong className="text-emerald-700">{cliMoney(g.monto)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Herramientas admin */}
              {isAdmin && (
                <div className="pt-2 border-t border-slate-100 space-y-2">
                  {!fusion ? (
                    <div className="flex gap-2">
                      <button onClick={abrirFusion} className="flex-1 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-black">🔗 Fusionar con un duplicado</button>
                      {(res.numPedidos || 0) === 0 && <button onClick={borrar} className="px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs font-black">🗑 Borrar ficha</button>}
                    </div>
                  ) : (
                    <div className="rounded-xl border-2 border-slate-200 p-3 space-y-2">
                      <div className="text-xs font-black text-slate-700">¿Qué ficha es la misma persona? Sus pedidos pasan a <u>{data.cliente.nombre}</u>.</div>
                      <input autoFocus className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none" placeholder="Buscar cliente duplicado…" value={fusionQ} onChange={e => setFusionQ(e.target.value)} />
                      <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                        {candidatosFusion.map(c => (
                          <button key={c.id} onClick={() => fusionar(c)} className="w-full text-left px-2 py-2 text-sm hover:bg-slate-50 flex justify-between gap-2">
                            <span className="font-bold text-slate-800 truncate">{c.nombre}</span>
                            <span className="text-xs text-slate-400 shrink-0">{c.numPedidos} ped.</span>
                          </button>
                        ))}
                      </div>
                      <button onClick={() => { setFusion(false); setFusionQ(''); }} className="text-xs font-bold text-slate-500">Cancelar</button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── LISTA DE CLIENTES (módulo propio) ──────────────────────────────
function ClientesPanel({ onClose, onOpenFicha, isAdmin }) {
  const [lista, setLista] = useState(null);
  const [q, setQ] = useState('');
  const [orden, setOrden] = useState('reciente'); // reciente | deuda | nombre | pedidos
  const [nuevo, setNuevo] = useState(false);
  const [nf, setNf] = useState({ nombre: '', telefono: '', mail: '', notas: '' });
  const [saving, setSaving] = useState(false);
  const load = (force = true) => cargarClientes(force).then(setLista);
  useEffect(() => { load(true); }, []);

  const crear = async () => {
    setSaving(true);
    const r = await apiFetch('/api/clientes', { method: 'POST', body: JSON.stringify(nf) }).catch(() => null);
    setSaving(false);
    if (r?.cliente && !r.ok) { // 409: ya existe
      if (window.confirm(`Ya existe "${r.cliente.nombre}". ¿Abrir su ficha?`)) onOpenFicha(r.cliente.id);
      return;
    }
    if (!r?.ok) { alert(r?.error || 'No se pudo crear'); return; }
    invalidarClientes(); setNuevo(false); setNf({ nombre: '', telefono: '', mail: '', notas: '' });
    await load(true); onOpenFicha(r.cliente.id);
  };

  const qn = normNombre(q);
  const visibles = (lista || [])
    .filter(c => !qn || normNombre(c.nombre).includes(qn) || normNombre(c.telefono).includes(qn) || normNombre(c.mail).includes(qn))
    .sort((a, b) => orden === 'deuda' ? (b.deuda || 0) - (a.deuda || 0)
      : orden === 'nombre' ? a.nombre.localeCompare(b.nombre)
      : orden === 'pedidos' ? (b.numPedidos || 0) - (a.numPedidos || 0)
      : String(b.ultimoPedido || b.createdAt || '').localeCompare(String(a.ultimoPedido || a.createdAt || '')));
  const totalDeuda = (lista || []).reduce((s, c) => s + (c.deuda || 0), 0);
  const deudores = (lista || []).filter(c => c.deuda > 0.01).length;

  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-4" style={{ background: 'rgba(15,23,42,0.55)' }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div className="w-full sm:max-w-2xl bg-white sm:rounded-[24px] rounded-t-[24px] shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '92vh', height: '92vh' }}
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ type: 'spring', damping: 24 }}>
        <div className="px-5 py-4 border-b border-slate-100 shrink-0 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-lg font-black text-slate-900">👥 Clientes</div>
              {lista && <div className="text-xs text-slate-500">{lista.length} clientes{isAdmin && deudores > 0 ? ` · ${deudores} deben ${cliMoney(totalDeuda)}` : ''}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setNuevo(v => !v)} className="px-3 py-2 rounded-xl bg-slate-900 text-white text-sm font-black">＋ Nuevo</button>
              <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold">✕</button>
            </div>
          </div>
          {nuevo && (
            <div className="rounded-xl border-2 border-slate-200 p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input autoFocus className="sm:col-span-2 rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none" placeholder="Nombre del cliente *" value={nf.nombre} onChange={e => setNf({ ...nf, nombre: e.target.value })} />
              <input className="rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none" placeholder="Teléfono" value={nf.telefono} onChange={e => setNf({ ...nf, telefono: e.target.value })} />
              <input className="rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none" placeholder="Mail" value={nf.mail} onChange={e => setNf({ ...nf, mail: e.target.value })} />
              <input className="sm:col-span-2 rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none" placeholder="Notas" value={nf.notas} onChange={e => setNf({ ...nf, notas: e.target.value })} />
              <button onClick={crear} disabled={saving || !nf.nombre.trim()} className="sm:col-span-2 py-2 rounded-lg bg-emerald-600 text-white text-sm font-black disabled:opacity-40">{saving ? 'Creando…' : 'Crear ficha'}</button>
            </div>
          )}
          <input className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-slate-400" placeholder="🔍 Buscar por nombre, teléfono o mail…" value={q} onChange={e => setQ(e.target.value)} />
          <div className="flex gap-1 flex-wrap">
            {[['reciente', 'Recientes'], ...(isAdmin ? [['deuda', 'Más deuda']] : []), ['pedidos', 'Más pedidos'], ['nombre', 'A-Z']].map(([k, l]) => (
              <button key={k} onClick={() => setOrden(k)} className={"px-3 py-1 rounded-full text-xs font-black " + (orden === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500')}>{l}</button>
            ))}
          </div>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
          {!lista && <div className="text-center text-slate-400 py-10 animate-pulse font-bold">Cargando clientes…</div>}
          {lista && visibles.length === 0 && <div className="text-center text-slate-400 py-10 text-sm">Sin resultados</div>}
          {visibles.map(c => (
            <button key={c.id} onClick={() => onOpenFicha(c.id)} className="w-full text-left px-5 py-3 hover:bg-slate-50">
              <div className="flex items-center justify-between gap-2">
                <div className="font-black text-slate-900 text-sm truncate">{c.nombre}</div>
                {isAdmin && (c.deuda > 0.01
                  ? <span className="shrink-0 text-xs font-black text-red-600">debe {cliMoney(c.deuda)}</span>
                  : c.numPedidos > 0 ? <span className="shrink-0 text-xs font-bold text-emerald-600">al día</span> : null)}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {c.numPedidos} pedido{c.numPedidos !== 1 ? 's' : ''}{c.activos ? ` (${c.activos} en curso)` : ''}
                {c.plataformas?.length ? ' · ' + c.plataformas.join(' / ') : ''}
                {c.ultimoPedido ? ' · ' + cliFecha(c.ultimoPedido) : ''}
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  GENERADOR DE DATA MATRIX (versión web del generador.py)
//  Formato: 51 + 8 dígitos + "_" + 144 + 10 dígitos
//  El 1º usa el bloque que escribes; los demás, bloque aleatorio.
//  La librería (bwip-js) se carga del CDN la primera vez que se abre,
//  así no hay que tocar package.json ni el build de EasyPanel.
// ═══════════════════════════════════════════════════════════════════
// Valores por defecto. Se pueden cambiar desde la pantalla (⚙️ Prefijos)
// y quedan guardados en este navegador.
const DM_DEFAULT = { prefijo: "51", prefijo2: "144", separador: "_", colaLen: 10 };
const DM_KEY = "royal_dm_config";
function dmLeerConfig() {
  try { return { ...DM_DEFAULT, ...JSON.parse(localStorage.getItem(DM_KEY) || "{}") }; }
  catch { return { ...DM_DEFAULT }; }
}
const DM_CDNS = [
  "https://cdn.jsdelivr.net/npm/bwip-js@4.11.4/dist/bwip-js-min.js",
  "https://unpkg.com/bwip-js@4.11.4/dist/bwip-js-min.js",
];
let _bwipPromise = null;
function cargarBwip() {
  if (window.bwipjs) return Promise.resolve(window.bwipjs);
  if (_bwipPromise) return _bwipPromise;
  _bwipPromise = new Promise((resolve, reject) => {
    const probar = (i) => {
      if (i >= DM_CDNS.length) { _bwipPromise = null; reject(new Error("No se pudo cargar la librería de códigos. Revisa tu internet.")); return; }
      const s = document.createElement("script");
      s.src = DM_CDNS[i]; s.async = true;
      s.onload = () => window.bwipjs ? resolve(window.bwipjs) : probar(i + 1);
      s.onerror = () => { s.remove(); probar(i + 1); };
      document.head.appendChild(s);
    };
    probar(0);
  });
  return _bwipPromise;
}
function dmDigitos(n) {
  const a = new Uint32Array(n);
  window.crypto.getRandomValues(a);
  return Array.from(a, x => x % 10).join("");
}
// Descarga como PNG de verdad. Antes se bajaba desde un link "data:" y varios
// navegadores (iPhone, algunos Android) lo guardaban sin extensión o como texto.
// Ahora se arma un archivo image/png real; en celular se abre el menú de
// compartir para "Guardar imagen" (va directo a Fotos).
function dmABlob(dataUrl) {
  const [cab, b64] = dataUrl.split(",");
  const mime = (cab.match(/:(.*?);/) || [])[1] || "image/png";
  const bin = atob(b64); const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return new Blob([u], { type: mime });
}
const dmNombrePng = n => /\.png$/i.test(n) ? n : n + ".png";
const dmEsMovil = () => /iPad|iPhone|iPod|Android/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
function dmBajarArchivo(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nombre; a.type = "image/png";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
async function dmDescargarVarios(lista) {
  const files = lista.map(c => new File([dmABlob(c.png)], dmNombrePng(c.nombre), { type: "image/png" }));
  if (dmEsMovil() && navigator.canShare && navigator.canShare({ files })) {
    try { await navigator.share({ files }); return; }
    catch (e) { if (e && e.name === "AbortError") return; }
  }
  for (const f of files) { dmBajarArchivo(f, f.name); await new Promise(r => setTimeout(r, 300)); }
}
function dmDescargar(dataUrl, nombre) {
  return dmDescargarVarios([{ png: dataUrl, nombre }]);
}

// ── CÓDIGO 128: una línea = un código de barras (números o texto) ──
function GeneradorCode128({ lib }) {
  const [texto, setTexto] = useState("");
  const [alto, setAlto] = useState(15);          // mm
  const [conTexto, setConTexto] = useState(true);
  const [copiado, setCopiado] = useState(null);
  const lineas = texto.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 50);
  const codigos = useMemo(() => {
    if (lib !== "ok" || !window.bwipjs) return [];
    return lineas.map((valor, i) => {
      const nombre = "code128_" + String(i + 1).padStart(2, "0") + ".png";
      // Code 128 solo admite ASCII (sin ñ ni acentos)
      if (/[^\x00-\x7F]/.test(valor)) return { valor, nombre, error: "Tiene ñ, acentos o símbolos raros. Code 128 no los admite." };
      try {
        const c = document.createElement("canvas");
        window.bwipjs.toCanvas(c, { bcid: "code128", text: valor, scale: 3, height: alto, includetext: conTexto, textxalign: "center", textsize: 11, paddingwidth: 12, paddingheight: 8, backgroundcolor: "FFFFFF" });
        return { valor, nombre, png: c.toDataURL("image/png") };
      } catch (e) { return { valor, nombre, error: "No se pudo codificar: " + (e?.message || e) }; }
    });
  }, [texto, alto, conTexto, lib]); // eslint-disable-line
  const buenos = codigos.filter(c => c.png);
  const agregar = v => setTexto(t => (t.trim() ? t.replace(/\s*$/, "") + "\n" : "") + v);
  const copiar = (t, id) => { copyToClipboard(t); setCopiado(id); setTimeout(() => setCopiado(c => c === id ? null : c), 1200); };
  const descargarTodos = () => dmDescargarVarios(buenos);
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Qué quieres codificar · una línea = un código</label>
        <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={4} autoFocus
          placeholder={"123456789012\nPEDIDO-A104\nCualquier texto"}
          className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-lg font-mono font-bold outline-none focus:border-slate-400 resize-y"/>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => agregar(dmDigitos(12))} className="px-3 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-black hover:bg-slate-200 active:scale-95">🎲 Número aleatorio (12)</button>
          <button onClick={() => agregar(dmDigitos(8))} className="px-3 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-black hover:bg-slate-200 active:scale-95">🎲 Aleatorio (8)</button>
          {texto && <button onClick={() => setTexto("")} className="px-3 py-2 rounded-xl bg-slate-100 text-slate-500 text-xs font-black hover:bg-slate-200">✕ Limpiar</button>}
          <div className="ml-auto flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600 cursor-pointer">
              <input type="checkbox" checked={conTexto} onChange={e => setConTexto(e.target.checked)} className="w-4 h-4"/> Texto abajo
            </label>
            <select value={alto} onChange={e => setAlto(Number(e.target.value))} className="rounded-xl border-2 border-slate-200 px-2 py-1.5 text-xs font-bold outline-none">
              {[[10, "Bajo"], [15, "Normal"], [22, "Alto"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="text-[11px] text-slate-400">{lineas.length ? lineas.length + " código" + (lineas.length !== 1 ? "s" : "") + " · se actualiza mientras escribes" : "Escribe arriba y el código aparece solo."}{texto.split("\n").filter(s => s.trim()).length > 50 ? " · Máximo 50 a la vez." : ""}</div>
      </div>

      {buenos.length > 1 && (
        <div className="flex gap-2">
          <button onClick={descargarTodos} className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-black text-sm active:scale-95">⬇ Descargar los {buenos.length} PNG</button>
          <button onClick={() => copiar(buenos.map(c => c.valor).join("\n"), "__all__")} className="px-4 py-3 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-black text-sm active:scale-95">
            {copiado === "__all__" ? "✓ Copiado" : "📋 Copiar valores"}
          </button>
        </div>
      )}
      {codigos.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {codigos.map((c, i) => (
            <div key={i + c.valor} className={"bg-white rounded-2xl border-2 p-3 space-y-2 " + (c.error ? "border-red-200" : "border-slate-100")}>
              <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">{c.nombre}</div>
              {c.error
                ? <div className="rounded-xl bg-red-50 text-red-700 text-xs font-bold p-3">{c.error}</div>
                : <div className="bg-white rounded-xl p-1"><img src={c.png} alt={c.valor} className="w-full h-28 object-contain" style={{ imageRendering: "pixelated" }}/></div>}
              <button onClick={() => copiar(c.valor, i)} className="w-full text-xs font-mono text-slate-600 break-all text-left hover:text-slate-900">
                {copiado === i ? "✓ copiado" : c.valor}
              </button>
              {c.png && <button onClick={() => dmDescargar(c.png, c.nombre)} className="w-full py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">⬇ PNG</button>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function GeneradorDataMatrix({ onClose }) {
  const [modo, setModo] = useState("dm"); // dm | c128
  const [bloque, setBloque] = useState("");
  const [cantidad, setCantidad] = useState(10);
  const [codigos, setCodigos] = useState([]); // [{valor, png, nombre}]
  const [error, setError] = useState("");
  const [copiado, setCopiado] = useState(null);
  const [lib, setLib] = useState(window.bwipjs ? "ok" : "cargando"); // cargando | ok | error
  const [cfg, setCfg] = useState(dmLeerConfig);
  const [verCfg, setVerCfg] = useState(false);
  const setCampo = (k, v) => setCfg(c => {
    const n = { ...c, [k]: v };
    try { localStorage.setItem(DM_KEY, JSON.stringify(n)); } catch {}
    return n;
  });
  const colaLen = Math.min(30, Math.max(0, parseInt(cfg.colaLen, 10) || 0));
  const ejemplo = `${cfg.prefijo}${bloque.padEnd(8, "·")}${cfg.separador}${cfg.prefijo2}${"·".repeat(colaLen)}`;

  useEffect(() => {
    cargarBwip().then(() => setLib("ok")).catch(e => { setLib("error"); setError(e.message); });
  }, []);

  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const generar = () => {
    setError("");
    if (!/^\d{8}$/.test(bloque)) { setError("El bloque debe tener exactamente 8 números."); return; }
    if (!window.bwipjs) { setError("La librería aún no carga. Espera un segundo."); return; }
    try {
      const lista = Array.from({ length: cantidad }, (_, i) => {
        const medio = i === 0 ? bloque : dmDigitos(8);
        const valor = `${cfg.prefijo}${medio}${cfg.separador}${cfg.prefijo2}${dmDigitos(colaLen)}`;
        const canvas = document.createElement("canvas");
        window.bwipjs.toCanvas(canvas, { bcid: "datamatrix", text: valor, scale: 8, padding: 4, backgroundcolor: "FFFFFF" });
        return { valor, png: canvas.toDataURL("image/png"), nombre: `codigo_${String(i + 1).padStart(2, "0")}.png` };
      });
      setCodigos(lista);
    } catch (e) {
      setError("No se pudo generar: " + (e?.message || e));
    }
  };

  const descargarTodos = () => dmDescargarVarios(codigos);

  const copiar = (texto, id) => {
    copyToClipboard(texto);
    setCopiado(id); setTimeout(() => setCopiado(c => c === id ? null : c), 1200);
  };

  return (
    <motion.div className="fixed inset-0 z-[90] flex flex-col bg-slate-100"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 bg-white border-b border-slate-200 shrink-0">
        <div>
          <div className="font-black text-lg text-slate-900">{modo === "dm" ? "🔲 Generador Data Matrix" : "▮▯▮ Generador Código 128"}</div>
          <div className="text-xs text-slate-400 font-mono">{modo === "dm" ? `${cfg.prefijo} + 8 dígitos ${cfg.separador || "(sin separador)"} ${cfg.prefijo2} + ${colaLen} dígitos` : "Números o texto · una línea = un código"}</div>
        </div>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold hover:bg-slate-200">✕</button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 max-w-4xl w-full mx-auto space-y-4">
        <div className="flex gap-1 p-1 rounded-xl bg-slate-200/70 w-fit mx-auto">
          {[["dm", "🔲 Data Matrix"], ["c128", "▮▯▮ Código 128"]].map(([k, l]) => (
            <button key={k} onClick={() => setModo(k)}
              className={"px-4 py-2 rounded-lg text-sm font-black transition-all " + (modo === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
          ))}
        </div>
        {lib === "error" && <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-red-700 text-sm font-bold">No se pudo cargar la librería de códigos. Revisa tu internet y vuelve a abrir.</div>}

        {modo === "c128" && <GeneradorCode128 lib={lib}/>}

        {modo === "dm" && (<>
        {/* ── PREFIJOS EDITABLES ── */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <button onClick={() => setVerCfg(v => !v)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">
            <span>⚙️ Prefijos y formato</span><span className="text-slate-400">{verCfg ? "▲" : "▼"}</span>
          </button>
          {verCfg && (
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["prefijo", "Prefijo 1"], ["separador", "Separador"], ["prefijo2", "Prefijo 2"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1 block">{l}</label>
                    <input value={cfg[k]} onChange={e => setCampo(k, e.target.value.replace(/\s/g, ""))}
                      className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-mono font-bold outline-none focus:border-slate-400"/>
                  </div>
                ))}
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1 block">Dígitos al final</label>
                  <input type="number" min={0} max={30} value={cfg.colaLen} onChange={e => setCampo("colaLen", e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-mono font-bold outline-none focus:border-slate-400"/>
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs font-mono text-slate-600 break-all">Así queda: {ejemplo}</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-400">Se guarda solo en este navegador.</span>
                <button onClick={() => { setCfg({ ...DM_DEFAULT }); try { localStorage.removeItem(DM_KEY); } catch {} }}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">↺ Volver a 51 / 144</button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Bloque intermedio (8 dígitos)</label>
          <div className="flex gap-2">
            <input
              value={bloque} inputMode="numeric" maxLength={8} autoFocus
              onChange={e => setBloque(e.target.value.replace(/\D/g, "").slice(0, 8))}
              onKeyDown={e => { if (e.key === "Enter") generar(); }}
              placeholder="12345678"
              className="flex-1 min-w-0 rounded-xl border-2 border-slate-200 px-4 py-3 text-2xl font-black tracking-[0.3em] tabular-nums outline-none focus:border-slate-400"
            />
            <button type="button" onClick={() => { setBloque(dmDigitos(8)); setError(""); }} title="Poner 8 números al azar"
              className="shrink-0 px-4 rounded-xl bg-slate-100 text-slate-700 font-black text-sm hover:bg-slate-200 active:scale-95 transition-all">🎲 Al azar</button>
          </div>
          <div className="flex items-center justify-between text-xs gap-2">
            <span className={bloque.length === 8 ? "text-emerald-600 font-bold" : "text-slate-400"}>{bloque.length}/8</span>
            {bloque.length === 8 && <span className="text-slate-500 font-mono truncate">1º: {ejemplo}</span>}
          </div>
          <div className="flex gap-3 items-end">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">Cantidad</label>
              <select value={cantidad} onChange={e => setCantidad(Number(e.target.value))}
                className="rounded-xl border-2 border-slate-200 px-3 py-3 text-sm font-bold outline-none">
                {[1, 5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <button onClick={generar} disabled={bloque.length !== 8 || lib !== "ok"}
              className="flex-1 py-3.5 rounded-xl bg-slate-900 text-white font-black text-sm disabled:opacity-30 active:scale-95 transition-all">
              {lib === "cargando" ? "⏳ Cargando..." : `⚡ Generar ${cantidad}`}
            </button>
          </div>
          {error && <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-red-700 text-sm font-bold">{error}</div>}
        </div>

        {codigos.length > 0 && (
          <>
            <div className="flex gap-2">
              <button onClick={descargarTodos} className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-black text-sm active:scale-95">⬇ Descargar los {codigos.length} PNG</button>
              <button onClick={() => copiar(codigos.map(c => c.valor).join("\n"), "__all__")} className="px-4 py-3 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-black text-sm active:scale-95">
                {copiado === "__all__" ? "✓ Copiado" : "📋 Copiar valores"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {codigos.map((c, i) => (
                <div key={c.nombre} className={"bg-white rounded-2xl border-2 p-3 space-y-2 " + (i === 0 ? "border-emerald-400" : "border-slate-100")}>
                  <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">
                    {c.nombre}{i === 0 && <span className="ml-1 text-emerald-600">· tu bloque</span>}
                  </div>
                  <img src={c.png} alt={c.valor} className="w-full aspect-square object-contain" style={{ imageRendering: "pixelated" }} />
                  <button onClick={() => copiar(c.valor, i)} className="w-full text-[10px] font-mono text-slate-600 break-all text-left hover:text-slate-900">
                    {copiado === i ? "✓ copiado" : c.valor}
                  </button>
                  <button onClick={() => dmDescargar(c.png, c.nombre)}
                    className="w-full py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">⬇ PNG</button>
                </div>
              ))}
            </div>
          </>
        )}
        </>)}
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  MAPA AMAZON FLEX
//  Buscas una ciudad, ZIP o dirección → se fija un punto y te lista
//  TODAS las zonas Flex ordenadas por cercanía, con millas y ZIP.
//  Leaflet se carga del CDN la primera vez que se abre (como bwip-js),
//  así no hay que tocar package.json ni el build.
// ═══════════════════════════════════════════════════════════════════
const FLEX_PINS_RAW = [{"state":"Alabama","city":"Birmingham","lat":33.5186,"lon":-86.8104,"zip":"35203"},{"state":"Alabama","city":"Huntsville","lat":34.7304,"lon":-86.5861,"zip":"35893"},{"state":"Alabama","city":"Mobile","lat":30.6954,"lon":-88.0399,"zip":"36601"},{"state":"Alabama","city":"Montgomery","lat":32.3668,"lon":-86.3,"zip":"36123"},{"state":"Alabama","city":"Ozark & Daleville","lat":31.4593,"lon":-85.6411,"zip":"36361"},{"state":"Alabama","city":"Troy","lat":31.8088,"lon":-85.97,"zip":"36082"},{"state":"Alaska","city":"Anchorage","lat":61.2181,"lon":-149.9003,"zip":"99509"},{"state":"Alaska","city":"Fairbanks","lat":64.8378,"lon":-147.7164,"zip":"99701"},{"state":"Arizona","city":"Phoenix","lat":33.4484,"lon":-112.074,"zip":"85001"},{"state":"Arizona","city":"Prescott Valley","lat":34.61,"lon":-112.3157,"zip":"86314"},{"state":"Arizona","city":"Show Low","lat":34.2542,"lon":-110.0298,"zip":"85902"},{"state":"Arizona","city":"Tucson","lat":32.2226,"lon":-110.9747,"zip":"85701"},{"state":"Arkansas","city":"Fayetteville-Springdale","lat":36.0626,"lon":-94.1574,"zip":"72702"},{"state":"Arkansas","city":"Hot Springs","lat":34.5037,"lon":-93.0552,"zip":"71951"},{"state":"Arkansas","city":"Jonesboro-Trumann","lat":35.8423,"lon":-90.7043,"zip":"72401"},{"state":"Arkansas","city":"Little Rock","lat":34.7465,"lon":-92.2896,"zip":"72201"},{"state":"Arkansas","city":"Yellville","lat":36.2295,"lon":-92.6849,"zip":"72677"},{"state":"California","city":"Bakersfield","lat":35.3733,"lon":-119.0187,"zip":"93302"},{"state":"California","city":"Fresno","lat":36.7378,"lon":-119.7871,"zip":"93721"},{"state":"California","city":"Glenn County","lat":39.5958,"lon":-122.3922,"zip":"95988"},{"state":"California","city":"Grass Valley","lat":39.2191,"lon":-121.0611,"zip":"95924"},{"state":"California","city":"Greater Los Angeles Area","lat":34.0522,"lon":-118.2437,"zip":"90009"},{"state":"California","city":"Napa","lat":38.2975,"lon":-122.2869,"zip":"94581"},{"state":"California","city":"Palm Desert","lat":33.7222,"lon":-116.3744,"zip":"92255"},{"state":"California","city":"Palmdale & Lancaster","lat":34.6868,"lon":-118.1542,"zip":"93534"},{"state":"California","city":"Sacramento","lat":38.5816,"lon":-121.4944,"zip":"94203"},{"state":"California","city":"Salinas & Monterey","lat":36.6777,"lon":-121.6555,"zip":"93902"},{"state":"California","city":"San Bernardino County","lat":34.1083,"lon":-117.2898,"zip":"92402"},{"state":"California","city":"San Diego","lat":32.7157,"lon":-117.1611,"zip":"92101"},{"state":"California","city":"San Francisco Bay Area","lat":37.7749,"lon":-122.4194,"zip":"94119"},{"state":"California","city":"Santa Barbara","lat":34.4208,"lon":-119.6982,"zip":"93102"},{"state":"California","city":"Stockton","lat":37.9577,"lon":-121.2908,"zip":"95296"},{"state":"California","city":"Temecula-Hemet","lat":33.4936,"lon":-117.1484,"zip":"92589"},{"state":"Colorado","city":"Colorado Springs","lat":38.8339,"lon":-104.8214,"zip":"80901"},{"state":"Colorado","city":"Denver","lat":39.7392,"lon":-104.9903,"zip":"80261"},{"state":"Colorado","city":"Fort Collins","lat":40.5853,"lon":-105.0844,"zip":"80523"},{"state":"Colorado","city":"Grand Junction","lat":39.0639,"lon":-108.5506,"zip":"81501"},{"state":"Connecticut","city":"Hartford & New Haven","lat":41.7658,"lon":-72.6734,"zip":"06161"},{"state":"Connecticut","city":"Springfield","lat":42.1015,"lon":-72.5898,"zip":"01101"},{"state":"District of Columbia (DC)","city":"Greater Washington D.C. Area","lat":38.9072,"lon":-77.0369,"zip":"20036"},{"state":"Delaware","city":"Salisbury-Seaford","lat":38.3607,"lon":-75.5994,"zip":"21801"},{"state":"Delaware","city":"Wilmington","lat":39.7391,"lon":-75.5398,"zip":"19889"},{"state":"Florida","city":"Destin","lat":30.3935,"lon":-86.4958,"zip":"32540"},{"state":"Florida","city":"Fort Myers","lat":26.6406,"lon":-81.8723,"zip":"33902"},{"state":"Florida","city":"Gainesville","lat":29.6516,"lon":-82.3248,"zip":"32627"},{"state":"Florida","city":"Greater West Palm Beach Area","lat":26.7153,"lon":-80.0534,"zip":"33402"},{"state":"Florida","city":"Jacksonville","lat":30.3322,"lon":-81.6557,"zip":"32255"},{"state":"Florida","city":"Lakeland-Winter Haven","lat":28.0395,"lon":-81.9498,"zip":"33804"},{"state":"Florida","city":"Marianna-Cottondale","lat":30.7744,"lon":-85.2271,"zip":"32446"},{"state":"Florida","city":"Melbourne","lat":28.0836,"lon":-80.6081,"zip":"32902"},{"state":"Florida","city":"Miami-Fort Lauderdale","lat":25.7617,"lon":-80.1918,"zip":"33131"},{"state":"Florida","city":"Orlando","lat":28.5383,"lon":-81.3792,"zip":"32802"},{"state":"Florida","city":"Pensacola","lat":30.4213,"lon":-87.2169,"zip":"32516"},{"state":"Florida","city":"Sarasota & Venice","lat":27.3364,"lon":-82.5307,"zip":"34276"},{"state":"Florida","city":"Tallahassee","lat":30.4383,"lon":-84.2807,"zip":"32302"},{"state":"Florida","city":"Tampa-St. Petersburg","lat":27.9506,"lon":-82.4572,"zip":"33606"},{"state":"Georgia","city":"Appling","lat":33.5404,"lon":-82.3151,"zip":"30917"},{"state":"Georgia","city":"Atlanta","lat":33.749,"lon":-84.388,"zip":"30302"},{"state":"Georgia","city":"Columbus","lat":32.4609,"lon":-84.9877,"zip":"31999"},{"state":"Georgia","city":"Savannah","lat":32.0809,"lon":-81.0912,"zip":"31401"},{"state":"Georgia","city":"Warner Robins-Macon","lat":32.621,"lon":-83.6324,"zip":"31093"},{"state":"Hawaii","city":"Oahu","lat":21.4389,"lon":-158.0001,"zip":"96797"},{"state":"Idaho","city":"Boise","lat":43.615,"lon":-116.2023,"zip":"83707"},{"state":"Idaho","city":"Idaho Falls","lat":43.4917,"lon":-112.0339,"zip":"83402"},{"state":"Idaho","city":"Jerome","lat":42.7243,"lon":-114.5188,"zip":"83338"},{"state":"Illinois","city":"Champaign-Urbana","lat":40.1164,"lon":-88.2434,"zip":"61824"},{"state":"Illinois","city":"Chicago","lat":41.8781,"lon":-87.6298,"zip":"60603"},{"state":"Illinois","city":"Peoria","lat":40.6936,"lon":-89.589,"zip":"61602"},{"state":"Illinois","city":"St. Louis","lat":38.627,"lon":-90.1994,"zip":"63150"},{"state":"Indiana","city":"Evansville","lat":37.9716,"lon":-87.5711,"zip":"47708"},{"state":"Indiana","city":"Fort Wayne","lat":41.0793,"lon":-85.1394,"zip":"46802"},{"state":"Indiana","city":"Gary","lat":41.5934,"lon":-87.3464,"zip":"46402"},{"state":"Indiana","city":"Indianapolis","lat":39.7684,"lon":-86.1581,"zip":"46255"},{"state":"Indiana","city":"Lafayette-Flora","lat":40.4167,"lon":-86.8753,"zip":"47901"},{"state":"Indiana","city":"Louisville","lat":38.2527,"lon":-85.7585,"zip":"40041"},{"state":"Indiana","city":"South Bend","lat":41.6764,"lon":-86.252,"zip":"46626"},{"state":"Indiana","city":"Terre Haute","lat":39.4667,"lon":-87.4139,"zip":"47801"},{"state":"Indiana","city":"Washington","lat":38.6593,"lon":-87.1728,"zip":"47501"},{"state":"Iowa","city":"Burlington","lat":40.8076,"lon":-91.1129,"zip":"52601"},{"state":"Iowa","city":"Davenport","lat":41.5236,"lon":-90.5776,"zip":"52801"},{"state":"Iowa","city":"Des Moines","lat":41.5868,"lon":-93.625,"zip":"50392"},{"state":"Iowa","city":"Dubuque","lat":42.5006,"lon":-90.6646,"zip":"52004"},{"state":"Iowa","city":"Iowa City","lat":41.6611,"lon":-91.5302,"zip":"52243"},{"state":"Iowa","city":"Mason City","lat":43.1536,"lon":-93.201,"zip":"50402"},{"state":"Iowa","city":"Omaha","lat":41.2565,"lon":-95.9345,"zip":"68101"},{"state":"Iowa","city":"Waterloo-Cedar Falls","lat":42.4928,"lon":-92.3426,"zip":"50702"},{"state":"Kansas","city":"Great Bend","lat":38.3644,"lon":-98.7648,"zip":"67530"},{"state":"Kansas","city":"Kansas City","lat":39.1141,"lon":-94.6275,"zip":"66117"},{"state":"Kansas","city":"McPherson","lat":38.3708,"lon":-97.6642,"zip":"67460"},{"state":"Kansas","city":"Wichita","lat":37.6872,"lon":-97.3301,"zip":"67202"},{"state":"Kentucky","city":"Lexington","lat":38.0406,"lon":-84.5037,"zip":"40507"},{"state":"Kentucky","city":"London-Corbin","lat":37.1289,"lon":-84.0833,"zip":"40742"},{"state":"Kentucky","city":"Paducah","lat":37.0834,"lon":-88.6,"zip":"42003"},{"state":"Louisiana","city":"Alexandria-Boyce","lat":31.3113,"lon":-92.4451,"zip":"71306"},{"state":"Louisiana","city":"Baton Rouge","lat":30.4515,"lon":-91.1871,"zip":"70801"},{"state":"Louisiana","city":"Lafayette","lat":30.2241,"lon":-92.0198,"zip":"70502"},{"state":"Louisiana","city":"Monroe","lat":32.5093,"lon":-92.1193,"zip":"71207"},{"state":"Louisiana","city":"New Orleans","lat":29.9511,"lon":-90.0715,"zip":"70140"},{"state":"Louisiana","city":"Shreveport","lat":32.5252,"lon":-93.7502,"zip":"71171"},{"state":"Maine","city":"Portland","lat":43.6591,"lon":-70.2568,"zip":"04101"},{"state":"Maryland","city":"Annapolis","lat":38.9784,"lon":-76.4922,"zip":"21404"},{"state":"Maryland","city":"Baltimore","lat":39.2904,"lon":-76.6122,"zip":"21283"},{"state":"Maryland","city":"Hagerstown","lat":39.6418,"lon":-77.72,"zip":"21741"},{"state":"Massachusetts","city":"Boston","lat":42.3601,"lon":-71.0589,"zip":"02117"},{"state":"Massachusetts","city":"Hyannis","lat":41.6526,"lon":-70.2829,"zip":"02601"},{"state":"Massachusetts","city":"Providence","lat":41.824,"lon":-71.4128,"zip":"02912"},{"state":"Massachusetts","city":"Worcester","lat":42.2626,"lon":-71.8023,"zip":"01601"},{"state":"Michigan","city":"Bad Axe-Croswell","lat":43.7997,"lon":-82.9888,"zip":"48413"},{"state":"Michigan","city":"Detroit","lat":42.3314,"lon":-83.0458,"zip":"48231"},{"state":"Michigan","city":"Gaylord","lat":45.0275,"lon":-84.6747,"zip":"49734"},{"state":"Michigan","city":"Grand Rapids-Kalamazoo","lat":42.9634,"lon":-85.6681,"zip":"49502"},{"state":"Michigan","city":"Lansing","lat":42.7325,"lon":-84.5555,"zip":"48901"},{"state":"Michigan","city":"Mount Pleasant-Midland","lat":43.5978,"lon":-84.7675,"zip":"48858"},{"state":"Michigan","city":"Saginaw","lat":43.4195,"lon":-83.9508,"zip":"48663"},{"state":"Michigan","city":"Traverse City","lat":44.7631,"lon":-85.6206,"zip":"49686"},{"state":"Minnesota","city":"Fergus Falls-Alexandria","lat":46.283,"lon":-96.0775,"zip":"56538"},{"state":"Minnesota","city":"Minneapolis","lat":44.9778,"lon":-93.265,"zip":"55440"},{"state":"Minnesota","city":"St. Cloud","lat":45.5579,"lon":-94.1632,"zip":"56302"},{"state":"Mississippi","city":"George County","lat":30.8754,"lon":-88.6363,"zip":"39452"},{"state":"Mississippi","city":"Hattiesburg","lat":31.3271,"lon":-89.2903,"zip":"39401"},{"state":"Mississippi","city":"Jackson","lat":32.2988,"lon":-90.1848,"zip":"39205"},{"state":"Mississippi","city":"Memphis","lat":35.1495,"lon":-90.049,"zip":"38101"},{"state":"Mississippi","city":"Starkville","lat":33.4504,"lon":-88.8184,"zip":"39759"},{"state":"Missouri","city":"Columbia-Jefferson City","lat":38.5767,"lon":-92.1735,"zip":"65102"},{"state":"Missouri","city":"Joplin","lat":37.0842,"lon":-94.5133,"zip":"64802"},{"state":"Missouri","city":"Kansas City","lat":39.0997,"lon":-94.5786,"zip":"64121"},{"state":"Missouri","city":"Trenton","lat":40.0789,"lon":-93.6166,"zip":"64683"},{"state":"Montana","city":"Billings","lat":45.7833,"lon":-108.5007,"zip":"59103"},{"state":"Montana","city":"Missoula","lat":46.8721,"lon":-113.994,"zip":"59806"},{"state":"Nebraska","city":"Grand Island","lat":40.9264,"lon":-98.342,"zip":"68801"},{"state":"Nebraska","city":"Lincoln","lat":40.8136,"lon":-96.7026,"zip":"68508"},{"state":"Nevada","city":"Churchill","lat":39.4736,"lon":-118.7775,"zip":"89407"},{"state":"Nevada","city":"Las Vegas","lat":36.1699,"lon":-115.1398,"zip":"89162"},{"state":"Nevada","city":"Reno","lat":39.5296,"lon":-119.8138,"zip":"89504"},{"state":"New Hampshire","city":"Bedford","lat":42.9461,"lon":-71.5098,"zip":"03110"},{"state":"New Hampshire","city":"Hooksett","lat":43.0762,"lon":-71.44,"zip":"03106"},{"state":"New Hampshire","city":"Manchester","lat":42.9956,"lon":-71.4548,"zip":"03105"},{"state":"New Hampshire","city":"Merrimack","lat":42.8651,"lon":-71.4933,"zip":"03054"},{"state":"New Hampshire","city":"Nashua","lat":42.7654,"lon":-71.4676,"zip":"03061"},{"state":"New Hampshire","city":"Plaistow","lat":42.8362,"lon":-71.0953,"zip":"03865"},{"state":"New Hampshire","city":"Salem","lat":42.7884,"lon":-71.2009,"zip":"03079"},{"state":"New Jersey","city":"Brick","lat":40.0581,"lon":-74.1098,"zip":"08723"},{"state":"New Jersey","city":"Cherry Hill","lat":39.9348,"lon":-75.0307,"zip":"08002"},{"state":"New Jersey","city":"Clifton","lat":40.8584,"lon":-74.1638,"zip":"07015"},{"state":"New Jersey","city":"Edison","lat":40.5187,"lon":-74.4121,"zip":"08899"},{"state":"New Jersey","city":"Elizabeth","lat":40.664,"lon":-74.2107,"zip":"07207"},{"state":"New Jersey","city":"Greater New York City","lat":40.7128,"lon":-74.006,"zip":"10007"},{"state":"New Jersey","city":"Hoboken","lat":40.7439,"lon":-74.0324,"zip":"07030"},{"state":"New Jersey","city":"Jersey City","lat":40.7178,"lon":-74.0431,"zip":"07302"},{"state":"New Jersey","city":"Lakewood","lat":40.0979,"lon":-74.2179,"zip":"08701"},{"state":"New Jersey","city":"New Jersey Shore","lat":40.0583,"lon":-74.4057,"zip":"08733"},{"state":"New Jersey","city":"Newark","lat":40.7357,"lon":-74.1724,"zip":"07175"},{"state":"New Jersey","city":"Orange","lat":40.7707,"lon":-74.2326,"zip":"07050"},{"state":"New Jersey","city":"Parsippany","lat":40.8579,"lon":-74.426,"zip":"07054"},{"state":"New Jersey","city":"Philadelphia","lat":39.9526,"lon":-75.1652,"zip":"19195"},{"state":"New Jersey","city":"Toms River","lat":39.9537,"lon":-74.1979,"zip":"08722"},{"state":"New Jersey","city":"Trenton & Freehold Township","lat":40.2206,"lon":-74.7597,"zip":"08608"},{"state":"New Jersey","city":"Union","lat":40.6976,"lon":-74.2632,"zip":"07083"},{"state":"New Jersey","city":"Woodbridge","lat":40.5576,"lon":-74.2846,"zip":"07095"},{"state":"New Mexico","city":"Alamogordo","lat":32.8995,"lon":-105.9603,"zip":"88311"},{"state":"New Mexico","city":"Albuquerque","lat":35.0844,"lon":-106.6504,"zip":"87158"},{"state":"New Mexico","city":"El Paso","lat":31.7619,"lon":-106.485,"zip":"79941"},{"state":"New Mexico","city":"Farmington-Bloomfield","lat":36.7281,"lon":-108.2087,"zip":"87401"},{"state":"New Mexico","city":"Santa Fe","lat":35.687,"lon":-105.9378,"zip":"87502"},{"state":"New York","city":"Greater New York City","lat":40.7128,"lon":-74.006,"zip":"10007"},{"state":"New York","city":"Long Island","lat":40.7891,"lon":-73.135,"zip":"11779"},{"state":"New York","city":"Poughkeepsie-Middletown","lat":41.7004,"lon":-73.921,"zip":"12601"},{"state":"New York","city":"Rochester","lat":43.1566,"lon":-77.6088,"zip":"14604"},{"state":"New York","city":"Syracuse","lat":43.0481,"lon":-76.1474,"zip":"13201"},{"state":"New York","city":"Utica-Rome","lat":43.1009,"lon":-75.2327,"zip":"13504"},{"state":"New York","city":"Yonkers/White Plains","lat":40.9312,"lon":-73.8987,"zip":"10702"},{"state":"North Carolina","city":"Asheville","lat":35.5951,"lon":-82.5515,"zip":"28801"},{"state":"North Carolina","city":"Charlotte","lat":35.2271,"lon":-80.8431,"zip":"28233"},{"state":"North Carolina","city":"Fayetteville-Lumberton","lat":35.0527,"lon":-78.8784,"zip":"28305"},{"state":"North Carolina","city":"Franklin-Highlands","lat":35.1821,"lon":-83.3812,"zip":"28744"},{"state":"North Carolina","city":"Greensboro","lat":36.0726,"lon":-79.792,"zip":"27402"},{"state":"North Carolina","city":"Kinston","lat":35.2627,"lon":-77.5816,"zip":"28502"},{"state":"North Carolina","city":"Raleigh","lat":35.7796,"lon":-78.6382,"zip":"27697"},{"state":"North Dakota","city":"Dickinson","lat":46.8792,"lon":-102.7896,"zip":"58602"},{"state":"North Dakota","city":"Fargo","lat":46.8772,"lon":-96.7898,"zip":"58106"},{"state":"North Dakota","city":"Grand Forks","lat":47.9253,"lon":-97.0329,"zip":"58206"},{"state":"North Dakota","city":"Minot-Stanley","lat":48.233,"lon":-101.296,"zip":"58702"},{"state":"Ohio","city":"Akron","lat":41.0814,"lon":-81.519,"zip":"44304"},{"state":"Ohio","city":"Cincinnati","lat":39.1031,"lon":-84.512,"zip":"45202"},{"state":"Ohio","city":"Cleveland","lat":41.4993,"lon":-81.6944,"zip":"44192"},{"state":"Ohio","city":"Dayton","lat":39.7589,"lon":-84.1916,"zip":"45412"},{"state":"Ohio","city":"Lima-Findlay","lat":40.7427,"lon":-84.1052,"zip":"45804"},{"state":"Ohio","city":"Toledo","lat":41.6528,"lon":-83.5379,"zip":"43660"},{"state":"Ohio","city":"Youngstown","lat":41.0998,"lon":-80.6495,"zip":"44503"},{"state":"Oklahoma","city":"Enid","lat":36.3956,"lon":-97.8784,"zip":"73702"},{"state":"Oklahoma","city":"Grove","lat":36.5934,"lon":-94.7686,"zip":"74344"},{"state":"Oklahoma","city":"Oklahoma City","lat":35.4676,"lon":-97.5164,"zip":"73123"},{"state":"Oklahoma","city":"Ponca-Arkansas City","lat":36.7068,"lon":-97.0858,"zip":"74601"},{"state":"Oklahoma","city":"Stillwater","lat":36.1156,"lon":-97.0584,"zip":"74077"},{"state":"Oklahoma","city":"Tulsa","lat":36.154,"lon":-95.9928,"zip":"74102"},{"state":"Oregon","city":"Altamont","lat":42.2046,"lon":-121.7378,"zip":"97603"},{"state":"Oregon","city":"Eugene","lat":44.0521,"lon":-123.0868,"zip":"97440"},{"state":"Oregon","city":"Grants Pass-Medford","lat":42.439,"lon":-123.3284,"zip":"97528"},{"state":"Pennsylvania","city":"Allentown","lat":40.6084,"lon":-75.4902,"zip":"18105"},{"state":"Pennsylvania","city":"Altoona","lat":40.5187,"lon":-78.3947,"zip":"16601"},{"state":"Pennsylvania","city":"Erie","lat":42.1292,"lon":-80.0851,"zip":"16512"},{"state":"Pennsylvania","city":"Lancaster","lat":40.0379,"lon":-76.3055,"zip":"17611"},{"state":"Pennsylvania","city":"Philadelphia","lat":39.9526,"lon":-75.1652,"zip":"19195"},{"state":"Pennsylvania","city":"Pittsburgh","lat":40.4406,"lon":-79.9959,"zip":"15212"},{"state":"Pennsylvania","city":"Trenton & Freehold Township","lat":40.2206,"lon":-74.7597,"zip":"08608"},{"state":"Pennsylvania","city":"Wilkes-Barre/Scranton","lat":41.2459,"lon":-75.8813,"zip":"18701"},{"state":"Pennsylvania","city":"Williamsport-Montgomery","lat":41.2412,"lon":-77.0011,"zip":"17701"},{"state":"Rhode Island","city":"Cranston","lat":41.7798,"lon":-71.4373,"zip":"02910"},{"state":"Rhode Island","city":"Providence","lat":41.824,"lon":-71.4128,"zip":"02912"},{"state":"Rhode Island","city":"Warwick","lat":41.7001,"lon":-71.4162,"zip":"02887"},{"state":"South Carolina","city":"Charleston","lat":32.7765,"lon":-79.9311,"zip":"29402"},{"state":"South Carolina","city":"Columbia","lat":34.0007,"lon":-81.0348,"zip":"29202"},{"state":"South Carolina","city":"Florence","lat":34.1954,"lon":-79.7626,"zip":"29501"},{"state":"South Carolina","city":"Greenville-Spartanburg","lat":34.8526,"lon":-82.394,"zip":"29616"},{"state":"South Carolina","city":"Myrtle Beach","lat":33.6891,"lon":-78.8867,"zip":"29577"},{"state":"South Carolina","city":"Spartanburg County","lat":34.9496,"lon":-81.932,"zip":"29305"},{"state":"South Dakota","city":"Rapid City","lat":44.0805,"lon":-103.231,"zip":"57703"},{"state":"South Dakota","city":"Sioux Falls","lat":43.5446,"lon":-96.7311,"zip":"57186"},{"state":"Tennessee","city":"Bristol-Kingsport","lat":36.5484,"lon":-82.5618,"zip":"37660"},{"state":"Tennessee","city":"Chattanooga","lat":35.0456,"lon":-85.3097,"zip":"37401"},{"state":"Tennessee","city":"Dover","lat":36.4856,"lon":-87.84,"zip":"37058"},{"state":"Tennessee","city":"Knoxville","lat":35.9606,"lon":-83.9207,"zip":"37929"},{"state":"Tennessee","city":"Nashville","lat":36.1627,"lon":-86.7816,"zip":"37243"},{"state":"Texas","city":"Abilene","lat":32.4487,"lon":-99.7331,"zip":"79604"},{"state":"Texas","city":"Amarillo","lat":35.222,"lon":-101.8313,"zip":"79159"},{"state":"Texas","city":"Austin","lat":30.2672,"lon":-97.7431,"zip":"78713"},{"state":"Texas","city":"Corpus Christi","lat":27.8006,"lon":-97.3964,"zip":"78427"},{"state":"Texas","city":"Dallas-Ft. Worth","lat":32.7767,"lon":-96.797,"zip":"75037"},{"state":"Texas","city":"Houston","lat":29.7604,"lon":-95.3698,"zip":"77052"},{"state":"Texas","city":"Lubbock","lat":33.5779,"lon":-101.8552,"zip":"79409"},{"state":"Texas","city":"McAllen-Edinburg-Mission","lat":26.2034,"lon":-98.23,"zip":"78505"},{"state":"Texas","city":"Odessa-Midland","lat":31.9974,"lon":-102.0779,"zip":"79711"},{"state":"Texas","city":"Paris","lat":33.6609,"lon":-95.5555,"zip":"75461"},{"state":"Texas","city":"Rio Grande City","lat":26.3795,"lon":-98.8203,"zip":"78582"},{"state":"Texas","city":"San Angelo","lat":31.4638,"lon":-100.437,"zip":"76902"},{"state":"Texas","city":"San Antonio","lat":29.4241,"lon":-98.4936,"zip":"78288"},{"state":"Texas","city":"Waco","lat":31.5493,"lon":-97.1467,"zip":"76702"},{"state":"Utah","city":"Provo-Orem","lat":40.2338,"lon":-111.6585,"zip":"84606"},{"state":"Utah","city":"Richfield","lat":38.7714,"lon":-112.0847,"zip":"84701"},{"state":"Utah","city":"Salt Lake City","lat":40.7608,"lon":-111.891,"zip":"84138"},{"state":"Utah","city":"St. George","lat":37.0965,"lon":-113.5684,"zip":"84770"},{"state":"Virginia","city":"Charlottesville","lat":38.0293,"lon":-78.4767,"zip":"22902"},{"state":"Virginia","city":"Edinburg","lat":38.8221,"lon":-78.5644,"zip":"22824"},{"state":"Virginia","city":"Richmond","lat":37.5407,"lon":-77.436,"zip":"23298"},{"state":"Virginia","city":"Roanoke","lat":37.271,"lon":-79.9414,"zip":"24023"},{"state":"Virginia","city":"Virginia Beach","lat":36.8529,"lon":-75.978,"zip":"23458"},{"state":"Virginia","city":"Warsaw-Kilmarnock","lat":37.7546,"lon":-76.7588,"zip":"22437"},{"state":"Washington","city":"Bellingham","lat":48.7519,"lon":-122.4787,"zip":"98225"},{"state":"Washington","city":"Greater Seattle Area","lat":47.6062,"lon":-122.3321,"zip":"98111"},{"state":"Washington","city":"Kennewick-Richland","lat":46.2087,"lon":-119.1361,"zip":"99336"},{"state":"Washington","city":"Kitsap","lat":47.6477,"lon":-122.6353,"zip":"98311"},{"state":"Washington","city":"Spokane","lat":47.6588,"lon":-117.426,"zip":"99209"},{"state":"Washington","city":"Union Gap-Yakima","lat":46.6021,"lon":-120.5059,"zip":"98901"},{"state":"Washington","city":"Wenatchee","lat":47.4235,"lon":-120.3103,"zip":"98807"},{"state":"West Virginia","city":"Martinsburg","lat":39.4562,"lon":-77.9636,"zip":"25402"},{"state":"Wisconsin","city":"Appleton","lat":44.2619,"lon":-88.4154,"zip":"54914"},{"state":"Wisconsin","city":"Eau Claire","lat":44.8113,"lon":-91.4985,"zip":"54702"},{"state":"Wisconsin","city":"La Crosse-Houston","lat":43.8014,"lon":-91.2396,"zip":"54601"},{"state":"Wisconsin","city":"Madison","lat":43.0731,"lon":-89.4012,"zip":"53706"},{"state":"Wisconsin","city":"Milwaukee","lat":43.0389,"lon":-87.9065,"zip":"53201"},{"state":"Wisconsin","city":"Prairie du Chien","lat":43.0517,"lon":-91.1413,"zip":"53821"},{"state":"Wisconsin","city":"Racine & Kenosha County","lat":42.7261,"lon":-87.7829,"zip":"53407"},{"state":"Wisconsin","city":"Wausau-Weston","lat":44.9591,"lon":-89.6301,"zip":"54401"},{"state":"Wyoming","city":"Casper","lat":42.8666,"lon":-106.3131,"zip":"82605"},{"state":"Wyoming","city":"Gillette","lat":44.2911,"lon":-105.5022,"zip":"82716"},{"state":"Wyoming","city":"Rock Springs-Green River","lat":41.5875,"lon":-109.2029,"zip":"82902"}];

// Algunas zonas aparecen repetidas en dos estados (NYC en NJ y NY, Philadelphia
// en NJ y PA...). Se juntan en una sola para que la lista no muestre duplicados.
const FLEX_PINS = (() => {
  const m = new Map();
  FLEX_PINS_RAW.forEach(p => {
    const k = p.city + '|' + p.lat + ',' + p.lon;
    if (m.has(k)) { const e = m.get(k); if (!e.states.includes(p.state)) e.states.push(p.state); }
    else m.set(k, { ...p, id: k, states: [p.state] });
  });
  return [...m.values()].map(p => ({ ...p, stateLabel: p.states.join(' / ') }));
})();
const FLEX_ESTADOS = [...new Set(FLEX_PINS_RAW.map(p => p.state))].sort();
const FX_ABREV = {"alabama":"al","alaska":"ak","arizona":"az","arkansas":"ar","california":"ca","colorado":"co","connecticut":"ct","delaware":"de","district of columbia":"dc","florida":"fl","georgia":"ga","hawaii":"hi","idaho":"id","illinois":"il","indiana":"in","iowa":"ia","kansas":"ks","kentucky":"ky","louisiana":"la","maine":"me","maryland":"md","massachusetts":"ma","michigan":"mi","minnesota":"mn","mississippi":"ms","missouri":"mo","montana":"mt","nebraska":"ne","nevada":"nv","new hampshire":"nh","new jersey":"nj","new mexico":"nm","new york":"ny","north carolina":"nc","north dakota":"nd","ohio":"oh","oklahoma":"ok","oregon":"or","pennsylvania":"pa","rhode island":"ri","south carolina":"sc","south dakota":"sd","tennessee":"tn","texas":"tx","utah":"ut","vermont":"vt","virginia":"va","washington":"wa","west virginia":"wv","wisconsin":"wi","wyoming":"wy"};

// ── Búsqueda tolerante: sin acentos, en español y con errores de tecleo ──
function fxNorm(s) {
  return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}
const FX_ALIAS = {
  'nueva york':'new york','nyc':'new york','filadelfia':'philadelphia','la':'los angeles',
  'sf':'san francisco','san fran':'san francisco','frisco':'san francisco',
  'nueva orleans':'new orleans','fenix':'phoenix','san luis':'st louis','saint louis':'st louis',
  'washington dc':'greater washington','dc':'greater washington',
  'nueva jersey':'new jersey','nuevo mexico':'new mexico','nuevo hampshire':'new hampshire',
  'carolina del norte':'north carolina','carolina del sur':'south carolina',
  'dakota del norte':'north dakota','dakota del sur':'south dakota','virginia occidental':'west virginia',
  'florida':'florida','tejas':'texas','california':'california'
};
const fxAlias = q => FX_ALIAS[q] || q;
function fxEdit(a, b) {
  const m = a.length, n = b.length; if (!m) return n; if (!n) return m;
  const dp = Array.from({ length: m + 1 }, (_, i) => i);
  for (let j = 1; j <= n; j++) {
    let prev = dp[0]; dp[0] = j;
    for (let i = 1; i <= m; i++) {
      const tmp = dp[i];
      dp[i] = Math.min(dp[i] + 1, dp[i - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return dp[m];
}
const fxTol = len => len <= 3 ? 0 : len <= 5 ? 1 : len <= 8 ? 2 : 3;
// Menor = mejor. null = no coincide.
function fxScore(q, field) {
  if (!q) return 0;
  if (field.includes(q)) return field.startsWith(q) ? 0 : 1;
  const qt = q.split(' ').filter(Boolean), ft = field.split(' ').filter(Boolean);
  let total = 0;
  for (const a of qt) {
    let best = Infinity;
    for (const b of ft) {
      if (b.startsWith(a)) { best = Math.min(best, 0.5); continue; }
      if (b.includes(a)) { best = Math.min(best, 1); continue; }
      const d = fxEdit(a, b);
      if (d <= fxTol(Math.max(a.length, b.length))) best = Math.min(best, 2 + d);
    }
    if (best === Infinity) return null;
    total += best;
  }
  return total / qt.length;
}
function fxMejorCiudad(raw) {
  const q = fxAlias(fxNorm(raw)); if (!q) return null;
  let best = null, bs = Infinity;
  FLEX_PINS.forEach(p => { const s = fxScore(q, fxNorm(p.city)); if (s != null && s < bs) { bs = s; best = p; } });
  return best ? { pin: best, score: bs } : null;
}
function fxDistMi(aLat, aLon, bLat, bLon) {
  const R = 3958.8, r = d => d * Math.PI / 180;
  const dLat = r(bLat - aLat), dLon = r(bLon - aLon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(r(aLat)) * Math.cos(r(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
async function fxGeocodeZip(zip) {
  try {
    const r = await fetch('https://api.zippopotam.us/us/' + zip);
    if (!r.ok) return null;
    const j = await r.json(); const p = j.places && j.places[0]; if (!p) return null;
    return { lat: +p.latitude, lon: +p.longitude, label: zip + ' · ' + p['place name'] + ', ' + p['state abbreviation'] };
  } catch { return null; }
}
async function fxGeocode(raw) {
  const partes = raw.split(',').map(s => s.trim()).filter(Boolean);
  const nombre = partes[0], estadoTxt = fxNorm(partes.slice(1).join(' '));
  // 1) Open-Meteo: gratis, sin clave. Si pusiste estado ("Gilroy, CA") lo usa para escoger.
  try {
    const r = await fetch('https://geocoding-api.open-meteo.com/v1/search?count=10&language=en&countryCode=US&name=' + encodeURIComponent(nombre));
    if (r.ok) {
      const res = ((await r.json()).results || []).filter(g => g.country_code === 'US');
      let g = res[0];
      if (estadoTxt) g = res.find(x => { const a = fxNorm(x.admin1); return a === estadoTxt || FX_ABREV[a] === estadoTxt; }) || null;
      if (g) return { lat: +g.latitude, lon: +g.longitude, label: [g.name, g.admin1].filter(Boolean).join(', ') };
    }
  } catch {}
  // 2) Respaldo: Nominatim (entiende direcciones completas)
  try {
    const r = await fetch('https://nominatim.openstreetmap.org/search?format=json&countrycodes=us&limit=1&q=' + encodeURIComponent(raw));
    if (!r.ok) return null;
    const j = await r.json(); if (!j.length) return null;
    return { lat: +j[0].lat, lon: +j[0].lon, label: j[0].display_name.split(',').slice(0, 3).join(',') };
  } catch { return null; }
}
let _leafletPromise = null;
function cargarLeaflet() {
  if (window.L && window.L.map) return Promise.resolve(window.L);
  if (_leafletPromise) return _leafletPromise;
  _leafletPromise = new Promise((resolve, reject) => {
    if (!document.getElementById('leaflet-css')) {
      const l = document.createElement('link');
      l.id = 'leaflet-css'; l.rel = 'stylesheet';
      l.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(l);
    }
    const urls = ['https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js', 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'];
    const probar = i => {
      if (i >= urls.length) { _leafletPromise = null; reject(new Error('No se pudo cargar el mapa. Revisa tu internet.')); return; }
      const s = document.createElement('script'); s.src = urls[i]; s.async = true;
      s.onload = () => (window.L && window.L.map) ? resolve(window.L) : probar(i + 1);
      s.onerror = () => { s.remove(); probar(i + 1); };
      document.head.appendChild(s);
    };
    probar(0);
  });
  return _leafletPromise;
}

function MapaFlexModal({ onClose }) {
  const [lib, setLib] = useState(window.L && window.L.map ? 'ok' : 'cargando');
  const [q, setQ] = useState('');
  const [ref, setRef] = useState(null);         // { lat, lon, label, pinId? }
  const [radio, setRadio] = useState(0);         // 0 = sin límite (en la unidad elegida)
  const [unidad, setUnidad] = useState('mi');
  const [estado, setEstado] = useState('');
  const [selId, setSelId] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [msg, setMsg] = useState('');
  const [verTodas, setVerTodas] = useState(false);
  const [copiado, setCopiado] = useState(null);
  const mapEl = useRef(null), mapRef = useRef(null), capas = useRef({});

  const fmt = mi => { const v = unidad === 'mi' ? mi : mi * 1.609344; return (v < 10 ? v.toFixed(1) : Math.round(v)) + ' ' + unidad; };

  useEffect(() => { cargarLeaflet().then(() => setLib('ok')).catch(() => setLib('error')); }, []);
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  // ── Crear el mapa una sola vez ──
  useEffect(() => {
    if (lib !== 'ok' || !mapEl.current || mapRef.current) return;
    const L = window.L;
    const map = L.map(mapEl.current, { preferCanvas: true }).setView([39.5, -98.35], 4);
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
      { maxZoom: 19, attribution: 'Mosaicos &copy; Esri' }).addTo(map);
    capas.current = { pins: L.layerGroup().addTo(map), extra: L.layerGroup().addTo(map), byId: {} };
    map.on('click', e => { setRef({ lat: e.latlng.lat, lon: e.latlng.lng, label: 'Punto en el mapa' }); setSelId(null); setVerTodas(false); setMsg(''); });
    mapRef.current = map;
    const t = setTimeout(() => map.invalidateSize(), 350);
    return () => { clearTimeout(t); map.remove(); mapRef.current = null; };
  }, [lib]);

  // ── Distancias desde el punto ──
  const cercanas = useMemo(() => !ref ? [] :
    FLEX_PINS.map(p => ({ ...p, mi: fxDistMi(ref.lat, ref.lon, p.lat, p.lon) })).sort((a, b) => a.mi - b.mi), [ref]);
  // Las 5 más cercanas (sin contar la zona donde ya estás parado)
  const top5 = useMemo(() => cercanas.filter(p => p.mi > 0.5).slice(0, 5), [cercanas]);

  const filas = useMemo(() => {
    if (ref) {
      let rows = cercanas;
      if (estado) rows = rows.filter(r => r.states.includes(estado));
      if (radio) { const maxMi = unidad === 'km' ? radio / 1.609344 : radio; rows = rows.filter(r => r.mi <= maxMi); }
      return rows;
    }
    let rows = FLEX_PINS.map(p => ({ ...p, mi: null }));
    if (estado) rows = rows.filter(r => r.states.includes(estado));
    const qq = fxAlias(fxNorm(q)), digits = q.replace(/\D/g, '');
    if (qq) {
      rows = rows.map(r => {
        const c = [fxScore(qq, fxNorm(r.city)), fxScore(qq, fxNorm(r.stateLabel)), digits && r.zip.includes(digits) ? 0 : null].filter(x => x != null);
        return { ...r, _s: c.length ? Math.min(...c) : null };
      }).filter(r => r._s != null).sort((a, b) => a._s - b._s || a.city.localeCompare(b.city));
    } else rows.sort((a, b) => a.stateLabel.localeCompare(b.stateLabel) || a.city.localeCompare(b.city));
    return rows;
  }, [ref, cercanas, estado, radio, unidad, q]);

  const limite = ref && !radio && !verTodas ? 15 : filas.length;
  const visibles = filas.slice(0, limite);

  // ── Pintar pines, punto, radio y líneas a las 5 más cercanas ──
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    const L = window.L, { pins, extra } = capas.current;
    pins.clearLayers(); extra.clearLayers(); capas.current.byId = {};
    const topIds = new Set(top5.map(p => p.id)), enLista = new Set(filas.map(f => f.id));
    const distDe = id => { const c = cercanas.find(x => x.id === id); return c ? c.mi : null; };
    FLEX_PINS.forEach(p => {
      const sel = p.id === selId, top = topIds.has(p.id), dentro = enLista.has(p.id);
      const mk = L.circleMarker([p.lat, p.lon], {
        radius: sel ? 10 : top ? 8 : 5, color: '#fff', weight: 2, bubblingMouseEvents: false,
        fillColor: sel ? '#1769ff' : top ? '#FF9900' : dentro ? '#f5b041' : '#94a3b8', fillOpacity: dentro ? 0.95 : 0.45,
      });
      const d = distDe(p.id);
      mk.bindPopup('<b>' + p.city + '</b><br>' + p.stateLabel + '<br>ZIP: <b>' + p.zip + '</b>' + (d != null ? '<br>Distancia: <b>' + fmt(d) + '</b> (línea recta)' : ''));
      if (top) mk.bindTooltip(fmt(d), { permanent: true, direction: 'top', offset: [0, -6], className: 'fx-tip' });
      mk.on('click', () => setSelId(p.id));
      pins.addLayer(mk); capas.current.byId[p.id] = mk;
    });
    if (ref) {
      const icon = L.divIcon({ className: '', iconSize: [22, 22], iconAnchor: [11, 20],
        html: '<div style="width:22px;height:22px;background:#1769ff;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 5px rgba(0,0,0,.5)"></div>' });
      extra.addLayer(L.marker([ref.lat, ref.lon], { icon, zIndexOffset: 2000 }).bindTooltip(ref.label));
      top5.forEach(p => extra.addLayer(L.polyline([[ref.lat, ref.lon], [p.lat, p.lon]], { color: '#FF9900', weight: 2, dashArray: '6 6', opacity: 0.85 })));
      if (radio) extra.addLayer(L.circle([ref.lat, ref.lon], { radius: (unidad === 'km' ? radio : radio * 1.609344) * 1000, color: '#1769ff', weight: 1.5, fillOpacity: 0.07 }));
    }
    if (selId && capas.current.byId[selId]) capas.current.byId[selId].openPopup();
  }, [lib, ref, cercanas, top5, filas, selId, radio, unidad]); // eslint-disable-line

  // Al fijar un punto nuevo: encuadrar el punto + las 5 más cercanas
  useEffect(() => {
    const map = mapRef.current; if (!map || !ref) return;
    const pts = [[ref.lat, ref.lon], ...top5.map(p => [p.lat, p.lon])];
    map.fitBounds(window.L.latLngBounds(pts), { padding: [40, 40], maxZoom: 9 });
  }, [ref, lib]); // eslint-disable-line

  // Al tocar una zona en la lista: llevar el mapa ahí
  useEffect(() => {
    const map = mapRef.current; if (!map || !selId) return;
    const p = FLEX_PINS.find(x => x.id === selId); if (!p) return;
    if (map.getZoom() < 7) map.setView([p.lat, p.lon], 8); else map.panTo([p.lat, p.lon]);
  }, [selId]);

  const fijarEnZona = (p, nota) => {
    setEstado(''); setSelId(null); setVerTodas(false); setRadio(0);
    setRef({ lat: p.lat, lon: p.lon, label: p.city + ' (' + p.stateLabel + ')', pinId: p.id });
    setMsg(nota || '');
  };

  const buscar = async (texto = q) => {
    const raw = texto.trim(); if (!raw) return;
    setMsg(''); setBuscando(true);
    try {
      const qa = fxAlias(fxNorm(raw));
      // Estado completo ("Texas") → filtra por estado
      const est = FLEX_ESTADOS.find(s => fxNorm(s) === qa);
      if (est) {
        setRef(null); setSelId(null); setEstado(est); setQ('');
        const pts = FLEX_PINS.filter(p => p.states.includes(est)).map(p => [p.lat, p.lon]);
        if (mapRef.current && pts.length) mapRef.current.fitBounds(window.L.latLngBounds(pts), { padding: [40, 40], maxZoom: 8 });
        setMsg('Zonas en ' + est + '. Toca "📍 Cerca de aquí" en una para ver distancias.');
        return;
      }
      // ZIP
      if (/^\d{5}$/.test(raw)) {
        const pin = FLEX_PINS.find(p => p.zip === raw);
        if (pin) { fijarEnZona(pin); return; }
        const g = await fxGeocodeZip(raw);
        if (g) { setEstado(''); setSelId(null); setVerTodas(false); setRef(g); return; }
        setMsg('No encontré el ZIP ' + raw + '.'); return;
      }
      // Ciudad Flex escrita bien (o casi): directo, sin internet
      const local = /\d/.test(raw) ? null : fxMejorCiudad(raw);
      if (local && local.score <= 1) { fijarEnZona(local.pin); return; }
      // Cualquier otro lugar (aunque no sea zona Flex): se ubica y se listan las cercanas
      const g = await fxGeocode(raw);
      if (g) { setEstado(''); setSelId(null); setVerTodas(false); setRef(g); return; }
      if (local) { fijarEnZona(local.pin, 'No encontré "' + raw + '" exacto. ¿Quisiste decir ' + local.pin.city + '?'); return; }
      setMsg('No encontré ese lugar. Prueba "Ciudad, Estado" (ej. Gilroy, CA) o un ZIP.');
    } finally { setBuscando(false); }
  };

  const miUbicacion = () => {
    if (!navigator.geolocation) { setMsg('Tu navegador no permite ubicación.'); return; }
    setMsg('Buscando tu ubicación…');
    navigator.geolocation.getCurrentPosition(
      pos => { setEstado(''); setSelId(null); setRef({ lat: pos.coords.latitude, lon: pos.coords.longitude, label: 'Mi ubicación' }); setMsg(''); },
      () => setMsg('No se pudo obtener tu ubicación (permiso denegado).'));
  };
  const limpiar = () => { setRef(null); setSelId(null); setRadio(0); setVerTodas(false); setMsg(''); setQ(''); setEstado(''); mapRef.current && mapRef.current.setView([39.5, -98.35], 4); };
  const copiar = (txt, id) => { copyToClipboard(txt); setCopiado(id); setTimeout(() => setCopiado(c => c === id ? null : c), 1200); };
  const masCercana = top5[0];
  const radios = unidad === 'mi' ? [25, 50, 100, 200] : [40, 80, 160, 320];

  return (
    <motion.div className="fixed inset-0 z-[90] flex flex-col bg-slate-100" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <style>{'.fx-tip{background:#232F3E;color:#fff;border:none;font-weight:800;font-size:11px;padding:2px 6px;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,.3)}.fx-tip:before{display:none}'}</style>
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-[#232F3E] text-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-[#FF9900] text-black flex items-center justify-center font-black text-lg shrink-0">a</div>
          <div className="min-w-0">
            <div className="font-black text-base leading-tight truncate">Mapa Amazon Flex</div>
            <div className="text-xs text-white/60">{FLEX_PINS.length} zonas · {FLEX_ESTADOS.length} estados</div>
          </div>
        </div>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 font-bold">✕</button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        {/* Mapa */}
        <div className="relative z-0 h-[40vh] lg:h-auto lg:flex-1 shrink-0 lg:shrink bg-slate-200">
          <div ref={mapEl} className="absolute inset-0"/>
          {lib === 'cargando' && <div className="absolute inset-0 flex items-center justify-center text-slate-500 font-bold animate-pulse">Cargando mapa…</div>}
          {lib === 'error' && <div className="absolute inset-0 flex items-center justify-center text-red-600 font-bold p-6 text-center">No se pudo cargar el mapa. Revisa tu internet y vuelve a abrirlo.<br/>La lista de abajo sí funciona.</div>}
        </div>

        {/* Panel */}
        <div className="flex-1 lg:flex-none lg:w-[420px] lg:order-first min-h-0 overflow-y-auto bg-white border-r border-slate-200">
          <div className="p-4 space-y-3 border-b border-slate-100 sticky top-0 bg-white z-10">
            <div className="flex gap-2">
              <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') buscar(); }}
                placeholder="Ciudad, ZIP o dirección (ej. San Francisco, 95020)"
                className="flex-1 min-w-0 rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF9900]"/>
              <button onClick={() => buscar()} disabled={buscando || !q.trim()}
                className="px-4 rounded-xl bg-[#FF9900] text-black font-black text-sm disabled:opacity-40 active:scale-95">{buscando ? '…' : 'Buscar'}</button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={miUbicacion} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold">📍 Mi ubicación</button>
              {(ref || estado || q) && <button onClick={limpiar} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold">✕ Limpiar</button>}
              <div className="ml-auto flex rounded-lg overflow-hidden border border-slate-200">
                {['mi', 'km'].map(u => <button key={u} onClick={() => { setUnidad(u); setRadio(0); }} className={'px-3 py-1.5 text-xs font-black ' + (unidad === u ? 'bg-[#232F3E] text-white' : 'bg-white text-slate-500')}>{u}</button>)}
              </div>
            </div>
            {!ref && (
              <select value={estado} onChange={e => setEstado(e.target.value)} className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none">
                <option value="">Todos los estados</option>{FLEX_ESTADOS.map(s => <option key={s}>{s}</option>)}
              </select>
            )}
            {msg && <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 font-bold">{msg}</div>}

            {ref && (
              <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 space-y-2">
                <div className="text-sm text-slate-800"><b>📍 {ref.label}</b></div>
                {ref.pinId
                  ? <div className="text-xs text-emerald-700 font-black">✅ Es zona Flex{masCercana ? ' · la siguiente más cerca: ' + masCercana.city + ' a ' + fmt(masCercana.mi) : ''}</div>
                  : masCercana && <div className="text-xs text-slate-700">Zona Flex más cercana: <b>{cercanas[0].city}</b> a <b>{fmt(cercanas[0].mi)}</b></div>}
                <div className="flex gap-1.5 flex-wrap items-center">
                  <span className="text-[11px] font-black text-slate-400 uppercase mr-1">Radio</span>
                  {[0, ...radios].map(r => (
                    <button key={r} onClick={() => setRadio(r)} className={'px-2.5 py-1 rounded-full text-xs font-black ' + (radio === r ? 'bg-[#232F3E] text-white' : 'bg-white text-slate-600 border border-slate-200')}>{r ? r + ' ' + unidad : 'Todas'}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="text-xs text-slate-500">
              {ref
                ? <><b>{filas.length}</b> zona{filas.length !== 1 ? 's' : ''}{radio ? ' dentro de ' + radio + ' ' + unidad : ''}, de la más cercana a la más lejana. Millas en línea recta; 🚗 abre la ruta real.</>
                : <><b>{filas.length}</b> de {FLEX_PINS.length} zonas. Busca un lugar o toca el mapa para medir distancias.</>}
            </div>
          </div>

          <div className="p-3 space-y-2">
            {visibles.length === 0 && <div className="text-center text-slate-400 text-sm py-8">Sin zonas aquí. Quita el radio o prueba otro lugar.</div>}
            {visibles.map((p, i) => {
              const aqui = ref && p.mi != null && p.mi <= 0.5;
              const top = ref && !aqui && top5.some(t => t.id === p.id);
              return (
                <div key={p.id} onClick={() => setSelId(p.id)}
                  className={'rounded-xl border-2 p-3 cursor-pointer transition-all ' + (p.id === selId ? 'border-[#1769ff] bg-blue-50' : top ? 'border-[#FF9900] bg-orange-50/50' : 'border-slate-100 hover:border-slate-300')}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-black text-slate-900 text-sm">
                        {ref && <span className="text-slate-400 mr-1">{i + 1}.</span>}{p.city}
                        {aqui && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[10px] font-black align-middle">AQUÍ</span>}
                      </div>
                      <div className="text-xs text-slate-500">{p.stateLabel}</div>
                    </div>
                    {p.mi != null && <span className={'shrink-0 px-2 py-0.5 rounded-full text-xs font-black ' + (top ? 'bg-[#FF9900] text-black' : 'bg-slate-100 text-slate-700')}>{aqui ? '0 ' + unidad : fmt(p.mi)}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    <button onClick={e => { e.stopPropagation(); copiar(p.zip, p.id); }} className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold tabular-nums">
                      {copiado === p.id ? '✓ copiado' : 'ZIP ' + p.zip + ' 📋'}
                    </button>
                    {ref && !aqui && (
                      <a href={'https://www.google.com/maps/dir/?api=1&origin=' + ref.lat + ',' + ref.lon + '&destination=' + p.lat + ',' + p.lon}
                        target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                        className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold">🚗 Ruta</a>
                    )}
                    {!aqui && (
                      <button onClick={e => { e.stopPropagation(); fijarEnZona(p); }} className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold">📍 Cerca de aquí</button>
                    )}
                  </div>
                </div>
              );
            })}
            {filas.length > limite && (
              <button onClick={() => setVerTodas(true)} className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-600 text-sm font-black">Ver las {filas.length - limite} restantes</button>
            )}
            <div className="text-[11px] text-slate-400 px-1 pt-1">El ZIP es el del centro de cada zona, como referencia. Las zonas grandes abarcan varios ZIP.</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  DECODIFICADOR VIN
//  Escribes los 17 caracteres → marca, país, año, dígito verificador,
//  especificaciones oficiales (NHTSA), recalls y links para revisar
//  papeles/robo según el país. Todo en el navegador: no toca el servidor
//  ni la base de datos de la app.
// ═══════════════════════════════════════════════════════════════════
const VIN_YEAR={A:1980,B:1981,C:1982,D:1983,E:1984,F:1985,G:1986,H:1987,J:1988,K:1989,L:1990,M:1991,N:1992,P:1993,R:1994,S:1995,T:1996,V:1997,W:1998,X:1999,Y:2000,'1':2001,'2':2002,'3':2003,'4':2004,'5':2005,'6':2006,'7':2007,'8':2008,'9':2009};
const VIN_TRANSLIT={A:1,B:2,C:3,D:4,E:5,F:6,G:7,H:8,J:1,K:2,L:3,M:4,N:5,P:7,R:9,S:2,T:3,U:4,V:5,W:6,X:7,Y:8,Z:9,'0':0,'1':1,'2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9};
const VIN_WEIGHTS=[8,7,6,5,4,3,2,10,0,9,8,7,6,5,4,3,2];
const VIN_WMI1={'1':'Estados Unidos','4':'Estados Unidos','5':'Estados Unidos','2':'Canadá','3':'México','6':'Australia','7':'Nueva Zelanda / EE. UU.','J':'Japón','L':'China','W':'Alemania'};
const VIN_TWO={'KL':'Corea del Sur','KM':'Corea del Sur','KN':'Corea del Sur','KP':'Corea del Sur','KA':'Sri Lanka','MA':'India','MB':'India','MC':'India','MD':'India','ME':'India','MH':'Indonesia','ML':'Tailandia','MM':'Tailandia','MN':'Tailandia','MP':'Tailandia','MR':'Tailandia','NL':'Turquía','NM':'Turquía','NA':'Irán','PL':'Malasia','PA':'Filipinas','RF':'Taiwán','RA':'Emiratos Árabes','SA':'Reino Unido','SB':'Reino Unido','SC':'Reino Unido','SH':'Reino Unido','SJ':'Reino Unido','SN':'Alemania','SU':'Polonia','TM':'Rep. Checa','TR':'Hungría','TS':'Hungría','TW':'Portugal','U5':'Eslovaquia','UU':'Rumanía','UN':'Irlanda','VF':'Francia','VG':'Francia','VN':'Francia','VR':'Francia','VS':'España','VW':'España','VA':'Austria','ZA':'Italia','ZC':'Italia','ZD':'Italia','ZF':'Italia','ZH':'Italia','ZL':'Italia','YS':'Suecia','YV':'Suecia','YB':'Bélgica','X3':'Rusia','XP':'Alemania','8A':'Argentina','9B':'Brasil','93':'Brasil','9F':'Colombia','8G':'Chile','8L':'Ecuador'};
const VIN_MAKER={
"1FA":"Ford","1FB":"Ford","1FC":"Ford","1FD":"Ford","1FM":"Ford","1FT":"Ford","1FU":"Freightliner","1FV":"Freightliner","2FA":"Ford","2FM":"Ford","2FT":"Ford","3FA":"Ford","3FE":"Ford","3FT":"Ford","WF0":"Ford","6FP":"Ford","9BF":"Ford","NM0":"Ford","MAJ":"Ford","MNA":"Ford","LJ8":"Ford","LVS":"Ford","8AF":"Ford",
"1G1":"Chevrolet","1G2":"Pontiac","1G3":"Oldsmobile","1G4":"Buick","1G6":"Cadillac","1G8":"Saturn","1GC":"Chevrolet","1GB":"Chevrolet","1GN":"Chevrolet","1GT":"GMC","1GK":"GMC","1GD":"GMC","1GY":"Cadillac","1GM":"Pontiac","2G1":"Chevrolet","2G2":"Pontiac","2GT":"GMC","3G1":"Chevrolet","3GN":"Chevrolet","3GC":"Chevrolet","3GT":"GMC","3GK":"GMC","KL1":"Chevrolet (GM Corea)","KL4":"Buick","KL7":"Chevrolet","KL8":"Chevrolet (GM Corea)","9BG":"Chevrolet","9GA":"Chevrolet (GM Colmotores)","9FB":"Renault (Sofasa)","8AG":"Chevrolet","LSG":"SAIC-GM",
"1C3":"Chrysler / Dodge","1C4":"Jeep / Dodge / Chrysler","1C6":"Ram","2C3":"Chrysler / Dodge","2C4":"Chrysler / Dodge","3C4":"Jeep / Chrysler","3C6":"Ram","3C7":"Ram","1B3":"Dodge","1B7":"Dodge","2B3":"Dodge","1D3":"Dodge","1D4":"Dodge","1D7":"Dodge / Ram","3D7":"Ram","1J4":"Jeep","1J8":"Jeep","ZAC":"Jeep (Italia)","2A4":"Chrysler",
"JTD":"Toyota","JTE":"Toyota","JTM":"Toyota","JTN":"Toyota","JTG":"Toyota","JTK":"Toyota / Scion","JTL":"Toyota","JT2":"Toyota","JT3":"Toyota","JT4":"Toyota","JTH":"Lexus","JTJ":"Lexus","2T1":"Toyota","2T2":"Lexus","2T3":"Toyota","4T1":"Toyota","4T3":"Toyota","4T4":"Toyota","5TD":"Toyota","5TF":"Toyota","5TE":"Toyota","5TB":"Toyota","5YF":"Toyota","58A":"Lexus","3TM":"Toyota","3TY":"Toyota","1NX":"Toyota","NMT":"Toyota","MR0":"Toyota","MHF":"Toyota","SB1":"Toyota","VNK":"Toyota","9BR":"Toyota","8AJ":"Toyota",
"1HG":"Honda","2HG":"Honda","3HG":"Honda","2HK":"Honda","5FN":"Honda","5J6":"Honda","5KB":"Honda","7FA":"Honda","19X":"Honda","JHM":"Honda","JHL":"Honda","SHH":"Honda","SHS":"Honda","MRH":"Honda","93H":"Honda","LHG":"Honda","3CZ":"Honda","5FR":"Acura","5J8":"Acura","19U":"Acura","JH4":"Acura","1HD":"Harley-Davidson","JH2":"Honda (moto)",
"1N4":"Nissan","1N6":"Nissan","3N1":"Nissan","3N6":"Nissan","3N8":"Nissan","5N1":"Nissan","JN1":"Nissan","JN8":"Nissan","VSK":"Nissan","SJN":"Nissan","MNT":"Nissan","JNK":"Infiniti","JNR":"Infiniti","5N3":"Infiniti","3PC":"Infiniti",
"WVW":"Volkswagen","WV1":"Volkswagen","WV2":"Volkswagen","WVG":"Volkswagen","1VW":"Volkswagen","3VW":"Volkswagen","3VV":"Volkswagen","9BW":"Volkswagen","8AW":"Volkswagen","LFV":"FAW-Volkswagen","LSV":"SAIC-Volkswagen","VWV":"Volkswagen","WAU":"Audi","WA1":"Audi","TRU":"Audi","93U":"Audi","WUA":"Audi Sport","WP0":"Porsche","WP1":"Porsche","VSS":"SEAT","TMB":"Škoda",
"WBA":"BMW","WBS":"BMW M","WBX":"BMW","WBY":"BMW i","4US":"BMW","5UX":"BMW","5UM":"BMW M","5YM":"BMW M","3MW":"BMW","LBV":"BMW Brilliance","WMW":"Mini","WMZ":"Mini",
"WDB":"Mercedes-Benz","WDC":"Mercedes-Benz","WDD":"Mercedes-Benz","WDF":"Mercedes-Benz","W1K":"Mercedes-Benz","W1N":"Mercedes-Benz","W1V":"Mercedes-Benz","4JG":"Mercedes-Benz","55S":"Mercedes-Benz","8AC":"Mercedes-Benz","WME":"smart",
"KMH":"Hyundai","KM8":"Hyundai","KMF":"Hyundai","5NP":"Hyundai","5NM":"Hyundai","NLH":"Hyundai","MAL":"Hyundai","TMA":"Hyundai","2HM":"Hyundai","KMT":"Genesis","KNA":"Kia","KND":"Kia","KNE":"Kia","3KP":"Kia","5XX":"Kia","5XY":"Kia","U5Y":"Kia",
"5YJ":"Tesla","7SA":"Tesla","7G2":"Tesla","LRW":"Tesla (China)","XP7":"Tesla (Alemania)","7PD":"Rivian",
"JF1":"Subaru","JF2":"Subaru","4S3":"Subaru","4S4":"Subaru",
"JM1":"Mazda","JM3":"Mazda","4F2":"Mazda","1YV":"Mazda","3MZ":"Mazda","3MV":"Mazda","3MD":"Mazda","7MM":"Mazda","MM8":"Mazda",
"YV1":"Volvo","YV4":"Volvo","LVY":"Volvo (China)","LYV":"Volvo (China)","YV2":"Volvo (camión)","4V4":"Volvo (camión)","LPS":"Polestar","YS3":"Saab",
"ZFA":"Fiat","9BD":"Fiat","8AP":"Fiat","ZFF":"Ferrari","ZAR":"Alfa Romeo","ZHW":"Lamborghini","ZAM":"Maserati","ZLA":"Lancia","ZCF":"Iveco","ZDM":"Ducati",
"SAL":"Land Rover","SAJ":"Jaguar","SCC":"Lotus","SCF":"Aston Martin","SCB":"Bentley","SCA":"Rolls-Royce",
"JA3":"Mitsubishi","JA4":"Mitsubishi","4A3":"Mitsubishi","4A4":"Mitsubishi","6MM":"Mitsubishi",
"VF1":"Renault","93Y":"Renault","8A1":"Renault","KNM":"Renault Samsung","VF3":"Peugeot","VR3":"Peugeot","VF7":"Citroën","VR7":"Citroën","VR1":"DS","W0L":"Opel","W0V":"Opel",
"JS1":"Suzuki (moto)","JS2":"Suzuki","JS3":"Suzuki","KL5":"Suzuki","MA3":"Suzuki (Maruti)","TSM":"Suzuki",
"JYA":"Yamaha (moto)","JKA":"Kawasaki (moto)","MA1":"Mahindra","MAT":"Tata","MPA":"Isuzu","KPT":"SsangYong",
"LGX":"BYD","LC0":"BYD","LJ1":"JAC","LVV":"Chery","L6T":"Geely","LNB":"BAIC","LGW":"Great Wall / Haval","LSJ":"MG (SAIC)",
"1XK":"Kenworth","1XP":"Peterbilt","1M1":"Mack","4UZ":"Freightliner","1L1":"Lincoln","1LN":"Lincoln","5L1":"Lincoln","5LM":"Lincoln","3LN":"Lincoln","1ME":"Mercury","4M2":"Mercury"
};
const VIN_MAKER2={'1F':'Ford','WF':'Ford','JT':'Toyota','JH':'Honda','JN':'Nissan','JF':'Subaru','JM':'Mazda','JA':'Mitsubishi','JS':'Suzuki','WV':'Volkswagen','WB':'BMW','WD':'Mercedes-Benz','W1':'Mercedes-Benz','WA':'Audi','WP':'Porsche','WM':'Mini','KM':'Hyundai','KN':'Kia','YV':'Volvo','SA':'Jaguar / Land Rover'};
const VIN_SA={'8':[['ABCDE','Argentina'],['FGHJ','Chile'],['KLMN','Ecuador'],['PRS','Perú'],['TUVW','Venezuela']],'9':[['ABCDE','Brasil'],['FGHJ','Colombia'],['KLMN','Paraguay'],['PRS','Uruguay'],['TUVW','Trinidad y Tobago'],['3456789','Brasil']]};
// Errores de captura típicos: qué se pudo haber leído mal
const VIN_LOOKALIKE={'0':'D8','1':'7L','2':'Z','3':'8','5':'S','6':'G','7':'1','8':'B03','9':'G','B':'8','D':'0','G':'6','L':'1','S':'5','Z':'2','U':'V','V':'UY','Y':'V','M':'N','N':'M'};
// Clases completas (Tailwind solo incluye clases escritas literalmente)
const VIN_GROUPS=[
  {label:'Fabricante', from:0, to:2,  cell:'bg-amber-100 text-amber-900 border-amber-300',   dot:'bg-amber-500'},
  {label:'Descriptor', from:3, to:7,  cell:'bg-slate-100 text-slate-800 border-slate-300',   dot:'bg-slate-400'},
  {label:'Verificador',from:8, to:8,  cell:'bg-emerald-100 text-emerald-900 border-emerald-300', dot:'bg-emerald-500'},
  {label:'Año',        from:9, to:9,  cell:'bg-violet-100 text-violet-900 border-violet-300', dot:'bg-violet-500'},
  {label:'Planta',     from:10,to:10, cell:'bg-slate-100 text-slate-800 border-slate-300',   dot:'bg-slate-400'},
  {label:'Serie',      from:11,to:16, cell:'bg-sky-100 text-sky-900 border-sky-300',          dot:'bg-sky-500'},
];
const VIN_SECTIONS=[
  {title:'Identidad',fields:[['Make','Marca'],['Model','Modelo'],['ModelYear','Año modelo'],['Series','Serie'],['Trim','Versión'],['VehicleType','Tipo'],['BodyClass','Carrocería'],['Doors','Puertas']]},
  {title:'Motor',fields:[['EngineCylinders','Cilindros'],['DisplacementL','Cilindrada (L)'],['FuelTypePrimary','Combustible'],['EngineHP','Potencia (HP)'],['EngineConfiguration','Configuración'],['EngineModel','Modelo de motor'],['ElectrificationLevel','Electrificación']]},
  {title:'Transmisión y tracción',fields:[['DriveType','Tracción'],['TransmissionStyle','Transmisión'],['TransmissionSpeeds','Velocidades']]},
  {title:'Fabricación',fields:[['Manufacturer','Fabricante'],['PlantCountry','País de planta'],['PlantState','Estado/Región'],['PlantCity','Ciudad'],['GVWR','Peso bruto (GVWR)']]},
];
const VIN_EJEMPLOS=[['Honda','1HGCM82633A004352'],['Tesla','5YJSA1E18HF000337'],['Ford','1FTFW1ET9DFC10312'],['Nissan MX','3N1AB7AP0KY250000']];

function vinRegion1(c){if(/[A-H]/.test(c))return'África';if(/[J-R]/.test(c))return'Asia';if(/[S-Z]/.test(c))return'Europa';if(/[1-5]/.test(c))return'Norteamérica';if(/[6-7]/.test(c))return'Oceanía';if(/[8-9]/.test(c))return'Sudamérica';return'—';}
function vinSouthAm(v){const r=VIN_SA[v[0]];if(!r)return null;for(const [set,c] of r)if(set.includes(v[1]))return c;return null;}
function vinOrigin(v){return VIN_TWO[v.slice(0,2)]||vinSouthAm(v)||VIN_WMI1[v[0]]||vinRegion1(v[0]);}
function vinMaker(v){return VIN_MAKER[v.slice(0,3)]||VIN_MAKER2[v.slice(0,2)]||null;}
const vinIsNA=v=>/[1-5]/.test(v[0]);
function vinCheck(v){let s=0;for(let i=0;i<17;i++){const t=VIN_TRANSLIT[v[i]];if(t===undefined)return null;s+=t*VIN_WEIGHTS[i];}const r=s%11;return r===10?'X':String(r);}
function vinYear(v){
  const base=VIN_YEAR[v[9]];if(base===undefined)return null;
  const max=new Date().getFullYear()+1;
  if(vinIsNA(v)){let y=/[A-Z]/.test(v[6])?base+30:base;if(y>max)y=base;return{y,alt:null,sure:true};}
  return base+30<=max?{y:base+30,alt:base,sure:false}:{y:base,alt:null,sure:false};
}
function vinFixes(v){
  const out=new Set();
  for(let i=0;i<17;i++){if(i===8)continue;const alts=VIN_LOOKALIKE[v[i]];if(!alts)continue;
    for(const a of alts){const c=v.slice(0,i)+a+v.slice(i+1);if(i===9&&VIN_YEAR[a]===undefined)continue;if(vinCheck(c)===c[8])out.add(c+'|'+i);}}
  const list=[...out].slice(0,4).map(s=>{const [vin,i]=s.split('|');return{vin,pos:+i};});
  const e=vinCheck(v);if(e)list.push({vin:v.slice(0,8)+e+v.slice(9),pos:8});
  return list;
}
function vinNormalize(raw){
  const fixes=[];let v='';
  for(const ch of String(raw||'').toUpperCase()){
    if(ch==='I'){v+='1';fixes.push('I→1');}
    else if(ch==='O'||ch==='Q'){v+='0';fixes.push(ch+'→0');}
    else if(/[A-Z0-9]/.test(ch))v+=ch;
  }
  return{v:v.slice(0,17),fixes:[...new Set(fixes)]};
}
function vinClean(x){if(x===null||x===undefined)return'';const s=String(x).trim();if(s===''||s==='0'||s==='Not Applicable'||s.toLowerCase()==='null')return'';return s;}
function vinLeerRecientes(){try{return JSON.parse(localStorage.getItem('royal_vin_recent')||'[]');}catch{return[];}}

function VinDecoderModal({ onClose, vinInicial = '' }) {
  const [texto, setTexto] = useState('');
  const [nota, setNota] = useState('');
  const [error, setError] = useState('');
  const [vin, setVin] = useState('');              // VIN decodificado
  const [specs, setSpecs] = useState(null);         // fila de NHTSA
  const [specEstado, setSpecEstado] = useState('idle'); // idle | cargando | ok | vacio | error
  const [recalls, setRecalls] = useState(null);     // null | [] | [...]
  const [pais, setPais] = useState(() => { try { return localStorage.getItem('royal_vin_pais') || 'us'; } catch { return 'us'; } });
  const [recientes, setRecientes] = useState(vinLeerRecientes);
  const [copiado, setCopiado] = useState(false);
  const inputRef = useRef(null);

  const guardarReciente = useCallback((v, label) => {
    setRecientes(prev => {
      const r = [{ v, label }, ...prev.filter(x => x.v !== v)].slice(0, 6);
      try { localStorage.setItem('royal_vin_recent', JSON.stringify(r)); } catch {}
      return r;
    });
  }, []);

  const decodificar = useCallback((valor) => {
    const v = String(valor || '').toUpperCase();
    setError('');
    if (v.length !== 17) { setError(`Faltan ${17 - v.length} caracteres. Un VIN tiene exactamente 17.`); inputRef.current?.focus(); return; }
    setVin(v); setSpecs(null); setRecalls(null); setSpecEstado('cargando'); setCopiado(false);
    const y = vinYear(v);
    guardarReciente(v, (vinMaker(v) || v.slice(0, 3)) + (y ? ' ' + y.y : ''));
  }, [guardarReciente]);

  const escribir = (raw) => {
    const { v, fixes } = vinNormalize(raw);
    setTexto(v); setError('');
    setNota(fixes.length ? `Corregí ${fixes.join(', ')}: los VIN nunca usan I, O ni Q.` : '');
    if (v.length === 17 && v !== vin) decodificar(v);
  };
  const usar = (v) => { setTexto(v); setNota(''); decodificar(v); };

  // VIN que llega desde otra parte de la app
  useEffect(() => { if (vinInicial) escribir(vinInicial); }, [vinInicial]); // eslint-disable-line
  useEffect(() => { try { localStorage.setItem('royal_vin_pais', pais); } catch {} }, [pais]);
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  // Especificaciones oficiales (NHTSA) + recalls del modelo
  useEffect(() => {
    if (!vin) return;
    let vivo = true;
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 10000);
    fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${vin}?format=json`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => {
        if (!vivo) return;
        const r = (d.Results && d.Results[0]) || {};
        const hay = VIN_SECTIONS.some(s => s.fields.some(f => vinClean(r[f[0]])));
        if (!hay) { setSpecEstado('vacio'); return; }
        setSpecs(r); setSpecEstado('ok');
        const mk = vinClean(r.Make), md = vinClean(r.Model), yr = vinClean(r.ModelYear);
        if (md) guardarReciente(vin, `${mk || vinMaker(vin) || vin.slice(0, 3)} ${md}${yr ? ' ' + yr : ''}`);
        if (mk && md && yr) {
          fetch(`https://api.nhtsa.gov/recalls/recallsByVehicle?make=${encodeURIComponent(mk)}&model=${encodeURIComponent(md)}&modelYear=${encodeURIComponent(yr)}`)
            .then(x => x.ok ? x.json() : Promise.reject())
            .then(j => { if (vivo) setRecalls(j.results || []); })
            .catch(() => {});
        }
      })
      .catch(() => { if (vivo) setSpecEstado('error'); })
      .finally(() => clearTimeout(to));
    return () => { vivo = false; ctrl.abort(); clearTimeout(to); };
  }, [vin, guardarReciente]);

  // ── Cálculos locales (sin internet) ──
  const d = useMemo(() => {
    if (!vin) return null;
    const exp = vinCheck(vin), ok = exp === vin[8], na = vinIsNA(vin);
    const fixes = ok ? [] : vinFixes(vin);
    return { exp, ok, na, fixes, suspects: new Set(fixes.map(f => f.pos)), origin: vinOrigin(vin), maker: vinMaker(vin), year: vinYear(vin) };
  }, [vin]);

  const g = k => vinClean(specs && specs[k]);
  const marcaMostrada = g('Make') || d?.maker || 'No identificada';
  const modelo = g('Model');

  const copiarResumen = () => {
    if (!d) return;
    const lineas = [
      `VIN: ${vin}`,
      `Marca: ${marcaMostrada}`,
      modelo && `Modelo: ${modelo}${g('Trim') ? ' ' + g('Trim') : ''}`,
      `Año: ${g('ModelYear') || (d.year ? d.year.y : '—')}`,
      `País: ${d.origin}`,
      g('DisplacementL') && `Motor: ${g('DisplacementL')} L${g('EngineCylinders') ? ' · ' + g('EngineCylinders') + ' cil.' : ''}${g('FuelTypePrimary') ? ' · ' + g('FuelTypePrimary') : ''}`,
      g('DriveType') && `Tracción: ${g('DriveType')}`,
      `Dígito verificador: ${d.ok ? 'válido' : 'NO cuadra'}`,
    ].filter(Boolean).join('\n');
    copyToClipboard(lineas);
    setCopiado(true); setTimeout(() => setCopiado(false), 1500);
  };

  const Link = ({ href, titulo, fuente, desc, destacado }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={"block rounded-xl border-2 p-3 hover:border-slate-400 transition-colors " + (destacado ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white")}>
      <div className="font-black text-sm text-slate-900">{titulo} <span className="text-slate-400">↗</span></div>
      <div className="text-xs text-slate-600 mt-1"><span className="font-bold text-slate-700">{fuente}</span> · {desc}</div>
    </a>
  );
  const linksPais = () => {
    const recallsVin = <Link key="rv" href={`https://www.nhtsa.gov/recalls?vin=${vin}`} titulo="Recalls pendientes de este auto" fuente="NHTSA" desc="Dice si ESTE auto ya tiene la reparación hecha. Para autos vendidos en EE. UU." destacado={pais === 'us'}/>;
    const nicb = <Link key="nicb" href="https://www.nicb.org/vincheck" titulo="Robo y salvamento en EE. UU." fuente="NICB" desc="¿Reportado robado o como pérdida total? 5 consultas al día." destacado={pais === 'us'}/>;
    if (pais === 'co') return [
      <Link key="runt" href="https://www.runt.gov.co/" titulo="Estado legal en el RUNT" fuente="Gobierno de Colombia" desc="«Consulta ciudadana» por VIN: SOAT, técnico-mecánica, embargos, prendas y matrícula." destacado/>,
      <Link key="simit" href="https://www.fcm.org.co/simit/" titulo="Multas pendientes (SIMIT)" fuente="Federación de Municipios" desc="Por placa. Las multas bloquean el traspaso hasta que se pagan." destacado/>,
      nicb, recallsVin];
    if (pais === 'mx') return [
      <Link key="repuve" href="http://www2.repuve.gob.mx:8080/ciudadania/consulta/" titulo="Robo en México (REPUVE)" fuente="Gobierno de México" desc="Gratis por NIV o placa. También revisa robo en EE. UU./Canadá." destacado/>,
      nicb, recallsVin];
    return [nicb, recallsVin];
  };

  const posGrupo = []; VIN_GROUPS.forEach(gr => { for (let i = gr.from; i <= gr.to; i++) posGrupo[i] = gr; });

  return (
    <motion.div className="fixed inset-0 z-[90] flex flex-col bg-slate-100" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 bg-white border-b border-slate-200 shrink-0">
        <div>
          <div className="font-black text-lg text-slate-900">🚗 Decodificador VIN</div>
          <div className="text-xs text-slate-400">Marca, año, país, validez, especificaciones y recalls</div>
        </div>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold hover:bg-slate-200">✕</button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 max-w-5xl w-full mx-auto space-y-4">
        {/* ── ENTRADA ── */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Número VIN (17 caracteres)</label>
          <div className="flex gap-2">
            <input ref={inputRef} autoFocus value={texto} spellCheck={false} autoCapitalize="characters" autoComplete="off"
              onChange={e => escribir(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') decodificar(texto); }}
              placeholder="1HGCM82633A004352"
              className="flex-1 min-w-0 rounded-xl border-2 border-slate-200 px-4 py-3 text-lg sm:text-2xl font-black tracking-[0.12em] font-mono outline-none focus:border-slate-400"/>
            <button onClick={() => decodificar(texto)}
              className="shrink-0 px-4 sm:px-6 rounded-xl bg-slate-900 text-white font-black text-sm active:scale-95">Decodificar</button>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
            <span className={texto.length === 17 ? "text-emerald-600 font-bold" : "text-slate-400"}>{texto.length}/17</span>
            <div className="flex gap-1.5 flex-wrap items-center">
              <span className="text-slate-400 font-bold">Probar:</span>
              {VIN_EJEMPLOS.map(([l, v]) => (
                <button key={v} onClick={() => usar(v)} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-bold hover:bg-slate-200">{l}</button>
              ))}
            </div>
          </div>
          {nota && <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-amber-800 text-xs font-bold">{nota}</div>}
          {error && <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-red-700 text-sm font-bold">{error}</div>}
          {recientes.length > 0 && (
            <div className="flex gap-1.5 flex-wrap items-center text-xs">
              <span className="text-slate-400 font-bold">Recientes:</span>
              {recientes.map(r => (
                <button key={r.v} onClick={() => usar(r.v)} title={r.v} className="px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 font-bold hover:border-slate-400">{r.label || r.v}</button>
              ))}
            </div>
          )}
          <div className="flex gap-1.5 flex-wrap items-center text-xs pt-1 border-t border-slate-100">
            <span className="text-slate-400 font-bold">Matriculado en:</span>
            {[['us', 'EE. UU.'], ['co', 'Colombia'], ['mx', 'México']].map(([k, l]) => (
              <button key={k} onClick={() => setPais(k)}
                className={"px-3 py-1 rounded-full font-black " + (pais === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200")}>{l}</button>
            ))}
          </div>
        </div>

        {d && (<>
          {/* ── DESGLOSE POR POSICIÓN ── */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <span className="text-xs font-black text-slate-400 uppercase tracking-wide">Desglose por posición</span>
              <button onClick={() => copyToClipboard(vin)} className="text-xs font-mono font-bold text-slate-600 hover:text-slate-900">{vin} 📋</button>
            </div>
            <div className="grid gap-0.5 sm:gap-1" style={{ gridTemplateColumns: 'repeat(17, minmax(0, 1fr))' }}>
              {vin.split('').map((c, i) => {
                const gr = posGrupo[i];
                let cls = gr.cell;
                if (i === 8) cls = d.ok ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-red-500 text-white border-red-600';
                const sospechosa = !d.ok && d.na && d.suspects.has(i) && i !== 8;
                return (
                  <div key={i} className={"rounded-md sm:rounded-lg border text-center py-1 sm:py-2 " + cls + (sospechosa ? " border-dashed border-2 border-red-500" : "")}>
                    <div className="text-[8px] sm:text-[10px] opacity-60 leading-none">{i + 1}</div>
                    <div className="font-mono font-black text-xs sm:text-lg leading-tight">{c}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-slate-500">
              {VIN_GROUPS.map(gr => (
                <span key={gr.label} className="flex items-center gap-1.5">
                  <span className={"w-2.5 h-2.5 rounded-full " + gr.dot}/><b className="text-slate-700">{gr.label}</b>
                  {gr.from === gr.to ? gr.from + 1 : (gr.from + 1) + '–' + (gr.to + 1)}
                </span>
              ))}
            </div>
          </div>

          {/* ── VEREDICTO ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide">Marca y modelo</div>
              <div className="font-black text-lg text-amber-700 leading-tight mt-1">{marcaMostrada}{modelo && <span className="text-slate-900"> {modelo}</span>}</div>
              {specEstado === 'cargando' && <div className="text-[11px] text-slate-400 animate-pulse mt-0.5">buscando modelo…</div>}
              <div className="text-xs text-slate-500 mt-1">{d.origin} · código {vin.slice(0, 3)}{vin[2] === '9' ? ' · fabricante pequeño' : ''}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide">Año modelo</div>
              <div className="font-black text-2xl text-slate-900 mt-1">{g('ModelYear') || (d.year ? d.year.y : '—')}</div>
              <div className="text-xs text-slate-500 mt-1">{!d.year ? `La posición 10 (“${vin[9]}”) no es un código de año válido` : d.year.sure ? 'Seguro (VIN de Norteamérica)' : d.year.alt ? `O ${d.year.alt} si es un auto antiguo` : 'Posición 10'}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide">Dígito verificador</div>
              <div className={"font-black text-2xl mt-1 " + (d.ok ? 'text-emerald-600' : 'text-red-600')}>{d.ok ? '✓ Válido' : d.na ? '✗ No cuadra' : 'No coincide'}</div>
              <div className="text-xs text-slate-500 mt-1">{d.ok ? 'VIN bien formado' : d.na ? `Debería ser “${d.exp}”. Casi seguro hay un carácter mal copiado` : `Esperado “${d.exp}”. Fuera de Norteamérica muchas marcas no lo usan`}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[11px] font-black text-slate-400 uppercase tracking-wide">Nº de serie</div>
              <div className="font-black text-xl font-mono text-slate-900 mt-1">{vin.slice(11)}</div>
              <div className="text-xs text-slate-500 mt-1">Planta “{vin[10]}”</div>
            </div>
          </div>

          {/* ── SUGERENCIAS SI EL VIN NO CUADRA ── */}
          {!d.ok && d.na && d.fixes.length > 0 && (
            <div className="rounded-2xl bg-red-50 border-2 border-red-200 p-4 text-sm text-red-900 space-y-2">
              <div><b>Este VIN no pasa la verificación.</b> En autos de Norteamérica siempre cuadra, así que casi seguro hay un error al copiarlo. Estas versiones sí cuadran (las casillas punteadas son las sospechosas):</div>
              <div className="flex flex-wrap gap-2">
                {d.fixes.map(f => (
                  <button key={f.vin} onClick={() => usar(f.vin)} title={`Cambia la posición ${f.pos + 1}`}
                    className="px-3 py-1.5 rounded-lg bg-white border border-red-200 font-mono text-xs text-slate-700 hover:border-red-400">
                    {f.vin.slice(0, f.pos)}<b className="text-red-600 text-sm">{f.vin[f.pos]}</b>{f.vin.slice(f.pos + 1)}
                  </button>
                ))}
              </div>
              <div className="text-xs">Compáralas con la placa del tablero o el marco de la puerta antes de elegir.</div>
            </div>
          )}

          {/* ── ESPECIFICACIONES ── */}
          <div>
            <div className="text-xs font-black text-slate-400 uppercase tracking-wide mb-2 px-1">Especificaciones <span className="ml-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 normal-case tracking-normal">gratis · NHTSA</span></div>
            {specEstado === 'cargando' && <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-400 font-bold animate-pulse">Consultando la base pública de la NHTSA…</div>}
            {specEstado === 'vacio' && <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-600"><b>No hay registro detallado para este VIN.</b> Pasa con autos vendidos fuera de Norteamérica o VIN mal copiados. El desglose de arriba sigue siendo válido.</div>}
            {specEstado === 'error' && (
              <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-600 space-y-2">
                <div><b>La NHTSA no respondió.</b> Revisa tu internet o ábrelo directo en su página.</div>
                <a href={`https://vpic.nhtsa.dot.gov/decoder/Decoder?VIN=${vin}`} target="_blank" rel="noopener noreferrer" className="inline-block px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-black">Abrir en NHTSA ↗</a>
              </div>
            )}
            {specEstado === 'ok' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {VIN_SECTIONS.map(sec => {
                  const filas = sec.fields.filter(f => vinClean(specs[f[0]]));
                  if (!filas.length) return null;
                  return (
                    <div key={sec.title} className="bg-white border border-slate-200 rounded-2xl p-4">
                      <div className="font-black text-sm text-slate-900 mb-2">{sec.title}</div>
                      <div className="divide-y divide-slate-100">
                        {filas.map(f => (
                          <div key={f[0]} className="flex justify-between gap-3 py-1.5 text-sm">
                            <span className="text-slate-500">{f[1]}</span>
                            <span className="font-bold text-slate-900 text-right">{vinClean(specs[f[0]])}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── RECALLS DEL MODELO ── */}
          {Array.isArray(recalls) && (
            recalls.length === 0 ? (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm font-black text-emerald-700">✓ Sin recalls para el {modelo} {g('ModelYear')}</div>
            ) : (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 space-y-2">
                <div className="font-black text-amber-800">⚠ {recalls.length} recall{recalls.length !== 1 ? 's' : ''} para el {modelo} {g('ModelYear')}</div>
                <div className="text-xs text-amber-700">Son del modelo, no de este auto. Para saber si ya se reparó, usa “Recalls pendientes de este auto” abajo.</div>
                {recalls.slice(0, 4).map((x, i) => (
                  <div key={i} className="rounded-xl bg-white border border-amber-100 p-3 text-xs">
                    <div className="font-black text-slate-800">{x.Component || 'Componente'} <span className="font-mono font-medium text-slate-400">{x.NHTSACampaignNumber || ''}</span></div>
                    <div className="text-slate-600 mt-1">{(x.Summary || '').slice(0, 180)}{(x.Summary || '').length > 180 ? '…' : ''}</div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* ── PAPELES Y ROBO ── */}
          <div>
            <div className="text-xs font-black text-slate-400 uppercase tracking-wide mb-2 px-1">Papeles, robo y recalls <span className="ml-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 normal-case tracking-normal">gratis</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{linksPais()}</div>
          </div>

          <div className="flex gap-2">
            <button onClick={copiarResumen} className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-black text-sm active:scale-95">{copiado ? '✓ Copiado' : '📋 Copiar resumen'}</button>
            <a href={`https://vpic.nhtsa.dot.gov/decoder/Decoder?VIN=${vin}`} target="_blank" rel="noopener noreferrer"
              className="px-4 py-3 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-black text-sm">Abrir en NHTSA ↗</a>
          </div>

          {/* ── HISTORIAL DE PAGO ── */}
          <details className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-600">
            <summary className="font-black text-slate-800 cursor-pointer">Historial completo (accidentes, dueños, odómetro): por qué cuesta</summary>
            <div className="space-y-2 mt-3">
              <p>Lo que venden Carfax, AutoCheck o EpicVIN <b>no es público</b>. Vive en la base federal <b>NMVTIS</b> de EE. UU. y solo proveedores con licencia la consultan. Precios aproximados, pueden cambiar:</p>
              {[['Proveedores NMVTIS directos', 'Solo título y odómetro', '≈ $3–15'], ['EpicVIN', 'Incluye subastas', '≈ $20'], ['AutoCheck', 'Fuerte en subastas', '≈ $30'], ['Carfax', 'Añade servicios de taller', '≈ $45']].map(([n, s, p]) => (
                <div key={n} className="flex justify-between gap-3 py-1 border-b border-slate-100">
                  <span><b className="text-slate-800">{n}</b> <span className="text-xs text-slate-400">{s}</span></span><span className="font-black text-slate-800">{p}</span>
                </div>
              ))}
              <a href="https://vehiclehistory.bja.ojp.gov/nmvtis_vehiclehistory" target="_blank" rel="noopener noreferrer" className="inline-block font-bold text-blue-600 underline">Lista oficial de proveedores autorizados ↗</a>
              <p className="text-xs">Colombia: el RUNT muestra gratis SOAT, técnico-mecánica, embargos y prendas; los dueños anteriores salen en el <b>certificado de tradición</b>. México: REPUVE es gratis.</p>
            </div>
          </details>
        </>)}

        <div className="text-[11px] text-slate-400 text-center pb-4">Especificaciones y recalls: NHTSA. Tus VIN recientes se guardan solo en este navegador.</div>
      </div>
    </motion.div>
  );
}

// ── MODO OSCURO ────────────────────────────────────────────────────
// La app usa colores fijos de Tailwind (bg-white, text-slate-900...). En vez de
// tocar cada componente, este tema los reemplaza cuando <html> tiene la clase
// "dark". Los links públicos (solicitud y seguimiento) se quedan como están.
const DARK_CSS = "html.dark .bg-red-50,html.dark .bg-red-50\\/30,html.dark .bg-red-50\\/40,html.dark .bg-red-50\\/50,html.dark .bg-red-50\\/60{background-color:rgba(239,68,68,0.10)!important}html.dark .hover\\:bg-red-50:hover{background-color:rgba(239,68,68,0.20)!important}html.dark .bg-red-100,html.dark .bg-red-100\\/30,html.dark .bg-red-100\\/40,html.dark .bg-red-100\\/50,html.dark .bg-red-100\\/60{background-color:rgba(239,68,68,0.16)!important}html.dark .hover\\:bg-red-100:hover{background-color:rgba(239,68,68,0.26)!important}html.dark .bg-red-200,html.dark .bg-red-200\\/30,html.dark .bg-red-200\\/40,html.dark .bg-red-200\\/50,html.dark .bg-red-200\\/60{background-color:rgba(239,68,68,0.24)!important}html.dark .hover\\:bg-red-200:hover{background-color:rgba(239,68,68,0.34)!important}html.dark .border-red-100,html.dark .border-red-200,html.dark .border-red-300{border-color:rgba(239,68,68,.35)!important}html.dark .text-red-700:not([class*=\"bg-red-300\"]):not([class*=\"bg-red-400\"]):not([class*=\"bg-red-500\"]):not([class*=\"bg-red-600\"]):not([class*=\"bg-red-700\"]):not([class*=\"bg-red-800\"]),html.dark .text-red-800:not([class*=\"bg-red-300\"]):not([class*=\"bg-red-400\"]):not([class*=\"bg-red-500\"]):not([class*=\"bg-red-600\"]):not([class*=\"bg-red-700\"]):not([class*=\"bg-red-800\"]),html.dark .text-red-900:not([class*=\"bg-red-300\"]):not([class*=\"bg-red-400\"]):not([class*=\"bg-red-500\"]):not([class*=\"bg-red-600\"]):not([class*=\"bg-red-700\"]):not([class*=\"bg-red-800\"]),html.dark .text-red-950:not([class*=\"bg-red-300\"]):not([class*=\"bg-red-400\"]):not([class*=\"bg-red-500\"]):not([class*=\"bg-red-600\"]):not([class*=\"bg-red-700\"]):not([class*=\"bg-red-800\"]){color:#fca5a5!important}html.dark .text-red-600:not([class*=\"bg-red-300\"]):not([class*=\"bg-red-400\"]):not([class*=\"bg-red-500\"]):not([class*=\"bg-red-600\"]):not([class*=\"bg-red-700\"]):not([class*=\"bg-red-800\"]){color:#f87171!important}html.dark .bg-rose-50,html.dark .bg-rose-50\\/30,html.dark .bg-rose-50\\/40,html.dark .bg-rose-50\\/50,html.dark .bg-rose-50\\/60{background-color:rgba(244,63,94,0.10)!important}html.dark .hover\\:bg-rose-50:hover{background-color:rgba(244,63,94,0.20)!important}html.dark .bg-rose-100,html.dark .bg-rose-100\\/30,html.dark .bg-rose-100\\/40,html.dark .bg-rose-100\\/50,html.dark .bg-rose-100\\/60{background-color:rgba(244,63,94,0.16)!important}html.dark .hover\\:bg-rose-100:hover{background-color:rgba(244,63,94,0.26)!important}html.dark .bg-rose-200,html.dark .bg-rose-200\\/30,html.dark .bg-rose-200\\/40,html.dark .bg-rose-200\\/50,html.dark .bg-rose-200\\/60{background-color:rgba(244,63,94,0.24)!important}html.dark .hover\\:bg-rose-200:hover{background-color:rgba(244,63,94,0.34)!important}html.dark .border-rose-100,html.dark .border-rose-200,html.dark .border-rose-300{border-color:rgba(244,63,94,.35)!important}html.dark .text-rose-700:not([class*=\"bg-rose-300\"]):not([class*=\"bg-rose-400\"]):not([class*=\"bg-rose-500\"]):not([class*=\"bg-rose-600\"]):not([class*=\"bg-rose-700\"]):not([class*=\"bg-rose-800\"]),html.dark .text-rose-800:not([class*=\"bg-rose-300\"]):not([class*=\"bg-rose-400\"]):not([class*=\"bg-rose-500\"]):not([class*=\"bg-rose-600\"]):not([class*=\"bg-rose-700\"]):not([class*=\"bg-rose-800\"]),html.dark .text-rose-900:not([class*=\"bg-rose-300\"]):not([class*=\"bg-rose-400\"]):not([class*=\"bg-rose-500\"]):not([class*=\"bg-rose-600\"]):not([class*=\"bg-rose-700\"]):not([class*=\"bg-rose-800\"]),html.dark .text-rose-950:not([class*=\"bg-rose-300\"]):not([class*=\"bg-rose-400\"]):not([class*=\"bg-rose-500\"]):not([class*=\"bg-rose-600\"]):not([class*=\"bg-rose-700\"]):not([class*=\"bg-rose-800\"]){color:#fda4af!important}html.dark .text-rose-600:not([class*=\"bg-rose-300\"]):not([class*=\"bg-rose-400\"]):not([class*=\"bg-rose-500\"]):not([class*=\"bg-rose-600\"]):not([class*=\"bg-rose-700\"]):not([class*=\"bg-rose-800\"]){color:#fb7185!important}html.dark .bg-orange-50,html.dark .bg-orange-50\\/30,html.dark .bg-orange-50\\/40,html.dark .bg-orange-50\\/50,html.dark .bg-orange-50\\/60{background-color:rgba(249,115,22,0.10)!important}html.dark .hover\\:bg-orange-50:hover{background-color:rgba(249,115,22,0.20)!important}html.dark .bg-orange-100,html.dark .bg-orange-100\\/30,html.dark .bg-orange-100\\/40,html.dark .bg-orange-100\\/50,html.dark .bg-orange-100\\/60{background-color:rgba(249,115,22,0.16)!important}html.dark .hover\\:bg-orange-100:hover{background-color:rgba(249,115,22,0.26)!important}html.dark .bg-orange-200,html.dark .bg-orange-200\\/30,html.dark .bg-orange-200\\/40,html.dark .bg-orange-200\\/50,html.dark .bg-orange-200\\/60{background-color:rgba(249,115,22,0.24)!important}html.dark .hover\\:bg-orange-200:hover{background-color:rgba(249,115,22,0.34)!important}html.dark .border-orange-100,html.dark .border-orange-200,html.dark .border-orange-300{border-color:rgba(249,115,22,.35)!important}html.dark .text-orange-700:not([class*=\"bg-orange-300\"]):not([class*=\"bg-orange-400\"]):not([class*=\"bg-orange-500\"]):not([class*=\"bg-orange-600\"]):not([class*=\"bg-orange-700\"]):not([class*=\"bg-orange-800\"]),html.dark .text-orange-800:not([class*=\"bg-orange-300\"]):not([class*=\"bg-orange-400\"]):not([class*=\"bg-orange-500\"]):not([class*=\"bg-orange-600\"]):not([class*=\"bg-orange-700\"]):not([class*=\"bg-orange-800\"]),html.dark .text-orange-900:not([class*=\"bg-orange-300\"]):not([class*=\"bg-orange-400\"]):not([class*=\"bg-orange-500\"]):not([class*=\"bg-orange-600\"]):not([class*=\"bg-orange-700\"]):not([class*=\"bg-orange-800\"]),html.dark .text-orange-950:not([class*=\"bg-orange-300\"]):not([class*=\"bg-orange-400\"]):not([class*=\"bg-orange-500\"]):not([class*=\"bg-orange-600\"]):not([class*=\"bg-orange-700\"]):not([class*=\"bg-orange-800\"]){color:#fdba74!important}html.dark .text-orange-600:not([class*=\"bg-orange-300\"]):not([class*=\"bg-orange-400\"]):not([class*=\"bg-orange-500\"]):not([class*=\"bg-orange-600\"]):not([class*=\"bg-orange-700\"]):not([class*=\"bg-orange-800\"]){color:#fb923c!important}html.dark .bg-amber-50,html.dark .bg-amber-50\\/30,html.dark .bg-amber-50\\/40,html.dark .bg-amber-50\\/50,html.dark .bg-amber-50\\/60{background-color:rgba(245,158,11,0.10)!important}html.dark .hover\\:bg-amber-50:hover{background-color:rgba(245,158,11,0.20)!important}html.dark .bg-amber-100,html.dark .bg-amber-100\\/30,html.dark .bg-amber-100\\/40,html.dark .bg-amber-100\\/50,html.dark .bg-amber-100\\/60{background-color:rgba(245,158,11,0.16)!important}html.dark .hover\\:bg-amber-100:hover{background-color:rgba(245,158,11,0.26)!important}html.dark .bg-amber-200,html.dark .bg-amber-200\\/30,html.dark .bg-amber-200\\/40,html.dark .bg-amber-200\\/50,html.dark .bg-amber-200\\/60{background-color:rgba(245,158,11,0.24)!important}html.dark .hover\\:bg-amber-200:hover{background-color:rgba(245,158,11,0.34)!important}html.dark .border-amber-100,html.dark .border-amber-200,html.dark .border-amber-300{border-color:rgba(245,158,11,.35)!important}html.dark .text-amber-700:not([class*=\"bg-amber-300\"]):not([class*=\"bg-amber-400\"]):not([class*=\"bg-amber-500\"]):not([class*=\"bg-amber-600\"]):not([class*=\"bg-amber-700\"]):not([class*=\"bg-amber-800\"]),html.dark .text-amber-800:not([class*=\"bg-amber-300\"]):not([class*=\"bg-amber-400\"]):not([class*=\"bg-amber-500\"]):not([class*=\"bg-amber-600\"]):not([class*=\"bg-amber-700\"]):not([class*=\"bg-amber-800\"]),html.dark .text-amber-900:not([class*=\"bg-amber-300\"]):not([class*=\"bg-amber-400\"]):not([class*=\"bg-amber-500\"]):not([class*=\"bg-amber-600\"]):not([class*=\"bg-amber-700\"]):not([class*=\"bg-amber-800\"]),html.dark .text-amber-950:not([class*=\"bg-amber-300\"]):not([class*=\"bg-amber-400\"]):not([class*=\"bg-amber-500\"]):not([class*=\"bg-amber-600\"]):not([class*=\"bg-amber-700\"]):not([class*=\"bg-amber-800\"]){color:#fcd34d!important}html.dark .text-amber-600:not([class*=\"bg-amber-300\"]):not([class*=\"bg-amber-400\"]):not([class*=\"bg-amber-500\"]):not([class*=\"bg-amber-600\"]):not([class*=\"bg-amber-700\"]):not([class*=\"bg-amber-800\"]){color:#fbbf24!important}html.dark .bg-yellow-50,html.dark .bg-yellow-50\\/30,html.dark .bg-yellow-50\\/40,html.dark .bg-yellow-50\\/50,html.dark .bg-yellow-50\\/60{background-color:rgba(234,179,8,0.10)!important}html.dark .hover\\:bg-yellow-50:hover{background-color:rgba(234,179,8,0.20)!important}html.dark .bg-yellow-100,html.dark .bg-yellow-100\\/30,html.dark .bg-yellow-100\\/40,html.dark .bg-yellow-100\\/50,html.dark .bg-yellow-100\\/60{background-color:rgba(234,179,8,0.16)!important}html.dark .hover\\:bg-yellow-100:hover{background-color:rgba(234,179,8,0.26)!important}html.dark .bg-yellow-200,html.dark .bg-yellow-200\\/30,html.dark .bg-yellow-200\\/40,html.dark .bg-yellow-200\\/50,html.dark .bg-yellow-200\\/60{background-color:rgba(234,179,8,0.24)!important}html.dark .hover\\:bg-yellow-200:hover{background-color:rgba(234,179,8,0.34)!important}html.dark .border-yellow-100,html.dark .border-yellow-200,html.dark .border-yellow-300{border-color:rgba(234,179,8,.35)!important}html.dark .text-yellow-700:not([class*=\"bg-yellow-300\"]):not([class*=\"bg-yellow-400\"]):not([class*=\"bg-yellow-500\"]):not([class*=\"bg-yellow-600\"]):not([class*=\"bg-yellow-700\"]):not([class*=\"bg-yellow-800\"]),html.dark .text-yellow-800:not([class*=\"bg-yellow-300\"]):not([class*=\"bg-yellow-400\"]):not([class*=\"bg-yellow-500\"]):not([class*=\"bg-yellow-600\"]):not([class*=\"bg-yellow-700\"]):not([class*=\"bg-yellow-800\"]),html.dark .text-yellow-900:not([class*=\"bg-yellow-300\"]):not([class*=\"bg-yellow-400\"]):not([class*=\"bg-yellow-500\"]):not([class*=\"bg-yellow-600\"]):not([class*=\"bg-yellow-700\"]):not([class*=\"bg-yellow-800\"]),html.dark .text-yellow-950:not([class*=\"bg-yellow-300\"]):not([class*=\"bg-yellow-400\"]):not([class*=\"bg-yellow-500\"]):not([class*=\"bg-yellow-600\"]):not([class*=\"bg-yellow-700\"]):not([class*=\"bg-yellow-800\"]){color:#fde047!important}html.dark .text-yellow-600:not([class*=\"bg-yellow-300\"]):not([class*=\"bg-yellow-400\"]):not([class*=\"bg-yellow-500\"]):not([class*=\"bg-yellow-600\"]):not([class*=\"bg-yellow-700\"]):not([class*=\"bg-yellow-800\"]){color:#facc15!important}html.dark .bg-lime-50,html.dark .bg-lime-50\\/30,html.dark .bg-lime-50\\/40,html.dark .bg-lime-50\\/50,html.dark .bg-lime-50\\/60{background-color:rgba(132,204,22,0.10)!important}html.dark .hover\\:bg-lime-50:hover{background-color:rgba(132,204,22,0.20)!important}html.dark .bg-lime-100,html.dark .bg-lime-100\\/30,html.dark .bg-lime-100\\/40,html.dark .bg-lime-100\\/50,html.dark .bg-lime-100\\/60{background-color:rgba(132,204,22,0.16)!important}html.dark .hover\\:bg-lime-100:hover{background-color:rgba(132,204,22,0.26)!important}html.dark .bg-lime-200,html.dark .bg-lime-200\\/30,html.dark .bg-lime-200\\/40,html.dark .bg-lime-200\\/50,html.dark .bg-lime-200\\/60{background-color:rgba(132,204,22,0.24)!important}html.dark .hover\\:bg-lime-200:hover{background-color:rgba(132,204,22,0.34)!important}html.dark .border-lime-100,html.dark .border-lime-200,html.dark .border-lime-300{border-color:rgba(132,204,22,.35)!important}html.dark .text-lime-700:not([class*=\"bg-lime-300\"]):not([class*=\"bg-lime-400\"]):not([class*=\"bg-lime-500\"]):not([class*=\"bg-lime-600\"]):not([class*=\"bg-lime-700\"]):not([class*=\"bg-lime-800\"]),html.dark .text-lime-800:not([class*=\"bg-lime-300\"]):not([class*=\"bg-lime-400\"]):not([class*=\"bg-lime-500\"]):not([class*=\"bg-lime-600\"]):not([class*=\"bg-lime-700\"]):not([class*=\"bg-lime-800\"]),html.dark .text-lime-900:not([class*=\"bg-lime-300\"]):not([class*=\"bg-lime-400\"]):not([class*=\"bg-lime-500\"]):not([class*=\"bg-lime-600\"]):not([class*=\"bg-lime-700\"]):not([class*=\"bg-lime-800\"]),html.dark .text-lime-950:not([class*=\"bg-lime-300\"]):not([class*=\"bg-lime-400\"]):not([class*=\"bg-lime-500\"]):not([class*=\"bg-lime-600\"]):not([class*=\"bg-lime-700\"]):not([class*=\"bg-lime-800\"]){color:#bef264!important}html.dark .text-lime-600:not([class*=\"bg-lime-300\"]):not([class*=\"bg-lime-400\"]):not([class*=\"bg-lime-500\"]):not([class*=\"bg-lime-600\"]):not([class*=\"bg-lime-700\"]):not([class*=\"bg-lime-800\"]){color:#a3e635!important}html.dark .bg-green-50,html.dark .bg-green-50\\/30,html.dark .bg-green-50\\/40,html.dark .bg-green-50\\/50,html.dark .bg-green-50\\/60{background-color:rgba(34,197,94,0.10)!important}html.dark .hover\\:bg-green-50:hover{background-color:rgba(34,197,94,0.20)!important}html.dark .bg-green-100,html.dark .bg-green-100\\/30,html.dark .bg-green-100\\/40,html.dark .bg-green-100\\/50,html.dark .bg-green-100\\/60{background-color:rgba(34,197,94,0.16)!important}html.dark .hover\\:bg-green-100:hover{background-color:rgba(34,197,94,0.26)!important}html.dark .bg-green-200,html.dark .bg-green-200\\/30,html.dark .bg-green-200\\/40,html.dark .bg-green-200\\/50,html.dark .bg-green-200\\/60{background-color:rgba(34,197,94,0.24)!important}html.dark .hover\\:bg-green-200:hover{background-color:rgba(34,197,94,0.34)!important}html.dark .border-green-100,html.dark .border-green-200,html.dark .border-green-300{border-color:rgba(34,197,94,.35)!important}html.dark .text-green-700:not([class*=\"bg-green-300\"]):not([class*=\"bg-green-400\"]):not([class*=\"bg-green-500\"]):not([class*=\"bg-green-600\"]):not([class*=\"bg-green-700\"]):not([class*=\"bg-green-800\"]),html.dark .text-green-800:not([class*=\"bg-green-300\"]):not([class*=\"bg-green-400\"]):not([class*=\"bg-green-500\"]):not([class*=\"bg-green-600\"]):not([class*=\"bg-green-700\"]):not([class*=\"bg-green-800\"]),html.dark .text-green-900:not([class*=\"bg-green-300\"]):not([class*=\"bg-green-400\"]):not([class*=\"bg-green-500\"]):not([class*=\"bg-green-600\"]):not([class*=\"bg-green-700\"]):not([class*=\"bg-green-800\"]),html.dark .text-green-950:not([class*=\"bg-green-300\"]):not([class*=\"bg-green-400\"]):not([class*=\"bg-green-500\"]):not([class*=\"bg-green-600\"]):not([class*=\"bg-green-700\"]):not([class*=\"bg-green-800\"]){color:#86efac!important}html.dark .text-green-600:not([class*=\"bg-green-300\"]):not([class*=\"bg-green-400\"]):not([class*=\"bg-green-500\"]):not([class*=\"bg-green-600\"]):not([class*=\"bg-green-700\"]):not([class*=\"bg-green-800\"]){color:#4ade80!important}html.dark .bg-emerald-50,html.dark .bg-emerald-50\\/30,html.dark .bg-emerald-50\\/40,html.dark .bg-emerald-50\\/50,html.dark .bg-emerald-50\\/60{background-color:rgba(16,185,129,0.10)!important}html.dark .hover\\:bg-emerald-50:hover{background-color:rgba(16,185,129,0.20)!important}html.dark .bg-emerald-100,html.dark .bg-emerald-100\\/30,html.dark .bg-emerald-100\\/40,html.dark .bg-emerald-100\\/50,html.dark .bg-emerald-100\\/60{background-color:rgba(16,185,129,0.16)!important}html.dark .hover\\:bg-emerald-100:hover{background-color:rgba(16,185,129,0.26)!important}html.dark .bg-emerald-200,html.dark .bg-emerald-200\\/30,html.dark .bg-emerald-200\\/40,html.dark .bg-emerald-200\\/50,html.dark .bg-emerald-200\\/60{background-color:rgba(16,185,129,0.24)!important}html.dark .hover\\:bg-emerald-200:hover{background-color:rgba(16,185,129,0.34)!important}html.dark .border-emerald-100,html.dark .border-emerald-200,html.dark .border-emerald-300{border-color:rgba(16,185,129,.35)!important}html.dark .text-emerald-700:not([class*=\"bg-emerald-300\"]):not([class*=\"bg-emerald-400\"]):not([class*=\"bg-emerald-500\"]):not([class*=\"bg-emerald-600\"]):not([class*=\"bg-emerald-700\"]):not([class*=\"bg-emerald-800\"]),html.dark .text-emerald-800:not([class*=\"bg-emerald-300\"]):not([class*=\"bg-emerald-400\"]):not([class*=\"bg-emerald-500\"]):not([class*=\"bg-emerald-600\"]):not([class*=\"bg-emerald-700\"]):not([class*=\"bg-emerald-800\"]),html.dark .text-emerald-900:not([class*=\"bg-emerald-300\"]):not([class*=\"bg-emerald-400\"]):not([class*=\"bg-emerald-500\"]):not([class*=\"bg-emerald-600\"]):not([class*=\"bg-emerald-700\"]):not([class*=\"bg-emerald-800\"]),html.dark .text-emerald-950:not([class*=\"bg-emerald-300\"]):not([class*=\"bg-emerald-400\"]):not([class*=\"bg-emerald-500\"]):not([class*=\"bg-emerald-600\"]):not([class*=\"bg-emerald-700\"]):not([class*=\"bg-emerald-800\"]){color:#6ee7b7!important}html.dark .text-emerald-600:not([class*=\"bg-emerald-300\"]):not([class*=\"bg-emerald-400\"]):not([class*=\"bg-emerald-500\"]):not([class*=\"bg-emerald-600\"]):not([class*=\"bg-emerald-700\"]):not([class*=\"bg-emerald-800\"]){color:#34d399!important}html.dark .bg-teal-50,html.dark .bg-teal-50\\/30,html.dark .bg-teal-50\\/40,html.dark .bg-teal-50\\/50,html.dark .bg-teal-50\\/60{background-color:rgba(20,184,166,0.10)!important}html.dark .hover\\:bg-teal-50:hover{background-color:rgba(20,184,166,0.20)!important}html.dark .bg-teal-100,html.dark .bg-teal-100\\/30,html.dark .bg-teal-100\\/40,html.dark .bg-teal-100\\/50,html.dark .bg-teal-100\\/60{background-color:rgba(20,184,166,0.16)!important}html.dark .hover\\:bg-teal-100:hover{background-color:rgba(20,184,166,0.26)!important}html.dark .bg-teal-200,html.dark .bg-teal-200\\/30,html.dark .bg-teal-200\\/40,html.dark .bg-teal-200\\/50,html.dark .bg-teal-200\\/60{background-color:rgba(20,184,166,0.24)!important}html.dark .hover\\:bg-teal-200:hover{background-color:rgba(20,184,166,0.34)!important}html.dark .border-teal-100,html.dark .border-teal-200,html.dark .border-teal-300{border-color:rgba(20,184,166,.35)!important}html.dark .text-teal-700:not([class*=\"bg-teal-300\"]):not([class*=\"bg-teal-400\"]):not([class*=\"bg-teal-500\"]):not([class*=\"bg-teal-600\"]):not([class*=\"bg-teal-700\"]):not([class*=\"bg-teal-800\"]),html.dark .text-teal-800:not([class*=\"bg-teal-300\"]):not([class*=\"bg-teal-400\"]):not([class*=\"bg-teal-500\"]):not([class*=\"bg-teal-600\"]):not([class*=\"bg-teal-700\"]):not([class*=\"bg-teal-800\"]),html.dark .text-teal-900:not([class*=\"bg-teal-300\"]):not([class*=\"bg-teal-400\"]):not([class*=\"bg-teal-500\"]):not([class*=\"bg-teal-600\"]):not([class*=\"bg-teal-700\"]):not([class*=\"bg-teal-800\"]),html.dark .text-teal-950:not([class*=\"bg-teal-300\"]):not([class*=\"bg-teal-400\"]):not([class*=\"bg-teal-500\"]):not([class*=\"bg-teal-600\"]):not([class*=\"bg-teal-700\"]):not([class*=\"bg-teal-800\"]){color:#5eead4!important}html.dark .text-teal-600:not([class*=\"bg-teal-300\"]):not([class*=\"bg-teal-400\"]):not([class*=\"bg-teal-500\"]):not([class*=\"bg-teal-600\"]):not([class*=\"bg-teal-700\"]):not([class*=\"bg-teal-800\"]){color:#2dd4bf!important}html.dark .bg-cyan-50,html.dark .bg-cyan-50\\/30,html.dark .bg-cyan-50\\/40,html.dark .bg-cyan-50\\/50,html.dark .bg-cyan-50\\/60{background-color:rgba(6,182,212,0.10)!important}html.dark .hover\\:bg-cyan-50:hover{background-color:rgba(6,182,212,0.20)!important}html.dark .bg-cyan-100,html.dark .bg-cyan-100\\/30,html.dark .bg-cyan-100\\/40,html.dark .bg-cyan-100\\/50,html.dark .bg-cyan-100\\/60{background-color:rgba(6,182,212,0.16)!important}html.dark .hover\\:bg-cyan-100:hover{background-color:rgba(6,182,212,0.26)!important}html.dark .bg-cyan-200,html.dark .bg-cyan-200\\/30,html.dark .bg-cyan-200\\/40,html.dark .bg-cyan-200\\/50,html.dark .bg-cyan-200\\/60{background-color:rgba(6,182,212,0.24)!important}html.dark .hover\\:bg-cyan-200:hover{background-color:rgba(6,182,212,0.34)!important}html.dark .border-cyan-100,html.dark .border-cyan-200,html.dark .border-cyan-300{border-color:rgba(6,182,212,.35)!important}html.dark .text-cyan-700:not([class*=\"bg-cyan-300\"]):not([class*=\"bg-cyan-400\"]):not([class*=\"bg-cyan-500\"]):not([class*=\"bg-cyan-600\"]):not([class*=\"bg-cyan-700\"]):not([class*=\"bg-cyan-800\"]),html.dark .text-cyan-800:not([class*=\"bg-cyan-300\"]):not([class*=\"bg-cyan-400\"]):not([class*=\"bg-cyan-500\"]):not([class*=\"bg-cyan-600\"]):not([class*=\"bg-cyan-700\"]):not([class*=\"bg-cyan-800\"]),html.dark .text-cyan-900:not([class*=\"bg-cyan-300\"]):not([class*=\"bg-cyan-400\"]):not([class*=\"bg-cyan-500\"]):not([class*=\"bg-cyan-600\"]):not([class*=\"bg-cyan-700\"]):not([class*=\"bg-cyan-800\"]),html.dark .text-cyan-950:not([class*=\"bg-cyan-300\"]):not([class*=\"bg-cyan-400\"]):not([class*=\"bg-cyan-500\"]):not([class*=\"bg-cyan-600\"]):not([class*=\"bg-cyan-700\"]):not([class*=\"bg-cyan-800\"]){color:#67e8f9!important}html.dark .text-cyan-600:not([class*=\"bg-cyan-300\"]):not([class*=\"bg-cyan-400\"]):not([class*=\"bg-cyan-500\"]):not([class*=\"bg-cyan-600\"]):not([class*=\"bg-cyan-700\"]):not([class*=\"bg-cyan-800\"]){color:#22d3ee!important}html.dark .bg-sky-50,html.dark .bg-sky-50\\/30,html.dark .bg-sky-50\\/40,html.dark .bg-sky-50\\/50,html.dark .bg-sky-50\\/60{background-color:rgba(14,165,233,0.10)!important}html.dark .hover\\:bg-sky-50:hover{background-color:rgba(14,165,233,0.20)!important}html.dark .bg-sky-100,html.dark .bg-sky-100\\/30,html.dark .bg-sky-100\\/40,html.dark .bg-sky-100\\/50,html.dark .bg-sky-100\\/60{background-color:rgba(14,165,233,0.16)!important}html.dark .hover\\:bg-sky-100:hover{background-color:rgba(14,165,233,0.26)!important}html.dark .bg-sky-200,html.dark .bg-sky-200\\/30,html.dark .bg-sky-200\\/40,html.dark .bg-sky-200\\/50,html.dark .bg-sky-200\\/60{background-color:rgba(14,165,233,0.24)!important}html.dark .hover\\:bg-sky-200:hover{background-color:rgba(14,165,233,0.34)!important}html.dark .border-sky-100,html.dark .border-sky-200,html.dark .border-sky-300{border-color:rgba(14,165,233,.35)!important}html.dark .text-sky-700:not([class*=\"bg-sky-300\"]):not([class*=\"bg-sky-400\"]):not([class*=\"bg-sky-500\"]):not([class*=\"bg-sky-600\"]):not([class*=\"bg-sky-700\"]):not([class*=\"bg-sky-800\"]),html.dark .text-sky-800:not([class*=\"bg-sky-300\"]):not([class*=\"bg-sky-400\"]):not([class*=\"bg-sky-500\"]):not([class*=\"bg-sky-600\"]):not([class*=\"bg-sky-700\"]):not([class*=\"bg-sky-800\"]),html.dark .text-sky-900:not([class*=\"bg-sky-300\"]):not([class*=\"bg-sky-400\"]):not([class*=\"bg-sky-500\"]):not([class*=\"bg-sky-600\"]):not([class*=\"bg-sky-700\"]):not([class*=\"bg-sky-800\"]),html.dark .text-sky-950:not([class*=\"bg-sky-300\"]):not([class*=\"bg-sky-400\"]):not([class*=\"bg-sky-500\"]):not([class*=\"bg-sky-600\"]):not([class*=\"bg-sky-700\"]):not([class*=\"bg-sky-800\"]){color:#7dd3fc!important}html.dark .text-sky-600:not([class*=\"bg-sky-300\"]):not([class*=\"bg-sky-400\"]):not([class*=\"bg-sky-500\"]):not([class*=\"bg-sky-600\"]):not([class*=\"bg-sky-700\"]):not([class*=\"bg-sky-800\"]){color:#38bdf8!important}html.dark .bg-blue-50,html.dark .bg-blue-50\\/30,html.dark .bg-blue-50\\/40,html.dark .bg-blue-50\\/50,html.dark .bg-blue-50\\/60{background-color:rgba(59,130,246,0.10)!important}html.dark .hover\\:bg-blue-50:hover{background-color:rgba(59,130,246,0.20)!important}html.dark .bg-blue-100,html.dark .bg-blue-100\\/30,html.dark .bg-blue-100\\/40,html.dark .bg-blue-100\\/50,html.dark .bg-blue-100\\/60{background-color:rgba(59,130,246,0.16)!important}html.dark .hover\\:bg-blue-100:hover{background-color:rgba(59,130,246,0.26)!important}html.dark .bg-blue-200,html.dark .bg-blue-200\\/30,html.dark .bg-blue-200\\/40,html.dark .bg-blue-200\\/50,html.dark .bg-blue-200\\/60{background-color:rgba(59,130,246,0.24)!important}html.dark .hover\\:bg-blue-200:hover{background-color:rgba(59,130,246,0.34)!important}html.dark .border-blue-100,html.dark .border-blue-200,html.dark .border-blue-300{border-color:rgba(59,130,246,.35)!important}html.dark .text-blue-700:not([class*=\"bg-blue-300\"]):not([class*=\"bg-blue-400\"]):not([class*=\"bg-blue-500\"]):not([class*=\"bg-blue-600\"]):not([class*=\"bg-blue-700\"]):not([class*=\"bg-blue-800\"]),html.dark .text-blue-800:not([class*=\"bg-blue-300\"]):not([class*=\"bg-blue-400\"]):not([class*=\"bg-blue-500\"]):not([class*=\"bg-blue-600\"]):not([class*=\"bg-blue-700\"]):not([class*=\"bg-blue-800\"]),html.dark .text-blue-900:not([class*=\"bg-blue-300\"]):not([class*=\"bg-blue-400\"]):not([class*=\"bg-blue-500\"]):not([class*=\"bg-blue-600\"]):not([class*=\"bg-blue-700\"]):not([class*=\"bg-blue-800\"]),html.dark .text-blue-950:not([class*=\"bg-blue-300\"]):not([class*=\"bg-blue-400\"]):not([class*=\"bg-blue-500\"]):not([class*=\"bg-blue-600\"]):not([class*=\"bg-blue-700\"]):not([class*=\"bg-blue-800\"]){color:#93c5fd!important}html.dark .text-blue-600:not([class*=\"bg-blue-300\"]):not([class*=\"bg-blue-400\"]):not([class*=\"bg-blue-500\"]):not([class*=\"bg-blue-600\"]):not([class*=\"bg-blue-700\"]):not([class*=\"bg-blue-800\"]){color:#60a5fa!important}html.dark .bg-indigo-50,html.dark .bg-indigo-50\\/30,html.dark .bg-indigo-50\\/40,html.dark .bg-indigo-50\\/50,html.dark .bg-indigo-50\\/60{background-color:rgba(99,102,241,0.10)!important}html.dark .hover\\:bg-indigo-50:hover{background-color:rgba(99,102,241,0.20)!important}html.dark .bg-indigo-100,html.dark .bg-indigo-100\\/30,html.dark .bg-indigo-100\\/40,html.dark .bg-indigo-100\\/50,html.dark .bg-indigo-100\\/60{background-color:rgba(99,102,241,0.16)!important}html.dark .hover\\:bg-indigo-100:hover{background-color:rgba(99,102,241,0.26)!important}html.dark .bg-indigo-200,html.dark .bg-indigo-200\\/30,html.dark .bg-indigo-200\\/40,html.dark .bg-indigo-200\\/50,html.dark .bg-indigo-200\\/60{background-color:rgba(99,102,241,0.24)!important}html.dark .hover\\:bg-indigo-200:hover{background-color:rgba(99,102,241,0.34)!important}html.dark .border-indigo-100,html.dark .border-indigo-200,html.dark .border-indigo-300{border-color:rgba(99,102,241,.35)!important}html.dark .text-indigo-700:not([class*=\"bg-indigo-300\"]):not([class*=\"bg-indigo-400\"]):not([class*=\"bg-indigo-500\"]):not([class*=\"bg-indigo-600\"]):not([class*=\"bg-indigo-700\"]):not([class*=\"bg-indigo-800\"]),html.dark .text-indigo-800:not([class*=\"bg-indigo-300\"]):not([class*=\"bg-indigo-400\"]):not([class*=\"bg-indigo-500\"]):not([class*=\"bg-indigo-600\"]):not([class*=\"bg-indigo-700\"]):not([class*=\"bg-indigo-800\"]),html.dark .text-indigo-900:not([class*=\"bg-indigo-300\"]):not([class*=\"bg-indigo-400\"]):not([class*=\"bg-indigo-500\"]):not([class*=\"bg-indigo-600\"]):not([class*=\"bg-indigo-700\"]):not([class*=\"bg-indigo-800\"]),html.dark .text-indigo-950:not([class*=\"bg-indigo-300\"]):not([class*=\"bg-indigo-400\"]):not([class*=\"bg-indigo-500\"]):not([class*=\"bg-indigo-600\"]):not([class*=\"bg-indigo-700\"]):not([class*=\"bg-indigo-800\"]){color:#a5b4fc!important}html.dark .text-indigo-600:not([class*=\"bg-indigo-300\"]):not([class*=\"bg-indigo-400\"]):not([class*=\"bg-indigo-500\"]):not([class*=\"bg-indigo-600\"]):not([class*=\"bg-indigo-700\"]):not([class*=\"bg-indigo-800\"]){color:#818cf8!important}html.dark .bg-violet-50,html.dark .bg-violet-50\\/30,html.dark .bg-violet-50\\/40,html.dark .bg-violet-50\\/50,html.dark .bg-violet-50\\/60{background-color:rgba(139,92,246,0.10)!important}html.dark .hover\\:bg-violet-50:hover{background-color:rgba(139,92,246,0.20)!important}html.dark .bg-violet-100,html.dark .bg-violet-100\\/30,html.dark .bg-violet-100\\/40,html.dark .bg-violet-100\\/50,html.dark .bg-violet-100\\/60{background-color:rgba(139,92,246,0.16)!important}html.dark .hover\\:bg-violet-100:hover{background-color:rgba(139,92,246,0.26)!important}html.dark .bg-violet-200,html.dark .bg-violet-200\\/30,html.dark .bg-violet-200\\/40,html.dark .bg-violet-200\\/50,html.dark .bg-violet-200\\/60{background-color:rgba(139,92,246,0.24)!important}html.dark .hover\\:bg-violet-200:hover{background-color:rgba(139,92,246,0.34)!important}html.dark .border-violet-100,html.dark .border-violet-200,html.dark .border-violet-300{border-color:rgba(139,92,246,.35)!important}html.dark .text-violet-700:not([class*=\"bg-violet-300\"]):not([class*=\"bg-violet-400\"]):not([class*=\"bg-violet-500\"]):not([class*=\"bg-violet-600\"]):not([class*=\"bg-violet-700\"]):not([class*=\"bg-violet-800\"]),html.dark .text-violet-800:not([class*=\"bg-violet-300\"]):not([class*=\"bg-violet-400\"]):not([class*=\"bg-violet-500\"]):not([class*=\"bg-violet-600\"]):not([class*=\"bg-violet-700\"]):not([class*=\"bg-violet-800\"]),html.dark .text-violet-900:not([class*=\"bg-violet-300\"]):not([class*=\"bg-violet-400\"]):not([class*=\"bg-violet-500\"]):not([class*=\"bg-violet-600\"]):not([class*=\"bg-violet-700\"]):not([class*=\"bg-violet-800\"]),html.dark .text-violet-950:not([class*=\"bg-violet-300\"]):not([class*=\"bg-violet-400\"]):not([class*=\"bg-violet-500\"]):not([class*=\"bg-violet-600\"]):not([class*=\"bg-violet-700\"]):not([class*=\"bg-violet-800\"]){color:#c4b5fd!important}html.dark .text-violet-600:not([class*=\"bg-violet-300\"]):not([class*=\"bg-violet-400\"]):not([class*=\"bg-violet-500\"]):not([class*=\"bg-violet-600\"]):not([class*=\"bg-violet-700\"]):not([class*=\"bg-violet-800\"]){color:#a78bfa!important}html.dark .bg-purple-50,html.dark .bg-purple-50\\/30,html.dark .bg-purple-50\\/40,html.dark .bg-purple-50\\/50,html.dark .bg-purple-50\\/60{background-color:rgba(168,85,247,0.10)!important}html.dark .hover\\:bg-purple-50:hover{background-color:rgba(168,85,247,0.20)!important}html.dark .bg-purple-100,html.dark .bg-purple-100\\/30,html.dark .bg-purple-100\\/40,html.dark .bg-purple-100\\/50,html.dark .bg-purple-100\\/60{background-color:rgba(168,85,247,0.16)!important}html.dark .hover\\:bg-purple-100:hover{background-color:rgba(168,85,247,0.26)!important}html.dark .bg-purple-200,html.dark .bg-purple-200\\/30,html.dark .bg-purple-200\\/40,html.dark .bg-purple-200\\/50,html.dark .bg-purple-200\\/60{background-color:rgba(168,85,247,0.24)!important}html.dark .hover\\:bg-purple-200:hover{background-color:rgba(168,85,247,0.34)!important}html.dark .border-purple-100,html.dark .border-purple-200,html.dark .border-purple-300{border-color:rgba(168,85,247,.35)!important}html.dark .text-purple-700:not([class*=\"bg-purple-300\"]):not([class*=\"bg-purple-400\"]):not([class*=\"bg-purple-500\"]):not([class*=\"bg-purple-600\"]):not([class*=\"bg-purple-700\"]):not([class*=\"bg-purple-800\"]),html.dark .text-purple-800:not([class*=\"bg-purple-300\"]):not([class*=\"bg-purple-400\"]):not([class*=\"bg-purple-500\"]):not([class*=\"bg-purple-600\"]):not([class*=\"bg-purple-700\"]):not([class*=\"bg-purple-800\"]),html.dark .text-purple-900:not([class*=\"bg-purple-300\"]):not([class*=\"bg-purple-400\"]):not([class*=\"bg-purple-500\"]):not([class*=\"bg-purple-600\"]):not([class*=\"bg-purple-700\"]):not([class*=\"bg-purple-800\"]),html.dark .text-purple-950:not([class*=\"bg-purple-300\"]):not([class*=\"bg-purple-400\"]):not([class*=\"bg-purple-500\"]):not([class*=\"bg-purple-600\"]):not([class*=\"bg-purple-700\"]):not([class*=\"bg-purple-800\"]){color:#d8b4fe!important}html.dark .text-purple-600:not([class*=\"bg-purple-300\"]):not([class*=\"bg-purple-400\"]):not([class*=\"bg-purple-500\"]):not([class*=\"bg-purple-600\"]):not([class*=\"bg-purple-700\"]):not([class*=\"bg-purple-800\"]){color:#c084fc!important}html.dark .bg-pink-50,html.dark .bg-pink-50\\/30,html.dark .bg-pink-50\\/40,html.dark .bg-pink-50\\/50,html.dark .bg-pink-50\\/60{background-color:rgba(236,72,153,0.10)!important}html.dark .hover\\:bg-pink-50:hover{background-color:rgba(236,72,153,0.20)!important}html.dark .bg-pink-100,html.dark .bg-pink-100\\/30,html.dark .bg-pink-100\\/40,html.dark .bg-pink-100\\/50,html.dark .bg-pink-100\\/60{background-color:rgba(236,72,153,0.16)!important}html.dark .hover\\:bg-pink-100:hover{background-color:rgba(236,72,153,0.26)!important}html.dark .bg-pink-200,html.dark .bg-pink-200\\/30,html.dark .bg-pink-200\\/40,html.dark .bg-pink-200\\/50,html.dark .bg-pink-200\\/60{background-color:rgba(236,72,153,0.24)!important}html.dark .hover\\:bg-pink-200:hover{background-color:rgba(236,72,153,0.34)!important}html.dark .border-pink-100,html.dark .border-pink-200,html.dark .border-pink-300{border-color:rgba(236,72,153,.35)!important}html.dark .text-pink-700:not([class*=\"bg-pink-300\"]):not([class*=\"bg-pink-400\"]):not([class*=\"bg-pink-500\"]):not([class*=\"bg-pink-600\"]):not([class*=\"bg-pink-700\"]):not([class*=\"bg-pink-800\"]),html.dark .text-pink-800:not([class*=\"bg-pink-300\"]):not([class*=\"bg-pink-400\"]):not([class*=\"bg-pink-500\"]):not([class*=\"bg-pink-600\"]):not([class*=\"bg-pink-700\"]):not([class*=\"bg-pink-800\"]),html.dark .text-pink-900:not([class*=\"bg-pink-300\"]):not([class*=\"bg-pink-400\"]):not([class*=\"bg-pink-500\"]):not([class*=\"bg-pink-600\"]):not([class*=\"bg-pink-700\"]):not([class*=\"bg-pink-800\"]),html.dark .text-pink-950:not([class*=\"bg-pink-300\"]):not([class*=\"bg-pink-400\"]):not([class*=\"bg-pink-500\"]):not([class*=\"bg-pink-600\"]):not([class*=\"bg-pink-700\"]):not([class*=\"bg-pink-800\"]){color:#f9a8d4!important}html.dark .text-pink-600:not([class*=\"bg-pink-300\"]):not([class*=\"bg-pink-400\"]):not([class*=\"bg-pink-500\"]):not([class*=\"bg-pink-600\"]):not([class*=\"bg-pink-700\"]):not([class*=\"bg-pink-800\"]){color:#f472b6!important}html.dark .bg-white,html.dark .bg-white\\/95{background-color:#111a2b!important}html.dark .bg-slate-50,html.dark .bg-slate-50\\/60,html.dark .bg-zinc-50{background-color:#162033!important}html.dark .bg-slate-100,html.dark .bg-zinc-100{background-color:#1e293b!important}html.dark .bg-slate-200,html.dark .bg-zinc-200{background-color:#2a3649!important}html.dark .bg-slate-900,html.dark .bg-zinc-900,html.dark .bg-slate-900\\/95{background-color:#24324a!important}html.dark .bg-slate-800{background-color:#2c3b55!important}html.dark .hover\\:bg-slate-50:hover{background-color:#1e293b!important}html.dark .hover\\:bg-slate-100:hover{background-color:#263247!important}html.dark .hover\\:bg-slate-200:hover{background-color:#334155!important}html.dark .hover\\:bg-slate-300:hover{background-color:#3d4a60!important}html.dark .hover\\:bg-zinc-200:hover{background-color:#334155!important}html.dark .hover\\:bg-slate-800:hover{background-color:#334155!important}html.dark .hover\\:bg-slate-700:hover{background-color:#3d4a60!important}html.dark .group:hover .group-hover\\:bg-slate-300{background-color:#475569!important}html.dark .min-h-screen.bg-slate-100,html.dark .min-h-screen.bg-slate-900,html.dark .fixed.inset-0.bg-slate-100,html.dark .fixed.bg-zinc-50{background-color:#0a0f1a!important}html.dark .text-slate-900{color:#f1f5f9!important}html.dark .text-slate-800{color:#e2e8f0!important}html.dark .text-slate-700{color:#cbd5e1!important}html.dark .text-slate-600{color:#a5b4c8!important}html.dark .text-slate-500{color:#8b9ab0!important}html.dark .text-zinc-900{color:#f4f4f5!important}html.dark .text-zinc-600{color:#a1a1aa!important}html.dark .hover\\:text-slate-900:hover,html.dark .hover\\:text-slate-700:hover,html.dark .hover\\:text-slate-600:hover{color:#f8fafc!important}html.dark .border-slate-100{border-color:#1e293b!important}html.dark .border-slate-200{border-color:#2a3649!important}html.dark .border-slate-300{border-color:#3a475c!important}html.dark .border-zinc-100{border-color:#1e293b!important}html.dark .border-zinc-200{border-color:#2a3649!important}html.dark .hover\\:border-slate-300:hover,html.dark .hover\\:border-slate-400:hover{border-color:#475569!important}html.dark{color-scheme:dark}html.dark body{background:#0a0f1a}html.dark input:not([type=checkbox]):not([type=radio]):not([type=range]),html.dark select,html.dark textarea{background-color:#0f1828;color:#e2e8f0}html.dark option{background:#0f1828;color:#e2e8f0}html.dark ::placeholder{color:#64748b}";
function aplicarModoOscuro(on) {
  if (!document.getElementById('royal-dark-css')) {
    const s = document.createElement('style'); s.id = 'royal-dark-css'; s.textContent = DARK_CSS;
    document.head.appendChild(s);
  }
  const publica = /^\/(solicitud|seguimiento)\//.test(window.location.pathname);
  document.documentElement.classList.toggle('dark', !!on && !publica);
}

export default function App() {


  const [user,setUser]               = useState(null);
  const [loading,setLoading]         = useState(true);
  const [orders,setOrders]           = useState([]);
  const [celebration,setCelebration] = useState(false);
  const [focusId,setFocusId]         = useState(null);
  const [showAdd,setShowAdd]         = useState(false);
  const [showInventario,setShowInventario] = useState(false);
  const [showGenerador,setShowGenerador] = useState(false);
  const [showMapaFlex,setShowMapaFlex] = useState(false);
  const [showVin,setShowVin] = useState(false);
  const [modoOscuro,setModoOscuro] = useState(() => { try { return localStorage.getItem('royal_dark') === '1'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem('royal_dark', modoOscuro ? '1' : '0'); } catch {} aplicarModoOscuro(modoOscuro); }, [modoOscuro]);
  // "Te deben" en todo lado: mismo número que Finanzas (cartera reconciliada)
  const [deudaTotal, setDeudaTotal] = useState(null);
  const cargarDeuda = useCallback(() => {
    if (!getToken()) return;
    apiFetch('/api/reporte/cartera').then(r => { if (r && r.resumen) setDeudaTotal(r.resumen.porCobrar || 0); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.rol !== 'admin') return;
    cargarDeuda();
    const t = setInterval(cargarDeuda, 120000);
    return () => clearInterval(t);
  }, [user?.id, user?.rol, cargarDeuda]); // eslint-disable-line
  const [showAdmin,setShowAdmin]     = useState(false);
  const [showLinks,setShowLinks]     = useState(false);
  const [showCobros,setShowCobros]   = useState(false);
  const [showArchivados,setShowArchivados] = useState(false);
  const [showOptimizar,setShowOptimizar] = useState(false);
  const [optimizando,setOptimizando] = useState(null); // {total,hecho,cambiados,fotosOptim,corriendo}
  const [garantiaId,setGarantiaId]   = useState(null);
  const [showInbox, setShowInbox]    = useState(false);
  const [inboxCount, setInboxCount]  = useState(0);
  const [rentasId,setRentasId]       = useState(null);
  const [showFinance,setShowFinance] = useState(false);
  const [editId,setEditId]           = useState(null);
  const [showClientes,setShowClientes] = useState(false); // módulo Clientes
  const [fichaId,setFichaId]         = useState(null);    // ficha de cliente abierta
  const [addFotos,setAddFotos]       = useState([]);
  const [usuarios,setUsuarios]       = useState([]);
  const [tituloNuevoEstado,setTituloNuevoEstado] = useState('vacio');
  const [form,setForm] = useState({cliente:"",clienteId:"",titulo:"",plataforma:PLATAFORMAS[0],estado:"Pendiente Revisión",prioridad:"Media",entrega:"",notas:"",chatPendiente:false,rehacer:false,asignadoA:"",
    mail:"",dato:"",telefonoName:"",dl:"",dob:"",ssn:"",tipoCuenta:"",creditoDebito:"",precio:"",abonado:"",saldo:"",pagoHasta:"",documentNumber:"",report:"",whyDeactivated:"",infoDatoFinal:""});
  const [showCamposExcel, setShowCamposExcel] = useState(true);
  const [showLobby, setShowLobby]    = useState(true);
  const [showCobrosPage, setShowCobrosPage] = useState(false);
  const [showFinanzasPage, setShowFinanzasPage] = useState(false);
  const [carteraPagoOrder, setCarteraPagoOrder] = useState(null); // modal pago al entregar
  const [cierreCobroOrder, setCierreCobroOrder] = useState(null); // modal revisar deuda al no aprobar/cancelar

  const pollingRef = useRef(null);
  const keepAliveRef = useRef(null);

  // Keep-alive: ping cada 4 min para que el servidor no duerma (usa /api/health, sin auth)
  useEffect(() => {
    const ping = () => fetch("/api/health").catch(()=>{});
    ping(); // ping inmediato al cargar la página
    keepAliveRef.current = setInterval(ping, 4 * 60 * 1000); // cada 4 minutos
    return () => clearInterval(keepAliveRef.current);
  }, []);

  const loadOrders = useCallback(() => apiFetch("/api/pedidos").then(data=>{
    const all = data||[];
    setOrders(all);
    setInboxCount((all).filter(s => s.bandeja === 'nueva').length);
    setLoading(false);
    return all;
  }).catch(err=>{ console.warn("loadOrders error:", err); setLoading(false); }), []);

  // Polling automático cada 30s para mantener pedidos actualizados
  const startPolling = useCallback((fetchFn) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => fetchFn(), 30000);
  }, []);

  const [loadingStatus, setLoadingStatus] = useState("Conectando con el servidor...");
  const [loadingError, setLoadingError] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const loadingTimerRef = useRef(null);

  const startLoadingTimer = useCallback(() => {
    setLoadingSeconds(0);
    if (loadingTimerRef.current) clearInterval(loadingTimerRef.current);
    loadingTimerRef.current = setInterval(() => setLoadingSeconds(s => s + 1), 1000);
  }, []);
  const stopLoadingTimer = useCallback(() => { if (loadingTimerRef.current) clearInterval(loadingTimerRef.current); }, []);

  const runBoot = useCallback(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    setLoadingError(false);
    setLoading(true);
    startLoadingTimer();
    setLoadingStatus("Verificando sesión...");
    const safetyTimer = setTimeout(() => {
      stopLoadingTimer();
      setLoadingError(true);
      setLoadingStatus("El servidor no responde. ¿Hay conexión a internet?");
    }, 20000);
    apiFetch("/api/me").then(data => {
      if (data?.id) {
        setUser(data);
        setLoadingStatus("Cargando tus pedidos...");
        // Cargar lista de usuarios para cualquier admin (super y secundario)
        if (data.rol === "admin") apiFetch("/api/usuarios").then(u => setUsuarios(u || []));
        let attempts = 0;
        const tryLoad = () => {
          attempts++;
          loadOrders().then(all => {
            clearTimeout(safetyTimer);
            stopLoadingTimer();
            if (all === undefined && attempts < 3) {
              // Solo reintentar si hubo error real (undefined), no si simplemente no hay pedidos aún
              setLoadingStatus("Reintentando (" + attempts + "/3)...");
              setTimeout(tryLoad, 2000 * attempts);
            } else {
              startPolling(loadOrders);
              setLoading(false);
            }
          }).catch(() => {
            clearTimeout(safetyTimer);
            stopLoadingTimer();
            setLoadingError(true);
            setLoadingStatus("No se pudieron cargar los pedidos.");
          });
        };
        tryLoad();
      } else {
        clearTimeout(safetyTimer);
        stopLoadingTimer();
        clearToken(); setLoading(false);
      }
    }).catch(() => {
      clearTimeout(safetyTimer);
      stopLoadingTimer();
      setLoadingError(true);
      setLoadingStatus("No se pudo conectar con el servidor.");
    });
    return () => { clearTimeout(safetyTimer); stopLoadingTimer(); if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [loadOrders, startPolling, startLoadingTimer, stopLoadingTimer]);

  useEffect(() => { return runBoot(); }, []);

  const handleLogin = u => {
    setSessionExpired(false);
    setUser(u);
    // FIX: cargar usuarios también en login fresco (antes solo en runBoot →
    // el dropdown "Asignar a usuario" salía vacío hasta recargar la página)
    if (u?.rol === "admin") apiFetch("/api/usuarios").then(x => setUsuarios(x || [])).catch(()=>{});
    loadOrders().then(()=>startPolling(loadOrders));
  };
  const loadInboxCount = () => loadOrders();

  const handleLogout = async () => {
    await apiFetch("/api/logout",{method:"POST"});
    clearToken(); setUser(null); setOrders([]);
  };

const sync = async updated => {
    setOrders(prev=>prev.map(o=>o.id===updated.id?updated:o));
    try {
      // No mandar fotos en sync — el servidor preserva las existentes
      // Solo se mandan fotos cuando el usuario las edita explícitamente desde el modal
      const { fotos, ...sinFotos } = updated;
      const result = await apiFetch("/api/pedidos/"+updated.id,{method:"PUT",body:JSON.stringify(sinFotos)});
      if (!result?.ok && result?.error) {
        console.error("Error al guardar pedido:", result.error);
        loadOrders();
      }
    } catch(err) {
      console.error("Error de red al guardar pedido:", err);
      loadOrders();
    }
  };
  const updateOrder = (id,patch) => { const o=orders.find(o=>o.id===id); if(o) sync({...o,...patch}); };
  const CYCLE_ESTADOS = ["Pendiente Revisión","Aceptado","En Producción","Pendiente Aprobación","Terminado","VERI FACIAL","Entregado"];
  const cycleStatus = id => {
    const o=orders.find(o=>o.id===id); if(!o) return;
    const cur = o.estado === "Rehacer" ? "En Producción" : o.estado;
    const idx = CYCLE_ESTADOS.indexOf(cur);
    const next = CYCLE_ESTADOS[(idx+1) % CYCLE_ESTADOS.length];
    const now = new Date().toISOString();
    sync({...o, estado:next, stateChangedAt:now, completedAt:next==="Entregado"?now:o.completedAt});
  };
  const markDone = id => {
    const o=orders.find(o=>o.id===id); if(!o) return;
    const now = new Date().toISOString();
    if (o.estado === "VERI FACIAL") {
      // Veri Facial PASÓ → Entregado. Al entregar: mostrar modal de cobro si es admin.
      sync({...o, estado:"Entregado", stateChangedAt:now, completedAt:now, chatPendiente:false});
      setFocusId(null);
      if (user?.rol === "admin") setCarteraPagoOrder({...o, estado:"Entregado", completedAt:now});
    } else if (o.estado === "Terminado") {
      // Terminado ya NO entrega directo: pasa a la última verificación (VERI FACIAL).
      sync({...o, estado:"VERI FACIAL", stateChangedAt:now, chatPendiente:false});
      setFocusId(null);
    } else {
      sync({...o, estado:"Terminado", stateChangedAt:now, completedAt:now, chatPendiente:false});
      setHechasHoy(h => h + 1);
      setCelebration(true);
      setFocusId(null);
    }
  };
  const sendToRework = id => {
    const o=orders.find(o=>o.id===id); if(!o) return;
    sync({...o, estado:"Rehacer", stateChangedAt:new Date().toISOString(), prioridad:"Alta", rehacer:true}); setFocusId(null);
  };
  const removeOrder = async id => {
    setOrders(prev=>prev.filter(o=>o.id!==id));
    await apiFetch("/api/pedidos/"+id,{method:"DELETE"});
  };
  const addOrder = async () => {
    if(!form.cliente.trim()) return;
    const asignado = form.asignadoA ? usuarios.find(u=>u.id===form.asignadoA) : null;
    // Un pedido NUEVO nunca puede nacer en un estado terminal (Entregado, Terminado,
    // Intento Fallido, Cancelado, etc.). Si por lo que sea el estado viene así, lo
    // forzamos a "Pendiente Revisión" para que no salga gris/opaco como ya hecho.
    const estadosTerminales = ["Entregado","Terminado","Intento Fallido","Cancelado","NOT APPROVED","DIRTY BACKGROUND","DESACTIVADA"];
    const estadoInicial = (!form.estado || estadosTerminales.includes(form.estado)) ? "Pendiente Revisión" : form.estado;
    if(tituloNuevoEstado==='ocupado'){ alert("Ese título ya lo tiene otro pedido. Cámbialo antes de guardar."); return; }
    const n={...form,estado:estadoInicial,id:crypto.randomUUID(),cliente:form.cliente.trim(),titulo:(form.titulo||'').trim().replace(/\s+/g,' '),completedAt:null,fotos:addFotos,
      userName:user.nombre, asignadoA: asignado?.id||"", asignadoNombre: asignado?.nombre||""};
    setOrders(prev=>[n,...prev]);
    try {
      const result = await apiFetch("/api/pedidos",{method:"POST",body:JSON.stringify(n)});
      if (result?.ok && result.clienteId) {
        setOrders(prev=>prev.map(o=>o.id===n.id?{...o,clienteId:result.clienteId,cliente:result.cliente||o.cliente,titulo:result.titulo??o.titulo}:o));
        invalidarClientes();
      }
      if (!result?.ok) {
        // Si falló el guardado, remover el pedido del estado local
        setOrders(prev=>prev.filter(o=>o.id!==n.id));
        alert(result?.error || "Error al guardar el pedido. Intentá de nuevo.");
        return;
      }
    } catch(err) {
      setOrders(prev=>prev.filter(o=>o.id!==n.id));
      alert("Error de conexión. El pedido no se guardó.");
      return;
    }
    setForm({cliente:"",clienteId:"",titulo:"",plataforma:PLATAFORMAS[0],estado:"Pendiente Revisión",prioridad:"Media",entrega:"",notas:"",chatPendiente:false,rehacer:false,asignadoA:"",
      mail:"",dato:"",telefonoName:"",dl:"",dob:"",ssn:"",tipoCuenta:"",creditoDebito:"",precio:"",abonado:"",saldo:"",pagoHasta:"",documentNumber:"",report:"",whyDeactivated:"",infoDatoFinal:""});
    setShowCamposExcel(true);
    setAddFotos([]); setShowAdd(false);
    setFocusId(n.id); // abrir el pedido recién creado para verlo enseguida
  };
  const saveEdit = async updated => {
    // Actualizar estado local inmediatamente
    setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
    try {
      // FIX: separar guardado de metadata y fotos
      // 1) Guardar metadata sin fotos primero (más liviano, menos riesgo de timeout)
      const { fotos, ...sinFotos } = updated;
      const resultMeta = await apiFetch("/api/pedidos/" + updated.id, {
        method: "PUT",
        body: JSON.stringify(sinFotos)
      });
      if (!resultMeta?.ok && resultMeta?.error) {
        console.error("Error al guardar metadata del pedido:", resultMeta.error);
        alert(resultMeta.error);
        loadOrders();
        return;
      }
      if (resultMeta?.clienteId) {
        setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, clienteId: resultMeta.clienteId, cliente: resultMeta.cliente || o.cliente, titulo: resultMeta.titulo ?? o.titulo } : o));
        invalidarClientes();
      }
      // 2) Guardar fotos por separado SOLO si vienen definidas.
      //    Si el editor no pudo cargarlas y el usuario no las tocó, `fotos`
      //    llega undefined y no tocamos las fotos existentes del servidor.
      if (Array.isArray(fotos)) {
        const resultFotos = await apiFetch("/api/pedidos/" + updated.id + "/fotos", {
          method: "PUT",
          body: JSON.stringify({ fotos })
        });
        if (!resultFotos?.ok && resultFotos?.error) {
          console.error("Error al guardar fotos:", resultFotos.error);
          // Fotos fallaron pero metadata guardó — no revertir todo
        }
      }
    } catch (err) {
      console.error("Error de red al guardar pedido:", err);
      loadOrders();
    }
    setEditId(null);
  };
  const reactivarOrder = async id => {
    const o=orders.find(o=>o.id===id); if(!o) return;
    sync({...o, estado:"Pendiente Revisión", completedAt:null, rehacer:false});
    // Si a este pedido se le había cerrado el cobro (por no aprobado/cancelado),
    // al revivirlo su deuda tiene que revivir también. Si no, quedaría un pedido
    // activo y con precio que NO le cobra nadie, en silencio.
    if (o.cobroCerrado && user?.rol === "admin") {
      await apiFetch('/api/pedidos/'+id+'/reabrir-cobro', {method:'POST'}).catch(()=>{});
      loadOrders();
    }
  };
  // Optimiza TODAS las fotos guardadas, en una pasada, usando solo los dos
  // endpoints que ya tiene la app: GET /api/pedidos/:id/fotos y PUT /api/pedidos/:id
  const optimizarTodasLasFotos = async () => {
    if (optimizando?.corriendo) return;
    const lista = orders.slice();
    setOptimizando({ total: lista.length, hecho: 0, cambiados: 0, fotosOptim: 0, corriendo: true });
    let cambiados = 0, fotosOptim = 0;
    for (let i = 0; i < lista.length; i++) {
      const o = lista[i];
      try {
        const fotos = await apiFetch('/api/pedidos/'+o.id+'/fotos').catch(()=>null);
        if (Array.isArray(fotos) && fotos.length) {
          let cambio = false; const nuevas = [];
          for (const f of fotos) {
            const opt = await recompressDataUrl(f);
            if (opt && opt !== f) { cambio = true; fotosOptim++; }
            nuevas.push(opt || f);
          }
          if (cambio) {
            await apiFetch('/api/pedidos/'+o.id, { method:'PUT', body: JSON.stringify({ ...o, fotos: nuevas }) });
            cambiados++;
          }
        }
      } catch {}
      setOptimizando(prev => prev ? { ...prev, hecho: i+1, cambiados, fotosOptim } : prev);
      await new Promise(r=>setTimeout(r, 60)); // respiro para no saturar el servidor
    }
    setOptimizando(prev => prev ? { ...prev, corriendo: false } : prev);
    loadOrders();
  };
  const cancelOrder = id => {
    const o=orders.find(o=>o.id===id); if(!o) return;
    const updated = {...o, estado:"Cancelado", stateChangedAt:new Date().toISOString()};
    sync(updated);
    // Un pedido cancelado tiene el mismo problema que uno no aprobado: se
    // archiva, pero su deuda sigue viva. Se revisa antes de dejarlo ir.
    if (user?.rol === "admin") { setFocusId(null); setCierreCobroOrder(updated); }
  };
  const setAddFotosUpdater = useCallback(u=>setAddFotos(p=>typeof u==="function"?u(p):u),[]);

  // ── SESSION TIMEOUT 21 min ──────────────────────────────────────
  const SESSION_MS = 21 * 60 * 1000;
  const sessionTimerRef = useRef(null);
  const throttleRef = useRef(0);
  // FIX UX: antes el timeout te sacaba en silencio (perdías contexto sin saber
  // por qué). Ahora se muestra un aviso amable en la pantalla de login.
  const [sessionExpired, setSessionExpired] = useState(false);

  const resetSessionTimer = useCallback(() => {
    // Throttle: solo resetear máx cada 10 segundos para no desperdiciar CPU
    const now = Date.now();
    if (now - throttleRef.current < 10000) return;
    throttleRef.current = now;
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(() => {
      clearToken();
      setSessionExpired(true);
      setUser(null);
      setOrders([]);
    }, SESSION_MS);
  }, []); // sin dependencias — usa refs, no re-crea nunca

  const userLoggedIn = !!user;
  useEffect(() => {
    if (!userLoggedIn) {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      return;
    }
    // Iniciar timer al loguearse
    sessionTimerRef.current = setTimeout(() => {
      clearToken(); setSessionExpired(true); setUser(null); setOrders([]);
    }, SESSION_MS);
    const events = ["mousedown","keydown","touchstart","scroll"];
    events.forEach(e => window.addEventListener(e, resetSessionTimer, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, resetSessionTimer));
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, [userLoggedIn, resetSessionTimer]);

  const [searchQuery, setSearchQuery] = useState("");
  const [pinnedId, setPinnedId] = useState(null);
  // Cargar pin y aplazados guardados del servidor al iniciar sesión
  useEffect(() => {
    if (!user) return;
    apiFetch('/api/me/prefs').then(p => {
      if (p?.pinnedOrderId) setPinnedId(p.pinnedOrderId);
      // Los aplazados también viven en el servidor (sobreviven al cierre de
      // sesión y se sincronizan). Si el servidor aún no los tiene, subimos los
      // del dispositivo una vez para que a partir de ahí queden guardados.
      if (Array.isArray(p?.postponedOrderIds)) {
        setPostponedIds(p.postponedOrderIds);
      } else {
        setPostponedIds(prev => {
          if (prev.length > 0) {
            apiFetch('/api/me/prefs', { method:'PUT', body: JSON.stringify({ postponedOrderIds: prev }) }).catch(()=>{});
          }
          return prev;
        });
      }
    }).catch(()=>{}).finally(()=>{ postponedHydrated.current = true; });
  }, [user?.id]);
  const setPinnedIdPersist = (idOrFn) => {
    setPinnedId(prev => {
      const next = typeof idOrFn === "function" ? idOrFn(prev) : idOrFn;
      // Guardar en servidor (sincroniza entre dispositivos)
      apiFetch('/api/me/prefs', { method:'PUT', body: JSON.stringify({ pinnedOrderId: next || null }) }).catch(()=>{});
      // También en localStorage como fallback offline
      if (next) localStorage.setItem("royal_pinned", next);
      else localStorage.removeItem("royal_pinned");
      return next;
    });
  };
  const [viewMode, setViewMode] = useState("grupos"); // "grupos" | "cola"
  // ── AGRUPAR la cola de trabajo (como el inventario): "" | plataforma | estado | tipoCuenta | prioridad | asignadoNombre ──
  const [colaGroupBy, setColaGroupBy] = useState("");
  const [colaColapsados, setColaColapsados] = useState(() => new Set()); // grupos cerrados
  const [filterTipo, setFilterTipo] = useState(null); // null | tipoCuenta (Type del Excel)
  // ── MODO SOLO HOY (anti-abrumación TDAH) ──
  // Por defecto arranca activo: muestra una tarea a la vez en vez del muro completo.
  const [soloHoy, setSoloHoy] = useState(() => localStorage.getItem("royal_solo_hoy") !== "off");
  const toggleSoloHoy = (v) => { setSoloHoy(v); localStorage.setItem("royal_solo_hoy", v ? "on" : "off"); };
  const [hechasHoy, setHechasHoy] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("royal_hechas_hoy") || "{}");
      const hoy = new Date().toDateString();
      return raw.fecha === hoy ? (raw.count || 0) : 0;
    } catch { return 0; }
  });
  useEffect(() => {
    localStorage.setItem("royal_hechas_hoy", JSON.stringify({ fecha: new Date().toDateString(), count: hechasHoy }));
  }, [hechasHoy]);
  const [skippedIds, setSkippedIds] = useState([]);
  // ── APLAZADOS (mandados al final de la cola) ───────────────────────
  // Se guardan en el SERVIDOR (igual que el pin) para que sobrevivan al
  // cierre de sesión y se sincronicen entre dispositivos, con localStorage
  // como respaldo. Antes solo estaban en el dispositivo y se perdían.
  const [postponedIds, setPostponedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("royal_aplazados") || "[]"); } catch { return []; }
  });
  // true una vez leído el valor real del servidor: evita que un guardado
  // temprano (con el valor viejo del dispositivo) pise la lista buena.
  const postponedHydrated = useRef(false);
  useEffect(() => {
    localStorage.setItem("royal_aplazados", JSON.stringify(postponedIds));
    if (postponedHydrated.current) {
      apiFetch('/api/me/prefs', { method:'PUT', body: JSON.stringify({ postponedOrderIds: postponedIds }) }).catch(()=>{});
    }
  }, [postponedIds]);
  // Saca de la lista los pedidos ya entregados/archivados o que ya no existen.
  // No tocar nada hasta haber cargado pedidos Y el valor del servidor, para no
  // borrar la lista contra datos que aún no llegan.
  useEffect(() => {
    if (loading || orders.length === 0 || !postponedHydrated.current) return;
    setPostponedIds(prev => {
      const vivos = new Set(orders.filter(o => !isArchived(o) && o.estado !== "Entregado").map(o => o.id));
      const limpio = prev.filter(id => vivos.has(id));
      return limpio.length === prev.length ? prev : limpio;
    });
  }, [orders, loading]);
  // ── REINICIO AUTOMÁTICO DE LA RONDA ────────────────────────────────
  // Si TODOS los pedidos activos acabaron en "para después", la lista de
  // aplazados ya no significa nada: no queda nada "antes" que revisar y se
  // quedarían abajo, en gris, para siempre. Cuando eso pasa, se vacía sola:
  // todos vuelven arriba con su orden normal por urgencia y empieza otra vuelta.
  // Se mira `orders` (no `filteredOrders`) a propósito: un filtro o una búsqueda
  // que esconda los normales NO debe disparar el reinicio.
  const [rondaReiniciada, setRondaReiniciada] = useState(false);
  useEffect(() => {
    if (loading || !postponedHydrated.current) return;
    if (postponedIds.length === 0) return;
    const activos = orders.filter(o =>
      o.estado !== "Entregado" &&
      o.estado !== "Intento Fallido" &&
      o.estado !== "Esperando Cliente" &&
      o.estado !== "Background Check" &&
      !isArchived(o)
    );
    if (activos.length === 0) return;                       // nada que reiniciar
    if (activos.some(o => !postponedIds.includes(o.id))) return; // aún quedan normales
    setPostponedIds([]);   // ← se acabó la vuelta: todos de regreso a la cola
    setSkippedIds([]);     // los "muéstrame otra" también se limpian
    setRondaReiniciada(true);
  }, [orders, postponedIds, loading]);
  // El aviso de "ronda reiniciada" se va solo a los 6 segundos
  useEffect(() => {
    if (!rondaReiniciada) return;
    const t = setTimeout(() => setRondaReiniciada(false), 6000);
    return () => clearTimeout(t);
  }, [rondaReiniciada]);
  // Mandar al final / traer de vuelta
  const togglePostpone = (id) => {
    postponedHydrated.current = true; // una acción del usuario siempre debe persistir
    setPostponedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    if (pinnedId === id) setPinnedIdPersist(null); // fijar y aplazar a la vez no tiene sentido
  };
  const [filterChip, setFilterChip] = useState(null); // null | estado | prioridad | "urgente"
  const [filterPlat, setFilterPlat] = useState(null); // null | plataforma

  const focusOrder=focusId?orders.find(o=>o.id===focusId):null;
  const editOrder=editId?orders.find(o=>o.id===editId):null;
  const filteredOrders = useMemo(() => {
    let base = orders;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      base = base.filter(o =>
        o.cliente?.toLowerCase().includes(q) ||
        o.titulo?.toLowerCase().includes(q) ||
        o.plataforma?.toLowerCase().includes(q) ||
        o.notas?.toLowerCase().includes(q)
      );
    }
    if (filterChip === "urgente") {
      base = base.filter(o => {
        const d = days(o.entrega);
        return (d !== null && d <= 1 && o.estado !== "Entregado") || o.prioridad === "Alta";
      });
    } else if (filterChip === "Alta" || filterChip === "Media" || filterChip === "Baja") {
      base = base.filter(o => o.prioridad === filterChip);
    } else if (filterChip && ESTADOS.includes(filterChip)) {
      base = base.filter(o => o.estado === filterChip);
    }
    if (filterPlat) {
      base = base.filter(o => o.plataforma === filterPlat);
    }
    if (filterTipo) {
      base = base.filter(o => (o.tipoCuenta || "") === filterTipo);
    }
    return base;
  }, [orders, searchQuery, filterChip, filterPlat, filterTipo]);
  const grouped=useMemo(()=>{ const m={}; PLATAFORMAS.forEach(p=>{m[p]=filteredOrders.filter(o=>o.plataforma===p);}); return m; },[filteredOrders]);
  const nextTask = useMemo(()=>{
    if (pinnedId) {
      const pinned = filteredOrders.find(o=>o.id===pinnedId && o.estado!=="Entregado" && o.estado!=="Intento Fallido" && o.estado!=="Esperando Cliente"&&o.estado!=="Background Check");
      if (pinned) return { ...pinned, _pinned: true };
    }
    const activos = [...filteredOrders].filter(o=>o.estado!=="Entregado"&&o.estado!=="Intento Fallido"&&o.estado!=="Esperando Cliente"&&o.estado!=="Background Check"&&!isArchived(o))
      .sort((a,b)=>{
        // Urgencia real por fecha de entrega (+ prioridad); empate por antigüedad
        const uA = urgencyScore(a), uB = urgencyScore(b);
        if (uA !== uB) return uA - uB;
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeA - timeB;
      });
    // Los aplazados NO cuentan como "lo siguiente": se ignoran por completo
    // hasta que ya no quede ningún pedido normal por hacer.
    const normales = activos.filter(o=>!postponedIds.includes(o.id));
    const candidate = normales.find(o=>!skippedIds.includes(o.id));
    if (candidate) return candidate;
    if (normales.length>0) { setSkippedIds([]); return normales[0]; }
    // Si SOLO quedan aplazados, se muestran como tal (con bandera) para que la
    // pantalla avise "solo quedan los que aplazaste" y NO ofrezca "al final"
    // (eso los desaplazaría y volverían al principio en bucle).
    const aplazados = activos.filter(o=>postponedIds.includes(o.id));
    if (aplazados.length>0) return { ...aplazados[0], _aplazadoFallback: true };
    return null;
  },[filteredOrders, pinnedId, skippedIds, postponedIds]);
  // Lista para navegar en el modo focus: MISMO orden que la cola (por urgencia),
  // así el botón "Siguiente" avanza de verdad y no se queda gris por orden cruzado.
  const navigableQueue = useMemo(()=>{
    return [...filteredOrders]
      .filter(o=>o.estado!=="Entregado"&&!isArchived(o))
      .sort((a,b)=>{
        // Aplazados al fondo también al navegar entre tareas
        const aP = postponedIds.includes(a.id)?1:0, bP = postponedIds.includes(b.id)?1:0;
        if (aP !== bP) return aP - bP;
        const uA = urgencyScore(a), uB = urgencyScore(b);
        if (uA !== uB) return uA - uB;
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeA - timeB;
      });
  },[filteredOrders, postponedIds]);
  const [nextTaskPhoto, setNextTaskPhoto] = useState(null);
  const [nextTaskPhotoCount, setNextTaskPhotoCount] = useState(0);
  useEffect(() => {
    if (!nextTask?.id) { setNextTaskPhoto(null); setNextTaskPhotoCount(0); return; }
    setNextTaskPhoto(null);
    apiFetch('/api/pedidos/' + nextTask.id + '/fotos')
      .then(f => { if (Array.isArray(f) && f.length > 0) { setNextTaskPhoto(f[0]); setNextTaskPhotoCount(f.length); } else { setNextTaskPhoto(null); setNextTaskPhotoCount(0); } })
      .catch(() => {});
  }, [nextTask?.id]);
  const totalActive=orders.filter(o=>o.estado!=="Entregado"&&o.estado!=="Terminado"&&o.estado!=="Intento Fallido"&&o.estado!=="Esperando Cliente"&&o.estado!=="Background Check"&&!isArchived(o)).length;
  // Excluir archivados de los contadores de alertas
  const totalRework=orders.filter(o=>o.rehacer&&o.estado!=="Entregado"&&!isArchived(o)).length;
  const totalChat=orders.filter(o=>o.chatPendiente&&!isArchived(o)).length;

  // ── COBROS PRÓXIMOS ──────────────────────────────────────────────
  // Clientes que vencen esta semana (semanal: sin pago o último pago hace ≥5 días)
  // o créditos con saldo pendiente — ordenados por urgencia
  const porCobrar = useMemo(()=>{
    const now = Date.now();
    return orders
      .filter(o => !isArchived(o))
      .map(o => {
        if (o.tipoCobro === 'semanal' && o.montoSemanal) {
          const diasSinPago = o.ultimoPago
            ? Math.floor((now - new Date(o.ultimoPago+'T12:00').getTime()) / 86_400_000)
            : 999;
          if (diasSinPago >= 5) return { ...o, _diasSinPago: diasSinPago, _tipo:'semanal' };
        }
        if (o.tipoCobro === 'credito' && o.totalCredito) {
          const pagado = parseFloat(o.totalPagado||0);
          const total  = parseFloat(o.totalCredito||0);
          if (total > 0 && pagado < total) return { ...o, _falta: total-pagado, _tipo:'credito' };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a,b)=>(b._diasSinPago||0)-(a._diasSinPago||0));
  },[orders]);

  const setOrderStatus = (id, newEstado) => {
    const o=orders.find(o=>o.id===id); if(!o) return;
    const now = new Date().toISOString();
    const updated = {...o, estado:newEstado, stateChangedAt:now,
      completedAt: (newEstado==="Entregado"||newEstado==="Terminado") ? now : o.completedAt,
      rehacer: newEstado==="Rehacer" ? true : (newEstado!=="Rehacer" ? false : o.rehacer)
    };
    sync(updated);
    // Al marcar Entregado desde dropdown: mostrar modal de cobro si es admin
    if (newEstado === "Entregado" && user?.rol === "admin") {
      setCarteraPagoOrder(updated);
    }
    // Al matar un pedido (no aprobado, cancelado, desactivado...): el pedido se
    // archiva solo, pero su PLATA no. Como la deuda se deduce del precio, si
    // nadie revisa, ese pedido se queda en la fía para siempre. Abrimos la
    // revisión para cerrar el caso completo aquí y no a medias.
    if (ESTADOS_TERMINALES_NEG.includes(newEstado) && user?.rol === "admin") {
      setFocusId(null);
      setCierreCobroOrder(updated);
    }
  };

  const handlers = {
    onFocus:setFocusId, onCycle:cycleStatus,
    onSetStatus: setOrderStatus,
    onPin: (id) => setPinnedIdPersist(prev => prev===id ? null : id),
    onPostpone: togglePostpone,
    onChat:(id)=>updateOrder(id,{chatPendiente:!orders.find(o=>o.id===id)?.chatPendiente}),
    onRework:sendToRework, onDone:markDone, onRemove:removeOrder,
    onReactivar: reactivarOrder,
    onCancel: cancelOrder,
    onNotes:(id,txt)=>updateOrder(id,{notas:txt}), onEdit:setEditId,
    onGarantia: (id) => setGarantiaId(id),
    onRentas: (id) => setRentasId(id),
    onReasign: async (id, userId, userName) => {
      await apiFetch('/api/pedidos/'+id+'/asignar',{method:'PUT',body:JSON.stringify({userId,userName})});
      setOrders(prev=>prev.map(o=>o.id===id?{...o,asignadoA:userId,asignadoNombre:userName}:o));
    },
    onTrackLink: async (id) => {
      const r = await apiFetch('/api/track-link/'+id, {method:'POST'});
      if (r?.trackId) { navigator.clipboard.writeText(window.location.origin+'/seguimiento/'+r.trackId); alert('Link de seguimiento copiado al portapapeles ✅'); }
    },
    onRevokeTrackLink: async (id) => {
      if (!window.confirm('¿Eliminar el link de seguimiento? El cliente ya no podrá acceder.')) return;
      await apiFetch('/api/track-link/'+id, {method:'DELETE'});
      alert('Link eliminado ✅');
    },
    onSetTrackExpiry: async (id, fecha) => {
      await apiFetch('/api/track-link/'+id, {method:'POST', body:JSON.stringify({expiresAt: fecha})});
      alert('Expiración actualizada ✅');
    },
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="text-center max-w-xs w-full">
        <div className={"text-6xl mb-4 " + (loadingError ? "" : "animate-bounce")}>{loadingError ? "⚠️" : "📦"}</div>
        <div className={"text-base font-black mb-2 " + (loadingError ? "text-red-600" : "text-slate-700")}>{loadingStatus}</div>
        {!loadingError && (
          <div className="text-slate-400 text-sm mb-4">{loadingSeconds}s esperando...</div>
        )}
        {!loadingError && (
          <div className="w-full bg-slate-200 rounded-full h-1.5 mb-4">
            <div className="bg-slate-600 h-1.5 rounded-full animate-pulse" style={{width: Math.min(loadingSeconds * 5, 95) + "%", transition:"width 1s linear"}}/>
          </div>
        )}
        {loadingError && (
          <div className="space-y-3 mt-4">
            <div className="text-slate-500 text-sm">El servidor tardó demasiado o no está disponible.</div>
            <button onClick={() => runBoot()} className="w-full py-3 rounded-xl bg-slate-900 text-white font-black text-sm active:scale-95 transition-all">🔄 Reintentar</button>
            <button onClick={() => { clearToken(); setLoading(false); setLoadingError(false); }} className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm">← Volver al login</button>
          </div>
        )}
      </div>
    </div>
  );

  const urlPath = window.location.pathname;
  const guestMatch = urlPath.match(/^\/solicitud\/([a-f0-9]+)$/);
  if (guestMatch) return <GuestForm token={guestMatch[1]}/>;
  const trackMatch = urlPath.match(/^\/seguimiento\/([a-f0-9]+)$/);
  if (trackMatch) return <TrackingPage trackId={trackMatch[1]}/>;

  if (!user) return <LoginScreen onLogin={handleLogin} notice={sessionExpired ? "🔒 Tu sesión se cerró por inactividad (21 min). Vuelve a entrar." : ""}/>;

  // ── COBROS PAGE (pantalla independiente) ───────────────────────
  if (showFinanzasPage || showCobrosPage) {
    return (
      <FinanzasPage
        orders={orders}
        usuarios={usuarios}
        oscuro={modoOscuro}
        onBack={() => { setShowFinanzasPage(false); setShowCobrosPage(false); cargarDeuda(); }}
        onOpenRentas={id => { setShowFinanzasPage(false); setTimeout(() => setRentasId(id), 150); }}
        onReasignar={async (orderId, userId, userName) => {
          await apiFetch('/api/pedidos/'+orderId+'/asignar', {method:'PUT', body:JSON.stringify({userId, userName})});
          setOrders(prev => prev.map(o => o.id===orderId ? {...o, asignadoA:userId, asignadoNombre:userName} : o));
        }}
        // Editar / cambiar estado / eliminar el pedido real desde Finanzas.
        // Usan las mismas funciones del hub → el cambio se ve en los dos lados.
        onSavePedido={saveEdit}
        onEliminarPedido={removeOrder}
        onCambiarEstadoPedido={async (id, estado) => {
          const o = orders.find(x => x.id === id); if (!o) return;
          const now = new Date().toISOString();
          await sync({ ...o, estado, stateChangedAt: now,
            completedAt: (estado === "Entregado" || estado === "Terminado") ? now : o.completedAt,
            rehacer: estado === "Rehacer" });
        }}
      />
    );
  }

  // ── LOBBY ADMIN ───────────────────────────────────────────────
  if (user.rol === "admin" && showLobby) {
    const totalActive = orders.filter(o=>o.estado!=="Entregado"&&o.estado!=="Terminado"&&!isArchived(o)).length;
    const totalRework = orders.filter(o=>o.rehacer&&o.estado!=="Entregado"&&!isArchived(o)).length;
    const totalChat   = orders.filter(o=>o.chatPendiente&&!isArchived(o)).length;
    const totalArch   = orders.filter(o=>isArchived(o)).length;
    const porCobrarLobby = orders.filter(o => !isArchived(o) && (
      (o.tipoCobro==='semanal' && o.montoSemanal && (o.ultimoPago ? Math.floor((Date.now()-new Date(o.ultimoPago+'T12:00').getTime())/86400000) : 999) >= 5) ||
      (o.tipoCobro==='credito' && o.totalCredito && parseFloat(o.totalPagado||0) < parseFloat(o.totalCredito||0))
    )).length;
    const fmtDeuda = n => '$' + Math.round(n).toLocaleString('en-US');

    const cards = [
      { emoji:"📦", title:"Pedidos", desc:"Ver y gestionar todos los pedidos activos", color:"bg-slate-900", badges: [
        totalActive > 0 && { label: totalActive+" activos", color:"bg-slate-700 text-white" },
        totalRework > 0 && { label:"🔄 "+totalRework+" rehacer", color:"bg-rose-500 text-white" },
        totalChat   > 0 && { label:"💬 "+totalChat+" chat", color:"bg-orange-400 text-white" },
      ].filter(Boolean), action: ()=>setShowLobby(false) },
      { emoji:"👥", title:"Clientes", desc:"Fichas de clientes: todos sus pedidos de cada fuente, deuda y pagos", color:"bg-sky-700", badges:[], action: ()=>{ setShowLobby(false); setTimeout(()=>setShowClientes(true),100); } },
      { emoji:"📦", title:"Inventario", desc:"Cuentas y cupos en stock: disponibles, apartadas y vendidas", color:"bg-emerald-700", badges:[], action: ()=>{ setShowLobby(false); setTimeout(()=>setShowInventario(true),100); } },
      { emoji:"🗺️", title:"Mapa Flex", desc:"Busca una ciudad o ZIP y ve las zonas Amazon Flex más cercanas, con millas y ZIP", color:"bg-orange-500", badges:[], action: ()=>{ setShowLobby(false); setTimeout(()=>setShowMapaFlex(true),100); } },
      { emoji:"🚗", title:"VIN", desc:"Decodifica un VIN: marca, modelo, año, validez, recalls y dónde revisar robo o papeles", color:"bg-indigo-600", badges:[], action: ()=>{ setShowLobby(false); setTimeout(()=>setShowVin(true),100); } },
      { emoji:"🔲", title:"Códigos", desc:"Data Matrix y código de barras 128: genera, al azar o a mano, y descarga en PNG", color:"bg-slate-700", badges:[], action: ()=>{ setShowLobby(false); setTimeout(()=>setShowGenerador(true),100); } },
      { emoji:"📥", title:"Bandeja", desc:"Solicitudes nuevas de clientes por revisar", color:"bg-blue-600", badges: [
        inboxCount > 0 && { label: inboxCount+" nuevas", color:"bg-red-500 text-white" },
      ].filter(Boolean), action: ()=>{ setShowLobby(false); setTimeout(()=>setShowInbox(true),100); } },
      { emoji:"💰", title:"Finanzas y cobros", desc:"Cuánto te deben, qué entró y salió, gráficas y a quién cobrar", color:"bg-emerald-600", badges: [
        deudaTotal > 0.01 && { label: "💸 Te deben "+fmtDeuda(deudaTotal), color:"bg-red-500 text-white" },
        porCobrarLobby > 0 && { label: porCobrarLobby+" por cobrar", color:"bg-amber-400 text-black" },
      ].filter(Boolean), action: ()=>{ setShowLobby(false); setTimeout(()=>setShowFinanzasPage(true),100); } },
      { emoji:"🔗", title:"Links", desc:"Generar links de solicitud para clientes", color:"bg-violet-600", badges:[], action: ()=>{ setShowLobby(false); setTimeout(()=>setShowLinks(true),100); } },
      { emoji:"👑", title:"Admin", desc:"Usuarios, contraseñas y configuración", color:"bg-amber-500", badges:[], action: ()=>{ setShowLobby(false); setTimeout(()=>setShowAdmin(true),100); } },
      { emoji:"📦", title:"Archivados", desc:"Pedidos entregados hace más de 6 horas", color:"bg-zinc-600", badges: [
        totalArch > 0 && { label: totalArch+" pedidos", color:"bg-zinc-400 text-white" },
      ].filter(Boolean), action: ()=>{ setShowLobby(false); setTimeout(()=>setShowArchivados(true),100); } },
      { emoji:"🗜️", title:"Optimizar fotos", desc:"Comprime las fotos viejas y pesadas para que carguen rápido", color:"bg-teal-600", badges:[], action: ()=>{ setShowLobby(false); setTimeout(()=>setShowOptimizar(true),100); } },
    ];

    return (
      <div className="min-h-screen bg-slate-100" style={{fontFamily:"'Inter','DM Sans',system-ui,sans-serif"}}>
        <div className="w-full max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10 space-y-6 lg:space-y-8">
          {/* Encabezado: centrado en celular, a lo ancho en PC */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="text-4xl">👑</div>
              <div>
                <div className="text-2xl lg:text-3xl font-black text-slate-900">Royal — Panel Admin</div>
                <div className="text-slate-400 text-sm">Hola {user.nombre}, ¿qué vas a hacer hoy?</div>
              </div>
            </div>
            <div className="self-center flex gap-2"><button onClick={()=>setModoOscuro(v=>!v)} title={modoOscuro?"Modo claro":"Modo oscuro"} className="px-3 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-black active:scale-95 transition-transform">{modoOscuro?"☀️":"🌙"}</button>
            <button onClick={handleLogout} className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-500 text-sm font-bold hover:bg-slate-50 transition-colors">Cerrar sesión</button></div>
          </div>
          {/* Tarjetas: se acomodan solas según el ancho de la pantalla
              (1 columna en celular, 2 en tablet, 3-6 en PC) */}
          <div className="grid gap-4 lg:gap-5" style={{gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 270px), 1fr))"}}>
            {cards.map((card,i) => (
              <motion.button key={i} whileTap={{scale:0.97}} whileHover={{scale:1.02, y:-2}}
                onClick={card.action}
                className={"w-full h-full min-h-[190px] flex flex-col text-left rounded-[24px] p-6 text-white shadow-lg hover:shadow-xl transition-shadow "+card.color}>
                <div className="text-4xl mb-3">{card.emoji}</div>
                <div className="font-black text-xl mb-1">{card.title}</div>
                <div className="text-white/70 text-sm mb-3">{card.desc}</div>
                {card.badges.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {card.badges.map((b,j) => (
                      <span key={j} className={"px-2.5 py-1 rounded-full text-xs font-black "+b.color}>{b.label}</span>
                    ))}
                  </div>
                )}
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" style={{fontFamily:"'Inter','DM Sans',system-ui,sans-serif"}}>
      {/* Aviso cuando la lista de aplazados se reinició sola */}
      <AnimatePresence>
        {rondaReiniciada && (
          <motion.div initial={{opacity:0,y:-20}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-20}}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded-2xl bg-slate-900 text-white shadow-2xl max-w-[92vw]">
            <div className="flex items-center gap-3">
              <span className="text-xl">🔄</span>
              <div className="text-sm leading-tight">
                <div className="font-black">Vuelta nueva</div>
                <div className="text-slate-300">Ya no quedaban pedidos sin aplazar, así que todos volvieron a la cola normal.</div>
              </div>
              <button onClick={()=>setRondaReiniciada(false)} className="ml-1 text-slate-400 hover:text-white font-black text-lg leading-none">×</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>{celebration&&<Celebration onDone={()=>setCelebration(false)}/>}</AnimatePresence>
      <AnimatePresence>{showClientes&&<ClientesPanel isAdmin={user.rol==="admin"} onClose={()=>setShowClientes(false)} onOpenFicha={id=>setFichaId(id)}/>}</AnimatePresence>
      <AnimatePresence>{fichaId&&<ClienteFichaModal clienteId={fichaId} isAdmin={user.rol==="admin"} onClose={()=>setFichaId(null)} onChanged={loadOrders} onOpenPedido={p=>{ setFichaId(null); setShowClientes(false); if (p.esRentaDirecta) setRentasId(p.id); else if (orders.some(o=>o.id===p.id)) setFocusId(p.id); else alert('Ese pedido no está en tu lista. Recarga e intenta de nuevo.'); }}/>}</AnimatePresence>
      <AnimatePresence>{carteraPagoOrder&&<CarteraPagoModal order={carteraPagoOrder} onClose={()=>setCarteraPagoOrder(null)} onSaldado={()=>setCarteraPagoOrder(null)}/>}</AnimatePresence>
      <AnimatePresence>{showInventario&&<InventarioModal onClose={()=>setShowInventario(false)} isAdmin={user.rol==="admin"}/>}</AnimatePresence>
      <AnimatePresence>{showGenerador&&<GeneradorDataMatrix onClose={()=>setShowGenerador(false)}/>}</AnimatePresence>
      <AnimatePresence>{showMapaFlex&&<MapaFlexModal onClose={()=>setShowMapaFlex(false)}/>}</AnimatePresence>
      <AnimatePresence>{showVin&&<VinDecoderModal onClose={()=>setShowVin(false)}/>}</AnimatePresence>
      <AnimatePresence>{cierreCobroOrder&&<CierreCobroModal order={cierreCobroOrder} onClose={()=>setCierreCobroOrder(null)} onDatosCambiados={loadOrders}/>}</AnimatePresence>
      <AnimatePresence>{focusOrder&&<FocusMode onVerCliente={id=>{ setFocusId(null); setFichaId(id); }} order={focusOrder} onClose={()=>setFocusId(null)} onDone={markDone} onRework={sendToRework} onChat={handlers.onChat} onEdit={setEditId} onGarantia={setGarantiaId} onRentas={setRentasId} onPin={handlers.onPin} pinnedId={pinnedId} isAdmin={user.rol==="admin"} onSetStatus={setOrderStatus} navigableOrders={navigableQueue} onNavigate={setFocusId} onCancel={cancelOrder} onTrackLink={handlers.onTrackLink} onRevokeTrackLink={handlers.onRevokeTrackLink} onSetTrackExpiry={handlers.onSetTrackExpiry} onReasign={handlers.onReasign} onRemove={handlers.onRemove} onPostpone={handlers.onPostpone} isPostponed={postponedIds.includes(focusOrder?.id)}/>}</AnimatePresence>
      <AnimatePresence>{showAdmin&&<AdminPanel onClose={()=>setShowAdmin(false)} currentUser={user}/>}</AnimatePresence>
      <AnimatePresence>{showLinks&&<LinksPanel onClose={()=>setShowLinks(false)}/>}</AnimatePresence>
      <AnimatePresence>{rentasId&&<RentasModal order={orders.find(o=>o.id===rentasId)||{id:rentasId,cliente:"",plataforma:""}} onClose={()=>setRentasId(null)} onUpdate={(u)=>setOrders(prev=>prev.map(o=>o.id===rentasId?{...o,...u}:o))}/>}</AnimatePresence>
      <AnimatePresence>{garantiaId&&<GarantiaModal order={orders.find(o=>o.id===garantiaId)||{id:garantiaId}} onClose={()=>setGarantiaId(null)} onSave={(g)=>setOrders(prev=>prev.map(o=>o.id===garantiaId?{...o,...g}:o))}/>}</AnimatePresence>
      <AnimatePresence>{editOrder&&<EditModal order={editOrder} orders={orders} onSave={saveEdit} onClose={()=>setEditId(null)}/>}</AnimatePresence>
      <AnimatePresence>{showInbox&&<InboxPanel onClose={()=>{setShowInbox(false);loadOrders();}} onAccept={(s)=>{ loadOrders(); }} onReject={()=>loadOrders()}/>}</AnimatePresence>
      <AnimatePresence>{showCobros&&<CobrosPanel porCobrar={porCobrar} onClose={()=>setShowCobros(false)} onOpenRentas={id=>{setShowCobros(false);setRentasId(id);}}/>}</AnimatePresence>
      <AnimatePresence>{showArchivados&&<ArchivadosPanel orders={orders.filter(o=>isArchived(o))} onClose={()=>setShowArchivados(false)} onReactivar={reactivarOrder} onReasign={handlers.onReasign}/>}</AnimatePresence>
      <AnimatePresence>{showOptimizar&&(
        <motion.div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.6)"}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>{ if(!optimizando?.corriendo) setShowOptimizar(false); }}>
          <motion.div onClick={e=>e.stopPropagation()} initial={{y:40,opacity:0}} animate={{y:0,opacity:1}} className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-2">🗜️</div>
              <div className="text-xl font-black text-slate-900">Optimizar todas las fotos</div>
              <div className="text-slate-500 text-sm mt-1">Comprime las fotos viejas y pesadas de todos los pedidos para que carguen rápido. No baja las que ya están livianas.</div>
            </div>

            {!optimizando && (
              <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-3 text-amber-800 text-xs leading-relaxed">
                ⚠️ Revisa <b>todos</b> los pedidos y descarga sus fotos, así que puede tardar varios minutos y usar datos. Hazlo una vez, con buena conexión. Puedes seguir usando la app cuando termine.
              </div>
            )}

            {optimizando && (
              <div className="mt-5">
                <div className="flex items-center justify-between text-sm font-bold text-slate-600 mb-1">
                  <span>{optimizando.corriendo ? "Optimizando..." : "¡Listo!"}</span>
                  <span>{optimizando.hecho} / {optimizando.total}</span>
                </div>
                <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-teal-500 transition-all" style={{width: (optimizando.total? Math.round(optimizando.hecho/optimizando.total*100):0)+"%"}}/>
                </div>
                <div className="text-xs text-slate-400 mt-2 text-center">
                  {optimizando.cambiados} pedido{optimizando.cambiados!==1?'s':''} optimizado{optimizando.cambiados!==1?'s':''} · {optimizando.fotosOptim} foto{optimizando.fotosOptim!==1?'s':''} comprimida{optimizando.fotosOptim!==1?'s':''}
                </div>
              </div>
            )}

            <div className="mt-5 flex gap-2">
              {!optimizando?.corriendo && (!optimizando ? (
                <button onClick={optimizarTodasLasFotos} className="flex-1 py-3 rounded-2xl bg-teal-600 text-white font-black active:scale-95 transition-all">Comenzar</button>
              ) : (
                <button onClick={()=>{ setOptimizando(null); setShowOptimizar(false); }} className="flex-1 py-3 rounded-2xl bg-slate-900 text-white font-black active:scale-95 transition-all">Cerrar</button>
              ))}
              {!optimizando?.corriendo && !optimizando && (
                <button onClick={()=>setShowOptimizar(false)} className="px-5 py-3 rounded-2xl bg-slate-100 text-slate-500 font-bold active:scale-95 transition-all">Cancelar</button>
              )}
              {optimizando?.corriendo && (
                <div className="flex-1 py-3 rounded-2xl bg-slate-100 text-slate-400 font-bold text-center text-sm">No cierres esta ventana…</div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>

      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-full mx-auto px-4 py-3 flex items-center gap-3 overflow-x-auto" style={{scrollbarWidth:'none',msOverflowStyle:'none'}}>
          <div className="flex items-center shrink-0 mr-auto">
            <div>
              <div className="text-base font-black text-slate-900">Royal — Pedidos</div>
              <div className="text-xs text-slate-400">👤 {user.nombre} {user.rol==="admin"&&"· 👑 Admin"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden sm:flex px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">{totalActive} activos</span>
            {totalRework>0&&<span className="px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold animate-pulse">🔄 {totalRework}</span>}
            {totalChat>0&&<span className="px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">💬 {totalChat}</span>}
            {porCobrar.length>0&&<button onClick={()=>setShowCobros(true)} className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-black hover:bg-emerald-200 active:scale-95 transition-all">💸 {porCobrar.length} por cobrar</button>}
            {orders.some(o=>isArchived(o))&&<button onClick={()=>setShowArchivados(true)} className="px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-500 text-xs font-bold hover:bg-zinc-200 active:scale-95 transition-all">📦 {orders.filter(o=>isArchived(o)).length}</button>}
            {user.rol==="admin"&&<>
            <button onClick={()=>{ setShowInbox(true); }} className="px-3 py-2 rounded-xl bg-blue-100 text-blue-700 text-sm font-black active:scale-95 transition-transform relative">
              📥{inboxCount>0&&<span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-xs font-black flex items-center justify-center leading-none">{inboxCount}</span>}
            </button>
            <button onClick={()=>setShowFinanzasPage(true)} title="Finanzas y cobros" className="px-3 py-2 rounded-xl bg-green-100 text-green-700 text-sm font-black active:scale-95 transition-transform whitespace-nowrap">💰{deudaTotal > 0.01 && <span className="ml-1.5 text-red-600">{'$'+Math.round(deudaTotal).toLocaleString('en-US')}</span>}</button>
            <button onClick={()=>setShowClientes(true)} title="Clientes" className="px-3 py-2 rounded-xl bg-sky-100 text-sky-700 text-sm font-black active:scale-95 transition-transform">👥</button>

            <button onClick={()=>setShowAdmin(true)} className="px-3 py-2 rounded-xl bg-amber-100 text-amber-700 text-sm font-black active:scale-95 transition-transform">👑</button>
            <button onClick={()=>setShowLinks(true)} className="px-3 py-2 rounded-xl bg-emerald-100 text-emerald-700 text-sm font-black active:scale-95 transition-transform">🔗</button>
          </>}
            <button onClick={()=>toggleSoloHoy(!soloHoy)} title={soloHoy?"Ver todo":"Modo foco — una tarea a la vez"}
              className={"px-3 py-2 rounded-xl text-sm font-black active:scale-95 transition-all "+(soloHoy?"bg-emerald-500 text-white":"bg-emerald-100 text-emerald-700")}>
              🎯
            </button>
            <div className="flex rounded-xl overflow-hidden border-2 border-slate-200">
              <button onClick={()=>setViewMode("grupos")} className={"px-3 py-2 text-sm font-black transition-all "+(viewMode==="grupos"?"bg-slate-900 text-white":"bg-white text-slate-400 hover:bg-slate-50")} title="Vista por plataforma">⊞</button>
              <button onClick={()=>setViewMode("cola")} className={"px-3 py-2 text-sm font-black transition-all "+(viewMode==="cola"?"bg-slate-900 text-white":"bg-white text-slate-400 hover:bg-slate-50")} title="Cola de trabajo">☰</button>
            </div>
            <button onClick={()=>setModoOscuro(v=>!v)} title={modoOscuro?"Modo claro":"Modo oscuro"} className="px-3 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-black active:scale-95 transition-transform">{modoOscuro?"☀️":"🌙"}</button>
            <button onClick={()=>setShowVin(true)} title="Decodificador VIN" className="px-3 py-2 rounded-xl bg-indigo-100 text-indigo-700 text-sm font-black active:scale-95 transition-transform">🚗</button>
            <button onClick={()=>setShowMapaFlex(true)} title="Mapa Amazon Flex" className="px-3 py-2 rounded-xl bg-orange-100 text-orange-700 text-sm font-black active:scale-95 transition-transform">🗺️</button>
            <button onClick={()=>setShowGenerador(true)} title="Generador Data Matrix" className="px-3 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-black active:scale-95 transition-transform">🔲</button>
            <button onClick={()=>setShowInventario(true)} className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-black active:scale-95 transition-transform">📦 Inventario</button>
            <button onClick={()=>setShowAdd(v=>!v)} className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-black active:scale-95 transition-transform">+ Nuevo</button>
            <button onClick={handleLogout} className="px-3 py-2 rounded-xl bg-slate-100 text-slate-500 text-sm font-medium active:scale-95 transition-transform">Salir</button>
            {user.rol==="admin" && <button onClick={()=>setShowLobby(true)} className="px-3 py-2 rounded-xl bg-slate-100 text-slate-500 text-sm font-medium active:scale-95 transition-transform">🏠</button>}
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto p-4 space-y-4">
        {soloHoy ? (
          <SoloHoyMode
            task={nextTask}
            photo={nextTaskPhoto}
            photoCount={nextTaskPhotoCount}
            hechas={hechasHoy}
            totalPendientes={totalActive}
            onStart={setFocusId}
            onSkip={(id)=>setSkippedIds(prev=>[...prev, id])}
            onPostpone={togglePostpone}
            onVerTodo={()=>toggleSoloHoy(false)}
            userName={user.nombre}
          />
        ) : (
        <>
        {/* ── BARRA DE BÚSQUEDA ── */}
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-base">🔍</span>
          <input
            className="w-full rounded-2xl border-2 border-slate-200 bg-white pl-10 pr-4 py-3 text-sm outline-none focus:border-slate-400 font-medium placeholder:text-slate-400 transition-all"
            placeholder="Buscar por nombre o plataforma..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={()=>setSearchQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-lg font-bold">✕</button>
          )}
        </div>

        {/* ── FILTROS RÁPIDOS ── */}
        {(()=>{
          // Cuando hay filtro activo, los contadores reflejan los pedidos ya filtrados
          const base = (filterChip || filterPlat || filterTipo || searchQuery.trim()) ? filteredOrders : orders;
          const urgentes = base.filter(o=>!isArchived(o)&&o.estado!=="Entregado"&&(o.prioridad==="Alta"||(days(o.entrega)!==null&&days(o.entrega)<=1))).length;
          const chips = [
            ...(urgentes>0 ? [{ key:"urgente", label:"🚨 Urgentes", count:urgentes, bg:"bg-red-500", text:"text-white" }] : []),
            ...PRIORIDADES.map(p=>({ key:p, label:p==="Alta"?"🔥 Alta":p==="Media"?"🟡 Media":"🟢 Baja", count:base.filter(o=>o.prioridad===p&&o.estado!=="Entregado"&&!isArchived(o)).length, bg:p==="Alta"?"bg-rose-100":"bg-slate-100", text:p==="Alta"?"text-rose-700":"text-slate-600" })).filter(c=>c.count>0),
            ...ESTADOS.filter(e=>e!=="Entregado"&&e!=="Cancelado").map(e=>{ const sc=SC[e]||{}; return { key:e, label:sc.emoji+" "+e, count:base.filter(o=>o.estado===e&&!isArchived(o)).length, bg:sc.bg||"bg-slate-100", text:sc.text||"text-slate-600" }; }).filter(c=>c.count>0),
          ];
          if(chips.length===0) return null;
          return (
           <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{scrollbarWidth:'none', msOverflowStyle:'none', WebkitOverflowScrolling:'touch'}}>
              {filterChip&&(
                <button onClick={()=>setFilterChip(null)} className="shrink-0 px-3 py-1.5 rounded-full text-xs font-black bg-slate-900 text-white active:scale-95 transition-all">✕ Quitar</button>
              )}
              {chips.map(c=>(
                <button key={c.key} onClick={()=>setFilterChip(filterChip===c.key?null:c.key)}
                  className={"shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all active:scale-95 "+(filterChip===c.key?"ring-2 ring-slate-900 ring-offset-2 scale-105 "+c.bg+" "+c.text:c.bg+" "+c.text)}>
                  {c.label} <span className="bg-black/10 rounded-full px-1.5 py-0.5 font-black">{c.count}</span>
                </button>
              ))}
            </div>
          );
        })()}
        {/* ── FILTRO POR APP ── */}
        {(()=>{
          const platsConPedidos = PLATAFORMAS.filter(p => orders.some(o => o.plataforma === p && !isArchived(o) && o.estado !== "Entregado"));
          if (platsConPedidos.length <= 1) return null;
          return (
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{scrollbarWidth:'none', msOverflowStyle:'none', WebkitOverflowScrolling:'touch'}}>
              {filterPlat && (
                <button onClick={()=>setFilterPlat(null)} className="shrink-0 px-3 py-1.5 rounded-full text-xs font-black bg-slate-900 text-white active:scale-95 transition-all">✕ Todas</button>
              )}
              {platsConPedidos.map(p => {
                const pc = PC[p];
                const count = orders.filter(o => o.plataforma === p && !isArchived(o) && o.estado !== "Entregado").length;
                const active = filterPlat === p;
                return (
                  <button key={p} onClick={()=>setFilterPlat(filterPlat===p?null:p)}
                    className={"shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all active:scale-95 " + (active ? pc.bg + " text-white ring-2 ring-offset-1 ring-slate-900" : pc.light + " " + pc.text)}>
                    {pc.emoji} {p} <span className="bg-black/10 rounded-full px-1.5 py-0.5 font-black">{count}</span>
                  </button>
                );
              })}
            </div>
          );
        })()}
        {/* ── FILTRO POR TIPO (Type del Excel) ── */}
        {(()=>{
          const tiposConPedidos = EXCEL_TYPE.filter(t => orders.some(o => (o.tipoCuenta||"")===t && !isArchived(o) && o.estado!=="Entregado"));
          if (tiposConPedidos.length <= 1) return null;
          return (
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{scrollbarWidth:'none', msOverflowStyle:'none', WebkitOverflowScrolling:'touch'}}>
              {filterTipo && (
                <button onClick={()=>setFilterTipo(null)} className="shrink-0 px-3 py-1.5 rounded-full text-xs font-black bg-slate-900 text-white active:scale-95 transition-all">✕ Todos los tipos</button>
              )}
              {tiposConPedidos.map(t => {
                const count = orders.filter(o => (o.tipoCuenta||"")===t && !isArchived(o) && o.estado!=="Entregado").length;
                const active = filterTipo === t;
                return (
                  <button key={t} onClick={()=>setFilterTipo(filterTipo===t?null:t)}
                    className={"shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all active:scale-95 " + (active ? "bg-indigo-600 text-white ring-2 ring-offset-1 ring-slate-900" : "bg-indigo-50 text-indigo-700")}>
                    🏷️ {t} <span className="bg-black/10 rounded-full px-1.5 py-0.5 font-black">{count}</span>
                  </button>
                );
              })}
            </div>
          );
        })()}
        {/* ── AGRUPAR LA COLA DE TRABAJO (como el inventario) ── */}
        {viewMode==="cola" && (
          <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-2xl px-3 py-2">
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-wide shrink-0">Agrupar por</span>
            {GRUPOS_TRABAJO.map(g=>{
              const activo = colaGroupBy===g.k;
              return (
                <button key={g.k||'none'} onClick={()=>{ setColaGroupBy(g.k); setColaColapsados(new Set()); }}
                  className={"shrink-0 px-3 py-1.5 rounded-full text-xs font-black transition-all active:scale-95 border-2 "+(activo?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-500 border-slate-200 hover:border-slate-400")}>
                  {g.l}
                </button>
              );
            })}
          </div>
        )}
        {nextTask&&(()=>{
          const p=PC[nextTask.plataforma]; const d=days(nextTask.entrega);
          return (
            <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} className="rounded-[32px] overflow-hidden shadow-2xl"
              style={{background:"linear-gradient(135deg,"+p.color+"33 0%,#0f172a 50%)",borderLeft:"6px solid "+p.color}}>
              <div className="bg-slate-900/95 p-6 md:p-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <motion.div animate={{scale:[1,1.2,1]}} transition={{repeat:Infinity,duration:1.5}} className={"w-3 h-3 rounded-full "+(nextTask._aplazadoFallback?"bg-amber-400":"bg-emerald-400")}/>
                    <span className={"text-xs font-black uppercase tracking-widest "+(nextTask._aplazadoFallback?"text-amber-300":"text-emerald-400")}>
                      {nextTask._pinned ? "📌 FIJADA POR ADMIN"
                        : nextTask._aplazadoFallback ? "⌛ SOLO QUEDAN LOS QUE APLAZASTE"
                        : "👇 HAZ ESTO AHORA — no pienses más"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {!nextTask._pinned&&!nextTask._aplazadoFallback&&(
                      <button onClick={()=>setSkippedIds(prev=>[...prev, nextTask.id])}
                        title="Saltar esta tarea y ver la siguiente (vuelve a aparecer luego)"
                        className="px-3 py-1.5 rounded-xl text-xs font-black transition-all active:scale-95 bg-slate-700 text-slate-300 hover:bg-slate-600">
                        ⏭ Siguiente
                      </button>
                    )}
                    {!nextTask._pinned&&!nextTask._aplazadoFallback&&(
                      <button onClick={()=>togglePostpone(nextTask.id)}
                        title="Mandarla al final: no vuelve a aparecer arriba hasta revisar todo lo demás"
                        className="px-3 py-1.5 rounded-xl text-xs font-black transition-all active:scale-95 bg-amber-400/90 text-amber-950 hover:bg-amber-400">
                        ⌛ Al final
                      </button>
                    )}
                    {nextTask._aplazadoFallback&&(
                      <button onClick={()=>togglePostpone(nextTask.id)}
                        title="Quitarla de aplazados y volver a la cola normal"
                        className="px-3 py-1.5 rounded-xl text-xs font-black transition-all active:scale-95 bg-amber-400 text-amber-950 hover:bg-amber-300">
                        ⬆️ Traer de vuelta
                      </button>
                    )}
                    {user.rol==="admin"&&!nextTask._aplazadoFallback&&(
                      <button onClick={()=>setPinnedIdPersist(pinnedId===nextTask.id?null:nextTask.id)}
                        title={pinnedId===nextTask.id?"Quitar fijación":"Fijar esta tarea primero"}
                        className={"px-3 py-1.5 rounded-xl text-xs font-black transition-all active:scale-95 "+(pinnedId===nextTask.id?"bg-amber-400 text-black":"bg-slate-700 text-slate-300 hover:bg-amber-400 hover:text-black")}>
                        {pinnedId===nextTask.id?"📌 Fijada":"📌 Fijar"}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className={"inline-flex items-center gap-2 px-4 py-1.5 rounded-full "+p.bg+" text-white text-sm font-black"}>{p.emoji} {nextTask.plataforma}</div>
                      {(()=>{ const s=SC[nextTask.estado]||{}; return <div className={"inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-black "+s.bg+" "+s.text}>{s.emoji} {nextTask.estado}</div>; })()}
                    </div>
                    <div className="text-5xl md:text-6xl font-black text-white leading-none">{nextTask.cliente}</div>
                    <div><TituloChip order={nextTask} tono="oscuro" className="text-sm px-3 py-1"/></div>
                    <div className="flex flex-wrap gap-2">
                      {nextTask.rehacer&&<motion.span animate={{scale:[1,1.08,1]}} transition={{repeat:Infinity,duration:.8}} className="px-3 py-1.5 rounded-full bg-rose-500 text-white text-sm font-black">🔄 REHACER</motion.span>}
                      {nextTask.chatPendiente&&<span className="px-3 py-1.5 rounded-full bg-orange-500 text-white text-sm font-black animate-pulse">💬 CLIENTE ESPERA</span>}
                      {d!==null&&d<=1&&<motion.span animate={{scale:[1,1.1,1]}} transition={{repeat:Infinity,duration:.7}} className="px-3 py-1.5 rounded-full bg-red-600 text-white text-sm font-black">{d===0?"🚨 HOY":"🚨 MAÑANA"}</motion.span>}
                      {nextTask.prioridad==="Alta"&&!nextTask.rehacer&&<span className="px-3 py-1.5 rounded-full bg-amber-400 text-black text-sm font-black">🔥 ALTA PRIORIDAD</span>}
                    </div>
                    {nextTask.notas&&<div className="text-slate-300 text-sm max-w-md line-clamp-2">{nextTask.notas}</div>}
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {nextTaskPhoto&&(
                      <div className="relative cursor-pointer shrink-0" onClick={()=>setFocusId(nextTask.id)}>
                        <img src={nextTaskPhoto} alt="" className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-2xl border-2 border-white/20 shadow-xl"/>
                        {nextTaskPhotoCount>1&&(
                          <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs font-black px-1.5 py-0.5 rounded-lg leading-tight">+{nextTaskPhotoCount-1}</div>
                        )}
                      </div>
                    )}
                    <motion.button whileHover={{scale:1.03}} whileTap={{scale:.96}} onClick={()=>setFocusId(nextTask.id)}
                      className="shrink-0 px-10 py-6 rounded-2xl text-slate-900 text-2xl font-black shadow-2xl"
                      style={{background:p.color==="#1d1d1d"?"#ffffff":p.color}}>
                      ▶ EMPEZAR →
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })()}

        {!nextTask&&(
          <div className="rounded-[28px] bg-emerald-500 p-8 text-center text-white">
            <div className="text-5xl mb-2">🏆</div><div className="text-3xl font-black">¡Todo al día!</div>
          </div>
        )}

        <AnimatePresence>
          {showAdd&&(
            <motion.div className="fixed inset-0 z-[80] flex flex-col sm:items-center sm:justify-center sm:p-4" style={{background:'rgba(15,23,42,0.55)'}}
              initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              onClick={e=>{if(e.target===e.currentTarget){setShowAdd(false);setAddFotos([]);}}}>
              <motion.div className="w-full h-full sm:h-auto sm:max-w-2xl bg-white sm:rounded-[24px] shadow-2xl flex flex-col overflow-hidden sm:max-h-[92vh]"
                initial={{y:30,opacity:0}} animate={{y:0,opacity:1}} exit={{y:30,opacity:0}} transition={{type:'spring',damping:24}}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                  <div className="text-lg font-black text-slate-900">🆕 Registrar pedido nuevo</div>
                  <button onClick={()=>{setShowAdd(false);setAddFotos([]);}} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold flex items-center justify-center hover:bg-slate-200">✕</button>
                </div>
                <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ClientePicker className="sm:col-span-2" value={form.cliente} clienteId={form.clienteId}
                    onChange={({cliente,clienteId})=>setForm(f=>({...f,cliente,clienteId}))}
                    onPrefill={d=>{ setForm(f=>({...f, telefonoName:f.telefonoName||d.telefonoName, mail:f.mail||d.mail})); setShowCamposExcel(true); }}
                    onVerFicha={id=>setFichaId(id)} orders={orders}/>
                  <TituloPedidoInput className="sm:col-span-2" value={form.titulo} onChange={v=>setForm(f=>({...f,titulo:v}))}
                    cliente={form.cliente} clienteId={form.clienteId} orders={orders} onEstado={setTituloNuevoEstado}/>
                  <select className="rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none font-bold" value={form.plataforma} onChange={e=>setForm({...form,plataforma:e.target.value})}>{PLATAFORMAS.map(p=><option key={p}>{p}</option>)}</select>
                  <select className="rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none" value={form.prioridad} onChange={e=>setForm({...form,prioridad:e.target.value})}>{PRIORIDADES.map(p=><option key={p}>{p}</option>)}</select>
                  <select className="rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none" value={form.estado} onChange={e=>setForm({...form,estado:e.target.value})}>{ESTADOS.map(s=><option key={s}>{s}</option>)}</select>
                  <input type="date" className="rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none" value={form.entrega} onChange={e=>setForm({...form,entrega:e.target.value})}/>
                  {user.rol==="admin"&&usuarios.length>0&&(
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">👤 Asignar a usuario</label>
                      <select className="w-full rounded-xl border-2 border-violet-200 bg-violet-50 px-4 py-3 text-sm outline-none focus:border-violet-400 font-bold text-violet-800" value={form.asignadoA} onChange={e=>setForm({...form,asignadoA:e.target.value})}>
                        <option value="">— Sin asignar (pedido general) —</option>
                        {usuarios.map(u=><option key={u.id} value={u.id}>{u.nombre} (@{u.username})</option>)}
                      </select>
                    </div>
                  )}
                  <div className="sm:col-span-2 flex gap-6">
                    <label className="flex items-center gap-2 text-sm cursor-pointer font-medium"><input type="checkbox" className="w-4 h-4" checked={form.chatPendiente} onChange={e=>setForm({...form,chatPendiente:e.target.checked})}/>💬 Chat pendiente</label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer font-medium"><input type="checkbox" className="w-4 h-4" checked={form.rehacer} onChange={e=>setForm({...form,rehacer:e.target.checked})}/>🔄 Rehacer</label>
                  </div>
                  <textarea className="sm:col-span-2 rounded-xl border-2 border-slate-200 px-4 py-3 text-sm outline-none resize-none" rows={2} placeholder="Notas..." value={form.notas} onChange={e=>setForm({...form,notas:e.target.value})}/>
                  <div className="sm:col-span-2">
                    <div className="text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wide">📷 Fotos de la orden</div>
                    <PhotoUpload fotos={addFotos} onChange={setAddFotosUpdater}/>
                  </div>

                  {/* ── CAMPOS DEL EXCEL (plegable) ── */}
                  <div className="sm:col-span-2">
                    <button type="button" onClick={()=>setShowCamposExcel(v=>!v)}
                      className="w-full flex items-center justify-between rounded-xl bg-emerald-50 border-2 border-emerald-200 px-4 py-3 text-emerald-800 font-bold text-sm hover:bg-emerald-100 transition-all">
                      <span>📊 Datos para el Excel {showCamposExcel ? "(ocultar)" : "(opcional)"}</span>
                      <span>{showCamposExcel ? "▲" : "▼"}</span>
                    </button>
                    {showCamposExcel && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                        {[
                          {k:"mail",label:"Mail",type:"email"},
                          {k:"telefonoName",label:"Teléfono o Name"},
                          {k:"dato",label:"Dato"},
                          {k:"dl",label:"DL (licencia)"},
                          {k:"dob",label:"DOB (nacimiento)"},
                          {k:"ssn",label:"SSN"},
                          // Desplegables iguales a la validación del Google Sheet:
                          {k:"tipoCuenta",label:"Type",options:EXCEL_TYPE},
                          {k:"creditoDebito",label:"Crédito/Débito",options:EXCEL_CREDITO_DEBITO},
                          // Precio y Abonado enlazados: el Saldo se calcula solo.
                          {k:"precio",label:"Precio ($)",type:"number",num:true},
                          {k:"abonado",label:"Abonado ($)",type:"number",num:true},
                          {k:"saldo",label:"Saldo (auto: Precio − Abonado)",type:"number",readOnly:true},
                          {k:"pagoHasta",label:"Pago hasta",type:"date"},
                          {k:"documentNumber",label:"Document Number"},
                          {k:"report",label:"Report"},
                          {k:"whyDeactivated",label:"Why deactivated"},
                          {k:"infoDatoFinal",label:"Info dato final"},
                        ].map(f=>(
                          <div key={f.k} className={f.k==="infoDatoFinal"||f.k==="whyDeactivated"?"sm:col-span-2":""}>
                            <label className="text-xs font-bold text-slate-400 mb-1 block">{f.label}</label>
                            {f.options ? (
                              <select className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-400 bg-white"
                                value={form[f.k]} onChange={e=>setForm({...form,[f.k]:e.target.value})}>
                                <option value="">— Sin definir —</option>
                                {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                                {form[f.k] && !f.options.includes(form[f.k]) && <option value={form[f.k]}>{form[f.k]} (valor viejo)</option>}
                              </select>
                            ) : (
                              <input type={f.type||"text"} readOnly={!!f.readOnly}
                                className={"w-full rounded-lg border-2 px-3 py-2 text-sm outline-none "+(f.readOnly?"border-slate-200 bg-slate-100 text-slate-500 font-bold":"border-slate-200 focus:border-emerald-400")}
                                value={form[f.k]}
                                onChange={e=>{
                                  const v = e.target.value;
                                  if (!f.num) { setForm({...form,[f.k]:v}); return; }
                                  setForm(p=>{
                                    const nf = {...p, [f.k]: v};
                                    const pr = parseFloat(nf.precio);
                                    const ab = parseFloat(nf.abonado)||0;
                                    nf.saldo = isNaN(pr) ? "" : String(Math.max(0, +(pr-ab).toFixed(2)));
                                    return nf;
                                  });
                                }}/>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2 flex justify-end gap-3">
                    <button onClick={()=>{setShowAdd(false);setAddFotos([]);}} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 active:scale-95 transition-all">Cancelar</button>
                    <button onClick={addOrder} disabled={tituloNuevoEstado==='ocupado'} className="px-5 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 active:scale-95 transition-all disabled:opacity-40">Guardar pedido</button>
                  </div>
                </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {viewMode==="cola" ? (()=>{
          // Estados que van al fondo de la cola (en pausa). Los terminales
          // (NOT APPROVED, DESACTIVADA, etc.) ya salen por isArchived.
          const pausados = new Set(["Esperando Cliente","Background Check","Intento Fallido"]);
          const cola = [...filteredOrders]
            .filter(o=> searchQuery.trim() ? true : (o.estado!=="Entregado"&&!isArchived(o)))
            .sort((a,b)=>{
              // Orden por "tier": normales arriba, aplazados después, pausados al fondo
              const tier = x => pausados.has(x.estado) ? 2 : (postponedIds.includes(x.id) ? 1 : 0);
              const tA = tier(a), tB = tier(b);
              if(tA !== tB) return tA - tB;
              // El fijado (pin) manda dentro de su grupo
              if(pinnedId&&a.id===pinnedId) return -1;
              if(pinnedId&&b.id===pinnedId) return 1;
              // Urgencia real por fecha de entrega (+ prioridad). Al mover la
              // fecha, este puntaje cambia y el pedido se reordena solo.
              const uA = urgencyScore(a), uB = urgencyScore(b);
              if (uA !== uB) return uA - uB;
              // Empate: el más antiguo primero
              const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
              const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
              return timeA - timeB;
            });
          if(cola.length===0) return null;
          // Índice global por urgencia (para numeración y resaltar "lo siguiente"),
          // aunque la cola se muestre agrupada. El orden de la cola no cambia.
          const idx = new Map(cola.map((o,k)=>[o.id,k]));
          const groupMeta = GRUPOS_TRABAJO.find(g=>g.k===colaGroupBy);
          const groupEmoji = (key) => {
            if(colaGroupBy==="plataforma")     return (PC[key]||{}).emoji||"📱";
            if(colaGroupBy==="estado")         return (SC[key]||{}).emoji||"•";
            if(colaGroupBy==="prioridad")      return key==="Alta"?"🔥":key==="Media"?"🟡":"🟢";
            if(colaGroupBy==="tipoCuenta")     return "🏷️";
            if(colaGroupBy==="asignadoNombre") return "👤";
            return "•";
          };
          // Grupos: se ordenan por su miembro más urgente (encuentro en la cola)
          const secciones = (()=>{
            if(!colaGroupBy) return [{key:null, rows:cola}];
            const map=new Map();
            cola.forEach(o=>{ const g=String(o[colaGroupBy]||"").trim()||"— Sin dato —"; if(!map.has(g))map.set(g,[]); map.get(g).push(o); });
            return [...map.entries()].map(([key,rows])=>({key,rows}));
          })();
          const toggleGrupo = g => setColaColapsados(prev=>{ const n=new Set(prev); n.has(g)?n.delete(g):n.add(g); return n; });
          // Una fila de la cola (reutilizable dentro y fuera de los grupos)
          const renderRow = (o)=>{
                const i=idx.get(o.id);
                const p=PC[o.plataforma]; const s=SC[o.estado]||{}; const d=days(o.entrega);
                const isPausado=pausados.has(o.estado);
                const aplazado=postponedIds.includes(o.id);
                const dim=isPausado||aplazado;
                const isNext=i===0&&!isPausado&&!aplazado; const isPinned=pinnedId===o.id;
                return (
                  <motion.div key={o.id} layout initial={{opacity:0,x:-10}} animate={{opacity:dim?0.8:1,x:0}} transition={{delay:i*0.03}}
                    className={"flex items-center gap-3 rounded-2xl px-4 py-3 border-2 "+(isNext?"bg-white border-emerald-400 shadow-md":isPinned?"bg-white border-amber-400":aplazado&&!isPausado?"bg-amber-50/60 border-amber-200":dim?"bg-slate-50 border-slate-200":"bg-white border-slate-100")}>
                    <div className={"w-8 h-8 rounded-xl flex items-center justify-center text-sm font-black shrink-0 "+(isNext?"bg-emerald-500 text-white":isPinned?"bg-amber-400 text-black":aplazado&&!isPausado?"bg-amber-200 text-amber-700":dim?"bg-slate-200 text-slate-400":"bg-slate-100 text-slate-400")}>
                      {isPinned?"📌":aplazado&&!isPausado?"⌛":isPausado?(s.emoji||"⏸"):isNext?"▶":i+1}
                    </div>
                    <div className={"w-1.5 h-10 rounded-full shrink-0"} style={{background:dim?"#cbd5e1":p.color}}/>
                    <ColaThumb orderId={o.id}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={"font-black truncate "+(dim?"text-slate-400":"text-slate-900")}>{o.cliente}</span>
                        <TituloChip order={o} className={dim?"opacity-60":""}/>
                        {aplazado&&!isPausado&&<span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">⌛ Al final</span>}
                        {o.rehacer&&<span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">🔄</span>}
                        {o.chatPendiente&&<span className="px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold animate-pulse">💬</span>}
                        {d!==null&&d<=1&&o.estado!=="Terminado"&&!dim&&<span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{d===0?"🚨Hoy":"🚨Mañana"}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-400">{p.emoji} {o.plataforma}</span>
                        <StatusSelector order={o} onCycle={handlers.onCycle} isAdmin={user.rol==="admin"} onSetStatus={handlers.onSetStatus}/>
                        {o.prioridad==="Alta"&&!dim&&<span className="text-xs text-red-500 font-bold">🔥 Alta</span>}
                      </div>
                    </div>
                    <button onClick={()=>handlers.onPostpone(o.id)}
                      title={aplazado?"Traer de vuelta a la cola":"Mandar al final — lo revisas después"}
                      className={"shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-base active:scale-95 transition-all "+(aplazado?"bg-amber-100 text-amber-700 hover:bg-amber-200":"bg-slate-100 text-slate-400 hover:bg-slate-200")}>
                      {aplazado?"⬆️":"⤵️"}
                    </button>
                    <button onClick={()=>handlers.onFocus(o.id)}
                      className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm active:scale-95 transition-all"
                      style={isPausado
                        ? (o.estado==="Intento Fallido"
                            ? {background:"#fee2e2", color:"#ef4444"}
                            : o.estado==="Background Check"
                              ? {background:"#e0e7ff", color:"#6366f1"}
                              : {background:"#fef3c7", color:"#d97706"})
                        : {background:(p.color==="#1d1d1d"?"#334155":p.color), color:"#fff"}}>
                      {isPausado?(s.emoji||"▶"):"▶"}
                    </button>
                  </motion.div>
                );
          };
          return (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1 pb-1">
                <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Cola de trabajo</span>
                <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold">{cola.length}</span>
                {colaGroupBy && <span className="text-xs text-slate-400 font-bold">· por {groupMeta?.l} ({secciones.length})</span>}
              </div>
              {secciones.map(sec=>{
                if(sec.key===null) return sec.rows.map(renderRow);
                const cerrado = colaColapsados.has(sec.key);
                return (
                  <React.Fragment key={"sec-"+sec.key}>
                    <div onClick={()=>toggleGrupo(sec.key)} className="flex items-center gap-2 mt-3 mb-1 px-1 cursor-pointer select-none">
                      <span className="text-slate-400 text-xs w-3">{cerrado?"▸":"▾"}</span>
                      <span className="text-sm font-black text-slate-700 truncate">{groupEmoji(sec.key)} {sec.key}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold">{sec.rows.length}</span>
                    </div>
                    {!cerrado && sec.rows.map(renderRow)}
                  </React.Fragment>
                );
              })}
            </div>
          );
        })() : (
        <div className="space-y-3">
          {(()=>{
            // Rota el orden cada día para que no siempre aparezca Amazon arriba
            const dayOffset = new Date().getDay(); // 0-6
            const rotated = [...PLATAFORMAS.slice(dayOffset % PLATAFORMAS.length), ...PLATAFORMAS.slice(0, dayOffset % PLATAFORMAS.length)];
            // Primero las que tienen pedidos activos, respetando rotación
            const conActivos = rotated.filter(p=>(grouped[p]||[]).some(o=>o.estado!=="Entregado"&&o.estado!=="Intento Fallido"&&o.estado!=="Esperando Cliente"&&o.estado!=="Background Check"&&!isArchived(o)));
            const sinActivos = rotated.filter(p=>!conActivos.includes(p));
            return [...conActivos,...sinActivos].map(p=><Group key={p} plataforma={p} orders={grouped[p]||[]} handlers={handlers} pinnedId={pinnedId} isAdmin={user.rol==="admin"} nextTaskId={nextTask?.id} forceShowArchived={!!searchQuery.trim()}/>);
          })()}
        </div>
        )}

        <div className="rounded-[20px] bg-slate-900 text-white p-5 text-center">
          <div className="font-black text-base">"Una cosa a la vez. La que está arriba. Nada más."</div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
